/* =================================================================
   JV Proposal Builder -- JavaScript
   Transplanted from GAS ProposalBuilder.html
   All google.script.run calls converted to jvBridge.callAdmin()
   ================================================================= */

/* =================================================================
   STATE
   ================================================================= */
var state = {
  brides: [],
  // Empty-state confusion fix (2026-08-10, BRIEFS/HANDOFF-proposal-builder-empty-
  // state-confusion-2026-08-10.md): 'No brides match this filter' at renderBrides()
  // used to fire on filtered.length === 0 alone, which cannot tell a genuine zero-
  // match filter apart from a data load that never actually delivered anything (an
  // empty/stale cache island, a live fetch that failed after retry). Confirmed: the
  // cache-paint path can legitimately store { ok:true, brides:[] } and still marks
  // paintedFromCache = true, which then suppresses the real error state on a
  // subsequent live failure too. 'live' only flips true once proposalGetPendingBrides
  // has actually resolved with a payload (never for the cache paint, which is a
  // provisional first-paint guess, not authoritative). See loadBrides().
  bridesLoadState: 'pending', // 'pending' | 'cache' | 'live' | 'failed'
  currentBride: null,
  workspace: null,
  activeStage: 'enquiry',
  activeFilter: 'all',     // default to All (every active enquiry, needs-action first)
  activeSortOrder: 'action-priority',
  editedLineItems: null,   // legacy, kept for backward compat; new code uses priceOverrides
  priceOverrides: {},      // sparse map: { '<uuid>': { rate?, qty? } } survives recalcs
  lineItemsManuallyEdited: false,
  requestToken: 0,
  bridesRequestToken: 0,
  clientSearchResults: [],
  manualPartyMembers: [],  // P.4: ad-hoc party members added via UI
  noTrialRequired: false,  // when true, trial line items are excluded from pricing
  brideWithMe: false,      // back-compat: derived from bookingTier === 'jessica'
  // JESS RULING 2026-11-03: an unknown booking tier defaults to proTeam, never jessica.
  // This initialiser was ALREADY internally inconsistent before the change: brideWithMe above
  // is false (which is what actually prices the screen, via jvBridePricing_) while this said
  // 'jessica' (which is only the label and what gets put in payloads). So a fresh builder showed
  // proTeam money under a jessica label. Both now say proTeam.
  // KEEP THESE TWO IN STEP at every site that sets either one. brideWithMe is the pricing input;
  // bookingTier is the label and the payload field. Changing one alone is how the screen and the
  // server end up quoting a bride two different numbers.
  bookingTier: 'proTeam',  // 'jessica' | 'proTeam'
  bookingTierLockedAt: '', // ISO timestamp set on send; non-empty = tier read-only
  travelOriginOverride: null, // null = use global default, 'current', or 'future' (per-quote)
  travelOriginLockedAt: '',   // ISO timestamp set on send; non-empty = travel origin read-only
  travelOriginGlobalDefault: 'current', // 'current' or 'future', read-only mirror of the Jess-set default, loaded once at init
  travelOriginFutureAddressSet: false, // whether Jess has a future address on file at all; gates whether the Travel Origin card renders
  assignedArtists: [],     // [{ artistName, suburb, roundTripKm, fee, manualAdded }]
  juniorBridesmaids: 0,    // subset of bridesmaid count (age 10-14); 25%-off-base, max 2
  proposalHistory: [],     // previously-sent versions, loaded on bride select
  travelSuppressions: {},  // sticky per-line travel delete: { '<travelKey>': true }. Survives
                           // recalcs + round-trips to GAS so a removed travel line stays gone
                           // until restored. See CLAUDE.md / travel-fee-delete-bug-2026-06-17.
  ssotEnabled: false,      // SSOT (Phase 3): mirror of the backend JV_PROPOSAL_SSOT_ENABLED
                           // flag, surfaced via the proposalGetWorkspace response. When true,
                           // the SSOT frontend paths (applyCalcResponse_, server-recalc tier
                           // flip) run; when false the legacy paths run, byte-identical.
  correctableArtistCount: false // RATCHET FIX (2026-07-22): mirror of the backend
                           // JV_PROPOSAL_ARTIST_COUNT_CORRECTABLE flag, surfaced via the
                           // proposalGetWorkspace response (correctableArtistCount). When
                           // false (default), recalcAndMerge_'s artist-count resolve
                           // reproduces the old monotonic-up behaviour byte-for-byte.
};

// Booking-tier boundary-normalize (FIX proposal-tier-naming-collision 2026-07-15).
// The cross-system canonical tier form is snake_case ('jessica' / 'pro_team'), which is
// what BookingTier.gs and the SSOT persist store on WRITE. This builder speaks camelCase
// 'proTeam' and compares with `=== 'proTeam'`. With the SSOT flag ON, a server calc can
// echo bookingTier / availability.pricingTier as 'pro_team', which our load readers
// (applyCalcResponse_, applyWorkspaceCalculation_, the initial load reader) would fail to
// match, silently resetting the toggle back to jessica. Coerce a stored canonical value
// to builder-native at the point a server calc lands (belt-and-braces mirror of the GAS
// read-boundary fix in proposalBuilder_normalizeCalcJson_). Mutates and returns the object.
// 'jessica' is identical in both forms (pass-through).
function normalizeCalcTier_(calc) {
  if (!calc || typeof calc !== 'object') return calc;
  if (calc.bookingTier === 'pro_team') calc.bookingTier = 'proTeam';
  if (calc.availability && calc.availability.pricingTier === 'pro_team') {
    calc.availability.pricingTier = 'proTeam';
  }
  return calc;
}

// Initial load always opens on the All filter, sorted by state.activeSortOrder's
// default ('action-priority' -- needs-action first, Jess decision 2026-07-27,
// commit 907a5b3b). jv_proposal_sort in localStorage is write-only (nothing reads
// it back on load) so there is no saved-preference override to apply here.

var STAGES = [
  {
    id: 'enquiry',
    count: '01',
    name: 'Enquiry Details',
    note: 'Review the bride\'s enquiry data before building the proposal.'
  },
  {
    id: 'builder',
    count: '02',
    name: 'Package Builder',
    note: 'Edit line items, pricing, and travel to finalise the proposal.'
  },
  {
    id: 'review',
    count: '03',
    name: 'Review',
    note: 'Preview the proposal and check the bride\'s view before sending.'
  },
  {
    id: 'send',
    count: '04',
    name: 'Send',
    note: 'Confirm and send the proposal to the bride.'
  }
];

/* =================================================================
   LOCAL PRICING ENGINE
   Mirrors proposalBackfill_localEstimate_ in GAS/ProposalBackfill.gs.
   Gives instant line-item renders before the GAS/JV-AUTO round-trip
   returns. GAS response always reconciles afterward.
   ================================================================= */
// Bride pricing splits: "with team" = Jessica not attending; "with me" = Jessica leads the bride chair
// CANONICAL SOURCE: JV AUTO Config.gs PRICING.bride (jessica/proTeam). These literals MUST
// match it exactly, and the CRM copy in ProposalBuilderAPI.gs proposalRecalculate_localPricing_.
// Jessica-tier Hair Only / Makeup Only = 225 (P1b, 2026-06-03).
var BRIDE_PRICING_TEAM_ = { 'Hair & Makeup': 325, 'Hair Only': 195, 'Makeup Only': 195 };
var BRIDE_PRICING_ME_   = { 'Hair & Makeup': 350, 'Hair Only': 225, 'Makeup Only': 225 };

var BASELINE_PRICING_ = {
  bride:      BRIDE_PRICING_TEAM_,  // default; overridden at runtime by state.brideWithMe
  trial:      { 'Hair & Makeup': 295, 'Hair Only': 185, 'Makeup Only': 195 },
  bridesmaid: { 'Hair & Makeup': 295, 'Hair Only': 195, 'Makeup Only': 195 },
  mother:     { 'Hair & Makeup': 285, 'Hair Only': 185, 'Makeup Only': 185 },
  guest:      { 'Hair & Makeup': 285, 'Hair Only': 185, 'Makeup Only': 185 },
  flowergirl: { 'Hair & Makeup':  35, 'Hair Only':  35, 'Makeup Only':  35 }
};

function localPricingCategory_(role) {
  // 'mother' MUST be checked before 'bride' -- "Mother of Bride" / "Mother of
  // the Bride" contains the substring "bride", so a bride-first check
  // silently mis-categorises every Mother/Guest party member as the BRIDE
  // herself (wrong price, wrong label, confirmed to persist through Save --
  // BRIEFS/RESULTS-COWORK-wedding-year-pricing-e2e-grade3-audit-2026-08-25.md
  // Finding 1). Matches the already-correct GAS-side cat_() in
  // ProposalBuilderAPI.gs, which has carried this exact ordering + comment
  // since before this client-side copy was written -- this file simply never
  // got the same fix.
  var r = String(role || '').toLowerCase();
  if (r.indexOf('bridesmaid') >= 0) return 'bridesmaid';
  if (r.indexOf('mother')     >= 0) return 'mother';
  if (r.indexOf('bride')      >= 0) return 'bride';
  if (r.indexOf('flower')     >= 0) return 'flowergirl';
  return 'guest';
}

/* =================================================================
   PHASE 3: single-source pricing (Shopify engine "(c)").
   When the shop metafield custom.pricing_source_shopify is on, the section
   embeds window.JV_PROPOSAL_PRICING = { enabled:true, rows:[{id,price}] } (one
   row per ACTIVE service metaobject, the same _ServiceConfig store the GAS
   engines read). These resolvers reshape those rows into the builder card shape
   and prefer it over the hardcoded BASELINE_/BRIDE_PRICING_ literals.
   Flag off (default) gives the literals (zero behaviour change).
   Decision 5 (no silent stale literal): if the flag is on but the embedded card
   is incomplete we disable the embed, console.error + banner loudly, and the
   keeper rolls back. We never quietly serve a half source / half literal quote.
   Golden parity is proven cent for cent by the CRM-side JV_pricingParity_run().
   ================================================================= */
var _jvEmbeddedCard = undefined; // undefined=not built, null=unavailable, object=card

function jvBuildEmbeddedCard_(rows) {
  var svcName = { hm: 'Hair & Makeup', hair: 'Hair Only', makeup: 'Makeup Only' };
  function tail(id) { return String(id).split('.').pop(); }
  function sv(id) { return svcName[tail(id)] || null; }
  var card = { bride: { jessica: {}, proTeam: {} }, trial: {}, bridesmaid: {}, mother: {}, guest: {}, flowergirl: {} };
  var fg = null;
  (rows || []).forEach(function (r) {
    var id = String(r.id), p = Number(r.price) || 0, s = sv(id);
    if (id.indexOf('bride.jessica.') === 0 && s) card.bride.jessica[s] = p;
    else if (id.indexOf('bride.proteam.') === 0 && s) card.bride.proTeam[s] = p;
    else if (id.indexOf('trial.') === 0 && s) card.trial[s] = p;
    else if (id.indexOf('bridesmaid.') === 0 && s) card.bridesmaid[s] = p;
    else if (id.indexOf('motherguest.above_collar.') === 0) { /* dead key in calc, ignore */ }
    else if (id.indexOf('motherguest.') === 0 && s) { card.mother[s] = p; card.guest[s] = p; }
    else if (id === 'flowergirl.makeover') fg = p;
  });
  if (fg != null) card.flowergirl = { 'Hair & Makeup': fg, 'Hair Only': fg, 'Makeup Only': fg };
  // Completeness check: every key the builder reads must be a number.
  var svcs = ['Hair & Makeup', 'Hair Only', 'Makeup Only'], gaps = [];
  function chk(label, m) { svcs.forEach(function (s) { if (typeof m[s] !== 'number') gaps.push(label + '.' + s); }); }
  chk('bride.jessica', card.bride.jessica); chk('bride.proTeam', card.bride.proTeam);
  chk('trial', card.trial); chk('bridesmaid', card.bridesmaid);
  chk('mother', card.mother); chk('guest', card.guest); chk('flowergirl', card.flowergirl);
  if (gaps.length) { card._gaps = gaps; }
  return card;
}

function jvEmbeddedCard_() {
  if (_jvEmbeddedCard !== undefined) return _jvEmbeddedCard;
  _jvEmbeddedCard = null;
  try {
    var src = (typeof window !== 'undefined') && window.JV_PROPOSAL_PRICING;
    if (src && src.enabled === true && src.rows && src.rows.length) {
      var card = jvBuildEmbeddedCard_(src.rows);
      if (card._gaps) {
        if (typeof showBanner === 'function') showBanner('Pricing source incomplete, using built-in rates. Tell Jess to fix _ServiceConfig.', 'error');
        try { console.error('[jv-pricing] embedded _ServiceConfig card incomplete, reverting to literals. Missing:', card._gaps); } catch (e) {}
        _jvEmbeddedCard = null;
      } else {
        _jvEmbeddedCard = card;
      }
    }
  } catch (e) { try { console.error('[jv-pricing] embedded card build failed:', e); } catch (e2) {} _jvEmbeddedCard = null; }
  return _jvEmbeddedCard;
}

/* =================================================================
   WEDDING-YEAR D1 PRICING (2026-08-24) -- instant-estimate overlay
   BRIEFS/BUILD-proposal-builder-agent-role1-wedding-year-pricing-2026-08-24.md
   calcLocalEstimate_ is an INSTANT client-side guess shown before the GAS/JV-AUTO
   round-trip returns; that round-trip ALWAYS reconciles the numbers afterward (see
   the LOCAL PRICING ENGINE header comment above). Per Jess's decision this session,
   the instant estimate should be correct from FIRST PAINT for a wedding-year quote
   too, not just after reconciliation -- so this fetches the D1 client-rate-card for
   the bride's wedding year and overlays it onto the same card shape
   jvBridePricing_/jvPartyPricing_ already read (bride/trial/bridesmaid; D1 does not
   cover mother/guest/flowergirl -- those keep reading the embedded/literal card
   untouched, same "partial swap" scope as the two GAS engines).

   TRANSPORT: this Worker (jv-next-pilot) returns NO Access-Control-Allow-Origin
   header on its public workers.dev URL (confirmed live 2026-08-24 -- a plain
   cross-origin fetch() from jessicavegas.com would be blocked by CORS). The
   existing CORS relay Worker (broken-truth-efdd.calm-hill-fd5b.workers.dev,
   DEPOLYMENT/cloudflare-cors-relay/worker.js, role 8's file) already forwards
   several routes to jv-next-pilot via a same-origin-safe server-side service
   binding (env.JV_PILOT) -- see its GET /team/jobs branch for the established
   pattern. This function calls the SAME relay at a new `/pricing/client-rate-card`
   path, matching that existing proxy convention. THE RELAY-SIDE ROUTE DOES NOT
   EXIST YET -- adding it is role 8's file, flagged to them, not built here (see
   this brief's result file). Until it lands, every call below fails closed to the
   embedded/literal card (never blocks the instant estimate, never throws).
   ================================================================= */
var _jvD1YearCache = {}; // weddingYear -> { bride:{}, trial:{}, bridesmaid:{} } | null (fetch failed/pending)
var _jvD1FetchInFlight = {}; // weddingYear -> true while a fetch is in flight

function jvD1RelayBaseUrl_() {
  return (window.jvBridgeConfig && window.jvBridgeConfig.gasUrl) ||
    'https://broken-truth-efdd.calm-hill-fd5b.workers.dev';
}

function jvD1ServiceTypeLabel_(suffix) {
  if (suffix === 'hair') return 'Hair Only';
  if (suffix === 'makeup') return 'Makeup Only';
  if (suffix === 'hm') return 'Hair & Makeup';
  return null;
}

/* Kick off (once per year, de-duplicated) a background fetch of the D1 rate card
   for a wedding year. Never blocks the caller -- the instant estimate renders
   with whatever's cached NOW (embedded/literal card on first call for a year),
   then re-renders itself once the fetch resolves via the caller's own re-render
   trigger (renderStageBuilder, already called on every workspace load/edit). */
function jvD1EnsureYearFetched_(weddingYear) {
  if (!weddingYear || _jvD1YearCache[weddingYear] !== undefined || _jvD1FetchInFlight[weddingYear]) return;
  _jvD1FetchInFlight[weddingYear] = true;
  var nativeFetch = (window._jvNativeFetch || window.fetch).bind(window);
  var url = jvD1RelayBaseUrl_() + '/pricing/client-rate-card?year=' + encodeURIComponent(weddingYear);
  nativeFetch(url, { method: 'GET' })
    .then(function (resp) { return resp.ok ? resp.json() : null; })
    .then(function (body) {
      _jvD1FetchInFlight[weddingYear] = false;
      if (!body || body.ok !== true || !Array.isArray(body.rates)) {
        _jvD1YearCache[weddingYear] = null; // fetch failed/relay route not live yet -- fall back silently
        return;
      }
      var card = { brideJessica: {}, bridePro: {}, trial: {}, bridesmaid: {}, mother: {} };
      body.rates.forEach(function (r) {
        if (!r || r.found !== true) return;
        var key = String(r.serviceKey || '');
        var cents = Number(r.priceIncGstCents);
        if (!key || isNaN(cents)) return;
        var dollars = cents / 100;
        if (key.indexOf('bride.') === 0) {
          // Bride tier split (2026-08-25): bride.* rows carry a .jessica/.pro_team
          // suffix -- no longer one shared price for both tiers.
          if (key.slice(-8) === '.jessica') {
            var lj = jvD1ServiceTypeLabel_(key.slice('bride.'.length, key.length - 8));
            if (lj) card.brideJessica[lj] = dollars;
          } else if (key.slice(-9) === '.pro_team') {
            var lp = jvD1ServiceTypeLabel_(key.slice('bride.'.length, key.length - 9));
            if (lp) card.bridePro[lp] = dollars;
          }
        } else if (key.indexOf('trial.') === 0) {
          var l2 = jvD1ServiceTypeLabel_(key.split('.')[1]);
          if (l2) card.trial[l2] = dollars;
        } else if (key.indexOf('party.') === 0) {
          var l3 = jvD1ServiceTypeLabel_(key.split('.')[1]);
          if (l3) card.bridesmaid[l3] = dollars;
        } else if (key.indexOf('mother.') === 0) {
          // Mother/Guest (2026-08-25): D1-covered as of this date, tier-
          // independent like trial/party -- only bride.* is tier-split.
          var l4 = jvD1ServiceTypeLabel_(key.split('.')[1]);
          if (l4) card.mother[l4] = dollars;
        }
      });
      // Only cache a genuinely complete card -- a partial response (relay mid-
      // rollout, D1 seed gap) falls back to the embedded/literal card entirely
      // rather than silently mixing D1 prices for some services and old prices
      // for others within the same instant estimate.
      var svcs = ['Hair & Makeup', 'Hair Only', 'Makeup Only'];
      var complete = svcs.every(function (s) { return typeof card.brideJessica[s] === 'number'; })
        && svcs.every(function (s) { return typeof card.bridePro[s] === 'number'; })
        && svcs.every(function (s) { return typeof card.trial[s] === 'number'; })
        && svcs.every(function (s) { return typeof card.bridesmaid[s] === 'number'; })
        && svcs.every(function (s) { return typeof card.mother[s] === 'number'; });
      _jvD1YearCache[weddingYear] = complete ? card : null;
      // Re-render so the now-resolved D1 prices replace the placeholder estimate,
      // same convention as the existing async Studio Schedule card fetch above.
      // GUARD (2026-08-25, Check 1's intermittent "Add to Party" click bug in
      // BRIEFS/RESULTS-COWORK-pricing-fixes-live-verify-2026-08-25-part2.md):
      // renderStageBuilder() replaces the ENTIRE #workspace innerHTML,
      // including the open "Add party member" <details> panel and whatever
      // the admin has already typed into it. This fetch resolves on a totally
      // async timer with no relation to what the admin is doing -- if it
      // lands in the split second between filling the form and clicking "Add
      // to Party," the button the click lands on can be torn out of the DOM
      // mid-click, swallowing the click with zero console/network signal
      // (confirmed intermittent, reproduces on some attempts and not others,
      // exactly matching this race). Skip this specific background re-render
      // while that panel is open and mid-fill -- the D1 prices will still
      // apply on the NEXT render this function already triggers elsewhere
      // (adding the member calls recalcAndMerge_ + renderStageBuilder via
      // addManualPartyMember), so nothing is lost, only deferred past the
      // admin's current interaction.
      var _addMemberPanelOpen = document.getElementById('addMemberPanel');
      var _addMemberMidFill = _addMemberPanelOpen && _addMemberPanelOpen.open && (
        (document.getElementById('manualMemberName') && document.getElementById('manualMemberName').value)
        || document.activeElement === document.getElementById('manualMemberName')
        || document.activeElement === document.getElementById('manualMemberRole')
      );
      if (complete && state.workspace && !_addMemberMidFill && typeof renderStageBuilder === 'function') {
        try { renderStageBuilder(); } catch (e) {}
      }
    })
    .catch(function () {
      _jvD1FetchInFlight[weddingYear] = false;
      _jvD1YearCache[weddingYear] = null;
    });
}

/* =================================================================
   SERVICE SSoT READER CUTOVER (2026-10-16, issue #2a Slice 3)
   Shadow-mode scaffolding for /services/catalog?include=proposal on
   jv-crm-mirror. Mode read from window.JV_SERVICE_SSOT_READER_MODE
   (emitted by the section Liquid from the wrangler var):
     'off'/undefined — do nothing (default).
     'shadow'        — fetch catalog in parallel with legacy, log diff
                       once per session, STILL USE legacy result.
     'primary'       — fetch catalog, use it as the pricing source
                       (legacy = fallback-on-error). NOT WIRED THIS SLICE
                       in the pricing path — only the diff logger is
                       wired. Primary cutover is a dedicated follow-up
                       after shadow-mode diff proves parity.
   Same transport convention as the D1 wedding-year rate card above:
   routes through the cors-relay (`broken-truth-efdd...workers.dev`)
   same-origin-safely. The relay-side `/services/catalog` forwarder
   does not exist yet — flagged as owed in the handoff. Until it
   lands every call below fails closed to legacy (never blocks render,
   never throws).
   ================================================================= */
var _jvSsotCatalog = null;           // last fetch result (array of services) | null
var _jvSsotFetchInFlight = false;
var _jvSsotDiffLogged = false;       // once-per-session guard — log a short summary

function jvSsotReaderMode_() {
  try {
    var m = String(window.JV_SERVICE_SSOT_READER_MODE || '').toLowerCase();
    if (m === 'shadow' || m === 'primary') return m;
  } catch (e) {}
  return 'off';
}

/* Slice 6 (2026-10-16, issue #2a) — proposal-builder-specific reader mode.
   SEPARATE from jvSsotReaderMode_() above (which the admin-invoicing surface
   also observes) so the proposal path can flip to 'primary' independently
   of admin invoicing, which stays on its own shadow soak. Read from
   window.JV_PROPOSAL_SSOT_READER_MODE (emitted by the section Liquid).
   Values: 'off' | 'shadow' | 'primary'. Default 'off'. 'shadow' falls back
   to legacy pricing (parity with Slice 3). 'primary' makes CF catalog
   unitPriceDollars authoritative in calcLocalEstimate_; legacy pricing
   remains fallback-on-error ONLY (missing catalog, missing tier row,
   non-finite price). Fail-closed: any catalog shortfall drops back to
   legacy for THAT tier, never throws, never blocks render. */
function jvProposalSsotReaderMode_() {
  try {
    var m = String(window.JV_PROPOSAL_SSOT_READER_MODE || '').toLowerCase();
    if (m === 'shadow' || m === 'primary') return m;
  } catch (e) {}
  return 'off';
}

/* Resolve the CF catalog's `proposal.unitPriceDollars` for a given
   (bucket, performedByNorm, serviceLabel) key. Returns a finite Number or
   null. Pure read, no side effects. Catalog not yet fetched -> null -> caller
   falls back to legacy. */
function jvProposalSsotPrice_(bucket, performedByNorm, serviceLabel) {
  if (!_jvSsotCatalog || !_jvSsotCatalog.length) return null;
  var svcSuffix = serviceLabel === 'Hair & Makeup' ? 'hm'
                : serviceLabel === 'Hair Only'     ? 'hair'
                : serviceLabel === 'Makeup Only'   ? 'makeup' : null;
  if (!svcSuffix) return null;
  var wantTier = bucket + '_' + svcSuffix;
  for (var i = 0; i < _jvSsotCatalog.length; i++) {
    var s = _jvSsotCatalog[i];
    if (!s || !s.proposal) continue;
    if (s.proposal.tierKey !== wantTier) continue;
    if (bucket === 'bride' && performedByNorm &&
        String(s.proposal.performedByNorm || '').toLowerCase() !== String(performedByNorm).toLowerCase()) continue;
    var p = Number(s.proposal.unitPriceDollars);
    return isFinite(p) ? p : null;
  }
  return null;
}

function jvSsotEnsureFetched_() {
  if (jvSsotReaderMode_() === 'off') return;
  if (_jvSsotCatalog !== null || _jvSsotFetchInFlight) return;
  _jvSsotFetchInFlight = true;
  var nativeFetch = (window._jvNativeFetch || window.fetch).bind(window);
  var url = jvD1RelayBaseUrl_() + '/services/catalog?include=proposal';
  nativeFetch(url, { method: 'GET' })
    .then(function (resp) { return resp.ok ? resp.json() : null; })
    .then(function (body) {
      _jvSsotFetchInFlight = false;
      if (!body || body.ok !== true || !Array.isArray(body.services)) {
        _jvSsotCatalog = []; // fail closed — legacy still runs
        return;
      }
      _jvSsotCatalog = body.services;
    })
    .catch(function () { _jvSsotFetchInFlight = false; _jvSsotCatalog = []; });
}

/* Shadow diff logger. Called once per session from calcLocalEstimate_ after
   it has produced its legacy result. Compares the legacy per-tier unit
   prices against the CF catalog's `proposal.unitPriceDollars` for the same
   tierKey + performedByNorm. Never throws, never blocks render, never
   mutates state. Produces one `[jv-ssot-shadow]` console.info summary and
   bails. */
function jvSsotShadowDiff_(legacyBride, legacyParty) {
  if (jvSsotReaderMode_() !== 'shadow') return;
  if (_jvSsotDiffLogged) return;
  jvSsotEnsureFetched_();
  if (!_jvSsotCatalog || !_jvSsotCatalog.length) return;
  _jvSsotDiffLogged = true;
  try {
    var diffs = [];
    _jvSsotCatalog.forEach(function (s) {
      if (!s.proposal || !s.proposal.tierKey) return;
      var parts = s.proposal.tierKey.split('_');
      var bucket = parts[0], svcSuffix = parts[1];
      var legacyMap;
      if (bucket === 'bride') {
        legacyMap = s.proposal.performedByNorm === 'jessica' ? (legacyBride.jessica || legacyBride) : (legacyBride.proTeam || legacyBride);
      } else if (legacyParty && legacyParty[bucket]) {
        legacyMap = legacyParty[bucket];
      } else return;
      var label = svcSuffix === 'hm' ? 'Hair & Makeup' : svcSuffix === 'hair' ? 'Hair Only' : svcSuffix === 'makeup' ? 'Makeup Only' : null;
      if (!label || !legacyMap || legacyMap[label] == null) return;
      var legacyPrice = Number(legacyMap[label]);
      var cfPrice = Number(s.proposal.unitPriceDollars);
      if (!isFinite(legacyPrice) || !isFinite(cfPrice)) return;
      if (Math.abs(legacyPrice - cfPrice) > 0.5) {
        diffs.push({ tier: s.proposal.tierKey, performedBy: s.proposal.performedByNorm, legacy: legacyPrice, cf: cfPrice });
      }
    });
    if (diffs.length) {
      try { console.info('[jv-ssot-shadow] catalog diffs (count=' + diffs.length + '):', diffs); } catch (e) {}
    } else {
      try { console.info('[jv-ssot-shadow] parity: legacy == CF for all overlapping tiers'); } catch (e) {}
    }

    // PERSIST (2026-10-26, Issue #2a): same once-per-session summary row the backend's own
    // header comment expects (service-catalog-shadow-log.mjs), via the relay's
    // POST /services/catalog/shadow-log proxy (services-catalog-proxy.mjs). Both the relay
    // flag (RELAY_SERVICES_CATALOG_SHADOW_LOG_ENABLED) and jv-crm-mirror's own
    // (SERVICE_CATALOG_SHADOW_LOG_ENABLED) default OFF, so this is a safe no-op fire-and-forget
    // until both are flipped -- matches the existing _pqNf precedent elsewhere in this file.
    try {
      var _ssotNf = (window._jvNativeFetch || window.fetch).bind(window);
      var _ssotEmail = (state.workspace && state.workspace.bride && state.workspace.bride.email) || '';
      var _ssotBrideName = (state.workspace && state.workspace.bride && state.workspace.bride.name) || '';
      var _ssotFirst = diffs[0] || {};
      _ssotNf(jvD1RelayBaseUrl_() + '/services/catalog/shadow-log', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          email: _ssotEmail,
          brideName: _ssotBrideName,
          diffCount: diffs.length,
          tierKey: _ssotFirst.tier,
          performedBy: _ssotFirst.performedBy,
          legacyPrice: _ssotFirst.legacy,
          cfPrice: _ssotFirst.cf
        })
      }).catch(function (e) {
        try { console.log('[jv-ssot-shadow] shadow-log write failed: ' + (e && e.message)); } catch (e2) {}
      });
    } catch (eLog) { /* never let the persistence write affect anything */ }
  } catch (e) { try { console.warn('[jv-ssot-shadow] diff failed:', e); } catch (e2) {} }
}

/* Wedding year from the current workspace's bride, or null if no date is set. */
function jvCurrentWeddingYear_() {
  var iso = state.workspace && state.workspace.bride && state.workspace.bride.weddingDate;
  if (!iso) return null;
  var m = String(iso).match(/^(\d{4})-\d{2}-\d{2}/);
  return m ? Number(m[1]) : null;
}

/* FIX (2026-09-04, frozen-pricing display bug, HANDOFF-proposal-builder-agent-
   frozen-pricing-display-and-payto-copy-2026-09-04.md): client-side mirror of
   proposalBuilder_shouldUseFrozenPricingForStatus_ / ...ForRow_ (ProposalBuilderAPI.gs).
   Once a proposal has been quoted (sent, edited-after-send, or accepted), the
   price the bride was originally shown must never silently change just because
   she's viewed again during the current wedding year's D1 rate window -- this is
   the SAME "proposals, once sent, must maintain the pricing they were originally
   quoted at" rule (Jess, 2026-07-27) the GAS side already enforces on every
   recalculate. Before this fix, jvBridePricing_/jvPartyPricing_ pulled live D1
   wedding-year rates with ZERO awareness of this, so simply opening an
   already-sent proposal for a routine edit (artist count, timing, add-ons) could
   silently reprice the whole card mid-session against current rates, not the
   frozen ones -- confirmed live via a real bride's PROPOSAL_CALC_JSON showing a
   FROZEN_QUOTE_NO_SNAPSHOT flag the server-side gate had already set, while the
   client-side estimate had no matching awareness at all.
   Mirrors the GAS status set exactly (SENT / DRAFT_EDIT / ACCEPTED), with the
   same sent-date secondary evidence GAS uses for a row that's temporarily
   presenting as READY during an edit flow. */
function jvIsProposalFrozen_() {
  var proposal = state.workspace && state.workspace.proposal;
  if (!proposal) return false;
  var status = String(proposal.status || '').trim().toUpperCase();
  // SIGNED added 2026-09-08, same fix as proposalBuilder_shouldUseFrozenPricingForStatus_
  // (ProposalBuilderAPI.gs) -- the real signing writer stamps PROPOSAL_STATUS
  // literally as 'Signed', which this check never recognised before.
  if (status === 'SENT' || status === 'DRAFT_EDIT' || status === 'ACCEPTED' || status === 'SIGNED') return true;
  return !!(proposal.sentDate || proposal.quoteSentAtDate);
}

/* Bride rate map for the active tier. Priority: D1 wedding-year card (if fetched
   and complete for this year) > embedded _ServiceConfig card > hardcoded literals.
   BRIDE TIER SPLIT (2026-08-25, reverses the note this used to carry): D1 now
   carries a SEPARATE price per tier (bride.hm.jessica vs bride.hm.pro_team) --
   jessica and proTeam read their own D1 row, matching the GAS engines' 2026-08-25
   fix.
   Frozen quotes skip the D1 lookup entirely (see jvIsProposalFrozen_ above) --
   falls straight through to the embedded/literal card, same fallback order the
   server-side gate already uses when no pricing snapshot exists. */
function jvBridePricing_(brideWithMe) {
  var year = jvIsProposalFrozen_() ? null : jvCurrentWeddingYear_();
  var legacy;
  if (year) {
    jvD1EnsureYearFetched_(year); // fire-and-forget; re-renders on resolve
    var d1 = _jvD1YearCache[year];
    if (d1) {
      var d1Tier = brideWithMe ? d1.brideJessica : d1.bridePro;
      if (d1Tier) legacy = d1Tier;
    }
  }
  if (!legacy) {
    var card = jvEmbeddedCard_();
    legacy = card ? (brideWithMe ? card.bride.jessica : card.bride.proTeam)
                  : (brideWithMe ? BRIDE_PRICING_ME_ : BRIDE_PRICING_TEAM_);
  }
  // Slice 6 (2026-10-16, issue #2a): CF-primary overlay for proposal reader.
  // Frozen quotes NEVER re-price (same rule the D1 branch above already
  // follows), so skip the overlay entirely. Non-primary -> legacy wins.
  if (jvIsProposalFrozen_()) return legacy;
  if (jvProposalSsotReaderMode_() !== 'primary') return legacy;
  jvSsotEnsureFetched_();
  var performedByNorm = brideWithMe ? 'jessica' : 'pro_team';
  var out = { 'Hair & Makeup': legacy['Hair & Makeup'], 'Hair Only': legacy['Hair Only'], 'Makeup Only': legacy['Makeup Only'] };
  ['Hair & Makeup', 'Hair Only', 'Makeup Only'].forEach(function (label) {
    var cf = jvProposalSsotPrice_('bride', performedByNorm, label);
    if (cf != null) out[label] = cf;
  });
  return out;
}

/* Party-category rate map (trial/bridesmaid/mother/guest/flowergirl). D1 only
   covers trial + bridesmaid ("party" in D1's vocabulary) -- mother/guest/
   flowergirl always fall through to the embedded/literal card, per the "partial
   swap" scope decision (D1 has no above-collar/mother-vs-guest distinction).
   Frozen quotes skip the D1 lookup entirely -- see jvIsProposalFrozen_ above. */
function jvPartyPricing_(cat) {
  var year = jvIsProposalFrozen_() ? null : jvCurrentWeddingYear_();
  var legacy;
  if (year && (cat === 'trial' || cat === 'bridesmaid')) {
    jvD1EnsureYearFetched_(year);
    var d1 = _jvD1YearCache[year];
    if (d1 && d1[cat]) legacy = d1[cat];
  }
  // Mother/Guest (2026-08-25): D1-covered as of this date, one shared 'mother'
  // bucket in the cache -- both cat values ('mother' and 'guest', see
  // localPricingCategory_) read the SAME D1 row, matching how the CRM/JV AUTO
  // engines alias mother/guest to one price.
  if (!legacy && year && (cat === 'mother' || cat === 'guest')) {
    jvD1EnsureYearFetched_(year);
    var d1m = _jvD1YearCache[year];
    if (d1m && d1m.mother) legacy = d1m.mother;
  }
  if (!legacy) {
    var card = jvEmbeddedCard_();
    legacy = card ? (card[cat] || card.guest)
                  : (BASELINE_PRICING_[cat] || BASELINE_PRICING_.guest);
  }
  // Slice 6 (2026-10-16, issue #2a): CF-primary overlay. Frozen quotes skip.
  // Only buckets the CF catalog actually serves are overlaid; missing tier
  // in catalog -> legacy wins for that label (fail-closed per-label).
  if (jvIsProposalFrozen_()) return legacy;
  if (jvProposalSsotReaderMode_() !== 'primary') return legacy;
  jvSsotEnsureFetched_();
  var out = { 'Hair & Makeup': legacy['Hair & Makeup'], 'Hair Only': legacy['Hair Only'], 'Makeup Only': legacy['Makeup Only'] };
  ['Hair & Makeup', 'Hair Only', 'Makeup Only'].forEach(function (label) {
    var cf = jvProposalSsotPrice_(cat, null, label);
    if (cf != null) out[label] = cf;
  });
  return out;
}

function normaliseServiceType_(svc) {
  var s = String(svc || '');
  if (s === 'Hair Only' || s === 'Makeup Only') return s;
  return 'Hair & Makeup';
}

function manualAdditionServiceType_(services) {
  var h = services && services.indexOf('Hair')   >= 0;
  var m = services && services.indexOf('Makeup') >= 0;
  if (h && m) return 'Hair & Makeup';
  if (m) return 'Makeup Only';
  return 'Hair Only';
}

// Resolve a manual member's service type: prefer the explicit serviceType field
// (set by the per-card selector) and fall back to the legacy services[] array so
// pre-existing members still price correctly.
function manualMemberSvc_(m) {
  if (m && m.serviceType) return normaliseServiceType_(m.serviceType);
  return manualAdditionServiceType_((m && m.services) || []);
}
// Service type -> the legacy services[] array, so server sites that still read
// services[] (timing, runsheet reconciliation) stay in sync with the selector.
function manualSvcToServicesArr_(svc) {
  if (svc === 'Hair Only')   return ['Hair'];
  if (svc === 'Makeup Only') return ['Makeup'];
  return ['Hair', 'Makeup'];
}

/* -- Travel line suppression (per-line sticky delete) ------------------------
   Travel fee lines are calc-derived (one per artist, plus trial / spray-tan), so
   they do NOT live in editedLineItems and the trash button can't remove them the
   normal way. Instead each travel line is identified by a stable key (its
   description with the volatile "(123km return)" / "[manual override]" / "*estimate"
   suffixes stripped) and recorded in state.travelSuppressions. All travel emit /
   preserve paths skip suppressed keys; Restore clears the key and the structured
   travel arrays (calc.weddingTravel / trialTravel / sprayTanTravel) rebuild the line.
   GAS-side parity (CRM + JV AUTO) is folded in by the frozen-per-version-links build. */
// The calc engines separate travel descriptions with an em dash (U+2014), e.g.
// "Wedding Day Travel <dash> Sarah". Built via fromCharCode so the source carries no
// literal em dash (project rule) while reconstructed keys still match engine output.
/* BUG FIX (2026-09-15, BUG-trial-travel-LABEL-still-renders-for-tbc-2026-09-15.md):
   GAS suppresses the trial travel FEE for in_studio/tbc, but this file rendered the
   LABEL regardless -- it had zero awareness the suppression existed, so a TBC trial
   still showed "Trial Travel: Jessica Vegas". Reads calc.trialLocationMode, the same
   authoritative field GAS uses -- deliberately NOT a second source of truth.
   Jess's wording, verbatim, do not paraphrase or shorten. */
var TRIAL_TRAVEL_TBC_LABEL_ = 'Travel fee to be confirmed upon trial location confirmation';
function trialTravelModeSuppressed_(calc) {
  var m = calc && calc.trialLocationMode;
  return m === 'in_studio' || m === 'tbc';
}

var _TRAVEL_DASH = ': '; // P3 (2026-07-09): colon separator, not an em dash (Locked Rule 2)
// STABLE travel-line identity (2026-06-22 travel-delete fix): keyed on travel
// type + REAL artist, never the (anonymisable) description. Matches the GAS
// proposalBuilder_travelId_ so a delete keyed here is honoured server-side too.
function travelId_(type, artist) {
  var a = String(artist || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return String(type || 'wedding') + ':' + (a || 'artist');
}
function travelLineKey_(item) {
  if (!item) return null;
  var cat  = String(item.category || '').toLowerCase();
  var desc = String(item.description || item.name || '');
  if (cat.indexOf('travel') < 0 && desc.toLowerCase().indexOf('travel') < 0) return null;
  // Stable id first; legacy description-key only for rows that predate the stamps.
  if (item._travelId) return String(item._travelId);
  if (item._travelType) return travelId_(item._travelType, item._travelArtist);
  return desc.toLowerCase()
    .split('(')[0]
    .replace(/\[.*?\]/g, '')
    .replace(/\*estimate/g, '')
    .trim();
}
function isTravelSuppressed_(item) {
  var key = travelLineKey_(item);
  return !!(key && state.travelSuppressions && state.travelSuppressions[key]);
}

// Visible wedding-day travel: the canonical weddingTravel array with deleted
// (suppressed) artists removed and capped to the artist count. Single source for
// EVERY wedding-travel visual (artist chips, the travel breakdown rows, the assigned
// artists panel) so they all agree with the line items and with "N artists required".
// The underlying array stays full so raising the count or restoring a line rebuilds it.
function proposalVisibleWeddingTravel_(calc) {
  var wt = (calc && (calc.weddingTravel || (calc.travel && calc.travel.weddingTravel))) || [];
  var sup = state.travelSuppressions || {};
  var cap = Number(calc && calc.artistsRequired) || 0;
  var out = [];
  for (var i = 0; i < wt.length; i++) {
    var t = wt[i];
    if (!t) continue;
    if (sup[travelId_('wedding', t.artist || 'Artist')]) continue;
    if (cap > 0 && out.length >= cap) break;
    out.push(t);
  }
  return out;
}

// A1 2026-11-02 zero-total defect fix (BRIEFS/SCOPE-sub-b1-a1-zero-total-render-
// snapshot-fix-2026-11-02.md): when D1 brides.proposal_calc_json is empty but an
// ACTIVE proposal_pricing_snapshots row holds real line items (the bride-facing
// token data), the admin builder previously rendered A$0.00 / zero items because
// state.workspace.calculation was null and nothing filled the gap. This renders
// FROM the snapshot in read-mostly mode -- never edits, never flushes back.
// Flag-gated, default OFF (window.JV_PB_SNAPSHOT_FALLBACK_ENABLED unset = falsy).
// A populated calc ALWAYS wins -- see hasCalc check below, independent of flag.
function shouldUseSnapshotFallback_() {
  var ws = state.workspace || {};
  var calc = ws.calculation;
  var hasCalc = !!(calc && typeof calc === 'object' &&
                   Array.isArray(calc.lineItems) && calc.lineItems.length > 0);
  if (hasCalc) return false; // populated calc wins ALWAYS, no fallback
  var p = ws.proposal || {};
  var tok = p.tokenData || {};
  if (!Array.isArray(tok.lineItems) || tok.lineItems.length === 0) return false;
  if (p.tokenStatus !== 'ACTIVE') return false; // not VIEWED/SIGNED/EXPIRED/VOID
  return !!window.JV_PB_SNAPSHOT_FALLBACK_ENABLED; // flag-gated
}

// Builds a minimal, read-only calc shape from the sent snapshot's tokenData.
// The _fromSnapshot:true marker lets flushToGas_() and every save/recalc path
// refuse to persist this synthetic calc back to GAS/D1 -- see flushToGasInner_.
function buildCalcFromSnapshot_(proposal) {
  proposal = proposal || {};
  var tokenData = proposal.tokenData || {};
  var lineItems = Array.isArray(tokenData.lineItems) ? tokenData.lineItems : [];
  var subtotal = proposal.tokenSubtotal;
  var gst = proposal.tokenGst;
  var total = proposal.tokenTotal;

  if (subtotal == null || gst == null || total == null) {
    var lineSubtotal = lineItems.reduce(function(sum, item) {
      return sum + Number(item && item.total || 0);
    }, 0);
    if (subtotal == null) subtotal = lineSubtotal;
    if (gst == null) gst = Math.round(lineSubtotal * 0.10 * 100) / 100;
    if (total == null) total = Number(subtotal) + Number(gst);
  }

  return {
    lineItems: lineItems,
    subtotal: Number(subtotal),
    gst: Number(gst),
    total: Number(total),
    _fromSnapshot: true
  };
}

// Inserts the read-only "rendering from snapshot" notice above the line-items
// panel and disables the Recalculate / Preview / Send controls, when the
// current workspace is being shown via the snapshot-fallback path. Safe no-op
// when the fallback isn't active (flag OFF, or a real calc/non-eligible token).
// Is the workspace CURRENTLY showing a fallback-rendered calc (regardless of whether
// shouldUseSnapshotFallback_() would still say "build one now")? Checks the calc's own
// _fromSnapshot marker first -- set once by buildCalcFromSnapshot_ and never cleared
// except by Reactivate Proposal -- falling back to a fresh eligibility check only for
// the rare path where this runs before the load-time trigger (ff9d2fb48) has populated
// the calc yet. Needed because shouldUseSnapshotFallback_()'s own hasCalc check (by
// design) returns false once lineItems.length > 0 -- which becomes true the moment the
// fallback has actually done its job, silently hiding the warning/disabled state right
// when it most needs to stay visible (2026-11-02 regression, caught in the 3rd re-verify).
function pbSnapshotFallbackActive_() {
  var calc = state.workspace && state.workspace.calculation;
  if (calc && calc._fromSnapshot) return true;
  return shouldUseSnapshotFallback_();
}

function pbSnapshotFallbackNoticeHtml_() {
  if (!pbSnapshotFallbackActive_()) return '';
  return '<div class="card" style="background:var(--brand-bg-soft);border:1px solid var(--brand-primary);' +
    'border-radius:10px;padding:12px 14px;margin-bottom:14px;font-size:13px;color:var(--brand-dark)">' +
    '<i class="fa-solid fa-circle-info"></i> Rendering from sent snapshot (ACTIVE token). ' +
    'Edits are disabled; click <strong>Reactivate Proposal</strong> to resume editing.' +
    '</div>';
}

function pbApplySnapshotFallbackUiState_() {
  if (!pbSnapshotFallbackActive_()) return;
  ['recalcBtn', 'generatePreviewBtn', 'refreshPreviewBtn', 'approveBtn'].forEach(function(id) {
    var el = document.getElementById(id);
    if (el) {
      el.disabled = true;
      el.setAttribute('data-snapshot-fallback-disabled', 'true');
    }
  });
}

function calcLocalEstimate_(party, manualAdditions) {
  party           = party           || [];
  manualAdditions = manualAdditions || [];

  var lineItems      = [];
  var subtotal       = 0;
  var fullPriced     = 0;
  var hmCount        = 0;   // Hair & Makeup attendees (cap 3 per artist)
  var soloCount      = 0;   // Hair Only + Makeup Only attendees (cap 8 per artist)
  var flowergirlRows = [];

  // Junior bridesmaid discount: subset of the bridesmaid count, 25% off BASE rate,
  // max 2 per wedding (upgrades stay full price). Parity with GAS
  // proposalRecalculate_localPricing_ and JV AUTO ProposalCalculator.gs STEP 3.
  var JR_PCT = 25, JR_MAX = 2;
  var juniorsRemaining = Math.min(Math.max(0, Number(state.juniorBridesmaids) || 0), JR_MAX);

  // SINGLE-SOURCE TRAVEL (2026-06-23): build ALL travel line items from the structured
  // arrays (weddingTravel / trialTravel / sprayTanTravel) ONLY. Never harvest travel
  // from the displayed line items (which may be anonymised or unstamped). This gives
  // one line per artist (no double render) and a stable stamp on every line so a
  // delete (suppression) sticks by id. The bride viewer anonymises separately. _rowId
  // plus any manual fee override are carried from the prior travel line of the same
  // stable id, so a hand-typed travel fee survives recalc. Wedding lines are capped to
  // the artist count in recalcAndMerge_ (after the override is settled).
  var existingCalc  = state.workspace && state.workspace.calculation;
  // A1 2026-11-02 zero-total defect fix: when the real calc is empty/null but an
  // ACTIVE snapshot has real line items, render from the snapshot instead of
  // falling through to an empty render. See shouldUseSnapshotFallback_ above.
  if (shouldUseSnapshotFallback_()) {
    existingCalc = buildCalcFromSnapshot_(state.workspace.proposal);
  }
  // ROOT CAUSE FIX (2026-11-02, snapshot-fallback E2E verify FAIL): everything below this
  // block rebuilds lineItems from state.workspace.party / manualAdditions -- the CURRENT
  // enquiry composition -- and only ever reads existingCalc for travel-line id matching.
  // For a snapshot-fallback calc (built from the ACTIVE token, no live party composition
  // behind it), that left lineItems/subtotal/total at zero even though the snapshot had
  // real priced items, because nothing downstream of buildCalcFromSnapshot_ ever returned
  // its lineItems. Fallback is read-only by design (_fromSnapshot:true, never flushed to
  // GAS/D1 -- see flushToGasInner_), so short-circuit here and hand back exactly what was
  // sent, never recompute it from today's party/pricing.
  if (existingCalc && existingCalc._fromSnapshot) {
    return {
      lineItems:           existingCalc.lineItems,
      normalizedLineItems: existingCalc.lineItems,
      subtotal:            existingCalc.subtotal,
      gst:                 existingCalc.gst,
      total:               existingCalc.total,
      artistsRequired:     Math.max(1, Number(existingCalc.artistsRequired) || 1),
      travelBreakdown:     existingCalc.travelBreakdown || [],
      noTrialRequired:     state.noTrialRequired,
      source:              'snapshot_fallback',
      _fromSnapshot:       true
    };
  }
  var travelItems   = [];
  if (existingCalc) {
    var _prevItems = (state.editedLineItems) ||
                     existingCalc.normalizedLineItems || existingCalc.lineItems || [];
    var _prevTravelById = {};
    _prevItems.forEach(function(li) {
      var d = String(li.description || li.name || '').toLowerCase();
      var c = String(li.category || '').toLowerCase();
      if (c.indexOf('travel') < 0 && d.indexOf('travel') < 0) return;
      var k = travelLineKey_(li);
      if (k) _prevTravelById[k] = li;
    });
    var _emitTravel = function(type, artist, label, fee, rTripKm) {
      if (!(fee > 0)) return;
      var id = travelId_(type, artist || 'Artist');
      if (state.travelSuppressions && state.travelSuppressions[id]) return; // deleted, stays gone
      var km = rTripKm ? Math.round(rTripKm) : 0;
      var line = { description: label + (km ? ' (' + km + 'km return)' : ''),
                   unitPrice: fee, qty: 1, total: fee, category: 'travel', taxable: true,
                   _travelType: type, _travelArtist: (artist || 'Artist'), _travelId: id };
      var prev = _prevTravelById[id];
      if (prev) {
        if (prev._rowId) line._rowId = prev._rowId;
        var pTot = Number(prev.total != null ? prev.total : ((prev.qty || 1) * (prev.unitPrice || prev.rate || 0)));
        if (prev._userEdited || (pTot >= 0 && Math.abs(pTot - fee) > 0.5)) {
          line.unitPrice = Number(prev.unitPrice != null ? prev.unitPrice : prev.rate);
          if (isNaN(line.unitPrice)) line.unitPrice = fee;
          line.qty = Number(prev.qty != null ? prev.qty : 1);
          line.total = pTot;
          line._userEdited = !!prev._userEdited;
        }
      }
      travelItems.push(line);
    };
    // BUG FIX (2026-09-15, BUG-tbc-trial-still-charges-travel-fee-2026-09-15.md): Jess's
    // settled rule -- charge trial travel only when the trial is at the bride's home.
    // in_studio/tbc suppress, in_home/unset charges. Previously only the artist-keyed
    // state.travelSuppressions map (checked inside _emitTravel) gated this line, which
    // is fragile (its id can drift from the id computed here if the assigned artist
    // changes or no trial travel existed yet when In Studio/TBC was clicked). Read
    // trialLocationMode directly, in addition to the suppression map, mirroring the
    // same fix applied to the GAS engines (ProposalBuilderAPI.gs
    // proposalBuilder_rebuildTravelLineItems_ / proposalRecalculate_localPricing_ /
    // proposalBuilder_preserveTravelSlices_) so all three calc engines agree.
    var _ttModeSuppressedLocal2 = (existingCalc.trialLocationMode === 'in_studio' ||
      existingCalc.trialLocationMode === 'tbc');
    if (!state.noTrialRequired && !_ttModeSuppressedLocal2) {
      var _tt = existingCalc.trialTravel || (existingCalc.travel && existingCalc.travel.trialTravel);
      if (_tt) _emitTravel('trial', trialTravelModeSuppressed_(existingCalc) ? '' : (_tt.artist || 'Artist'), trialTravelModeSuppressed_(existingCalc) ? TRIAL_TRAVEL_TBC_LABEL_ : ('Trial Travel' + _TRAVEL_DASH + (_tt.artist || 'Artist')), _tt.fee, _tt.roundTripKm);
    }
    var _wt = existingCalc.weddingTravel || (existingCalc.travel && existingCalc.travel.weddingTravel) || [];
    _wt.forEach(function(t) { _emitTravel('wedding', t.artist || 'Artist', 'Wedding Day Travel' + _TRAVEL_DASH + (t.artist || 'Artist'), t.fee, t.roundTripKm); });
    var _stt = existingCalc.sprayTanTravel || (existingCalc.travel && existingCalc.travel.sprayTanTravel);
    if (_stt) _emitTravel('spray', _stt.artist || 'Artist', 'Spray Tan Travel' + _TRAVEL_DASH + (_stt.artist || 'Artist'), _stt.fee, _stt.roundTripKm || 0);
  }

  // Build line items from party composition
  party.forEach(function(p) {
    var cat  = localPricingCategory_(p.role);
    var svc  = normaliseServiceType_(p.serviceType);
    var qty  = Math.max(0, p.qty !== undefined ? Number(p.qty) : 1);
    if (qty === 0) return;

    var bridePricing = jvBridePricing_(state.brideWithMe);
    var unitPrice = cat === 'bride' ? bridePricing[svc] : jvPartyPricing_(cat)[svc];

    if (cat === 'bride') {
      // Bride service line — append tier label so description matches viewer/email
      var brideTotal = unitPrice * qty;
      var brideTierLabel = state.brideWithMe ? 'with Jessica Vegas' : 'with JV Pro Team';
      lineItems.push({ description: 'Bride ' + svc + (qty > 1 ? ' x' + qty : '') + ' (' + brideTierLabel + ')', unitPrice: unitPrice, qty: qty, total: brideTotal, category: 'bride' });
      subtotal += brideTotal;
      fullPriced += qty;
      if (svc === 'Hair Only' || svc === 'Makeup Only') soloCount += qty; else hmCount += qty;
      // Trial (always included with bride, priced separately)
      if (!state.noTrialRequired) {
        var trialPrice = jvPartyPricing_('trial')[svc];
        var trialDesc = qty > 1 ? (qty + ' x Bridal Trial: ' + svc) : ('Bridal Trial: ' + svc);
        lineItems.push({ description: trialDesc, unitPrice: trialPrice, qty: qty, total: trialPrice * qty, category: 'trial' });
        subtotal += trialPrice * qty;
      }
    } else if (cat === 'flowergirl') {
      flowergirlRows.push({ svc: svc, unitPrice: unitPrice, qty: qty });
    } else {
      var roleLabel = p.role ? (p.role.charAt(0).toUpperCase() + p.role.slice(1)) : p.role;
      if (cat === 'bridesmaid' && juniorsRemaining > 0) {
        // Peel off up to juniorsRemaining of this bridesmaid line at the junior rate.
        var disc = Math.min(juniorsRemaining, qty);
        juniorsRemaining -= disc;
        var fullQ = qty - disc;
        if (fullQ > 0) {
          var fullTotal = unitPrice * fullQ;
          lineItems.push({ description: (fullQ > 1 ? fullQ + ' x ' : '') + roleLabel + ' ' + svc, unitPrice: unitPrice, qty: fullQ, total: fullTotal, category: cat });
          subtotal += fullTotal;
        }
        var jrUnit = Math.round(unitPrice * (1 - JR_PCT / 100) * 100) / 100;
        var jrTotal = Math.round(jrUnit * disc * 100) / 100;
        lineItems.push({ description: 'Junior Bridesmaid ' + svc + ' (' + JR_PCT + '% off)', unitPrice: jrUnit, qty: disc, total: jrTotal, category: cat });
        subtotal += jrTotal;
      } else {
        var itemTotal = unitPrice * qty;
        var desc = (qty > 1 ? qty + ' x ' : '') + roleLabel + ' ' + svc;
        lineItems.push({ description: desc, unitPrice: unitPrice, qty: qty, total: itemTotal, category: cat });
        subtotal += itemTotal;
      }
      fullPriced += qty;
      if (svc === 'Hair Only' || svc === 'Makeup Only') soloCount += qty; else hmCount += qty;
    }
  });

  // Manual ad-hoc additions (qty + per-card service type).
  // 4.3 (2026-07-10): the junior 25%-off peel-off also applies to manually-added
  // BRIDESMAIDS, sharing the same juniorsRemaining counter as the party loop above so the
  // max-2 cap spans both sources (parity with CRM proposalRecalculate_localPricing_).
  // FIX (2026-07-23, flowergirl cap bug): a manually-added flowergirl was falling through
  // to the flat-rate branch below (never checked cat === 'flowergirl'), skipping the
  // complimentary-cap logic entirely. Routed into flowergirlRows here (mirrors CRM fix in
  // ProposalBuilderAPI.gs) so the cap below sees the true combined enquiry+manual count.
  var _nonFgManualAdditions = [];
  manualAdditions.forEach(function(m) {
    var mCatFg = localPricingCategory_(m.role);
    if (mCatFg === 'flowergirl') {
      var mQtyFg = Math.max(1, (typeof m.qty === 'number' && m.qty > 0) ? m.qty : 1);
      flowergirlRows.push({ svc: manualMemberSvc_(m), unitPrice: jvPartyPricing_('flowergirl')[manualMemberSvc_(m)], qty: mQtyFg });
    } else {
      _nonFgManualAdditions.push(m);
    }
  });
  manualAdditions = _nonFgManualAdditions;
  manualAdditions.forEach(function(m) {
    var cat       = localPricingCategory_(m.role);
    var svc       = manualMemberSvc_(m);
    var qty       = Math.max(1, (typeof m.qty === 'number' && m.qty > 0) ? m.qty : 1);
    var unitPrice = jvPartyPricing_(cat)[svc];
    var mName     = String(m.name || m.role);
    if (cat === 'bridesmaid' && juniorsRemaining > 0) {
      var mDisc = Math.min(juniorsRemaining, qty);
      juniorsRemaining -= mDisc;
      var mFullQ = qty - mDisc;
      if (mFullQ > 0) {
        var mFullTotal = unitPrice * mFullQ;
        lineItems.push({ description: (mFullQ > 1 ? mFullQ + ' x ' : '') + mName + ' (' + m.role + ') ' + svc, unitPrice: unitPrice, qty: mFullQ, total: mFullTotal, category: cat });
        subtotal += mFullTotal;
      }
      var mJrUnit = Math.round(unitPrice * (1 - JR_PCT / 100) * 100) / 100;
      var mJrTotal = Math.round(mJrUnit * mDisc * 100) / 100;
      lineItems.push({ description: mName + ' (Junior ' + m.role + ') ' + svc + ' (' + JR_PCT + '% off)', unitPrice: mJrUnit, qty: mDisc, total: mJrTotal, category: cat });
      subtotal += mJrTotal;
    } else {
      var total = unitPrice * qty;
      var desc  = (qty > 1 ? qty + ' x ' : '') + mName + ' (' + m.role + ') ' + svc;
      lineItems.push({ description: desc, unitPrice: unitPrice, qty: qty, total: total, category: cat });
      subtotal += total;
    }
    fullPriced += qty;
    if (svc === 'Hair Only' || svc === 'Makeup Only') soloCount += qty; else hmCount += qty;
  });

  // T&C 16.1: only 3+ full-priced adult wedding-day services unlock the first 2 free flower girls.
  var _adultServiceCount = lineItems.concat((state.editedLineItems || []).filter(function(li) {
    return li && li.custom && !(li._rowId && state.priceOverrides && state.priceOverrides[li._rowId] && Number(state.priceOverrides[li._rowId].qty) === 0);
  })).reduce(function(count, li) {
    var cat = String(li.category || '').toLowerCase();
    var price = Number(li.unitPrice != null ? li.unitPrice : li.rate);
    return ['bride', 'bridesmaid', 'mother', 'guest'].indexOf(cat) >= 0 &&
      !/junior/i.test(String(li.description || '')) && price > 0
      ? count + Math.max(0, Number(li.qty) || 0) : count;
  }, 0);
  var _fgUnits = [];
  flowergirlRows.forEach(function(fg) { for (var fgi = 0; fgi < fg.qty; fgi++) _fgUnits.push(fg.svc); });
  var FG_COMP_MAX = 2;
  var _fgRateBySvc = {};
  flowergirlRows.forEach(function(fg) { _fgRateBySvc[fg.svc] = fg.unitPrice; });
  var _fgFreeCount = 0, _fgChargedCount = 0, _fgChargedTotal = 0;
  _fgUnits.forEach(function(svc, fgIdx) {
    if (_adultServiceCount >= 3 && fgIdx < FG_COMP_MAX) { _fgFreeCount++; }
    else { _fgChargedCount++; _fgChargedTotal += (_fgRateBySvc[svc] || BASELINE_PRICING_.flowergirl[svc]); }
  });
  if (_fgFreeCount > 0) {
    lineItems.push({ description: 'Flower Girl - Complimentary (3+ party)' + (_fgFreeCount > 1 ? ' x' + _fgFreeCount : ''), subtitle: '', unitPrice: 0, qty: _fgFreeCount, total: 0, category: 'flowergirl' });
  }
  if (_fgChargedCount > 0) {
    lineItems.push({ description: 'Flower Girl - Mini Makeover' + (_fgChargedCount > 1 ? ' x' + _fgChargedCount : ''), subtitle: _adultServiceCount >= 3 ? 'Maximum of ' + FG_COMP_MAX + ' complimentary services reached' : 'Complimentary with 3 or more full-priced adult wedding-day services', unitPrice: (_fgChargedTotal / _fgChargedCount), qty: _fgChargedCount, total: _fgChargedTotal, category: 'flowergirl' });
    subtotal += _fgChargedTotal;
  }
  fullPriced += _fgUnits.length;

  // Re-append preserved travel items
  travelItems.forEach(function(li) {
    lineItems.push(li);
    subtotal += Number(li.total || ((li.qty || 1) * (li.unitPrice || li.rate || 0)));
  });

  var gst   = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;

  // Slice 3 (2026-10-16, issue #2a) — shadow-mode SSoT reader. Fire the
  // catalog fetch (no-op after first call) and log a diff summary against
  // the legacy per-tier maps THIS invocation used. Legacy result below is
  // UNCHANGED — shadow mode never affects what the bride/admin sees.
  try {
    jvSsotEnsureFetched_();
    jvSsotShadowDiff_(
      { jessica: jvBridePricing_(true), proTeam: jvBridePricing_(false) },
      { trial: jvPartyPricing_('trial'), bridesmaid: jvPartyPricing_('bridesmaid'),
        mother: jvPartyPricing_('mother'), guest: jvPartyPricing_('guest'),
        flowergirl: jvPartyPricing_('flowergirl') }
    );
  } catch (e) { /* shadow diff never throws into the pricing path */ }

  return {
    lineItems:           lineItems,
    normalizedLineItems: lineItems,
    subtotal:            subtotal,
    gst:                 gst,
    total:               total,
    artistsRequired:     Math.max(Math.ceil(hmCount / 3), Math.ceil(soloCount / 8), 1),
    travelBreakdown:     (existingCalc && existingCalc.travelBreakdown) || [],
    noTrialRequired:     state.noTrialRequired,
    source:              'local_estimate'
  };
}

/**
 * RATCHET FIX (2026-07-22) JS mirror of proposalArtistCount_resolve_ (CRM
 * ProposalArtistCountRatchetFix_2026_07_22.gs) and jvAutoArtistCount_resolve_
 * (JV AUTO ProposalCalculator.gs). Byte-identical body to the CRM helper's
 * harness copy so 3-engine parity holds. See that file for the full rule
 * writeup: flag OFF reproduces the old monotonic-up ("stored greater than 0
 * always wins") behaviour; flag ON only a genuine admin override stays
 * sticky, a derived value can correct down.
 * @private
 */
function proposalArtistCountResolve_(freshTrueCount, storedCount, adminOverride, enabled) {
  var fresh  = Math.max(1, Number(freshTrueCount) || 0);
  var stored = Math.max(0, Number(storedCount) || 0);
  if (!enabled) return stored > 0 ? stored : fresh;
  if (adminOverride && stored > 0) return stored;
  return fresh;
}

/**
 * Is the stored artist count a GENUINE, carried-forward admin override (as
 * opposed to a derived value that merely happens to be stored)? Mirror of
 * proposalArtistCount_isAdminOverride_ (CRM ProposalArtistCountRatchetFix_
 * 2026_07_22.gs), same 3 markers.
 * @private
 */
function isAdminOverrideJs_(calc) {
  if (!calc || typeof calc !== 'object') return false;
  if (calc.artistsRequiredOverride === true) return true;
  var tl = calc.timingLocks;
  if (tl && tl.artistsRequired === true) return true;
  if (calc.weddingArtistsLockedAt) return true;
  return false;
}

/* =================================================================
   PATH 2 - CLIENT-OWNS-STATE: local recalc + merge + flush + draft
   -----------------------------------------------------------------
   Every edit handler does: mutate state, recalcAndMerge_(), render,
   markDirty_(), saveDraft_(). NO GAS call on edits.

   flushToGas_() is the SINGLE persistence path. Called by Save /
   Generate Preview / Send / Publish / Resend. It posts the full
   current state to GAS, which writes PROPOSAL_CALC_JSON + syncs
   Proposal Tokens atomically and returns the canonical calcJson.
   ================================================================= */

/**
 * Wraps calcLocalEstimate_ with override-wins + carry-forward semantics so
 * the resulting calcJson is a complete drop-in for state.workspace.calculation.
 * Mutates state.workspace.calculation in place and returns it for chaining.
 */
function recalcAndMerge_() {
  if (!state.workspace) return null;
  var existing = state.workspace.calculation || {};
  var fresh = calcLocalEstimate_(state.workspace.party || [], state.manualPartyMembers || []);

  // Override-wins (Invariant 1): preserve sticky overrides from existing calc.
  // To revert a field to formula, admin clears the input (handled in setTimingField).
  var overriddenFinish  = String(existing.finishTime || '').trim();
  var overriddenArtists = Number(existing.artistsRequired) || 0;
  var overriddenStart   = String(existing.estimatedStartTime || '').trim();

  fresh.finishTime         = overriddenFinish;
  // RATCHET FIX (2026-07-22): mirror of proposalArtistCount_resolve_ (CRM
  // ProposalArtistCountRatchetFix_2026_07_22.gs, jvAutoArtistCount_resolve_ in
  // JV AUTO). Flag-gated via state.correctableArtistCount (server-driven, see
  // proposalGetWorkspace payload.correctableArtistCount). OFF reproduces the old
  // "stored > 0 always wins" behaviour byte-for-byte.
  fresh.artistsRequired = proposalArtistCountResolve_(
    fresh.artistsRequired, overriddenArtists, isAdminOverrideJs_(existing), !!state.correctableArtistCount);
  // Carry the marker forward so it survives client-only recalcs.
  if (existing.artistsRequiredOverride === true) {
    fresh.artistsRequiredOverride = true;
    fresh.artistsRequiredOverriddenAt = existing.artistsRequiredOverriddenAt;
  }
  fresh.estimatedStartTime = overriddenStart;
  fresh.timingLocks        = existing.timingLocks || {};

  // Carry-forward CRM-side state that local calc doesn't compute
  fresh.selectedAddress    = existing.selectedAddress  || null;
  fresh.selectedAddresses  = existing.selectedAddresses || {};
  fresh.weddingTravel      = existing.weddingTravel    || [];
  fresh.trialTravel        = existing.trialTravel      || null;
  fresh.sprayTanTravel     = existing.sprayTanTravel   || null;
  fresh.travel             = existing.travel           || null;
  fresh.travelLocks        = existing.travelLocks      || {};
  // BUG FIX (2026-09-15, BUG-tbc-trial-still-charges-travel-fee-2026-09-15.md): same
  // missing-carry-forward class as trialLocationMode's own drop bug (fixed earlier
  // today, GAS ProposalBuilderAPI.gs). calcLocalEstimate_ builds `fresh` from scratch
  // and never sets trialLocationMode on it, so every local recalc (fires on nearly
  // every Page-2 edit) was silently dropping the mode from state.workspace.calculation
  // -- and since flushToGas_() sends state.workspace.calculation.trialLocationMode
  // nowhere explicitly (the GAS side reads it off the PERSISTED calc, not a payload
  // field), a dropped client-side mode here would desync the segmented control's
  // displayed selection from whatever GAS still has stored, even after the GAS-side
  // carry-forward fix. Mirrors the artistsRequiredOverride carry-forward just above.
  if (existing.trialLocationMode) fresh.trialLocationMode = existing.trialLocationMode;
  if (existing.trialLocationPending) fresh.trialLocationPending = existing.trialLocationPending;
  // Sticky per-line travel deletes. state wins (a delete/restore mutates it before
  // recalc); write back so calc + state + draft stay in lockstep across recalcs.
  fresh.travelSuppressions = state.travelSuppressions || existing.travelSuppressions || {};
  state.travelSuppressions = fresh.travelSuppressions;
  fresh.travelKm           = existing.travelKm         || 0;
  fresh.travelCost         = existing.travelCost       || 0;
  fresh.assignedArtists    = Array.isArray(existing.assignedArtists) ? existing.assignedArtists : [];
  fresh.manualAdditions    = state.manualPartyMembers  || [];
  fresh.brideWithMe        = state.brideWithMe;
  fresh.bookingTier        = state.bookingTier || 'jessica';
  fresh.bookingTierLockedAt= existing.bookingTierLockedAt || '';
  fresh.flags              = existing.flags            || [];
  fresh.availability       = existing.availability     || {};
  fresh.hollywoodWavesCount= existing.hollywoodWavesCount || 0;
  fresh.juniorBridesmaids  = Number(state.juniorBridesmaids) || 0;
  // Money-bug continuation (commit 83e09e80, BRIEFS/HANDOVER-full-gc-root-cause-found-2026-07-16.md):
  // recalcAndMerge_ runs SYNCHRONOUSLY, before any server round-trip, on every edit handler
  // (artist stepper, tier change, timing fields, etc). Without this line the admin's chosen
  // paymentOption (e.g. Pay in Full, Direct Debit) is wiped out here, before the 4 async
  // preserve sites (post proposalRecalculateWithAddress response) even run, so those sites
  // have nothing left to preserve by the time they execute.
  fresh.paymentOption      = existing.paymentOption || null;

  // BUILD-2 fix (2026-07-02): recalcAndMerge_ rebuilds `fresh` from scratch on every
  // local Recalculate, so packageVariants must be explicitly carried forward or the
  // variant session (or a base recalc while variants exist) silently drops the array.
  // This is the client-side counterpart to the flushToGas_ preservation at line ~736.
  if (Array.isArray(existing.packageVariants) && existing.packageVariants.length > 0) {
    fresh.packageVariants = existing.packageVariants;
  }

  // Package notes (2026-08-04) carry forward for the SAME reason as packageVariants above:
  // recalcAndMerge_ rebuilds `fresh` from scratch, so anything not explicitly preserved is
  // silently lost on the next local Recalculate. Notes are non-priced free text and are
  // deliberately NOT part of any subtotal/GST/total -- they live in their own array and are
  // never pushed into lineItems, because every total here is derived by summing lineItems
  // and a note in that array would be summed as a $0 row at best and corrupt the quote at
  // worst.
  if (Array.isArray(existing.packageNotes) && existing.packageNotes.length > 0) {
    fresh.packageNotes = existing.packageNotes;
  }

  // Cap wedding-day travel lines to the (now-settled) artist count so setting
  // "Artists" = 2 shows exactly 2 wedding travel lines. Runs after fresh.artistsRequired
  // is finalised; the structured arrays keep every artist so raising the count restores.
  proposalCapWeddingTravelLines_(fresh);

  // Invariant 2: bride-line tier overlay (local mirror).
  // Bride line description is already labelled correctly in calcLocalEstimate_ via
  // brideTierLabel ("with Jessica Vegas" / "with JV Pro Team"), priced via
  // BRIDE_PRICING_ME_ / BRIDE_PRICING_TEAM_. Defensive re-overlay here to guarantee
  // the bride line matches state.bookingTier even if a future calcLocalEstimate_
  // change forgets it. Also re-derives subtotal/gst/total from the capped line items.
  proposalOverlayBookingTierLocal_(fresh, state.bookingTier || 'jessica');

  // FIX (2026-07-23, confirmed via live Cowork repro): calcLocalEstimate_ rebuilds
  // lineItems from scratch on every party edit and has no knowledge of catalog/custom
  // rows added earlier in the session (e.g. a Hollywood Waves upgrade), so they were
  // silently dropped the moment a party member was added/changed. Every caller of
  // recalcAndMerge_ nulls state.editedLineItems right after this returns, so it is read
  // here BEFORE that happens, the one point where the pre-edit snapshot (still holding
  // the custom rows) and the freshly-rebuilt calc both exist. Re-uses the same rescue
  // helper already proven for the address-recalc fix (_pbReappendCustomLineItems_),
  // so the fix applies once, at the shared root, instead of patching every caller.
  //
  // A custom row the admin already DELETED (removeLineItem, for a row with a _rowId
  // that already round-tripped through a save) is removed via a qty:0 priceOverride,
  // not by splicing it out of state.editedLineItems -- so without this filter the
  // rescue above would wrongly resurrect an already-deleted upgrade line. Drop any
  // row whose _rowId has a zero-qty override before treating it as "still present".
  var _pbLiveCustomRows = (state.editedLineItems || []).filter(function(li) {
    if (!li || !li.custom) return true; // non-custom rows are irrelevant to the filter below
    var ov = li._rowId && state.priceOverrides ? state.priceOverrides[li._rowId] : null;
    return !(ov && Number(ov.qty) === 0);
  });
  _pbReappendCustomLineItems_(fresh, _pbLiveCustomRows);

  state.workspace.calculation = fresh;

  /* SHADOW PARITY CHECK, jv-proposal-engine POST /pricing/quote (2026-10-12,
     Issue 8 lane, Consolidator ask after live parity confirmed byte-exact
     against a real 2027 CRM proposal -- see
     BRIEFS/RESULTS-issue8-proposal-offgas-2026-10-12.md). This is the ONE
     real caller wired this session, matching the existing
     JV_PROPOSAL_ENGINE_SHADOW pattern above (never replaces, races, or
     blocks the local `fresh` result -- it fires in the background and only
     console-logs a diff).

     KILL FLAG: window.JV_PRICING_QUOTE_SHADOW must be explicitly true.
     Default (unset) = OFF = zero extra network calls, byte-identical to the
     old behaviour. SEPARATE from JV_PROPOSAL_ENGINE_SHADOW above -- that one
     shadows the /workspace RECALC route (GAS's stored-calc healing path);
     this one shadows /pricing/quote, the FRESH-quote compute this lane's
     brief is actually about. They can be flipped independently.

     Does NOT flip PRICING_QUOTE_ENABLED on the Worker (that stays Jess's/
     the Consolidator's call) and does NOT touch GAS. If the Worker-side
     flag is off, the route returns {fallback:'gas'} and this logs a
     "disabled" line, nothing else -- safe to leave wired while the server
     flag stays off. */
  if (window.JV_PRICING_QUOTE_SHADOW === true) {
    try {
      var _pqWeddingDate = (state.workspace.bride && state.workspace.bride.weddingDate) || '';
      var _pqName = state.currentBride || (state.workspace.bride && (state.workspace.bride.firstName + ' ' + state.workspace.bride.lastName)) || '';
      if (_pqWeddingDate) {
        var _pqBody = {
          weddingDate: _pqWeddingDate,
          bookingTier: state.bookingTier || 'jessica',
          party: state.workspace.party || [],
          manualAdditions: state.manualPartyMembers || [],
          noTrialRequired: !!state.noTrialRequired,
          juniorBridesmaids: Number(state.juniorBridesmaids) || 0,
        };
        var _pqNf = (window._jvNativeFetch || window.fetch).bind(window);
        // RELAY FIX (2026-10-22, Issue #8): jv-proposal-engine's /pricing/quote started
        // requiring body.secret (D1_SYNC_SECRET) on 2026-10-12 as a security fix -- this
        // browser caller never sent one, so every live call since has 401'd silently (see
        // proxy-entities-proposal-engine-shadow.mjs header for the full evidence trail).
        // Route through the cors-relay's proposal-engine-shadow entity instead, which
        // injects the secret server-side; same window._pbAdminProxyBase this page already
        // emits for the proposal-pending-brides relay call above. If the proxy base isn't
        // available (older cached page, metafield unset), fall through to the direct call
        // for parity with the old behaviour -- it will keep 401ing exactly as it does today,
        // not a regression.
        var _pqUrl = window._pbAdminProxyBase
          ? String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-engine-shadow/quote'
          : 'https://jv-proposal-engine.calm-hill-fd5b.workers.dev/pricing/quote';
        _pqNf(_pqUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' }, // simple request, no CORS preflight
          body: JSON.stringify(_pqBody)
        })
          .then(function (r) { return r.json(); })
          .then(function (engineRes) {
            if (!engineRes || engineRes.ok !== true) {
              console.log('[JV_PRICING_QUOTE_SHADOW] no comparable result for ' + _pqName + ': ' +
                (engineRes && (engineRes.reason || engineRes.fallback)) || 'unknown');
              return;
            }
            // quote-engine.js never computes travel itself (its own documented
            // boundary -- travel is caller-resolved, left to its own SSoT).
            // `fresh` DOES include travel lines (calcLocalEstimate_ builds them
            // from the structured travel arrays), so strip them here for an
            // apples-to-apples non-travel-services comparison -- exactly the
            // comparison this session proved byte-exact against 2 real live
            // CRM proposals before wiring this caller.
            var _pqLocalNonTravelSubtotal = 0;
            (fresh.lineItems || []).forEach(function (li) {
              if (String(li.category || '').toLowerCase() === 'travel') return;
              _pqLocalNonTravelSubtotal += Number(li.total || 0);
            });
            _pqLocalNonTravelSubtotal = Math.round(_pqLocalNonTravelSubtotal * 100) / 100;
            var _pqEngineSubtotal = Number(engineRes.data && engineRes.data.subtotal);
            var _pqDiff = (Number.isFinite(_pqLocalNonTravelSubtotal) && Number.isFinite(_pqEngineSubtotal))
              ? Math.round((_pqEngineSubtotal - _pqLocalNonTravelSubtotal) * 100) / 100 : null;
            console.log('[JV_PRICING_QUOTE_SHADOW] COMPARE for ' + _pqName + ': localNonTravelSubtotal=' + _pqLocalNonTravelSubtotal +
              ' engineSubtotal=' + (Number.isFinite(_pqEngineSubtotal) ? _pqEngineSubtotal : '(none)') +
              ' diff=' + (_pqDiff === null ? '(not comparable)' : _pqDiff) +
              (_pqDiff === 0 ? ' -- MATCH' : (_pqDiff === null ? '' : ' -- MISMATCH')));

            // PERSIST (2026-10-12, Phase 3 live-soak): same durable D1 table
            // the sibling JV_PROPOSAL_ENGINE_SHADOW harness already uses
            // (proposal_shadow_comparisons via jv-proposal-engine's
            // POST /shadow-log), tagged with engine_source so this lane's
            // fresh-quote comparisons can be filtered separately from the
            // /workspace-recalc shadow's rows. "gas" side here is actually
            // the client's own local pre-reconciliation estimate (the
            // closest thing to ground truth available synchronously inside
            // recalcAndMerge_, which runs BEFORE any GAS round-trip) --
            // labelled honestly as such via engine_source, not claimed to be
            // GAS's reconciled number.
            try {
              var _pqLocalNonTravelGst = Math.round(_pqLocalNonTravelSubtotal * 0.10 * 100) / 100;
              var _pqLocalNonTravelTotal = Math.round((_pqLocalNonTravelSubtotal + _pqLocalNonTravelGst) * 100) / 100;
              var _pqEmail = (state.workspace.bride && state.workspace.bride.email) || '';
              _pqNf('https://jv-proposal-engine.calm-hill-fd5b.workers.dev/shadow-log', {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                body: JSON.stringify({
                  email: _pqEmail,
                  brideName: _pqName,
                  gas: { subtotal: _pqLocalNonTravelSubtotal, total: _pqLocalNonTravelTotal },
                  engine: {
                    ok: true,
                    source: 'pricing_quote_fresh_vs_local',
                    subtotal: _pqEngineSubtotal,
                    total: Number(engineRes.data && engineRes.data.total)
                  }
                })
              }).catch(function (e) {
                console.log('[JV_PRICING_QUOTE_SHADOW] shadow-log write failed for ' + _pqName + ': ' + (e && e.message));
              });
            } catch (eLog) { /* never let the persistence write affect anything */ }
          })
          .catch(function (e) {
            console.log('[JV_PRICING_QUOTE_SHADOW] fetch failed for ' + _pqName + ': ' + (e && e.message));
          });
      }
    } catch (eShadow) { /* never let the shadow check affect the real local calc */ }
  }

  return fresh;
}

/**
 * Client-side mirror of GAS proposalBuilder_overlayBookingTier_. Ensures the
 * bride line item label + price matches the active booking tier.
 */
function proposalOverlayBookingTierLocal_(calc, tier) {
  if (!calc || !calc.lineItems) return;
  // Snapshot-fallback calcs are read-only (see calcLocalEstimate_'s _fromSnapshot
  // short-circuit): re-pricing the bride line from TODAY's tier pricing would
  // overwrite the sent snapshot's locked-in numbers, exactly what the fallback
  // exists to avoid.
  if (calc._fromSnapshot) return;
  var isJessica = tier === 'jessica';
  var pricing = jvBridePricing_(isJessica);
  var label = isJessica ? 'with Jessica Vegas' : 'with JV Pro Team';
  for (var i = 0; i < calc.lineItems.length; i++) {
    var li = calc.lineItems[i];
    if (li.category !== 'bride') continue;
    var svc = 'Hair & Makeup';
    var desc = String(li.description || '');
    if (desc.indexOf('Hair Only')  >= 0) svc = 'Hair Only';
    else if (desc.indexOf('Makeup Only') >= 0) svc = 'Makeup Only';
    var unit = pricing[svc] || pricing['Hair & Makeup'];
    var qty = Number(li.qty || 1);
    li.unitPrice = unit;
    li.total = unit * qty;
    li.description = 'Bride ' + svc + (qty > 1 ? ' x' + qty : '') + ' (' + label + ')';
  }
  // Re-derive subtotal/gst/total from line items
  var subtotal = 0;
  for (var k = 0; k < calc.lineItems.length; k++) {
    subtotal += Number(calc.lineItems[k].total || 0);
  }
  calc.subtotal = subtotal;
  calc.gst   = Math.round(subtotal * 0.10 * 100) / 100;
  calc.total = calc.subtotal + calc.gst;
  calc.normalizedLineItems = calc.lineItems;
}

/**
 * Caps the number of wedding-day travel line items to calc.artistsRequired so the
 * admin can quote a specific artist count (e.g. 2 = her + 1) even when more artists
 * are auto-allocated. Keeps the first N wedding-travel lines in array order. Mirror of
 * the GAS cap inside proposalBuilder_rebuildTravelLineItems_. Does NOT touch the
 * structured travel arrays, so raising the count restores the dropped lines on the
 * next recalc. No-op when the count is unset (0).
 */
function proposalCapWeddingTravelLines_(calc) {
  if (!calc || !Array.isArray(calc.lineItems)) return;
  // Same read-only contract as proposalOverlayBookingTierLocal_ above: a
  // snapshot-fallback calc's lineItems are the sent snapshot verbatim and must
  // not be filtered by today's artist count.
  if (calc._fromSnapshot) return;
  var cap = Number(calc.artistsRequired) || 0;
  if (cap <= 0) return;
  var kept = 0;
  calc.lineItems = calc.lineItems.filter(function(li) {
    var type = li._travelType || '';
    var d = String(li.description || li.name || '').toLowerCase();
    var isWedding = (type === 'wedding') ||
      (d.indexOf('travel') >= 0 && d.indexOf('wedding') >= 0);
    if (!isWedding) return true;
    kept++;
    return kept <= cap;
  });
}

/* ── DRAFT PERSISTENCE ────────────────────────────────
   Drafts survive an accidental tab close between flushes. Keyed per bride.
   Restored on selectBride if newer than the last successful flush. */
var _draftDirty = false;
var _draftSaveTimer = null;

function draftKey_(brideName) {
  return 'jv_proposal_draft_v1::' + String(brideName || '').toLowerCase();
}

function markDirty_() {
  _draftDirty = true;
  // Bump requestToken so any in-flight flush response can't clobber this newer
  // local edit. The flush response's `if (_flTok === state.requestToken)` guard
  // will fail → state stays as the user just typed it. Same Path 1 pattern.
  state.requestToken++;
  renderSavePill_();
  if (_draftSaveTimer) clearTimeout(_draftSaveTimer);
  _draftSaveTimer = setTimeout(saveDraft_, 350);
}

function clearDirty_() {
  _draftDirty = false;
  if (state.currentBride) {
    try { localStorage.removeItem(draftKey_(state.currentBride)); } catch (e) {}
  }
  renderSavePill_();
}

function saveDraft_() {
  if (!_draftDirty || !state.currentBride || !state.workspace) return;
  try {
    var snap = {
      ts: Date.now(),
      calculation: state.workspace.calculation || {},
      party: state.workspace.party || [],
      manualPartyMembers: state.manualPartyMembers || [],
      noTrialRequired: !!state.noTrialRequired,
      brideWithMe: !!state.brideWithMe,
      bookingTier: state.bookingTier,
      editedLineItems: state.editedLineItems || null,
      priceOverrides: state.priceOverrides || {},
      travelSuppressions: state.travelSuppressions || {},
      lineItemsManuallyEdited: !!state.lineItemsManuallyEdited
    };
    localStorage.setItem(draftKey_(state.currentBride), JSON.stringify(snap));
  } catch (e) { /* quota / private mode — non-fatal */ }
}

function loadDraft_(brideName) {
  try {
    var raw = localStorage.getItem(draftKey_(brideName));
    if (!raw) return null;
    var snap = JSON.parse(raw);
    if (!snap || !snap.ts) return null;
    // Shape guard (2026-10-12, jfg pollution hardening): a draft written by an older
    // build, or half-written mid-crash, must never be offered for restore -- restoring
    // it replaces the server calc with garbage and the hydrate below throws, which
    // blocks the bride's builder on THIS browser forever (the draft survives reloads).
    // Invalid shape = discard it, load the server version.
    var c = snap.calculation;
    if (c != null && (typeof c !== 'object' || Array.isArray(c) ||
        (c.lineItems != null && !Array.isArray(c.lineItems)))) {
      try { localStorage.removeItem(draftKey_(brideName)); } catch (e2) {}
      return null;
    }
    return snap;
  } catch (e) {
    // Unparseable draft: remove it so it cannot poison every future open.
    try { localStorage.removeItem(draftKey_(brideName)); } catch (e3) {}
    return null;
  }
}

/* Workspace payload sanitiser (2026-10-12, jfg pollution hardening). Months of live
   testing on the standing test bride showed the builder must survive whatever shape
   the saved proposal data comes back in, for ANY bride: a calculation that is a
   string (double-encoded JSON), an array, null, or an object whose lineItems is not
   an array. Coerces to a safe, renderable shape and returns a warning string when it
   had to repair something (null when the payload was already clean). Never throws. */
function pbSanitizeWorkspacePayload_(payload) {
  var notes = [];
  try {
    var calc = payload.calculation;
    if (typeof calc === 'string') {
      try { calc = JSON.parse(calc); notes.push('calculation was double-encoded'); }
      catch (e) { calc = null; notes.push('calculation was unreadable text'); }
    }
    if (calc != null && (typeof calc !== 'object' || Array.isArray(calc))) {
      calc = null; notes.push('calculation had an invalid shape');
    }
    if (calc) {
      ['lineItems', 'normalizedLineItems'].forEach(function(k) {
        if (calc[k] != null && !Array.isArray(calc[k])) { calc[k] = []; notes.push(k + ' was not a list'); }
        if (Array.isArray(calc[k])) {
          var before = calc[k].length;
          calc[k] = calc[k].filter(function(li) { return li && typeof li === 'object'; });
          if (calc[k].length !== before) notes.push('dropped ' + (before - calc[k].length) + ' blank ' + k);
        }
      });
      if (calc.priceOverrides != null && (typeof calc.priceOverrides !== 'object' || Array.isArray(calc.priceOverrides))) {
        calc.priceOverrides = {}; notes.push('priceOverrides invalid');
      }
      if (calc.travelSuppressions != null && (typeof calc.travelSuppressions !== 'object' || Array.isArray(calc.travelSuppressions))) {
        calc.travelSuppressions = {}; notes.push('travelSuppressions invalid');
      }
    }
    payload.calculation = calc || {};
    if (payload.party != null && !Array.isArray(payload.party)) { payload.party = []; notes.push('party was not a list'); }
    if (payload.bride != null && (typeof payload.bride !== 'object' || Array.isArray(payload.bride))) { payload.bride = {}; notes.push('bride record invalid'); }
    if (payload.proposal != null && (typeof payload.proposal !== 'object' || Array.isArray(payload.proposal))) { payload.proposal = {}; notes.push('proposal record invalid'); }
  } catch (eSan) {
    notes.push('sanitiser error: ' + (eSan && eSan.message));
  }
  if (notes.length) {
    try { console.warn('[proposal-builder] workspace payload repaired:', notes.join('; ')); } catch (e4) {}
    return 'Some saved proposal data for this bride was invalid and has been ignored (' + notes.join('; ') +
      '). Check the line items, then Save to rewrite a clean copy.';
  }
  return null;
}

function renderSavePill_() {
  var pill = document.getElementById('savePill');
  var discardBtn = document.getElementById('discardDraftBtn');
  if (_flushInFlight) {
    if (pill) {
      pill.textContent = 'Saving...';
      pill.className = '';
      pill.style.display = 'inline-flex';
    }
    if (discardBtn) discardBtn.style.display = 'none';
    return;
  }
  if (_draftDirty) {
    if (pill) {
      pill.textContent = 'Unsaved changes';
      pill.className = 'save-pill-dirty';
      pill.style.display = 'inline-flex';
    }
    if (discardBtn) discardBtn.style.display = '';
    return;
  }
  // FIX (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-confirmation-
  // 2026-09-30.md): once a proposal has genuinely been sent, the header pill
  // should say so -- Jess's own report was that right after a real send this
  // pill still read "Saved" (leftover from an earlier autosave), giving zero
  // signal the send actually happened. Checked AFTER dirty/in-flight (a fresh
  // edit or an in-progress save always takes visual priority) but BEFORE the
  // generic "Saved" fallback, so this never overrides a real unsaved-changes
  // warning. Reuses the same green .save-pill-saved styling -- this is a
  // status upgrade of "saved", not a different visual language.
  var _pillProposal = (state.workspace && state.workspace.proposal) || null;
  var _pillStatusUpper = _pillProposal ? String(_pillProposal.status || '').trim().toUpperCase() : '';
  var _pillIsSent = _pillStatusUpper === 'SENT' || _pillStatusUpper === 'ACTIVE';
  if (_pillIsSent) {
    if (pill) {
      pill.textContent = 'Sent';
      pill.className = 'save-pill-saved';
      pill.style.display = 'inline-flex';
    }
    if (discardBtn) discardBtn.style.display = 'none';
    return;
  }
  if (_lastFlushAt) {
    if (pill) {
      pill.textContent = 'Saved';
      pill.className = 'save-pill-saved';
      pill.style.display = 'inline-flex';
    }
    if (discardBtn) discardBtn.style.display = 'none';
    return;
  }
  if (pill) { pill.style.display = 'none'; pill.className = ''; }
  if (discardBtn) discardBtn.style.display = 'none';
}

/* ── FLUSH TO GAS (single persistence path) ──────────
   Posts the full client state to proposalRecalculate. GAS writes
   PROPOSAL_CALC_JSON + syncs Proposal Tokens in one atomic op. */
var _flushInFlight = false;
var _lastFlushAt = 0;
var _currentDisplayItems = []; // display items cache for updateLineItem _rowId lookup

/* TIMING (2026-10-20, console-only, no behaviour change): measures how long flush-to-GAS and the send take, to find
   where a proposal send spends its time. Read with: window.__jvSendTimings. Safe to remove once the cause is found. */
function pbTimed_(label, fn) {
  var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  var done = function(outcome) {
    try {
      var ms = Math.round(((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - t0);
      (window.__jvSendTimings = window.__jvSendTimings || []).push({ step: label, ms: ms, outcome: outcome, at: new Date().toISOString() });
      console.log('[JV_SEND_TIMING] ' + label + ' ' + ms + 'ms (' + outcome + ')');
    } catch (_e) {}
  };
  var p = fn();
  if (p && typeof p.then === 'function') {
    p.then(function() { done('ok'); }, function() { done('error'); });
  } else { done('sync'); }
  return p;
}

function flushToGas_() {
  return pbTimed_('flushToGas', flushToGasInner_);
}

function flushToGasInner_() {
  return new Promise(function(resolve, reject) {
    if (!state.currentBride || !state.workspace) {
      return resolve({ ok: true, skipped: true });
    }
    if (!state.workspace.brideUid) {
      return reject(new Error('Cannot save: bride identity not loaded. Hard-refresh the page.'));
    }
    if (_travelAutoStatus === 'pending' || _travelAutoStatus === 'error') {
      return reject(new Error('Travel is still calculating or needs review. Resolve the travel quote before saving.'));
    }
    // A1 2026-11-02 zero-total defect fix, hard refusal (§3c): a snapshot-sourced
    // synthetic calc (see buildCalcFromSnapshot_) must NEVER be flushed back to
    // GAS/D1 -- it has lost all internal state (travel, timing locks, overrides)
    // and would overwrite the real snapshot with a lossy derived shape. No network
    // call is made.
    if (state.workspace.calculation && state.workspace.calculation._fromSnapshot === true) {
      return resolve({ ok: true, skipped: true, reason: 'snapshot_fallback_readonly' });
    }
    // Skip GAS flush while editing a variant. Variant state is local until
    // pvSaveAndExitVariantBuilder_() commits it back into the base calc.
    if (state.editingVariantIdx != null) {
      return resolve({ ok: true, skipped: true });
    }
    if (_flushInFlight) {
      // Coalesce — caller waits for current flush, then triggers a fresh one
      var poll = setInterval(function() {
        if (_flushInFlight) return;
        clearInterval(poll);
        flushToGas_().then(resolve, reject);
      }, 200);
      return;
    }
    _flushInFlight = true;
    renderSavePill_();

    var calc = state.workspace.calculation || {};
    // FIX (2026-07-06): snapshot the just-flushed variant state under a distinct
    // name. The .then() callback has its own hoisted `var calc = res.data.calculation`,
    // so referencing `calc` inside the callback would resolve to that (undefined
    // until assigned), not this pre-flush client calc.
    var _flushSourceVariants = calc.packageVariants;
    var partyOverrides = (state.workspace.party || []).filter(function(p) { return !(p && p.isManualAddition); }).map(function(p) { // 4.1: never send a manual member as an enquiry override
      return { role: p.role, serviceType: p.serviceType, qty: p.qty !== undefined ? Number(p.qty) : 1 };
    });

    // Trial-removal invariant (2026-07-17, money-bug — mirrors the GAS strip in
    // proposalRecalculate/proposalApproveAndSend): if trial has been removed, never let a
    // stale editedLineItems snapshot carry trial rows to GAS. Without this, the GAS
    // trusted-replace resurrects the trial under a noTrialRequired:true flag and the bride
    // is billed a trial the admin removed (confirmation screen right, sent wrong). Trial
    // detection matches GAS: category 'trial', or a travel line whose description contains
    // both 'travel' and 'trial'.
    var _editedLineItemsForFlush = state.editedLineItems || null;
    if (state.noTrialRequired && Array.isArray(_editedLineItemsForFlush)) {
      _editedLineItemsForFlush = _editedLineItemsForFlush.filter(function(li) {
        var cat  = String((li && li.category) || '').toLowerCase();
        var desc = String((li && (li.description || li.name)) || '').toLowerCase();
        var isTrialService = (cat === 'trial');
        var isTrialTravel  = (desc.indexOf('travel') >= 0 && desc.indexOf('trial') >= 0);
        return !(isTrialService || isTrialTravel);
      });
      if (!_editedLineItemsForFlush.length) _editedLineItemsForFlush = null;
    }

    // Path 2 extended payload — proposalRecalculate now accepts the full client
    // state. Falls back to legacy positional args when fields are absent (kept
    // for incremental Phase 2 rollout).
    var payload = {
      name: state.currentBride,
      brideUid: state.workspace.brideUid,
      // Email remains a diagnostic cross-check for the UID lookup.
      email: ((state.workspace && state.workspace.bride && state.workspace.bride.email) || '').toString().trim().toLowerCase(),
      weddingDate: String((state.workspace && state.workspace.bride && state.workspace.bride.weddingDate) || '').slice(0, 10),
      partyOverrides: partyOverrides,
      manualAdditions: state.manualPartyMembers || [],
      noTrialRequired: !!state.noTrialRequired,
      brideWithMe:    !!state.brideWithMe,
      bookingTier:    state.bookingTier || 'jessica',
      // Move-aware travel origin (2026-07-14): null = use the global default,
      // 'current' or 'future' pins this quote. Always sent (never undefined) so the
      // GAS-side hasOwnProperty check in shopifyBridge_proposalRecalculate always fires.
      travelOriginOverride: (state.travelOriginOverride === 'current' || state.travelOriginOverride === 'future')
        ? state.travelOriginOverride
        : null,
      // Extensions -- see Phase 2 GAS-side handler
      timingOverrides: {
        finishTime:         String(calc.finishTime || '').trim(),
        artistsRequired:    Number(calc.artistsRequired) || '',
        estimatedStartTime: String(calc.estimatedStartTime || '').trim()
      },
      timingLocks:       calc.timingLocks  || {},
      travelLocks:       calc.travelLocks  || {},
      // Sticky per-line travel deletes. GAS persists onto PROPOSAL_CALC_JSON; the calc
      // engines (CRM + JV AUTO) honour these once the frozen-per-version-links build folds
      // in the GAS side. Until then this round-trips harmlessly (unknown key is ignored).
      travelSuppressions: state.travelSuppressions || calc.travelSuppressions || {},
      editedLineItems:   _editedLineItemsForFlush,
      priceOverrides:    state.priceOverrides  || {},
      // Payment option: persists the admin's selection across saves.
      // type: 'stripe' | 'gc_5050' | 'gc_plan'. months only used when gc_plan.
      paymentOption:     calc.paymentOption || null,
      juniorBridesmaids: Number(state.juniorBridesmaids) || 0,
      // BUILD-3 (2026-07-02): persist variants so they survive a save + reload.
      // GAS proposalRecalculate does a read-modify-write and preserves this key.
      // FIX (2026-07-06): distinguish "absent = no change" from "present-but-empty
      // = clear". Send the array verbatim whenever it is an array (INCLUDING []),
      // plus a presence flag so the GAS side can tell a deliberate clear from a
      // legacy caller that simply omitted the key. Without this, "remove all" +
      // Save omitted the empty array entirely and the old variants survived reload.
      packageVariants:        Array.isArray(calc.packageVariants) ? calc.packageVariants : undefined,
      packageVariantsPresent: Array.isArray(calc.packageVariants),
      // Package notes (2026-08-04): non-priced free-text disclosures shown with the line
      // items. Same absent-vs-empty discipline as packageVariants above -- send the array
      // verbatim whenever it IS an array (including []), with a presence flag, so deleting
      // the last note actually clears it instead of the old notes surviving the reload.
      packageNotes:        Array.isArray(calc.packageNotes) ? calc.packageNotes : undefined,
      packageNotesPresent: Array.isArray(calc.packageNotes),
      // M4 (2026-07-09): persist custom appointment locations (label + address) so they
      // survive a workspace reload. The custom-location travel LINE ITEM already round-trips
      // as a custom edited line, but the location CARD (label/address) was frontend-only and
      // vanished on reload. GAS proposalRecalculate preserves customLocations onto
      // PROPOSAL_CALC_JSON (like packageVariants) and returns it on workspace load.
      // Presence flag lets the GAS side tell a deliberate clear ([]) from a legacy omit.
      customLocations: (state.workspace && Array.isArray(state.workspace.locations))
        ? state.workspace.locations.filter(function(l) { return l && l.type === 'custom'; })
        : undefined,
      customLocationsPresent: !!(state.workspace && Array.isArray(state.workspace.locations))
    };
    // A displayed automatic quote must be recalculated atomically on Save.
    // The relay refuses this request if Cloudflare cannot persist it.
    if (_travelAutoRequired) payload.travelAutoPersist = true;

    var _flTok = ++state.requestToken;
    pbRecalcWarm_('proposalRecalculate', payload)
      .then(function(res) {
        _flushInFlight = false;
        var data = (res && res.data) || res;
        // FIX (2026-07-16): a transport-layer failure (both fetch+XHR failing, or a
        // retry that also times out) resolves via jv-gas-bridge as {ok:false, error:...}
        // -- no `status` field at all. The `data.status === 'error'` check only catches
        // GAS-layer errors, so this shape slipped through, leaving data.calculation
        // undefined and crashing the packageVariants carry-forward below with
        // "Cannot set properties of undefined (setting 'packageVariants')". Most visible
        // on gc_plan sends (heavier payload, more likely to hit a cold-instance timeout).
        if (!data || data.status === 'error' || data.ok === false || !data.calculation) {
          renderSavePill_();
          return reject(new Error((data && (data.message || data.error)) || 'Save failed'));
        }
        if (_flTok === state.requestToken) {
          // No edits during flight; accept GAS canonical calc + mark saved.
          // BUILD-3 (2026-07-02): preserve packageVariants before GAS response
          // overwrites calculation. GAS proposalRecalculate does not return packageVariants
          // in the response shape; they live in PROPOSAL_CALC_JSON and are rehydrated on
          // workspace load, but we must not lose them in-session.
          // FIX (2026-07-06): only re-inject the cached variants when the just-flushed
          // state still HAS variants. If the user deliberately removed all variants,
          // calc.packageVariants is an empty array -- we must NOT re-add the old
          // non-empty cache over that deliberate clear, or the deletion round-trips
          // and reappears in-session (and looks reverted after reload too).
          var _flushedVariants = _flushSourceVariants;
          var _deliberateClear = Array.isArray(_flushedVariants) && _flushedVariants.length === 0;
          var _prevVariants = state.workspace.calculation && state.workspace.calculation.packageVariants;
          state.workspace.calculation = data.calculation;
          if (!_deliberateClear
              && _prevVariants && Array.isArray(_prevVariants) && _prevVariants.length > 0
              && !(data.calculation && data.calculation.packageVariants && data.calculation.packageVariants.length > 0)) {
            state.workspace.calculation.packageVariants = _prevVariants;
          } else if (_deliberateClear) {
            // Honour the deliberate clear: keep the empty array on the canonical calc.
            state.workspace.calculation.packageVariants = [];
          }
          // priceOverrides round-trip through GAS (stored in PROPOSAL_CALC_JSON).
          // Restore from returned calc; don't clear -- overrides survive recalcs.
          state.priceOverrides = (data.calculation && data.calculation.priceOverrides) || state.priceOverrides || {};
          state.editedLineItems = null;
          state.lineItemsManuallyEdited = false;
          // Sync server-computed timing fields back into local state so the
          // builder UI always reflects the latest GAS values after each save.
          // Use the defensive `data` (= res.data OR res, computed above), NOT
          // res.data directly: when the bridge returns the calc at the TOP level
          // (no .data wrapper) res.data is undefined, and reading
          // res.data.calculation here threw "Cannot read properties of undefined
          // (reading 'calculation')" and surfaced as "Save failed" (2026-07-07).
          var calc = data.calculation || {};
          if (calc.estimatedStartTime) state.workspace.calculation.estimatedStartTime = calc.estimatedStartTime;
          if (calc.artistsRequired) state.workspace.calculation.artistsRequired = calc.artistsRequired;
          // Move-aware travel origin: sync back from the canonical GAS response
          // (setTravelOriginOverride flushes immediately, unlike the instant-local
          // tier flip, so this is the round-trip point that picks up a server-side
          // lock stamp or a normalised value).
          state.travelOriginOverride = (calc.travelOriginOverride === 'current' || calc.travelOriginOverride === 'future') ? calc.travelOriginOverride : null;
          state.travelOriginLockedAt = calc.travelOriginLockedAt || '';
          _lastFlushAt = Date.now();
          clearDirty_();
        } else {
          // User typed during flush. The server-side write succeeded for the
          // pre-edit state, but local state has moved on. Keep the user's
          // newer edits + leave _draftDirty=true so the Unsaved pill stays.
          // Next Save will flush the newer state.
          _lastFlushAt = Date.now();
          renderSavePill_();
        }
        resolve({ ok: true, calculation: data.calculation });
      })
      .catch(function(err) {
        _flushInFlight = false;
        renderSavePill_();
        reject(err);
      });
  });
}

function manualSave_() {
  var btn = document.getElementById('saveDraftBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving...'; }
  // Belt-and-braces: pull current input values into state BEFORE flush, so an
  // input whose oninput/onblur didn't fire still gets persisted on click.
  if (!state.workspace) state.workspace = {};
  if (!state.workspace.calculation) state.workspace.calculation = {};
  // Note: the artist count no longer has a text input (it's the Wedding Day Team
  // stepper, which writes state.artistsRequired immediately via commitArtistsField_).
  ['timingFinishInput', 'timingStartInput'].forEach(function(id) {
    var el = document.getElementById(id);
    if (!el) return;
    var field = id === 'timingFinishInput' ? 'finishTime' : 'estimatedStartTime';
    // Write state directly. Routing through setTimingField per field is
    // unnecessary and we want zero render path here.
    state.workspace.calculation[field] = String(el.value || '').trim();
  });
  markDirty_();
  flushToGas_().then(function() {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save'; }
    showBanner('Saved', 'success');
    renderActiveStage();
  }).catch(function(err) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save'; }
    showBanner('Save failed: ' + getErrorMessage(err), 'error');
  });
}

function discardLocalChanges_() {
  if (!_draftDirty) return;
  if (!confirm('Discard unsaved changes and reload from the server?')) return;
  if (!state.currentBride) return;

  // #7: save a snapshot so the undo toast can restore it within 30s.
  var _discardedKey = draftKey_(state.currentBride);
  var _discardedSnap = null;
  try { _discardedSnap = localStorage.getItem(_discardedKey); } catch (e) {}
  try { localStorage.removeItem(_discardedKey); } catch (e) {}
  _draftDirty = false;

  // Show undo toast for 30 seconds, then reload normally.
  var _toastEl = document.createElement('div');
  _toastEl.id = 'discardUndoToast';
  _toastEl.setAttribute('role', 'status');
  _toastEl.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);'
    + 'background:var(--brand-dark,#1e1716);color:#fff;padding:12px 20px;border-radius:var(--radius-pill,3rem);'
    + 'font-size:13px;font-weight:600;z-index:9999;display:flex;align-items:center;gap:12px;'
    + 'box-shadow:0 4px 24px rgba(0,0,0,0.25)';
  _toastEl.innerHTML = 'Draft discarded'
    + (_discardedSnap
        ? ' <button onclick="window._undoDiscard()" style="background:var(--brand-primary);border:none;color:#fff;'
          + 'padding:4px 12px;border-radius:var(--radius-pill);cursor:pointer;font-size:12px;font-weight:700">Undo</button>'
        : '');
  document.body.appendChild(_toastEl);

  var _undoDone = false;
  window._undoDiscard = function() {
    if (_undoDone) return;
    _undoDone = true;
    if (_discardedSnap) {
      try { localStorage.setItem(_discardedKey, _discardedSnap); } catch (e) {}
    }
    _toastEl.remove();
    delete window._undoDiscard;
    // Re-load with restored draft
    selectBride(state.currentBride);
  };

  var _countdown = setTimeout(function() {
    if (_undoDone) return;
    _toastEl.remove();
    delete window._undoDiscard;
    selectBride(state.currentBride);
  }, 30000);
  _toastEl.querySelector && _toastEl.querySelector('button') && (_toastEl._clearFn = function() { clearTimeout(_countdown); });
}

/* =================================================================
   UTILITIES
   ================================================================= */
function escapeHtml(str) {
  var d = document.createElement('div');
  d.textContent = str || '';
  return d.innerHTML;
}

/** escapeHtml() is safe for TEXT CONTENT (browsers only entity-encode &/</> that
 * way) but NOT for an HTML ATTRIBUTE VALUE, where a literal double-quote would
 * break out of the surrounding "..." delimiter. Used for data-* attributes
 * whose value is read back via .dataset (2026-09-30, HANDOFF-proposal-builder-
 * apostrophe-name-bride-not-found-2026-09-30.md) so a bride's name can safely
 * live in an attribute instead of being re-embedded as JS source text inside
 * an inline onclick.
 * @private */
function escapeAttr_(str) {
  return escapeHtml(str).replace(/"/g, '&quot;');
}

function formatCurrency(n) {
  var v = Number(n) || 0;
  return '$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * WEDDING-YEAR TRAVEL RATE (2026-08-25): the "Per artist at $0.95/km ex GST,
 * return" label text under the Travel row was hardcoded -- correct for every
 * wedding before 2028, wrong for a 2028+ wedding now that GAS charges
 * $1.10/km ex GST for those (see the CRM/JV AUTO WeddingYearPricingSource_
 * 2026_08_24.gs travel resolvers). This never re-derives the rate itself
 * (no client-side D1 call, no risk of drifting from the real charged fee) --
 * it back-computes the EFFECTIVE per-km rate from the real fee + km GAS
 * already returned, so the label always matches what the bride is actually
 * being charged. Falls back to the historical "$0.95/km ex GST" string when
 * there's no real fee/km yet to derive from (TBC / empty states, before any
 * travel is calculated) -- same wording as before in that case, zero
 * behaviour change.
 *
 * GST FIX (2026-08-25, caught pre-deploy by the Consolidator): `totalFee`
 * (visibleWedding[].fee / calc.travelCost) is ALREADY ex-GST -- GAS's own
 * TravelCalculator.gs header is explicit: "GST is added on the subtotal at
 * the total, not baked into the line." GST is applied exactly once, in
 * ProposalCalculator.gs (quote.gst = subtotal * gstRate), after every line
 * item -- travel included -- has already been summed ex-GST. The first
 * version of this function divided fee/km by 1.10 a SECOND time on top of
 * that, understating the label (2028's real $1.10/km showed as "$1.00/km").
 * Fee is a straight dollar figure already, no unit conversion at all -- no
 * cents-to-dollars, no GST strip -- fee/km IS the ex-GST per-km rate.
 *
 * @param {number} totalFee - sum of fee (ex GST) across the travel legs being labelled
 * @param {number} totalKm - sum of roundTripKm across the same legs
 * @returns {string} e.g. "Per artist at $1.10/km ex GST, return"
 */
function wdtTravelRateLabel_(totalFee, totalKm) {
  var fee = Number(totalFee) || 0;
  var km = Number(totalKm) || 0;
  if (fee <= 0 || km <= 0) return 'Per artist at $0.95/km ex GST, return';
  var exGstPerKm = fee / km;
  return 'Per artist at $' + exGstPerKm.toFixed(2) + '/km ex GST, return';
}

/**
 * Formats a price in Moxie AUD style: "A$295.00 +10% GST" or "A$295.00"
 */
function formatAudGst(n, taxable) {
  var v = Number(n) || 0;
  var formatted = 'A$' + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (taxable) formatted += ' +10% GST';
  return formatted;
}

function formatDate(iso) {
  if (!iso) return '\u2014';
  var d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
}

function getErrorMessage(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  return err.message || String(err);
}

/* =================================================================
   SEND-OUTCOME STATE MODEL (2026-09-17, external-GPT-approved build,
   item (a) "immediate containment" -- Jess-approved direction from 4
   independent design reviews of BRIEFS/External GPT/Proposal Builder/
   Briefs/EXTERNAL-GPT-[PRODUCT-DESIGN]-proposal-builder-trust-and-
   workflow-2026-09-17.md).

   Replaces the old binary "is this a timeout, yes/no" check with a real
   4-state model: Sending / Sent / Failed / Outcome Unknown. The prior
   version conflated "we could not confirm it sent" with a hard error
   (styled + worded identically to a genuine failure) -- the exact thing
   the approved design says never to do, since a network-level
   ambiguity (timeout, dropped response) is NOT evidence the send
   failed; the send may well have succeeded server-side.

   pbClassifySendOutcome_ replaces pbIsSendTimeoutError_ -- same
   ambiguous-vs-genuine-error string matching (504/timeout/gateway/
   upstream => "could be either, must check", anything else => a real
   error the admin should see as such), but returns a named outcome
   instead of a boolean so callers can't accidentally treat "ambiguous"
   as "failed".

   pbResolveSendOutcome_ replaces pbRecoverAfterSendTimeout_ -- same
   re-check-the-real-status logic, but the terminal "could not confirm"
   case now shows a PERSISTENT banner (does not silently auto-fade after
   5s like a normal toast) styled as 'warn', never 'error', with an
   explicit "Check again" action -- an unresolved outcome must stay
   visible until the admin dismisses it or it resolves, not disappear
   and look identical to "nothing was wrong". */
function pbClassifySendOutcome_(err) {
  var msg = getErrorMessage(err).toLowerCase();
  var ambiguous = msg.indexOf('504') >= 0 ||
    msg.indexOf('gateway timeout') >= 0 ||
    msg.indexOf('timeout') >= 0 ||
    msg.indexOf('timed out') >= 0 ||
    msg.indexOf('upstream') >= 0;
  return ambiguous ? 'unknown' : 'failed';
}

/** Back-compat alias -- same classifier, boolean shape, for any external
 * caller that still expects the old yes/no signature. */
function pbIsSendTimeoutError_(err) {
  return pbClassifySendOutcome_(err) === 'unknown';
}

function pbShowOutcomeUnknownBanner_(brideName, resetBtn) {
  var slot = document.getElementById('bannerSlot');
  var msg = 'Send outcome unknown -- the request timed out before we heard back. ' +
    'This does NOT mean it failed; it may have gone through. Checking again will ' +
    'tell you for certain.';
  // Calls window.pbRecheckSendOutcome_ explicitly (not a bare bareword or a
  // .name-derived call) so this keeps working even if Shopify's theme-JS
  // minifier ever changes how it treats top-level function names -- the
  // banner's onclick only ever needs the one fixed, deliberately-exported
  // entry point, never an arbitrary caller-supplied function.
  slot.innerHTML = '<div class="banner banner-warn" role="alert">' +
    '<i class="fa-solid fa-circle-question"></i>' +
    '<span>' + escapeHtml(msg) + '</span>' +
    '<button type="button" class="btn btn-ghost btn-sm" style="margin-left:auto;white-space:nowrap" ' +
    'onclick="window.pbRecheckSendOutcome_(' + JSON.stringify(brideName) + ')">' +
    '<i class="fa-solid fa-rotate"></i> Check again</button></div>';
  clearTimeout(bannerTimer);
  // FIX (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-confirmation-
  // 2026-09-30.md, item 4): this used to call resetBtn() here unconditionally,
  // which re-enabled the Approve/Send button the instant the outcome went
  // ambiguous -- the page then looked completely unsent (button live again,
  // ready to click) while the real send may already be sitting in Gmail's
  // sent box. resetBtn() is now ONLY called from pbResolveSendOutcome_'s own
  // CONFIRMED-sent branch below, never from the still-unresolved case. The
  // button stays exactly as the initiating click left it (disabled,
  // "Sending...") until "Check again" resolves it one way or the other --
  // deliberately, so a second click can never fire a genuine double-send
  // while the first attempt's outcome is still unknown.
}

/**
 * Re-checks a bride's real proposal status after an ambiguous (timed-out)
 * send, and resolves the on-screen state to one of: Sent (confirmed),
 * Outcome Unknown (persistent, not auto-dismissed), on the FIRST check.
 * Exposed as window.pbRecheckSendOutcome_ so the persistent banner's own
 * "Check again" button (rendered as raw HTML, not a JS closure) can call
 * back into it.
 */
function pbResolveSendOutcome_(brideName, resetBtn) {
  showBanner('Checking whether the send actually completed...', 'info');
  return new Promise(function(resolve) {
    setTimeout(resolve, 8000);
  }).then(function() {
    return pbGetWorkspaceWarm_(brideName);
  }).then(function(res) {
    var payload = (res && res.data) || res;
    var status = String(payload && payload.proposal && payload.proposal.status || '').trim().toUpperCase();
    if (payload && payload.status !== 'error' && (status === 'SENT' || status === 'ACTIVE')) {
      state.workspace = payload;
      if (Array.isArray(payload.history)) state.proposalHistory = payload.history;
      state.activeStage = 'send';
      renderStageBar();
      renderStageSend();
      patchBrideLocal_(brideName, {
        proposalStatus: 'SENT',
        sentDate: (payload.proposal && payload.proposal.sentDate) || new Date().toISOString().split('T')[0]
      });
      loadBrides();
      if (typeof resetBtn === 'function') resetBtn();
      renderSavePill_();
      var _resolvedChannels = pbSendChannelSummary_(payload.proposal);
      showBanner('Confirmed sent. The earlier timeout did not stop the send from completing.' +
        (_resolvedChannels ? ' (' + _resolvedChannels + ')' : ''), 'success', true);
      return 'sent';
    }
    pbShowOutcomeUnknownBanner_(brideName, resetBtn);
    return 'unknown';
  }).catch(function() {
    pbShowOutcomeUnknownBanner_(brideName, resetBtn);
    return 'unknown';
  });
}

/** Re-check entry point the persistent "Check again" banner button calls.
 * A second/third check behaves identically to the first -- re-checks live
 * status, resolves to Sent (confirmed) or re-shows the same persistent
 * Outcome Unknown banner. Never auto-retries the SEND itself -- retrying
 * an ambiguous send blind could double-send; only the STATUS CHECK repeats. */
function pbRecheckSendOutcome_(brideName) {
  return pbResolveSendOutcome_(brideName, null);
}
window.pbRecheckSendOutcome_ = pbRecheckSendOutcome_;

function proposalBadgeClass(status) {
  var s = String(status || '').toUpperCase();
  if (s === 'SENT' || s === 'ACTIVE') return 'badge-sent';
  if (s === 'READY') return 'badge-ready';
  if (s === 'EXPIRED' || s === 'VOID') return 'badge-expired';
  if (s === 'ACCEPTED' || s === 'SIGNED') return 'badge-accepted';
  if (s === 'PENDING') return 'badge-pending';
  if (s === 'DRAFT_EDIT') return 'badge-ready';  // P.1: edit mode badge
  if (s === 'DECLINED')   return 'badge-declined';
  return 'badge-enquiry';
}

/** Sanitise proposalStatus -- if it contains JSON or is excessively long, derive a clean label */
function sanitiseProposalStatus(raw) {
  var s = String(raw || '').trim();
  if (!s) return 'NEW';
  // If it looks like JSON, derive status from content
  if (s.charAt(0) === '{' || s.charAt(0) === '[' || s.length > 30) {
    try {
      var obj = JSON.parse(s);
      if (obj && obj.STATUS) return String(obj.STATUS).toUpperCase();
    } catch (e) { /* not valid JSON, just truncate */ }
    return 'READY';
  }
  return s;
}

/* =================================================================
   BANNER
   ================================================================= */
var bannerTimer = null;
/**
 * @param {string} msg
 * @param {string} [type] success|error|warn|info
 * @param {boolean} [persistent] When true, does NOT auto-clear after 5s. Used
 *   for a genuinely unresolved state (send-outcome-unknown) that must stay
 *   visible until the admin dismisses it or the outcome resolves -- an
 *   ambiguous result auto-fading away like a normal toast would look
 *   identical to "nothing was wrong", which is the opposite of the point.
 *   A later showBanner() call (persistent or not) always replaces it.
 */
function showBanner(msg, type, persistent) {
  type = type || 'info';
  var slot = document.getElementById('bannerSlot');
  var icons = { success: 'fa-circle-check', error: 'fa-circle-xmark', warn: 'fa-triangle-exclamation', info: 'fa-circle-info' };
  // Persistent banners (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-
  // confirmation-2026-09-30.md) need a way for the admin to dismiss them --
  // without this they'd sit on screen until the next unrelated showBanner()
  // call happened to replace them.
  var dismissBtn = persistent
    ? '<button type="button" aria-label="Dismiss" ' +
      'style="margin-left:auto;background:none;border:none;cursor:pointer;opacity:0.7;padding:2px" ' +
      'onclick="document.getElementById(\'bannerSlot\').innerHTML=\'\'">' +
      '<i class="fa-solid fa-xmark"></i></button>'
    : '';
  slot.innerHTML = '<div class="banner banner-' + type + '">' +
    '<i class="fa-solid ' + (icons[type] || icons.info) + '"></i>' +
    '<span>' + escapeHtml(msg) + '</span>' + dismissBtn + '</div>';
  clearTimeout(bannerTimer);
  if (!persistent) {
    bannerTimer = setTimeout(function() { slot.innerHTML = ''; }, 5000);
  }
}

/** "29 Sep 3:42pm" -- a short, human timestamp for send-confirmation UI.
 * @private */
function pbNowStamp_() {
  var d = new Date();
  var h = d.getHours(), m = d.getMinutes();
  var ap = h >= 12 ? 'pm' : 'am';
  var h12 = h % 12; if (h12 === 0) h12 = 12;
  var mm = m < 10 ? '0' + m : String(m);
  return formatDate(d.toISOString().split('T')[0]) + ' ' + h12 + ':' + mm + ap;
}

/**
 * Builds a plain-text per-channel send summary from the proposal's SERVER-
 * RECORDED fields (proposal.emailSentAt / proposal.smsSentAt, i.e. CRM
 * columns PROPOSAL_EMAIL_SENT / PROPOSAL_SMS_SENT) -- durable across a page
 * reload or a different admin session, not a client-only flag that vanishes
 * the moment the tab is refreshed. Empty string if neither is set (e.g. a
 * Publish-only send with no notification, or a deferred send still pending).
 * @private
 */
function pbSendChannelSummary_(proposal) {
  proposal = proposal || {};
  var parts = [];
  if (proposal.emailSentAt) parts.push('Email sent ' + proposal.emailSentAt);
  if (proposal.smsSentAt) parts.push('SMS sent ' + proposal.smsSentAt);
  return parts.join('  |  ');
}

/**
 * Resets the header bar (title, meta, action buttons, save pill) back to its
 * pre-selection "no bride chosen" state. selectBride() already sets its own
 * header values on a normal bride switch -- this is for the OTHER case: an
 * action that removes the current bride from the workspace entirely (e.g.
 * Mark Unavailable & Notify closing the lead) without navigating to a new
 * bride. Without this the header silently kept showing the JUST-CLOSED
 * bride's name/date with Save/Recalculate/Review & Send still enabled, over
 * an empty "Select a bride" workspace panel underneath (HANDOFF-proposal-
 * builder-no-persistent-sent-confirmation-2026-09-30.md, addendum).
 * @private
 */
function pbResetHeaderToEmpty_() {
  var titleEl = document.getElementById('bTitle');
  if (titleEl) titleEl.textContent = 'Select a Bride';
  var metaEl = document.getElementById('bMeta');
  if (metaEl) metaEl.textContent = 'Choose a bride from the left to start building their proposal.';
  var recalcBtn = document.getElementById('recalcBtn');
  if (recalcBtn) recalcBtn.disabled = true;
  var sendBtn = document.getElementById('sendBtn');
  if (sendBtn) sendBtn.disabled = true;
  var sendLabel = document.getElementById('sendBtnLabel');
  if (sendLabel) sendLabel.textContent = 'Review & Send';
  var badgeSlot = document.getElementById('sendBtnStatusBadge');
  if (badgeSlot) badgeSlot.style.display = 'none';
  var savePill = document.getElementById('savePill');
  if (savePill) { savePill.style.display = 'none'; savePill.className = ''; }
  var discardBtn = document.getElementById('discardDraftBtn');
  if (discardBtn) discardBtn.style.display = 'none';
}

/* =================================================================
   TOAST + BLOCKING LOADING OVERLAY
   These were called by the Edit/Create-proposal flows (and now the
   add/remove-artist flow) but were never defined, throwing a
   ReferenceError that left the admin with no feedback. Defined here in
   the soft-luxury style, reusing showBanner + the .loader/.spinner CSS.
   ================================================================= */
function showToast(msg, type) { showBanner(msg, type); }

// Safety timeout (Tier A Item 3, 2026-06-23): if a GAS request stalls (Apps
// Script cold start + a slow handler can blow past any reasonable wait), the
// overlay must NOT trap the admin behind a permanent spinner. After this many
// ms we auto-dismiss and surface a non-blocking banner telling them to reload.
// The underlying request may still complete server-side, reloading shows truth.
//
// BUG FIX (2026-09-22, confirmed live: a proposal genuinely sent successfully
// while the admin saw "reload the page to check current status"): this was
// hardcoded to 90000 while snippets/jv-gas-bridge.liquid's own TIMEOUT_MS
// (the actual network-request abort timer) is 150000. Since 90s < 150s, on
// ANY send taking 90-150s (real, documented GAS cold-start territory) this
// blunt overlay timer fired FIRST and showed the generic reload message,
// while the real jvBridge.callAdmin(...) promise was still in flight and had
// not rejected yet, so the well-built pbClassifySendOutcome_/
// pbResolveSendOutcome_ recovery flow (lines ~1471-1545, which re-checks the
// real status and shows "Confirmed sent" or a persistent "Check again"
// banner) never got a chance to run for this exact failure window. It is
// only wired to each send call site's own .catch() (a genuine 150s+
// timeout/504/network failure), never to this separate, generic overlay
// timer, which is intentionally used by 12+ non-send operations too
// (travel calc, draft save, etc.) where a send-specific status check would
// be meaningless.
//
// FIX: raised above the network timeout with real margin, so the network
// layer's own timeout/rejection always fires first and the existing smart
// recovery flow gets the chance it was built for. NOT wired directly to
// pbResolveSendOutcome_ here (the more "self-healing" alternative) because
// this timer is shared by every showLoadingOverlay() caller project-wide,
// most of which are NOT sends, doing that would need a per-call-site
// "is this a send" flag threaded through show/hideLoadingOverlay, a larger
// change than this fix warrants tonight.
//
// STILL A HARDCODED DUPLICATE OF A VALUE OWNED BY A DIFFERENT FILE
// (snippets/jv-gas-bridge.liquid, Bridal Hub agent's file per AGENT-TEAM.md).
// These two numbers can drift apart again. The genuinely root-cause fix is
// exposing TIMEOUT_MS on window.jvBridge (it is currently a private closure
// variable, not read anywhere outside jv-gas-bridge.liquid itself) so this
// file can derive from ONE source instead of hardcoding a second copy.
// Flagged to Bridal Hub as a real follow-up, not done inline here since it
// touches a file this lane doesn't own.
var JV_OVERLAY_TIMEOUT_MS = 170000; // 150000 (bridge TIMEOUT_MS) plus 20000 margin
var _jvOverlayTimer = null;

function showLoadingOverlay(msg) {
  hideLoadingOverlay();
  var o = document.createElement('div');
  o.id = 'jvLoadingOverlay';
  o.setAttribute('role', 'status');
  o.setAttribute('aria-live', 'polite');
  o.style.cssText = 'position:fixed;inset:0;background:rgba(31,23,22,0.45);display:flex;align-items:center;justify-content:center;z-index:10000;padding:20px';
  o.innerHTML = '<div style="background:#fff;border-radius:var(--radius-large,1.5rem);padding:26px 30px;max-width:360px;width:100%;box-shadow:0 16px 48px rgba(169,112,108,0.2)">' +
    '<div class="loader" style="padding:0">' +
      '<div class="spinner"></div>' +
      '<div style="margin-top:12px;font-size:14px;font-weight:600;color:var(--brand-text,#2f2524);text-align:center">' + escapeHtml(msg || 'Working...') + '</div>' +
    '</div>' +
  '</div>';
  document.body.appendChild(o);

  // Arm the safety timeout. Cleared by hideLoadingOverlay() on a normal finish.
  if (_jvOverlayTimer) clearTimeout(_jvOverlayTimer);
  _jvOverlayTimer = setTimeout(function() {
    hideLoadingOverlay();
    try {
      showBanner('Still working. Reload the page to check current status.', 'warn');
    } catch (e) {}
  }, JV_OVERLAY_TIMEOUT_MS);
}

function hideLoadingOverlay() {
  if (_jvOverlayTimer) { clearTimeout(_jvOverlayTimer); _jvOverlayTimer = null; }
  var o = document.getElementById('jvLoadingOverlay');
  if (o) o.remove();
}

/* =================================================================
   SIDEBAR -- LOAD BRIDES
   ================================================================= */
// Optimistic local patch (2026-07-28, fix for BRIEFS/RESULT-proposal-builder-
// new-tab-not-clearing-2026-07-28.md, Cowork live repro): after a send/publish
// succeeds, the sidebar's correctness must not depend on the follow-up
// loadBrides() refetch actually landing. That refetch fires right after the
// heaviest possible GAS call (proposalApproveAndSend's own write/sync/comms
// chain), and the repro showed it can 504 at the Cloudflare Worker layer and
// fail completely silently -- state.brides never updates, the bride sits in
// the wrong tab until something else happens to trigger a fresh successful
// call (e.g. a page reload). Patch the ONE bride we already know changed,
// directly and instantly, so the UI is correct regardless of the refetch's
// fate; loadBrides() below is still called to reconcile everything else
// (other brides' states, counts, etc.) but is no longer load-bearing for
// THIS bride's own tab placement.
// BUG FIX (2026-09-02, live report: sent proposal did not move in the sidebar).
// Category rework (2026-09-01, commits 1b2fe3f1/678a7368) switched every filter
// branch in renderBrides() to key on the server-computed b.category field FIRST,
// falling back to b.proposalStatus only when b.category is falsy (see the
// "if (filter === 'sent') return cat ? cat === 'SENT' : (...)" pattern a few
// hundred lines below). Every existing patchBrideLocal_ call site patches
// proposalStatus but NEVER category -- so after an optimistic send/void/decline
// patch, b.category is still whatever it was BEFORE the action (e.g. 'DRAFT' or
// 'NEW'), the category-first check short-circuits true, and the proposalStatus
// fallback never runs. The bride silently stays under her old category until
// the next full loadBrides() refetch actually lands and overwrites category from
// the server -- exactly the failure mode patchBrideLocal_ was built to avoid in
// the first place (see the block comment above), just one field short of it.
// This mirrors proposalBuilder_deriveCategory_'s own status->category mapping
// (ProposalBuilderAPI.gs) for the specific statuses this file's patch call sites
// actually use -- it is deliberately NOT a full re-implementation of every
// branch in that function (no wedding-date/review/re-enquiry inputs are
// available client-side at patch time), only enough to keep an optimistic patch
// from contradicting the category-first filter it now has to pass.
var PB_STATUS_TO_CATEGORY_ = {
  'SENT': 'SENT', 'ACTIVE': 'SENT',
  'DECLINED': 'CLOSED_DECLINED',
  'VOID': 'EXPIRED'
};
function patchBrideLocal_(name, patch) {
  if (!name || !patch) return;
  for (var i = 0; i < state.brides.length; i++) {
    if (state.brides[i].name === name) {
      for (var k in patch) { if (patch.hasOwnProperty(k)) state.brides[i][k] = patch[k]; }
      if (patch.proposalStatus) {
        var mapped = PB_STATUS_TO_CATEGORY_[String(patch.proposalStatus).toUpperCase()];
        if (mapped) state.brides[i].category = mapped;
      }
      renderBrides();
      return;
    }
  }
}

function loadBrides(opts) {
  var paintedFromCache = false;
  var deepLinkFromCache = false;

  // Perf Lever 4 (Unit B) 2026-06-30: paint the inline island first (zero GAS) for
  // an instant list, THEN always refresh live in the background so new enquiries
  // appear automatically. The island only rebuilds daily at 5am, so it is a fast
  // first paint, not the source of truth. The live refresh below replaces it with
  // full-fidelity data (incl. rowNumber, which backs the newest-enquiry sort).
  // Island is active-brides only; includeDeclined always goes straight to live.
  if (!opts || !opts.includeDeclined) {
    try {
      var islandEl = document.getElementById('jv-pb-pending-cache');
      if (islandEl) {
        var cached = JSON.parse(islandEl.textContent || islandEl.innerHTML);
        if (cached && cached.ok && Array.isArray(cached.brides)) {
          state.brides = cached.brides;
          // Provisional only -- the live fetch below is what makes this authoritative.
          // Never 'live': a cache paint (even a non-empty one) has not been confirmed
          // against the real backend this page load, and the cache can legitimately
          // hold an empty brides:[] (see the bridesLoadState comment above).
          state.bridesLoadState = 'cache';
          renderBrides();
          paintedFromCache = true;
          // Deep-link candidate found on the INSTANT cache paint. Deliberately NOT
          // applied here -- see the deferred call below, after _lbTok is claimed.
          deepLinkFromCache = true;
        }
      }
    } catch (e) {
      // Island parse failed; live fetch below covers it
    }
  }

  // Only show the spinner when we have nothing on screen yet. If the cache
  // painted, the background refresh is silent (no flicker).
  if (!paintedFromCache) {
    document.getElementById('brideList').innerHTML =
      '<div class="loader"><div class="spinner"></div><div>Loading brides...</div></div>';
  }

  var reqPayload = {};
  if (opts && opts.includeDeclined) reqPayload.includeDeclined = true;

  // Race guard: bride-list refreshes own a token separate from workspace/edit
  // requests. The old shared state.requestToken made the initial loadBrides()
  // invalidate an immediate brideUid deep-link workspace response after that
  // response had already succeeded, leaving the loading skeleton forever.
  var _lbTok = ++state.bridesRequestToken;

  // Deep-link on the INSTANT cache paint, not only after the live fetch resolves.
  // The live fetch below pays the documented ~8-15s GAS cold start, so deep-linking
  // only in its .then() left an admin arriving from the bride profile staring at an
  // unfiltered ~90-row list for that whole wait -- the exact symptom reported in
  // BRIEFS/HANDOFF-proposal-builder-bride-deeplink-2026-08-28.md. The cache payload
  // carries `email` (ProposalPendingListCache.gs:129), so the match works here.
  //
  // ORDER remains useful for the instant cache paint, but list and workspace requests
  // no longer invalidate one another: selectBride() owns state.requestToken, while
  // only a newer loadBrides() call can supersede _lbTok.
  //
  // Quiet mode: the cache is a filtered, daily-5am subset, so a miss here is NOT proof
  // the bride is absent. It must not warn or latch -- the live pass re-runs against the
  // authoritative list and owns the "not found" message.
  if (deepLinkFromCache) pbApplyDeepLink_({ quiet: true });

  // D1 relay path (2026-10-15, overfetch fix, HANDOFF-proposalbuilder-
  // pendingbrides-overfetch-to-consolidator-2026-10-15.md + its own RESULTS
  // file). GAS's proposalGetPendingBrides returns the near-full roster (957
  // brides, 46s, live-probed) -- jv-crm-mirror's POST /proposal-pending-brides
  // already filters server-side with the SAME category logic
  // (deriveProposalPendingItem, a field-for-field port of GAS's own
  // proposalBuilder_deriveCategory_), it was just never wired up here. Reached
  // via the relay's generic PROXY_ENTITIES dispatcher (/partner/proposal-
  // pending-brides/read), Shopify-App-Proxy-signed + jv-admin-tag-checked,
  // D1_SYNC_SECRET injected server-side -- same pattern jv-admin-d1-search.js
  // already uses for bride-search, just inlined here rather than sharing that
  // file (its hardcoded /partner/bride-search/search path doesn't fit a
  // second entity without a parameterization pass this fix doesn't need).
  //
  // Scope: does NOT cover includeDeclined. Closed/Declined remains on its
  // dedicated legacy read until the mirror gains authoritative closed rows.
  // The active list is Cloudflare-only: transport, auth, malformed-envelope,
  // and stale-mirror failures are surfaced after one retry and never call GAS
  // (retired 2026-11-01, Jess decision d-b1-retire-gas-fallback-2026-11-01,
  // option A -- a persistent D1 failure must surface loudly, not silently
  // fall through to the slow/overfetching GAS path).
  if (!opts || !opts.includeDeclined) {
    var _d1Retrying = !!(opts && opts._retried);
    if (!window._pbAdminProxyBase) {
      failActiveBrideList_('Proposal list service is unavailable');
      return;
    }
    var _d1NativeFetch = (window._jvNativeFetch || window.fetch).bind(window);
    var _d1Url = String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-pending-brides/read';
    _d1NativeFetch(_d1Url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify({})
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (_lbTok !== state.bridesRequestToken) return; // a newer loadBrides() call owns the sidebar
        if (!body || body.ok !== true || !Array.isArray(body.brides)) {
          failActiveBrideList_((body && body.error) || 'Failed to load brides');
          return;
        }
        state.brides = body.brides;
        state.bridesLoadState = 'live';
        renderBrides();
        pbApplyDeepLink_();
      })
      .catch(function (err) {
        if (_lbTok !== state.bridesRequestToken) return;
        failActiveBrideList_(getErrorMessage(err));
      });
    return;
  }

  // Closed/Declined is not yet represented authoritatively in D1.
  loadBridesViaGas_();

  function failActiveBrideList_(message) {
    if (_lbTok !== state.bridesRequestToken) return;
    if (!_d1Retrying) {
      setTimeout(function() { loadBrides(Object.assign({}, opts, { _retried: true })); }, 3000);
      return;
    }
    state.bridesLoadState = 'failed';
    state.bridesLoadError = message || 'Failed to load brides';
    renderBrides();
  }

  // Dedicated legacy read for Closed/Declined only.
  function loadBridesViaGas_() {
  // Retry-once-with-backoff (2026-07-28, same fix as patchBrideLocal_ above):
  // the Cowork repro caught this call 504'ing at the Cloudflare Worker layer
  // when it fires right after a heavy proposalApproveAndSend chain, and the
  // failure was completely silent -- no console log, no retry, sidebar just
  // stayed stale. A single retry after the GAS instance has had a moment to
  // stop being contended fixes the common case; patchBrideLocal_ at the call
  // sites is the belt-and-braces fix that doesn't even need this to succeed.
  var _retrying = !!(opts && opts._retried);

  jvBridge.callAdmin('proposalGetPendingBrides', reqPayload)
    .then(function(res) {
      if (_lbTok !== state.bridesRequestToken) return; // a newer loadBrides() call is in flight
      // FIX (2026-09-23, BRIDGE_ADMIN_KEY rotation outage): res.ok === false is the
      // BRIDGE/TRANSPORT failure shape (a 401/403/network error resolved by
      // makeRequest's own catch chain as {ok:false, error:'...'}, no `data` key at
      // all -- confirmed live, see the curl repro in state/findings/
      // FIX-proposal-builder-lead-list-vanishes-on-auth-failure-2026-09-23.md).
      // Checking ONLY `payload.status === 'error'` (the SEPARATE, GAS-application-
      // level failure shape, {status:'error', message:'...'}) missed this entirely:
      // `res.data` is undefined on a transport failure, so `payload = res` itself,
      // which has no `.status` field at all -- `payload.status === 'error'` was
      // `undefined === 'error'`, always false. Execution fell through to the
      // SUCCESS branch, set `state.brides = payload.brides || []` (undefined ->
      // []), and stamped `bridesLoadState = 'live'` -- silently overwriting a good
      // cache-painted list with an empty one that then renders as the generic
      // "No brides match this filter" empty state instead of the real error/retry
      // path below. This is the "list flashes then disappears" bug Jess reported.
      // Belt-and-braces: check BOTH failure shapes explicitly, never assume only
      // one can occur.
      var payload = (res && res.data) || res;
      if (!payload || res.ok === false || payload.status === 'error') {
        console.warn('[loadBrides] proposalGetPendingBrides returned an error payload' +
          (_retrying ? ' (after retry)' : ', retrying once in 3s') + ':', (payload && payload.message) || (res && res.error));
        if (!_retrying) {
          setTimeout(function() { loadBrides(Object.assign({}, opts, { _retried: true })); }, 3000);
          return;
        }
        // Empty-state confusion fix (2026-08-10): the live fetch has now genuinely
        // failed (after one retry). Route through renderBrides() -- it now consults
        // bridesLoadState, so an empty cache-painted list correctly shows the error
        // state instead of the misleading "No brides match this filter". A non-empty
        // cache list is left on screen untouched (renderBrides() only overrides the
        // empty-list branch).
        state.bridesLoadState = 'failed';
        state.bridesLoadError = (payload && payload.message) || (res && res.error) || 'Failed to load brides';
        renderBrides();
        return;
      }
      state.brides = payload.brides || [];
      state.bridesLoadState = 'live';
      renderBrides();
      pbApplyDeepLink_();
    })
    .catch(function(err) {
      if (_lbTok !== state.bridesRequestToken) return; // a newer loadBrides() call is in flight
      console.warn('[loadBrides] proposalGetPendingBrides failed' +
        (_retrying ? ' (after retry), giving up' : ', retrying once in 3s') + ':', getErrorMessage(err));
      if (!_retrying) {
        setTimeout(function() { loadBrides(Object.assign({}, opts, { _retried: true })); }, 3000);
        return;
      }
      state.bridesLoadState = 'failed';
      state.bridesLoadError = getErrorMessage(err);
      // Same reasoning as the error-payload branch above: route through renderBrides()
      // so an empty cache-painted list shows the error state, not the empty-filter one.
      renderBrides();
    });
  }
}

// Deep-link from admin-bride-profile / other tools: /pages/proposal-builder?bride=<encoded email>
// or /pages/proposal-builder?bride=<email>&brideUid=<durable id>.
// Matched by EMAIL by default because that's the identifier most linking pages carry --
// but EMAIL IS NOT UNIQUE in this CRM (duplicate enquiry/re-enquiry rows are a documented
// recurring pattern -- see BrideUidResolver.gs for the two real row-destruction incidents
// this exact class of ambiguity already caused elsewhere). When a caller supplies the
// optional ?brideUid= param (2026-09-10, HANDOFF-proposal-deeplink-email-not-unique), it
// is matched FIRST and takes priority over email -- this is the durable, row-position-
// independent key, never a row number. Falls back to the email match only when brideUid
// is absent or not found in the current list, so every existing caller that only ever
// sends ?bride= keeps working exactly as before.
// Runs once per page load, only when no bride is selected yet, so it never fights a manual pick.
// opts.quiet: called against the provisional CDN cache paint rather than the live list.
// A miss there is INCONCLUSIVE (the cache is a filtered, daily-5am subset), so quiet mode
// neither warns nor latches -- it leaves the deep-link pending so the live pass re-runs
// against the authoritative list and owns the "not found" message. Only a definitive
// outcome (a hit, or a live-list miss) sets _pbDeepLinkApplied.
//
// DIRECT-LOAD FALLBACK (2026-09-10, BUILD-proposal-builder-ux-fixes item 6): state.brides
// (from proposalGetPendingBrides) is the ACTIVE/PENDING queue only -- a booked, archived,
// closed, or otherwise-resolved bride is never in it (confirmed live this session: a
// Kaylee Bain row with BOOKED?=YES and a samantharowe2017@icloud.com row with BOOKED?=Closed
// both silently missed the list-based match). That is exactly the kind of bride a stale/SENT
// Pipeline card (item 6's own use case) needs to deep-link to. Previously a list miss went
// straight to "not found", even when we had a real brideUid in hand. Now: if brideUid is
// present and the list match misses, attempt ONE direct load via selectBride anyway --
// selectBride calls proposalGetWorkspace independent of state.brides, and per this session's
// earlier brideUid fix, proposalBuilder_getBrideRowFast_ resolves by brideUid via the shared
// brideUid_resolve_ BEFORE ever touching the name argument, so passing the email as a
// placeholder name here is safe (it's never used for matching once brideUid is supplied).
// Email-only (no brideUid) deep links are UNCHANGED -- still show "not found" on a list miss,
// since there is no durable key to attempt a direct load with, and email alone is exactly the
// ambiguous signal this whole fix exists to move away from.
var _pbDeepLinkApplied = false;
function pbApplyDeepLink_(opts) {
  var quiet = !!(opts && opts.quiet);
  if (_pbDeepLinkApplied || state.currentBride) return;
  try {
    var params = new URLSearchParams(window.location.search);
    var deepLinkEmail = (params.get('bride') || '').trim().toLowerCase();
    var deepLinkUid = (params.get('brideUid') || '').trim();
    if (!deepLinkEmail && !deepLinkUid) return;

    var match = null;
    if (deepLinkUid) {
      match = (state.brides || []).find(function(b) {
        return b.brideUid && b.brideUid === deepLinkUid;
      });
    }
    if (!match && deepLinkEmail) {
      match = (state.brides || []).find(function(b) {
        return b.email && b.email.trim().toLowerCase() === deepLinkEmail;
      });
    }
    if (match) {
      _pbDeepLinkApplied = true;
      selectBride(match.name, match.brideUid);
      return;
    }
    if (quiet) return; // inconclusive against the cache; let the live pass decide

    if (deepLinkUid) {
      // Not in the active/pending list, but we have a durable key -- try a direct load
      // before giving up. The email (if present) is passed only as a required-but-unused
      // placeholder for proposalGetWorkspace's brideName argument (see comment above).
      // name= (2026-09-10, live-verify addendum): optional, display-only -- see
      // pbTryImmediateUidDeepLink_'s matching comment.
      var deepLinkName = (params.get('name') || '').trim();
      _pbDeepLinkApplied = true;
      selectBride(deepLinkEmail || deepLinkUid, deepLinkUid, deepLinkName);
      return;
    }

    _pbDeepLinkApplied = true;
    showBanner('Bride not found in this list (may be booked, expired, or older than 2 years). Search by name below.', 'warn');
  } catch (e) { /* deep-link parse error, non-fatal */ }
}

function setSortOrder(order) {
  state.activeSortOrder = order;
  try { localStorage.setItem('jv_proposal_sort', order); } catch (e) {}
  renderBrides();
}

// Option 1 (2026-07-27, Cowork investigation BRIEFS/RESULTS-proposal-builder-
// sort-redesign-2026-07-27.md, Jess picked "option 1"): the prior default
// (enquiry-desc, "newest enquiry first") interleaved SENT brides -- nothing to
// do until she replies -- with NEW/READY brides needing real action. Live data
// showed 9 of the top 15 rows were SENT, burying the 6 that actually needed
// work. Groups by action-needed first, tiebreaks by wedding date within each
// tier, so the top of the list is always the actual to-do list.
function actionPriorityTier_(b) {
  var ps = String(b.proposalStatus || '').toUpperCase();
  // Tier 3: done / archive -- resolved one way or another, nothing to action.
  // SIGNED added 2026-09-08: without it a signed bride fell through to Tier 1
  // ("needs action") and wrongly sorted to the top of the admin to-do list
  // alongside brand-new leads, the opposite of this sort's purpose.
  if (ps === 'ACCEPTED' || ps === 'SIGNED' || ps === 'DECLINED' || ps === 'EXPIRED' || ps === 'VOID') return 3;
  // Tier 2: waiting on the bride -- a proposal already went out.
  if (ps === 'SENT' || ps === 'ACTIVE') return 2;
  // Tier 1: needs action -- no proposal sent yet, or built but not sent
  // (NEW / LEAD / empty / DRAFT_EDIT / READY all land here).
  return 1;
}

function sortBrides(arr, opt_order) {
  var order = opt_order || state.activeSortOrder;
  var sorted = arr.slice().sort(function(a, b) {
    if (order === 'name-asc') {
      return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0;
    }
    if (order === 'enquiry-desc') {
      // Newest enquiry first. Bug 0b fix (2026-08-03, BRIEFS/RESULTS-COWORK-gmail-
      // enquiries-vs-proposal-builder-sorting-2026-08-03.md): the old comparator used
      // enquiryDate (yyyy-MM-dd, date-only), so same-day enquiries were indistinguishable
      // and fell through to the rowNumber tiebreak, which does not track actual submission
      // time. enquiryTimestamp (epoch ms, sent by the list loader since 2026-08-03) gives
      // real time-of-day precision when available. Falls back to the date-string + rowNumber
      // comparison below for legacy rows with no timestamp (Jess decision 5, 2026-07-09).
      var at = a.enquiryTimestamp || 0, bt = b.enquiryTimestamp || 0;
      if (at && bt) return bt - at;
      if (at && !bt) return -1;
      if (!at && bt) return 1;
      var ad = a.enquiryDate || '', bd = b.enquiryDate || '';
      if (ad && bd) return ad < bd ? 1 : ad > bd ? -1 : 0;
      if (ad && !bd) return -1;
      if (!ad && bd) return 1;
      return (b.rowNumber || 0) - (a.rowNumber || 0);
    }
    if (order === 'wedding-asc') {
      return a.weddingDate < b.weddingDate ? -1 : a.weddingDate > b.weddingDate ? 1 : 0;
    }
    // Default: action-priority (Tier 1 needs-action / Tier 2 sent-waiting /
    // Tier 3 done-archive), wedding date ascending within each tier.
    var at = actionPriorityTier_(a), bt = actionPriorityTier_(b);
    if (at !== bt) return at - bt;
    return a.weddingDate < b.weddingDate ? -1 : a.weddingDate > b.weddingDate ? 1 : 0;
  });
  return pinReEnquiries_(sorted);
}

/**
 * Item 3c (BUILD-item1-item3-planner-and-reenquiry-2026-07-19): re-enquiry rows are
 * pinned to the top of the list, ABOVE whatever sort order is active (Jess wants a
 * returning bride to be impossible to miss regardless of how the list is sorted).
 * Stable pin: re-enquiries keep their relative order (newest-enquiry-first, since the
 * array arriving here is already sorted), non-re-enquiries keep theirs.
 */
function pinReEnquiries_(arr) {
  var pinned = [], rest = [];
  for (var i = 0; i < arr.length; i++) {
    (arr[i].isReEnquiry ? pinned : rest).push(arr[i]);
  }
  return pinned.concat(rest);
}

// Category rework (2026-09-01, PLAN-proposal-builder-category-rework-2026-09-01.md).
// The old newBridePredicate/draftBridePredicate/needsReviewPredicate each re-derived
// a bride's category from raw fields client-side -- a second copy of logic that could
// silently drift from the server's own classification (exactly the class of bug this
// project's D1-mirror worker file (jv-crm-mirror/worker.js) documents happening twice
// already, enquiry_date and atRisk, between GAS and its own Cloudflare-side copy).
// proposalBuilder_deriveCategory_ (ProposalBuilderAPI.gs) is now the SINGLE source of
// truth; the server stamps its answer onto b.category and these three predicates just
// read it. Do not reintroduce client-side re-derivation here.
function newBridePredicate(b) {
  return String(b.category || '').toUpperCase() === 'NEW';
}
function draftBridePredicate(b) {
  return String(b.category || '').toUpperCase() === 'DRAFT';
}
function needsReviewPredicate(b) {
  return String(b.category || '').toUpperCase() === 'NEEDS_REVIEW';
}

/**
 * Needs Review assisted action desk, Phase 0/1
 * (BRIEFS/PB-needs-review-assisted-action-desk-2026-09-02.md).
 * Small relative-time label for an ISO timestamp -- no existing helper for
 * this in the file (checked before writing), plain-JS to match the rest of
 * this file's style, no new library dependency.
 */
function relativeTimeAgo_(isoString) {
  if (!isoString) return '';
  var then = new Date(isoString);
  if (isNaN(then.getTime())) return '';
  var diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return '';
  var mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  var hours = Math.floor(mins / 60);
  if (hours < 24) return hours + 'h ago';
  var days = Math.floor(hours / 24);
  return days + 'd ago';
}
function reEnquiryPredicate_(b) {
  return String(b.category || '').toUpperCase() === 'RE_ENQUIRY';
}

/**
 * Business Rule 2 (CLAUDE.md): archived brides are excluded from every surface.
 * A bride is archived when her WEDDING DATE is more than 60 days past. Brides with
 * no wedding date are active leads and are NEVER archived.
 *
 * This is a WEDDING-DATE test and is deliberately separate from `b.isActive`, which
 * the backend derives from proposal STATUS only (ProposalBuilderAPI.gs ~1942). The two
 * answer different questions: isActive = "is this lead still being worked", archived =
 * "is this wedding long gone". An EXPIRED bride is isActive===false yet legitimately
 * belongs under the Expired pill, unless her wedding was 17 months ago.
 *
 * Mirrors JV_isArchivedBride in BusinessRuleGuards.gs. Applied to BOTH the rendered
 * list and the pill counts so the two can never disagree.
 */
var PB_ARCHIVE_CUTOFF_DAYS = 60;
function isArchivedBride_(b) {
  var wd = b && b.weddingDate ? String(b.weddingDate).trim() : '';
  if (!wd) return false;                       // no date = active lead, never archived
  var m = wd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;                        // unparseable = fail OPEN, never hide a bride
  var wedding = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(wedding.getTime())) return false;
  var cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - PB_ARCHIVE_CUTOFF_DAYS);
  return wedding < cutoff;
}

/**
 * Closed/Declined = server category CLOSED_DECLINED (2026-09-01 category rework):
 * CLOSE_REASON/CLOSE_DATE set, PROPOSAL_STATUS===DECLINED, OR wedding date has
 * passed unbooked (the old separately-tracked "past wedding date" EXPIRED
 * definition folded in here, per Jess's own #7 wording -- "declined OR past
 * wedding date"). Falls back to the raw closeReason/closeDate fields if an
 * older backend response predates the category field, same defensive pattern
 * as the isActive fallback in the filter dispatch below.
 */
function isClosedBride_(b) {
  if (!b) return false;
  if (b.category) return String(b.category).toUpperCase() === 'CLOSED_DECLINED';
  return !!(String(b.closeReason || '').trim() || String(b.closeDate || '').trim());
}

// Category rework (2026-09-01): 'ready' retired, 're-enquiry' added. Counts now
// prefer b.category, falling back to the old proposalStatus checks only when an
// older backend response predates the category field (same defensive pattern
// used throughout this file, e.g. the isActive fallback in the filter dispatch).
function updateFilterPillCounts() {
  var counts = { 'new': 0, 'draft': 0, 'needs-review': 0, 're-enquiry': 0, 'sent': 0, 'accepted': 0, 'expired': 0 };
  for (var i = 0; i < state.brides.length; i++) {
    var b = state.brides[i];
    if (isArchivedBride_(b)) continue;         // counts must match the rendered list
    if (isClosedBride_(b)) continue;           // ditto; no 'declined' pill in this counts map
    var ps = String(b.proposalStatus || '').toUpperCase();
    var cat = String(b.category || '').toUpperCase();
    if (newBridePredicate(b))     counts['new']++;
    if (draftBridePredicate(b))   counts['draft']++;
    if (needsReviewPredicate(b))  counts['needs-review']++;
    if (reEnquiryPredicate_(b))   counts['re-enquiry']++;
    if (cat ? cat === 'SENT'     : (ps === 'SENT' || ps === 'ACTIVE'))  counts['sent']++;
    if (cat ? cat === 'ACCEPTED' : (ps === 'ACCEPTED' || ps === 'SIGNED')) counts['accepted']++;
    if (cat ? cat === 'EXPIRED'  : (ps === 'EXPIRED' || ps === 'VOID')) counts['expired']++;
  }
  var labels = {
    'new': 'New', 'draft': 'Draft', 'needs-review': 'Needs Review',
    're-enquiry': 'Re-enquiry', 'sent': 'Sent', 'accepted': 'Accepted', 'expired': 'Expired'
  };
  Object.keys(counts).forEach(function(k) {
    var pill = document.querySelector('.filter-btn[data-filter="' + k + '"]');
    if (!pill) return;
    var n = counts[k];
    pill.textContent = n > 0 ? labels[k] + ' ' + n : labels[k];
  });
}

var _globalSearchTimer = null;
// Honest search-progress UI (2026-09-10, BUILD-proposal-builder-ux-fixes item 8a). The
// global-search POST measured 20s/30s/40s pending across three real attempts, with the
// UI showing only static "Searching..." the whole time -- no spinner, no elapsed time,
// no timeout, no cancel. Retyping (the natural response to an apparently-stuck screen)
// discarded the in-flight request and restarted the 40s clock. This is presentation
// only (per the brief, item 8b names the underlying GAS cold-start cause as its own
// separate scoping item, not fixed here). _gsRequestSeq_ guards against a stale
// response painting over a NEWER search the admin already started.
var _gsRequestSeq_ = 0;
var _gsElapsedTimer_ = null;

// Renders a client-search result list into #globalSearchResults. Shared by both the
// CDN-cache path and the live GAS path so they can never silently diverge in shape.
function pbRenderClientSearchResults_(list, resultsEl) {
  state.clientSearchResults = list;
  if (!list.length) {
    resultsEl.innerHTML = '<div style="padding:8px 12px;font-size:12px;color:var(--muted)">No clients found</div>';
    return;
  }
  var html = '';
  for (var i = 0; i < list.length; i++) {
    var r = list[i];
    html += '<div style="padding:8px 12px;cursor:pointer;font-size:12px;border-bottom:1px solid var(--border-color,#e8ddd3)" ' +
      'onclick="selectClientSearchResult_(' + i + ')">' +
      '<div style="font-weight:600;color:var(--brand-dark)">' + escapeHtml(r.name || r.fullName || ((r.firstName || '') + ' ' + (r.lastName || '')).trim()) + '</div>' +
      '<div style="color:var(--muted)">' + escapeHtml(r.email || '') + '</div>' +
    '</div>';
  }
  resultsEl.innerHTML = html;
}

// Client-search CDN cache (2026-09-11, AdminClientSearchCache.gs, commit 96b8a58e,
// Cloudflare/Edge). Fixes the exact bottleneck the item-8a fix above only papered
// over: shopifyBridge_adminSearchClients paid a full-sheet read on every keystroke
// -- this reads the same data from a pre-warmed CDN island instead. Reuses
// pbInflateIslandValue_ (this file's own gzip-aware island decoder, already proven
// on the ProposalBrideCache path above) since the metafield writer uses the same
// Service Catalog CDN fast-path (2026-09-22, Step 2/3 of BUILD-proposal-builder-d1-
// full-cache-option-a-2026-08-05.md's Step 1 writer, ProposalServiceCatalogCache.gs).
// Reads shop.metafields.custom.proposal_service_catalog_json (embedded plain JSON,
// NOT gzip'd -- the writer uses a plain JSON.stringify, unlike the client-search
// cache above) as an instant CDN-served override for state.workspace.serviceCatalog,
// SHOP-WIDE and identical for every bride, so it only needs reading ONCE per page
// load, not once per selectBride() call. Falls back to whatever proposalGetWorkspace's
// own live response already carried (byte-identical to pre-cache behaviour) whenever
// the island is missing, empty, or malformed -- never a silent half-migrate.
var _pbServiceCatalogFromCdn = undefined; // undefined = not yet attempted, null = attempted+unavailable, array = loaded
function pbServiceCatalogFromCdn_() {
  if (_pbServiceCatalogFromCdn !== undefined) return _pbServiceCatalogFromCdn;
  try {
    var el = document.getElementById('jv-service-catalog-cache');
    var raw = el ? (el.textContent || el.innerHTML) : '';
    if (!raw || raw.trim() === '' || raw.trim() === 'null') { _pbServiceCatalogFromCdn = null; return null; }
    var parsed = JSON.parse(raw);
    var list = (parsed && Array.isArray(parsed.catalog)) ? parsed.catalog : null;
    _pbServiceCatalogFromCdn = (list && list.length) ? list : null;
  } catch (e) { _pbServiceCatalogFromCdn = null; }
  return _pbServiceCatalogFromCdn;
}

// JVGZ_-prefixed encoding as every other cache in this project. Loaded once per page
// load and cached in memory (_pbClientSearchCache) -- the island itself is only
// refreshed hourly/daily server-side, no reason to re-fetch/re-decode it per keystroke.
var _pbClientSearchCache = null; // null = not yet attempted, [] = attempted, empty/absent, array = loaded
var _pbClientSearchCacheLoading = null; // in-flight Promise, so concurrent keystrokes share one load
function pbLoadClientSearchCache_() {
  if (_pbClientSearchCache) return Promise.resolve(_pbClientSearchCache);
  if (_pbClientSearchCacheLoading) return _pbClientSearchCacheLoading;
  _pbClientSearchCacheLoading = new Promise(function(resolve) {
    try {
      var el = document.getElementById('jv-client-search-cache');
      var raw = el ? (el.textContent || el.innerHTML) : '';
      if (!raw || raw.trim() === 'null' || raw.trim() === '') { resolve(null); return; }
      pbInflateIslandValue_(raw).then(function(parsed) {
        var list = (parsed && Array.isArray(parsed.data)) ? parsed.data : null;
        _pbClientSearchCache = list; // null on failure -- every future call falls back to live, no repeated retries
        resolve(list);
      });
    } catch (e) { resolve(null); }
  });
  return _pbClientSearchCacheLoading;
}

// Mirrors adminCrm_normaliseSearchString_ (AdminCRMAPI.gs) exactly, so a client-side
// match against the cache's pre-built (server-normalised) haystack field behaves
// identically to the live search -- same casing rule, same smart-quote normalisation.
function pbNormaliseSearchString_(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[‘’‚‛′‵`]/g, "'")
    .trim();
}

function triggerGlobalSearch_() {
  var query = document.getElementById('search').value.trim();
  if (!query || query.length < 2) return;
  var results = document.getElementById('globalSearchResults');
  if (!results) return;
  results.style.display = 'block';
  clearTimeout(_globalSearchTimer);
  _globalSearchTimer = setTimeout(function() {
    var mySeq = ++_gsRequestSeq_;

    // Try the CDN cache FIRST. A miss/absent/unusable island falls straight through
    // to the exact same live path this always used -- never a regression, only ever
    // a speed win when the cache is present and warm.
    pbLoadClientSearchCache_().then(function(cacheList) {
      if (mySeq !== _gsRequestSeq_) return; // superseded by a newer keystroke
      if (Array.isArray(cacheList)) {
        var qNorm = pbNormaliseSearchString_(query);
        var matches = [];
        for (var i = 0; i < cacheList.length && matches.length < 25; i++) {
          var row = cacheList[i];
          if (row && row.haystack && row.haystack.indexOf(qNorm) !== -1) matches.push(row);
        }
        // Same relevance order the live route already returns (booked first, then
        // soonest wedding date) -- the cache's own rows are pre-sorted this way by
        // the shared search index (AdminClientSearchCache.gs), so no re-sort needed.
        pbRenderClientSearchResults_(matches, results);
        return;
      }
      liveGlobalSearch_(query, mySeq, results);
    });
  }, 300);
}

// The original live GAS search path, unchanged in behaviour -- extracted verbatim
// from triggerGlobalSearch_ so the CDN-cache path above can fall back to it exactly
// as before (2026-09-11, wiring AdminClientSearchCache.gs).
function liveGlobalSearch_(query, mySeq, results) {
  var startedAt = Date.now();
  clearInterval(_gsElapsedTimer_);

  function paintSearching_() {
    if (mySeq !== _gsRequestSeq_) return; // a newer search has since started
    var elapsedSec = Math.round((Date.now() - startedAt) / 1000);
    results.innerHTML = '<div style="padding:10px 12px;font-size:12px;color:var(--muted);display:flex;align-items:center;gap:8px">' +
      '<i class="fa-solid fa-spinner fa-spin"></i>' +
      '<span>Searching all clients, this can take up to a minute' + (elapsedSec > 0 ? ' (' + elapsedSec + 's)' : '') + '...</span>' +
    '</div>';
  }
  paintSearching_();
  _gsElapsedTimer_ = setInterval(paintSearching_, 1000);

  // Client-side timeout so an unusually slow/hung call resolves to a clear retry
  // rather than an indefinite "Searching..." -- does NOT abort the underlying
  // network request (jvBridge has no cancel primitive), just stops waiting on it
  // for THIS render; a late response is still discarded correctly by the mySeq guard.
  var timedOut = false;
  var timeoutTimer = setTimeout(function() {
    timedOut = true;
    if (mySeq !== _gsRequestSeq_) return;
    clearInterval(_gsElapsedTimer_);
    results.innerHTML = '<div style="padding:10px 12px;font-size:12px;color:var(--muted)">' +
      'Still searching after 60s, longer than usual.' +
      ' <button class="btn-link-inline" onclick="triggerGlobalSearch_()" style="font-size:12px;color:var(--brand-primary);font-weight:600;background:none;border:none;cursor:pointer;padding:0;text-decoration:underline">Retry</button>' +
    '</div>';
  }, 60000);

  jvBridge.callAdmin('bridge_admin_searchClients', { query: query, includeNonBridal: true })
    .then(function(res) {
      clearTimeout(timeoutTimer);
      clearInterval(_gsElapsedTimer_);
      if (mySeq !== _gsRequestSeq_ || timedOut) return; // superseded or already showed the timeout state
      var payload = (res && res.data) || res;
      var list = Array.isArray(payload) ? payload : (payload && payload.results) || (payload && payload.brides) || [];
      pbRenderClientSearchResults_(list, results);
    })
    .catch(function() {
      clearTimeout(timeoutTimer);
      clearInterval(_gsElapsedTimer_);
      if (mySeq !== _gsRequestSeq_ || timedOut) return;
      results.innerHTML = '<div style="padding:8px 12px;font-size:12px;color:var(--muted)">' +
        'Search failed.' +
        ' <button class="btn-link-inline" onclick="triggerGlobalSearch_()" style="font-size:12px;color:var(--brand-primary);font-weight:600;background:none;border:none;cursor:pointer;padding:0;text-decoration:underline">Retry</button>' +
      '</div>';
    });
}
/* IDENTITY SIDE-TABLE for brides that are NOT in state.brides (2026-11-03, Lane B).
   state.brides is the ACTIVE/PENDING queue only. "Search all clients"
   (selectClientSearchResult_ below) is the one route that can select a bride who
   was never in that list, and it used to hand selectBride() nothing but a display
   NAME -- discarding the email its own result row already carried. Everything
   downstream that needs a bride's durable identity re-derives it by looking the
   name up in state.brides, so for a search-selected bride every such lookup
   missed and the identity resolved to ''. pbGetWorkspaceWarm_ is where that bit:
   with no email it cannot evaluate the Cloudflare workspace scope at all, so the
   read fell through to GAS proposalGetWorkspace -- which WRITES. That made the
   Cloudflare workspace path unreachable through this route and put a
   write-on-read on every bride opened this way.

   Deliberately a separate map rather than pushing the search row into
   state.brides: those rows are a different shape (fullName/rowIndex, no
   name/rowNumber) and include non-bridal and booked clients, so merging them
   would both break renderBrides (which reads b.name.toLowerCase()) and put
   resolved clients back into the daily working queue the pill filters
   deliberately exclude.

   Keyed by the same `name` string selectBride() stores in state.currentBride, so
   the identity also survives the in-session reloads that call
   selectBride(state.currentBride) after a save, send or artist change.

   This repairs the MECHANISM only. It does not widen WHO is eligible: the
   Cloudflare read still requires the resolved email to be on
   window.JV_PB_WORKSPACE_CF_SCOPE_EMAILS, untouched here. */
var _pbIdentityByName = {};

/* The key is a DISPLAY NAME, which is the weakest identifier this codebase has, and a miss here
   is not a cosmetic failure: pbGetWorkspaceWarm_ falls through to
   jvBridge.callAdmin('proposalGetWorkspace'), which WRITES (the W1 first-calc branch). So a
   stored key and a lookup key that differ only in case or spacing silently convert a read-only
   Cloudflare load into a GAS write on a real bride.

   Observed 2026-11-03 (Main Consolidator, live on the real admin page): resolving
   'ZZ Stabled Collision Test' against a stored 'ZZ Stable1 Collision Test' returned null and went
   straight for the GAS write path. That specific miss was a typo in a probe, but the same miss is
   reachable in production without one: selectBride(state.currentBride) re-enters after a save,
   send or artist change with a name that was re-derived rather than carried, and
   selectClientSearchResult_ composes it from either `name`, `fullName`, or firstName + lastName
   depending on which search path served the row -- three spellings of one bride, one of which is
   whatever the CRM cell happens to hold, trailing spaces and all.

   Normalising both sides is the cheap half of the fix: case and whitespace can no longer split a
   key. It does NOT make the name a sound identifier, and it deliberately does not widen WHO is
   eligible -- the Cloudflare read still requires the resolved email to be on
   window.JV_PB_WORKSPACE_CF_SCOPE_EMAILS. The real fix is for callers to carry brideUid/email
   instead of a name, which is a larger change across every selectBride call site. */
function pbIdentityKey_(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function pbRememberIdentity_(name, email, brideUid) {
  var key = pbIdentityKey_(name);
  if (!key) return;
  var e = String(email || '').trim();
  var u = String(brideUid || '').trim();
  if (!e && !u) return;
  var prev = _pbIdentityByName[key] || {};
  _pbIdentityByName[key] = { email: e || prev.email || '', brideUid: u || prev.brideUid || '' };
}

function pbResolveIdentity_(name) {
  var key = pbIdentityKey_(name);
  return (key && _pbIdentityByName[key]) || null;
}

function selectClientSearchResult_(idx) {
  var r = state.clientSearchResults[idx];
  if (!r) return;
  // r.fullName (2026-09-11): the CDN-cache path's row shape, added alongside the
  // existing name/firstName+lastName fallbacks so both search paths resolve the
  // same way regardless of which one served this result.
  var name = r.name || r.fullName || ((r.firstName || '') + ' ' + (r.lastName || '')).trim();
  var strip = document.getElementById('globalSearchStrip');
  if (strip) strip.style.display = 'none';
  var searchInput = document.getElementById('search');
  if (searchInput) searchInput.value = '';
  // Keep the identity this result row already carries (see pbRememberIdentity_
  // above). The row is the ONLY place it exists: this bride is not in
  // state.brides, so nothing downstream can re-derive her email from her name.
  // bridge_admin_searchClients and the CDN search cache both return `email`;
  // neither returns brideUid today, so that stays optional.
  if (name) pbRememberIdentity_(name, r.email, r.brideUid);
  if (name) selectBride(name);
}

// P3d: branded loading state for the cold workspace load. A soft JV-marked
// skeleton (card placeholders + shimmer) so the page reads as assembling, not
// hanging. Replaces the bare "Loading CRM data..." spinner.
function buildPbLoadingSkeleton_() {
  function line(w, lg) { return '<div class="pb-skel-line w' + w + (lg ? ' lg' : '') + '"></div>'; }
  return '<div class="pb-skeleton" role="status" aria-label="Loading proposal workspace">' +
      '<div class="pb-skeleton-head">' +
        '<img class="pb-skeleton-mark" src="https://cdn.shopify.com/s/files/1/0331/8834/8036/files/JV_Logo_transparent.png?v=1613756889" alt="">' +
        '<div class="pb-skeleton-caption">Preparing the package builder</div>' +
      '</div>' +
      '<div class="pb-skel-card">' + line(40, true) + line(80) + line(60) + '</div>' +
      '<div class="pb-skel-card">' + line(30, true) + '<div class="pb-skel-grid"><span></span><span></span><span></span></div></div>' +
      '<div class="pb-skel-card">' + line(35, true) + line(90) + line(70) + line(50) + '</div>' +
    '</div>';
}

function renderBrides() {
  var list = document.getElementById('brideList');
  var rawQuery = document.getElementById('search') ? document.getElementById('search').value.trim() : '';
  var query = rawQuery.toLowerCase().replace(/[''‚‛′‵`]/g, "'");
  var filter = state.activeFilter;

  updateFilterPillCounts();

  /* SEARCH OVERRIDES THE FILTER (Jess, 2026-08-10).
     Typing a name is an explicit request for THAT bride. Hiding her because she
     sits in a category the current pill excludes makes the search look broken:
     Jess searched a closed-date enquiry, got nothing, and reasonably concluded
     the bride was unsearchable. She was loaded, just filtered out one line later.
     So when there is a query, the category pill stops narrowing and the search
     runs across every bride held in state, closed ones included. Archived is the
     one exception below: that is Business Rule 2, not a UI preference. */
  var searching = !!query;

  var filtered = state.brides.filter(function(b) {
    if (query && b.name.toLowerCase().indexOf(query) < 0) return false;
    if (isArchivedBride_(b)) return false;     // Business Rule 2, applies to EVERY filter
    if (searching) return true;                // named her explicitly: never hide her
    // Closed leads (Lost / Declined / Booked Out) are resolved and drop out too (Jess,
    // 2026-08-10). The Declined pill is the ONE deliberate exception: showing closed
    // brides is its entire purpose, so excluding them there would empty it.
    if (filter !== 'declined' && isClosedBride_(b)) return false;
    var ps = String(b.proposalStatus || '').toUpperCase();
    if (filter === 'all') {
      // Default working list = active leads only. Booked/accepted/expired/void/declined
      // drop out (still reachable via their dedicated filters below). Prefer the backend
      // isActive flag; fall back to a client-side status check if an older backend
      // response predates the flag.
      if (b.isActive === false) return false;
      if (b.isActive === undefined &&
          (ps === 'ACCEPTED' || ps === 'SIGNED' || ps === 'EXPIRED' || ps === 'VOID' ||
           b.status === 'YES' || b.status === 'TRUE' || b.status === 'BOOKED')) return false;
      return ps !== 'DECLINED';
    }
    // Bug fix (2026-08-10, BRIEFS/RESULTS-COWORK-E2E-GRADE3-proposal-surfaces-2026-08-10-
    // SESSION-B.md, L2): this branch used to `return true` unconditionally, so with the
    // exclusion above skipped for 'declined' (line ~1628), EVERY non-archived bride passed
    // through -- not just closed ones. The chip looked identical to "All Open" whenever the
    // search box was empty. isClosedBride_ is the same check the exclusion above already
    // uses; the declined branch must actively require it, not just decline to exclude it.
    // Category rework (2026-09-01): every branch below now prefers the server's
    // b.category (proposalBuilder_deriveCategory_, ProposalBuilderAPI.gs) over a
    // client-side re-derivation from raw proposalStatus. 'ready' pill retired --
    // its old ps==='READY' cohort now lands under 'draft' (category DRAFT), per
    // the traced/locked decision that Ready always meant an in-progress saved
    // proposal, same as the new tightened Draft definition.
    var cat = String(b.category || '').toUpperCase();
    if (filter === 'declined')     return isClosedBride_(b);
    if (filter === 'new')          return newBridePredicate(b);
    if (filter === 'draft')        return draftBridePredicate(b);
    if (filter === 'needs-review') return needsReviewPredicate(b);
    if (filter === 're-enquiry')   return reEnquiryPredicate_(b);
    if (filter === 'sent')         return cat ? cat === 'SENT' : (ps === 'SENT' || ps === 'ACTIVE');
    if (filter === 'expired')      return cat ? cat === 'EXPIRED' : (ps === 'EXPIRED' || ps === 'VOID');
    if (filter === 'accepted')     return cat ? cat === 'ACCEPTED' : (ps === 'ACCEPTED' || ps === 'SIGNED');
    return true;
  });

  // Cross-filter search hint (2026-08-10, Cowork E2E finding #5).
  //
  // "All" is a WORKING QUEUE, not literally everything: accepted/booked, expired and
  // closed brides are deliberately excluded so the daily list stays actionable. That
  // is correct, but it reads as a bug when you SEARCH: typing a booked bride's name
  // returned "No match in your queue" and pushed you to "Search all clients", which is
  // a slow round-trip and was itself capped (see
  // BRIEFS/HANDOFF-search-all-clients-truncated-index-2026-08-10.md). Both doors shut
  // on a bride the page already had in memory.
  //
  // So: when a search finds nothing under the current pill but DOES match brides the
  // pill is hiding, name the pill she is under and offer one click to get there. No
  // remote call, and no change to what any pill contains.
  //
  // Deliberately NOT solved by dumping accepted brides into "All" (Cowork's option b):
  // that puts ~92 resolved brides back in the daily queue, undoing the exclusion
  // shipped this morning in 6da40643.
  var hiddenMatches = [];
  if (rawQuery.length >= 2 && filtered.length === 0) {
    var seenPills = {};
    for (var hm = 0; hm < state.brides.length; hm++) {
      var hb = state.brides[hm];
      if (!hb || !hb.name) continue;
      if (hb.name.toLowerCase().indexOf(query) < 0) continue;
      if (isArchivedBride_(hb)) continue;   // genuinely archived stays hidden everywhere
      var hps = String(hb.proposalStatus || '').toUpperCase();
      var pill = null;
      if (isClosedBride_(hb) || hps === 'DECLINED')  pill = 'declined';
      else if (hps === 'ACCEPTED' || hps === 'SIGNED') pill = 'accepted';
      else if (hps === 'EXPIRED' || hps === 'VOID')  pill = 'expired';
      else if (hps === 'SENT' || hps === 'ACTIVE')   pill = 'sent';
      if (!pill || pill === filter || seenPills[pill]) continue;
      seenPills[pill] = true;
      hiddenMatches.push({ pill: pill, name: hb.name });
    }
  }
  var hintEl = document.getElementById('crossFilterHint');
  if (hintEl) {
    if (hiddenMatches.length) {
      var pillLabels = {
        'declined': 'Declined', 'accepted': 'Accepted',
        'expired': 'Expired', 'sent': 'Sent'
      };
      var hintHtml = '';
      for (var hh = 0; hh < hiddenMatches.length; hh++) {
        var pk = hiddenMatches[hh].pill;
        hintHtml += '<button class="btn-link-inline" onclick="setFilter(\'' + pk + '\')" ' +
          'style="font-size:12px;color:var(--brand-primary);font-weight:600;background:none;' +
          'border:none;cursor:pointer;padding:0;text-decoration:underline">' +
          escapeHtml(pillLabels[pk] || pk) + '</button>';
        if (hh < hiddenMatches.length - 2) hintHtml += ', ';
        else if (hh === hiddenMatches.length - 2) hintHtml += ' and ';
      }
      hintEl.innerHTML = 'Found under ' + hintHtml + '.';
      hintEl.style.display = 'block';
    } else {
      hintEl.style.display = 'none';
    }
  }

  // Progressive disclosure: show global search strip when query has no local matches.
  // Suppressed when the cross-filter hint is showing: the bride is already here in
  // memory, so a remote "search all clients" round-trip would be the wrong advice.
  var strip = document.getElementById('globalSearchStrip');
  if (strip) {
    var showStrip = rawQuery.length >= 2 && filtered.length === 0 &&
                    filter !== 'declined' && hiddenMatches.length === 0;
    strip.style.display = showStrip ? 'block' : 'none';
    if (!showStrip) {
      var results = document.getElementById('globalSearchResults');
      if (results) results.style.display = 'none';
    }
  }

  // Auto-sort work queues regardless of the sort dropdown (Jess decisions 3+4, 2026-07-09):
  // New = newest enquiry first; Draft = most-recently-edited first (touchedAt desc, then
  // row number for any legacy draft with no touched stamp). Other tabs honour the dropdown.
  if (filter === 'new') {
    filtered = sortBrides(filtered, 'enquiry-desc');
  } else if (filter === 'draft') {
    filtered = pinReEnquiries_(filtered.slice().sort(function(a, b) {
      var at = a.touchedAt || '', bt = b.touchedAt || '';
      if (at && bt) return at < bt ? 1 : at > bt ? -1 : 0;
      if (at && !bt) return -1;
      if (!at && bt) return 1;
      return (b.rowNumber || 0) - (a.rowNumber || 0);
    }));
  } else {
    filtered = sortBrides(filtered);
  }

  if (filtered.length === 0) {
    // Empty-state confusion fix (2026-08-10, BRIEFS/HANDOFF-proposal-builder-empty-
    // state-confusion-2026-08-10.md): filtered.length === 0 alone cannot tell a real
    // zero-match filter apart from data that never actually loaded. Two failure shapes
    // confirmed: (a) the cache island can legitimately hold { ok:true, brides:[] },
    // painting an empty list before the live fetch has even run; (b) the live fetch
    // can fail outright with state.brides still empty. Both leave filtered.length===0
    // for a reason that has nothing to do with the filter. Narrowing an ALREADY-
    // POPULATED base list to zero (e.g. Draft with no drafts) is a genuine empty
    // result and unaffected -- state.brides.length > 0 covers that case regardless
    // of load state.
    if (state.brides.length === 0 && state.bridesLoadState !== 'live') {
      if (state.bridesLoadState === 'failed') {
        list.innerHTML = '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i><div>' +
          escapeHtml(state.bridesLoadError || 'Failed to load brides') + '</div></div>';
      } else {
        list.innerHTML = '<div class="loader"><div class="spinner"></div><div>Loading brides...</div></div>';
      }
      return;
    }
    list.innerHTML = '<div class="empty-state"><i class="fa-solid fa-inbox"></i><div>No brides match this filter</div></div>';
    return;
  }

  var html = '';
  for (var i = 0; i < filtered.length; i++) {
    var b = filtered[i];
    var isActive = state.currentBride === b.name;
    var ps = sanitiseProposalStatus(b.proposalStatus);
    var bc = proposalBadgeClass(ps);
    // Draft visibility fix (2026-09-01, Jess: "not marked clearly if it's a draft
    // proposal or not"). The raw ps value here is the literal PROPOSAL_STATUS cell
    // (READY / DRAFT_EDIT), never actually the word "Draft" -- so the card badge
    // showed an internal status code instead of the clean label Jess is looking at.
    // The server's category field already resolves this exact ambiguity
    // (proposalBuilder_deriveCategory_, ProposalBuilderAPI.gs); when it says DRAFT,
    // show that word plainly instead of the raw status. Admin-only, as agreed --
    // no bride-facing equivalent, a draft is never sent/shared.
    var isDraftCard = String(b.category || '').toUpperCase() === 'DRAFT';
    var badgeLabel = isDraftCard ? 'DRAFT' : ps;
    var metaExtra = '';
    var isNeedsReviewCard = String(b.category || '').toUpperCase() === 'NEEDS_REVIEW';
    if (isNeedsReviewCard && b.reviewReason) {
      // Needs Review assisted action desk, Phase 0/1
      // (BRIEFS/PB-needs-review-assisted-action-desk-2026-09-02.md). Reason + age
      // only here -- the suggested-action line renders separately below the meta
      // row (reviewSuggestionRow), matching the closeRow pattern already used for
      // the New-filter close buttons.
      var reviewAge = relativeTimeAgo_(b.reviewRequestedAt);
      metaExtra = '<span style="font-size:11px;color:var(--muted)">' + escapeHtml(b.reviewReason) +
        (reviewAge ? ' · ' + reviewAge : '') + '</span>';
    } else if (ps === 'DECLINED' && b.closeDate) {
      metaExtra = '<span style="font-size:11px;color:var(--muted)">Closed ' + escapeHtml(b.closeDate) + '</span>';
    } else if ((ps === 'SENT' || ps === 'ACTIVE') && b.lastActionDate) {
      metaExtra = '<span style="font-size:11px;color:var(--muted)">Last action ' + escapeHtml(b.lastActionDate) + '</span>';
    } else if ((ps === 'SENT' || ps === 'ACTIVE') && b.sentDate) {
      metaExtra = '<span style="font-size:11px;color:var(--muted)">Sent ' + escapeHtml(b.sentDate) + '</span>';
    }
    var closeRow = '';
    if (filter === 'new' && newBridePredicate(b) && b.rowNumber) {
      var rn = b.rowNumber;
      var nm = escapeHtml(b.name).replace(/'/g, "\\'");
      // BUG FIX (2026-09-02, Lakeita incident): the email arg was never threaded
      // through this call at all -- closeEnquiry/doCloseEnquiry_ only ever received
      // rowNumber + reason + name. adminCrm_closeEnquiry's row-position staleness
      // guard (AdminCRMAPI.gs, added 2026-08-19, BUG-row-guard-silently-skipped-
      // for-no-email-brides-2026-08-19) REFUSES any close call with no email to
      // verify the row against -- "Cannot verify this bride's row without an email
      // on record." That guard postdates this call site (added 2026-07-02), so a
      // close that used to work started silently failing once the guard shipped.
      // A bride closed as "Unavailable" via these buttons never actually got
      // CLOSE_REASON/CLOSE_DATE written, and kept showing as a New enquiry forever
      // afterward -- exactly what Jess reported for Lakeita. Fix: thread the
      // email through, same as every other row-mutating call in this file already
      // does (see e.g. sendFromDeclineModal's bridge calls).
      var em = escapeHtml(b.email || '').replace(/'/g, "\\'");
      closeRow = '<div class="bride-item-close-row" onclick="event.stopPropagation()">' +
        '<button class="close-reason-btn" onclick="closeEnquiry(' + rn + ',\'Unavailable\',\'' + nm + '\',this,\'' + em + '\')">Unavailable</button>' +
        '<button class="close-reason-btn" onclick="closeEnquiry(' + rn + ',\'Not a fit\',\'' + nm + '\',this,\'' + em + '\')">Not a fit</button>' +
        '<button class="close-reason-btn" onclick="closeEnquiry(' + rn + ',\'No response\',\'' + nm + '\',this,\'' + em + '\')">No response</button>' +
      '</div>';
    }
    // Needs Review assisted action desk, Phase 0/1
    // (BRIEFS/PB-needs-review-assisted-action-desk-2026-09-02.md). Suggestion text
    // only -- visually secondary to the primary category badge (soft pill, no
    // border, matches this lane's "never competes with the primary CTA" comms
    // convention). Deliberately NO button here: no send, no apply, no CRM
    // mutation in this phase. Jess reads it and acts manually.
    //
    // Draft text (when present) shows inline rather than in the main detail
    // pane: proposalGetWorkspace (loaded by selectBride) does not carry these
    // new fields, and wiring that separate heavy GAS call was out of this
    // brief's Phase 0/1 scope. The draft is short (2-3 sentences) so an inline
    // toggle keeps it readable without a round trip. stopPropagation keeps the
    // toggle from also firing selectBride, matching the closeRow isolation
    // pattern already used a few lines above.
    var reviewSuggestionRow = '';
    if (isNeedsReviewCard && b.suggestedAction) {
      reviewSuggestionRow = '<div class="bride-item-review-suggestion">' +
        escapeHtml(b.suggestedAction) +
      '</div>';
      if (b.suggestedDraftText) {
        reviewSuggestionRow += '<div class="bride-item-review-draft" onclick="event.stopPropagation()">' +
          escapeHtml(b.suggestedDraftText) +
        '</div>';
      }
    }
    // Item 3c + 1e (BUILD-item1-item3-planner-and-reenquiry-2026-07-19): re-enquiry +
    // planner-managed pills, rendered ahead of the status badge so they read as the
    // most important signal on the card.
    var flagBadges = '';
    if (b.isReEnquiry) {
      flagBadges += '<span class="status-badge badge-reenquiry">Re-enquiry</span>';
    }
    if (b.clientCategory === 'Wedding Planner') {
      flagBadges += '<span class="status-badge badge-planner">Planner-managed</span>';
    }
    if (b.hasAltDateSlot) {
      flagBadges += '<span class="status-badge badge-reenquiry">2 dates' + (b.altDateLabel ? ': ' + escapeHtml(b.altDateLabel) : '') + '</span>';
    }
    // 2026-08-11 (Jycinda Jones incident): the date was already marked closed when
    // she enquired. She is a completely normal, visible lead (no decline, no
    // rejection email) -- this badge is a heads-up only, so Jess knows automatic
    // proposal-prep was skipped and to check availability before building her one.
    if (b.wasClosedAtEnquiry) {
      flagBadges += '<span class="status-badge badge-reenquiry" title="This date was marked closed to future enquiries when she submitted. Check Manage Closed Dates before building her proposal.">Date was closed at enquiry</span>';
    }

    // Bug 1 fix (2026-08-03, BRIEFS/RESULTS-COWORK-gmail-enquiries-vs-proposal-builder-
    // sorting-2026-08-03.md): the card date badge always showed the WEDDING date, even when
    // sorted "Newest enquiry first" -- so a correctly-sorted list still looked "all over the
    // place" because the visible date had nothing to do with the sort key. When enquiry-desc
    // is the active sort (either the dropdown or the auto-sorted "New" filter), show the
    // enquiry date instead. Every other sort mode is unchanged (still shows wedding date).
    var showingByEnquiry = (filter === 'new') || (state.activeSortOrder === 'enquiry-desc');
    var cardDateLabel = (showingByEnquiry && b.enquiryDate)
      ? 'Enquired ' + formatDate(b.enquiryDate)
      : formatDate(b.weddingDate);

    // FIX (2026-09-30, HANDOFF-proposal-builder-apostrophe-name-bride-not-found-
    // 2026-09-30.md): was an inline onclick="selectBride('<name>')" with the name
    // re-embedded as JS source text via escapeHtml + a hand-rolled apostrophe-
    // escape regex. Liz O'Brien could not be opened ("Bride not found: Liz
    // OBrien" -- the apostrophe was gone entirely by the time the server saw
    // it). Switched to a data-* attribute read back via the delegated click
    // listener (pbInitBrideListDelegatedClick_, near the page init block below)
    // -- the name now only ever goes through the browser's normal HTML
    // attribute parsing, never re-embedded as JS source text at all, so no
    // punctuation mark in a name can break this click path again.
    html += '<div class="bride-item' + (isActive ? ' active' : '') + '" data-bride-name="' + escapeAttr_(b.name) + '">' +
      '<div class="bride-item-name">' + escapeHtml(b.name) + '</div>' +
      '<div class="bride-item-meta">' +
        '<span>' + cardDateLabel + '</span>' +
        '<span class="status-badge ' + bc + '">' + escapeHtml(badgeLabel) + '</span>' +
        flagBadges +
        (b.partySize > 1 ? '<span>' + b.partySize + ' ppl</span>' : '') +
        metaExtra +
      '</div>' +
      reviewSuggestionRow +
      closeRow +
      '</div>';
  }
  list.innerHTML = html;
}

/* Searching must reach CLOSED brides too (Jess, 2026-08-10: "I MUST be able to
   search for brides in the closed category easily").

   Closed/declined rows are not in state.brides by default. They come from a
   SEPARATE GAS fetch that only ran when the Declined pill was clicked, so making
   renderBrides stop filtering was necessary but not sufficient: the rows simply
   were not loaded. The first real search therefore pulls the closed set in once
   and merges it, after which every later keystroke searches the full set with no
   further calls.

   Deliberately fires on the first search rather than on page load: it is a
   second cold GAS call (~7-10s on this project) and most sessions never need it.
   The list stays usable while it is in flight, and repaints when it lands. */
var _closedLoadedForSearch = false;
var _closedLoadInFlight = false;

function ensureClosedBridesLoaded_() {
  if (_closedLoadedForSearch || _closedLoadInFlight) return;
  if (state.activeFilter === 'declined') return; // that pill already loads them
  _closedLoadInFlight = true;
  try {
    jvBridge.callAdmin('proposalGetPendingBrides', { includeDeclined: true })
      .then(function(res) {
        /* Same defensive single-unwrap as loadBrides: the bridge wraps to
           { ok, data: { status, brides, message } }. */
        var payload = (res && res.data) || res;
        var rows = (payload && payload.brides) || [];
        if (!rows.length) return;
        var seen = {};
        state.brides.forEach(function(b) { if (b && b.email) seen[String(b.email).toLowerCase()] = true; });
        rows.forEach(function(b) {
          var k = b && b.email ? String(b.email).toLowerCase() : '';
          if (!k || seen[k]) return;   // never duplicate a bride already in the list
          seen[k] = true;
          state.brides.push(b);
        });
        _closedLoadedForSearch = true;
        renderBrides();
      })
      .catch(function() { /* non-fatal: search still works over the loaded set */ })
      .then(function() { _closedLoadInFlight = false; });
  } catch (e) {
    _closedLoadInFlight = false;
  }
}

document.getElementById('search').addEventListener('input', function() {
  var q = this.value.trim();
  if (q.length >= 2) ensureClosedBridesLoaded_();
  renderBrides();
});

function closeEnquiry(rowNumber, reason, brideName, btn, email) {
  if (!rowNumber || !reason) return;

  // Confirmation modal before committing.
  var labels = { 'Unavailable': 'Mark as unavailable', 'Not a fit': 'Mark as not a fit', 'No response': 'Mark as no response' };
  var overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.55);z-index:9999;display:flex;align-items:center;justify-content:center';
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:var(--radius-large,1.5rem);padding:2rem 2.5rem;max-width:360px;width:90%;box-shadow:0 16px 48px rgba(47,37,36,0.18);text-align:center">' +
      '<div style="font-size:1rem;font-weight:700;color:var(--brand-dark,#1e1716);margin-bottom:0.5rem">' + (labels[reason] || reason) + '</div>' +
      '<div style="font-size:0.875rem;color:var(--brand-text,#2f2524);margin-bottom:1.5rem">Remove <strong>' + escapeHtml(brideName || 'this enquiry') + '</strong> from your active list?</div>' +
      '<div style="display:flex;gap:0.75rem;justify-content:center">' +
        '<button id="ceCancel" style="padding:0.7rem 1.5rem;border-radius:3rem;border:2px solid rgba(47,37,36,0.18);background:transparent;font-weight:600;cursor:pointer;font-size:0.875rem">Cancel</button>' +
        '<button id="ceConfirm" style="padding:0.7rem 1.5rem;border-radius:3rem;border:none;background:var(--brand-primary,#c99591);color:#fff;font-weight:600;cursor:pointer;font-size:0.875rem">Yes, remove</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);
  overlay.querySelector('#ceCancel').onclick = function() { overlay.remove(); };
  overlay.querySelector('#ceConfirm').onclick = function() {
    overlay.remove();
    doCloseEnquiry_(rowNumber, reason, brideName, btn, email);
  };
}

function doCloseEnquiry_(rowNumber, reason, brideName, btn, email) {
  var row = btn && btn.closest('.bride-item-close-row');
  if (row) row.querySelectorAll('.close-reason-btn').forEach(function(b) { b.classList.add('closing'); });

  // BUG FIX (2026-09-02, Lakeita incident, see closeRow's own comment above for
  // full root-cause detail): email was never sent, so adminCrm_closeEnquiry's
  // row-position staleness guard (AdminCRMAPI.gs, added 2026-08-19) refused
  // every close call outright once it shipped -- CLOSE_REASON/CLOSE_DATE never
  // got written, and the bride kept showing as a New enquiry forever after.
  jvBridge.callAdmin('bridge_admin_generic', {
    functionName: 'adminCrm_closeEnquiry',
    params: { clientId: rowNumber, reason: reason, email: email }
  }).then(function(res) {
    if (res && res.ok) {
      // Remove from local state immediately so the list updates without a reload.
      state.brides = state.brides.filter(function(b) { return b.rowNumber !== rowNumber; });
      // Also wipe the inline island so a page reload does not flash the closed bride
      // before the live GAS fetch returns (island is rebuilt daily at 5am only).
      try {
        var island = document.getElementById('jv-pb-pending-cache');
        if (island) {
          var cached = JSON.parse(island.textContent || island.innerHTML);
          if (cached && Array.isArray(cached.brides)) {
            cached.brides = cached.brides.filter(function(b) { return b.rowNumber !== rowNumber; });
            island.textContent = JSON.stringify(cached);
          }
        }
      } catch (e) { /* non-fatal — live fetch is the source of truth */ }
      renderBrides();
    } else {
      if (row) row.querySelectorAll('.close-reason-btn').forEach(function(b) { b.classList.remove('closing'); });
      alert('Could not close enquiry: ' + ((res && res.message) || 'unknown error'));
    }
  }).catch(function() {
    if (row) row.querySelectorAll('.close-reason-btn').forEach(function(b) { b.classList.remove('closing'); });
    alert('Could not close enquiry. Please try again.');
  });
}

function setFilter(f) {
  var prevFilter = state.activeFilter;
  state.activeFilter = f;
  var btns = document.querySelectorAll('.filter-btn');
  for (var i = 0; i < btns.length; i++) {
    var df = btns[i].getAttribute('data-filter');
    var label = df || btns[i].textContent.trim().toLowerCase();
    btns[i].classList.toggle('active', label === f);
  }
  // Note (2026-07-27): this used to force-reset the sort to 'enquiry-desc' on
  // Needs Review, from back when that was the only sensible sort. It silently
  // fought the needs-action-first default (907a5b3b) every time this tab was
  // clicked. Removed -- every filter tab now honours whatever sort the user (or
  // the default) has selected, same as every other tab.
  // Declined filter fetches a separate data set from GAS
  if (f === 'declined') {
    loadBrides({ includeDeclined: true });
  } else if (prevFilter === 'declined') {
    // Returning from declined view: reload the normal active-brides set
    loadBrides();
  } else {
    renderBrides();
  }
}

/* =================================================================
   SELECT BRIDE -- LOAD WORKSPACE
   ================================================================= */
/* ─── Bride-picker sidebar collapse (manual + auto on bride select) ─── */
var PB_SIDEBAR_KEY = 'jv_pb_sidebar_collapsed';
var _pbSidebarUserExpanded = false;
function togglePbSidebar(force) {
  var root = document.querySelector('.jv-proposal-builder');
  if (!root) return;
  var willCollapse = (typeof force === 'boolean') ? force : !root.classList.contains('pb-sidebar-collapsed');
  root.classList.toggle('pb-sidebar-collapsed', willCollapse);
  var btn = document.getElementById('pbSidebarToggle');
  if (btn) {
    btn.setAttribute('aria-label', willCollapse ? 'Expand bride picker' : 'Collapse bride picker');
    btn.setAttribute('title', willCollapse ? 'Expand bride picker' : 'Collapse bride picker');
  }
  if (!willCollapse) _pbSidebarUserExpanded = true;
  try { localStorage.setItem(PB_SIDEBAR_KEY, willCollapse ? '1' : '0'); } catch (e) {}
}
/* Sidebar always starts EXPANDED so the bride list is visible and selectable.
   Auto-collapse happens on first bride selection (selectBride, below).
   Clear any stale stored state so returning users get a usable default. */
try { localStorage.removeItem(PB_SIDEBAR_KEY); } catch (e) {}
/* P1e: guarantee the bride picker is expanded + selectable on first load.
   Explicitly drop any stray collapsed class so .bride-list offsetWidth > 0 on load,
   independent of the stored key above (the diagnostic found it loading at width 0). */
(function pbEnsureSidebarExpandedOnLoad_() {
  var root = document.querySelector('.jv-proposal-builder');
  if (root) root.classList.remove('pb-sidebar-collapsed');
})();

/* ── Cold-read warm-retry (follow-up to the 2026-07-09 proposal-builder 504 fix) ──
   A cache-miss enquiry runs a LIVE JV AUTO travel calc on a cold GAS instance.
   The Cloudflare worker now allows that read up to 55s, but a rare very large
   enquiry can still exceed it -> the worker returns a 504. The bridge turns that
   non-JSON body into a RESOLVED failure envelope { ok:false, error:'parse' }
   (also 'timeout' / 'network' / 'transport'). proposalGetWorkspace is an
   idempotent read (its only side effect -- persisting the live calc + flipping
   PROPOSAL_STATUS to READY -- is itself idempotent), so ONE warm retry against
   the now-hot instance is safe and never double-applies. Mirrors the
   runsheet-builder warm-retry design (HANDOFF-getworkspace-client-warmretry-2026-07-08),
   scoped to this page. */
function pbIsColdReadFail_(res) {
  if (!res || res.ok !== false) return false;
  var s = String(res.error || '').toLowerCase();
  return s.indexOf('parse')   !== -1 || s.indexOf('timeout')   !== -1 ||
         s.indexOf('network') !== -1 || s.indexOf('504')       !== -1 ||
         s.indexOf('gateway') !== -1 || s.indexOf('transport') !== -1;
}

/* Shared terminal-status check (2026-09-10, BUILD-proposal-builder-ux-fixes item 10).
   Extracted from renderStageSend's own inline isVoid/isOtherTerminal logic (that
   function's exact set, unchanged) so the header (which needs to know "is sending
   possible right now" before Stage 4 has even rendered) and Stage 4 itself never
   silently diverge on what counts as terminal. Takes the RAW (already-uppercased)
   proposal.status string. */
function pbIsTerminalStatus_(statusUpper) {
  return statusUpper === 'VOID' || statusUpper === 'EXPIRED' || statusUpper === 'DECLINED' ||
    statusUpper === 'DEPOSIT PAID' || statusUpper === 'CALC_FAILED' ||
    statusUpper === 'SIGNED' || statusUpper === 'ACCEPTED';
}

/* Header status reflection (2026-09-10, BUILD-proposal-builder-ux-fixes item 10).
   Called once per workspace load (selectBride's live-fetch .then), so switching
   between brides always reflects the CURRENT bride, never a stale terminal state
   left over from whoever was open before. Does NOT change what the header button
   actually does (still goToSendStage(), a tab jump; the real send stays inside
   Stage 4's own gate) -- this only makes the button's visible state stop
   contradicting what Stage 4 will say once you get there. */
function pbUpdateHeaderSendButton_(proposal) {
  var statusUpper = String((proposal && proposal.status) || '').trim().toUpperCase();
  var btn = document.getElementById('sendBtn');
  var label = document.getElementById('sendBtnLabel');
  var badgeSlot = document.getElementById('sendBtnStatusBadge');
  if (!btn || !label) return;
  if (pbIsTerminalStatus_(statusUpper)) {
    var displayLabel = statusUpper === 'VOID' ? 'VOIDED' : statusUpper;
    label.textContent = 'View Status';
    btn.title = 'This proposal has reached a final status (' + displayLabel + '). See Stage 4 for details.';
    if (badgeSlot) {
      badgeSlot.style.display = '';
      badgeSlot.textContent = displayLabel;
    }
  } else {
    label.textContent = 'Review & Send';
    btn.removeAttribute('title');
    if (badgeSlot) badgeSlot.style.display = 'none';
  }
}

/* ══════════════════════════════════════════════════════════════
   D1/CDN FAST-PATH READ (Step 3, BUILD-proposal-builder-d1-full-cache-
   option-a-2026-08-05.md), data plumbing ONLY. Nothing calls
   pbTryCdnFastPath_ yet (not selectBride, not anywhere else); this proves
   the read path works in isolation before any cutover. Cutover (Step 4) is
   a SEPARATE, later, explicitly-verified change behind a kill flag.

   Pattern copied field-for-field from assets/jv-admin-bride-profile.js
   abpTryCdnFastPath_ / abpProfileCacheHandle_ / abpSha256Hex_ /
   abpInflateIslandValue_, same salted-handle scheme (shared salt,
   window._pbCacheSalt emitted server-side by sections/jv-proposal-builder
   .liquid's jv-admin gate, matching proposalBrideCache_handle_ / BrideCache
   .handleSalt_ byte-for-byte on the GAS side), same Storefront API GraphQL
   read, same gzip inflate via DecompressionStream. The one shape difference:
   ProposalBrideCache.gs stores ONE 'payload' field (not split bride/runsheet
   fields like the profile cache), so there is only one inflate call here.
   ══════════════════════════════════════════════════════════════ */

  var PB_SF_SHOP = 'jessicavegas.com';
  var PB_SF_VER  = '2026-01';

  // Servable window, matches PROPOSAL_BRIDE_CACHE_MAX_STALE_HOURS_ (24h) on
  // the GAS side (ProposalBrideCache.gs). A cache entry older than this is
  // treated as a miss so a stale price is never served without at least a
  // fresh recalc pass, even before Step 4 wires this into a real render.
  var PB_CDN_FRESH_MS = 24 * 60 * 60 * 1000;

  function pbCacheHandle_(email) {
    var salt = (typeof window !== 'undefined' && window._pbCacheSalt) ? String(window._pbCacheSalt) : '';
    var key  = String(email || '').trim().toLowerCase();
    if (!salt || !key || !(window.crypto && window.crypto.subtle)) return Promise.resolve(null);
    return pbSha256Hex_(key + '|' + salt).then(function (hex) {
      return hex ? ('proposal-bride-' + hex) : null;
    });
  }

  // SHA-256 to lowercase hex, matching GAS brideCache_sha256Hex_ byte-for-byte.
  function pbSha256Hex_(str) {
    try {
      var enc = new TextEncoder().encode(str);
      return window.crypto.subtle.digest('SHA-256', enc).then(function (buf) {
        var b = new Uint8Array(buf), out = '';
        for (var i = 0; i < b.length; i++) out += ('0' + b[i].toString(16)).slice(-2);
        return out;
      }).catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  // Parse a metaobject field value that is EITHER plain JSON OR a JSON string
  // literal "JVGZ_<base64gzip>" (adminCache_encodeForStore_ on the GAS side).
  // Mirrors abpInflateIslandValue_ exactly, including the quoted-vs-bare
  // marker defence (BUGFIX 2026-07-17 on the profile-cache reader).
  function pbInflateIslandValue_(raw) {
    return new Promise(function (resolve) {
      try {
        if (!raw) { resolve(null); return; }
        var s = String(raw).trim();
        var marker = s;
        if (marker.charAt(0) === '"' && marker.charAt(marker.length - 1) === '"') {
          try { marker = JSON.parse(s); } catch (e) { marker = s.slice(1, -1); }
        }
        if (typeof marker === 'string' && marker.indexOf('JVGZ_') === 0) {
          if (typeof DecompressionStream === 'undefined') { resolve(null); return; }
          var b64 = marker.slice(5);
          var bin = atob(b64);
          var bytes = new Uint8Array(bin.length);
          for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          var ds = new DecompressionStream('gzip');
          new Response(new Blob([bytes]).stream().pipeThrough(ds)).text()
            .then(function (txt) { try { resolve(JSON.parse(txt)); } catch (e) { resolve(null); } })
            .catch(function () { resolve(null); });
          return;
        }
        resolve(JSON.parse(s)); // plain object
      } catch (e) { resolve(null); }
    });
  }

  /**
   * Returns a Promise resolving to the cached workspace payload
   * ({ bride, calc, clientId, party, proposal, closedDateWarning,
   * isReEnquiry }, exactly the shape proposalBrideCache_write_ stores) or
   * null on any miss / no token / no salt / stale / error. Caller (once
   * wired in Step 4) always falls through to the live pbGetWorkspaceWarm_
   * on a null result, this function NEVER throws and never leaves the
   * caller without a live fallback path.
   */
  function pbTryCdnFastPath_(email) {
    var tok = (typeof window !== 'undefined' && window._pbSfToken) ? String(window._pbSfToken) : '';
    if (!tok || !email) return Promise.resolve(null);
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    return pbCacheHandle_(email).then(function (handle) {
      if (!handle) return null;
      var q = 'query($h:MetaobjectHandleInput!){ metaobject(handle:$h){ ' +
              'payload: field(key:"payload"){ value } b: field(key:"built_at"){ value } } }';
      var body = { query: q, variables: { h: { type: 'proposal_bride_cache', handle: handle } } };
      return nf('https://' + PB_SF_SHOP + '/api/' + PB_SF_VER + '/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': tok },
        body: JSON.stringify(body)
      })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var mo = d && d.data && d.data.metaobject;
        if (!mo) return null;
        var payloadRaw = mo.payload && mo.payload.value;
        var builtAtRaw = mo.b && mo.b.value;
        if (!payloadRaw) return null;
        var builtAtMs = builtAtRaw ? Date.parse(builtAtRaw) : NaN;
        if (isNaN(builtAtMs) || (Date.now() - builtAtMs) > PB_CDN_FRESH_MS) return null;
        return pbInflateIslandValue_(payloadRaw).then(function (obj) {
          // The stored payload IS the full proposalGetWorkspace return value
          // (ProposalBrideCache.gs caches it verbatim), so it must look like
          // one before we trust it: status ok, a bride, and a DERIVED
          // calculation object. Requiring `calculation` (not the raw `calc`)
          // is deliberate, it is the field selectBride actually renders from,
          // and its presence proves this is a full-shape entry rather than a
          // stale narrow-shape one written by the pre-2026-08-05 builder.
          if (!obj || obj.status !== 'ok' || !obj.bride || !obj.calculation) return null;
          return obj;
        });
      })
      .catch(function () { return null; });
    });
  }

/* STEP 4 CUTOVER (2026-08-05): CDN cache fast-path in front of the live call.
   Wrapped HERE rather than inside selectBride() deliberately: this function
   already returns the bridge envelope shape { ok, data }, so a cache hit can
   be handed back in the IDENTICAL shape and selectBride() plus the ~2000
   lines downstream of it need ZERO changes. Fewer moving parts on a money
   surface.

   KILL FLAG: window.JV_PROPOSAL_CACHE_FASTPATH must be explicitly true.
   Default (unset) = OFF = byte-identical to the old behaviour. Same pattern
   as window.JV_RSB_FASTPATH on the runsheet builder. Set it to false (or
   just unset it) to instantly disable the cache with no redeploy.

   FALLBACK IS UNCONDITIONAL: any miss, stale entry, missing token/salt,
   fetch error, or wrong-shape payload resolves null and falls straight
   through to the live call below, unchanged. pbTryCdnFastPath_ never throws
   and never rejects, so the live path cannot be skipped by an error. */

// A1 2026-11-02 JFG-only scope (Desk decisions a1-d1/d2/d3): same pattern as
// window.JV_PB_WORKSPACE_CF_SCOPE_EMAILS above -- scopes by the BRIDE record
// currently loaded into the workspace (state.workspace.bride.email), not by
// which admin is logged in. Each of the three dark flags below stays flipped
// true in the liquid template; this helper is what actually restricts the
// behaviour to Jess's own test bride record until the wider rollout decision.
function pbJfgOnlyScope_() {
  var email = (state && state.workspace && state.workspace.bride && state.workspace.bride.email) || '';
  return String(email).trim().toLowerCase() === 'jfg@live.com.au';
}

function pbGetWorkspaceWarm_(name, opt_brideUid) {
  var cfEmail = '';
  var cfBrideUid = String(opt_brideUid || '').trim();
  var cfBride = (state.brides || []).find(function(b) {
    return opt_brideUid ? b.brideUid === opt_brideUid : b.name === name;
  });
  if (cfBride && cfBride.email) cfEmail = String(cfBride.email).trim();
  if (!cfBrideUid && cfBride && cfBride.brideUid) cfBrideUid = String(cfBride.brideUid).trim();
  // A bride selected through "Search all clients" is NOT in state.brides, so the
  // lookup above misses and her identity would resolve to '' -- which made
  // cfScoped below unconditionally false and sent the read to GAS
  // proposalGetWorkspace (a WRITE) for every such bride. Recover the identity her
  // search result row carried (pbRememberIdentity_, near selectClientSearchResult_).
  // Preferred over the name-as-email guess below because it is a real field value,
  // not an inference from a display string.
  if (!cfEmail || !cfBrideUid) {
    var cfRemembered = pbResolveIdentity_(name);
    if (cfRemembered) {
      if (!cfEmail && cfRemembered.email) cfEmail = cfRemembered.email;
      if (!cfBrideUid && cfRemembered.brideUid) cfBrideUid = cfRemembered.brideUid;
    }
  }
  if (!cfEmail && String(name || '').indexOf('@') > 0) cfEmail = String(name).trim();
  var hasCfIdentity = !!(cfEmail || cfBrideUid);

  // Memoized (2026-09-30, HANDOFF-shadow-log-frontend-wiring-2026-09-30.md): the shadow
  // block below needs to OBSERVE the real GAS result to log a genuine comparison, but
  // must never cause a SECOND real network call to proposalGetWorkspace just to get a
  // reference to it. Caching the promise here means the shadow block can call
  // liveLoad_() eagerly (to get a handle to join against) and whichever branch further
  // down ALSO calls it (the direct-load branches, or the cache-hit background refresh)
  // transparently receives the exact same in-flight/settled promise -- one real call,
  // however many places in this function want to observe its outcome.
  var _liveLoadPromise = null;
  function liveLoad_() {
    if (_liveLoadPromise) return _liveLoadPromise;
    var workspaceScope = Array.isArray(window.JV_PB_WORKSPACE_CF_SCOPE_EMAILS)
      ? window.JV_PB_WORKSPACE_CF_SCOPE_EMAILS.map(function(e) { return String(e || '').trim().toLowerCase(); }) : [];
    var cfScoped = !!cfEmail && workspaceScope.indexOf(String(cfEmail).toLowerCase()) !== -1;
    var cfLoad = cfScoped && hasCfIdentity
      ? pbCfBuilderRead_('workspace', { email: cfEmail, brideUid: cfBrideUid }, function(b) { return b && b.ok === true && b.status === 'loaded' && b.bride; })
          .then(function(b) { return b ? { ok: true, data: b } : null; })
      : Promise.resolve(null);
    _liveLoadPromise = cfLoad.then(function(cfRes) {
      if (cfRes) return cfRes;
      if (cfScoped) return { ok: false, error: hasCfIdentity ? 'proposal_workspace_cf_unavailable' : 'proposal_workspace_cf_identity_unavailable' };
      return jvBridge.callAdmin('proposalGetWorkspace', { brideName: name, brideUid: cfBrideUid || undefined });
    });
    return _liveLoadPromise;
  }

  /* SHADOW PARITY CHECK, jv-proposal-engine /workspace (2026-09-23, Consolidator ask).
     KILL FLAG: window.JV_PROPOSAL_ENGINE_SHADOW must be explicitly true. Default
     (unset) = OFF = zero extra network calls, byte-identical to the old behaviour.

     THIS IS A SHADOW CHECK, NOT A DATA SOURCE. It fires the Worker call in the
     background, alongside the real liveLoad_() (never instead of it), and only
     console-logs a diff. It never replaces, races, or blocks what actually
     renders -- the real load ALWAYS resolves from liveLoad_() below, unchanged.

     WHY NOT A REAL SWITCH-OVER: the Worker's /workspace response shape
     ({ok, source, bride:{firstName,lastName,...}, data:priced}) does not match
     proposalGetWorkspace's shape ({status:'ok', bride:<full CRM row>,
     calculation:{...}}) closely enough to safely feed the ~2000 lines downstream
     that render from `calculation` today -- that would need a real adapter,
     verified field-by-field, not a flag flip. This shadow check is step one:
     prove parity is visible and measurable in the field before anyone builds
     that adapter. See jv-proposal-engine/test/parity-live.mjs for the
     equivalent offline harness this mirrors.

     Never blocks, never throws into the caller, matches the existing fastpath's
     "any error is silently swallowed" discipline.

     PERSISTENCE (2026-09-30, HANDOFF-shadow-log-frontend-wiring-2026-09-30.md):
     the engine-side numbers used to be console-log-only -- nothing captured the
     REAL GAS numbers to compare against, so a written row would have been half-
     useful (engine values, null GAS values). Once BOTH the engine fetch and the
     real GAS load (liveLoad_(), memoized above) have resolved, this now fires one
     additional fire-and-forget POST to jv-proposal-engine's own /shadow-log route
     (server-side persistence route, D1-backed, built the same night as this wiring
     -- see that handoff for route status/deploy state) carrying both sides. The
     existing console.log lines are NOT removed; the /shadow-log write is additive. */
  if (window.JV_PROPOSAL_ENGINE_SHADOW === true) {
    try {
      var _peEmail = '';
      try {
        var _peMatch = (state.brides || []).filter(function (b) { return b && b.name === name; })[0];
        _peEmail = (_peMatch && _peMatch.email) || '';
      } catch (eEmail) { _peEmail = ''; }
      if (_peEmail) {
        var _peNf = (window._jvNativeFetch || window.fetch).bind(window);
        // RELAY FIX (2026-10-22, Issue #8): /workspace started requiring body.secret
        // (D1_SYNC_SECRET) on 2026-10-12 (same security commit as /pricing/quote above) --
        // this caller never sent one, so every live call since has 401'd silently. Route
        // through the cors-relay entity that injects the secret server-side; falls through
        // to the direct (401ing, unchanged) call when the proxy base isn't emitted yet.
        var _peUrl = window._pbAdminProxyBase
          ? String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-engine-shadow/workspace'
          : 'https://jv-proposal-engine.calm-hill-fd5b.workers.dev/workspace';
        var _peEnginePromise = _peNf(_peUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' }, // simple request, no CORS preflight
          body: JSON.stringify({ email: _peEmail, brideName: name })
        })
        .then(function (r) { return r.json(); })
        .then(function (engineRes) {
          if (!engineRes || engineRes.ok !== true || !engineRes.data) {
            console.log('[JV_PROPOSAL_ENGINE_SHADOW] no comparable result for ' + name + ': ' +
              (engineRes && (engineRes.reason || engineRes.fallback)) || 'unknown');
            return { ok: false, reason: (engineRes && (engineRes.reason || engineRes.fallback)) || 'no comparable result' };
          }
          console.log('[JV_PROPOSAL_ENGINE_SHADOW] engine responded for ' + name +
            ' (source=' + engineRes.source + '): subtotal=' + engineRes.data.subtotal +
            ' total=' + engineRes.data.total +
            ' -- compare manually against the real GAS render for this bride.');
          return {
            ok: true,
            source: engineRes.source || '',
            subtotal: engineRes.data.subtotal,
            total: engineRes.data.total
          };
        })
        .catch(function (e) {
          console.log('[JV_PROPOSAL_ENGINE_SHADOW] fetch failed for ' + name + ': ' + (e && e.message));
          return { ok: false, reason: (e && e.message) || 'fetch failed' };
        });

        // Join with the real GAS result once BOTH sides have settled, then persist
        // one comparison row. Attaching this .then() to liveLoad_()'s memoized
        // promise does not consume it or alter what any other caller receives.
        liveLoad_().then(function (gasRes) {
          return _peEnginePromise.then(function (engineSide) {
            try {
              var gasPayload = (gasRes && gasRes.data) || gasRes;
              var gasCalc = gasPayload && gasPayload.calculation;
              // No real GAS number to compare against (cold-read failure, error
              // envelope, etc.) -- skip the write rather than log a half-null row.
              if (!gasCalc) return;
              // REAL COMPARISON LOG (2026-10-01, Consolidator ask after a live
              // verify pass found "compare manually against the real GAS
              // render" was literal -- the engine-only line above never
              // printed the GAS side, so parity could only be checked by eye
              // against a second page read. This line fires once both sides
              // have settled (same join this function already does for the
              // /shadow-log write below) and prints both totals plus the
              // signed diff, so a console read alone proves agreement or
              // disagreement without a second manual lookup.
              try {
                var _peGasTotal = Number(gasCalc.total);
                var _peEngineTotal = (engineSide && engineSide.ok) ? Number(engineSide.total) : NaN;
                var _peDiff = (Number.isFinite(_peGasTotal) && Number.isFinite(_peEngineTotal))
                  ? Math.round((_peEngineTotal - _peGasTotal) * 100) / 100 : null;
                console.log('[JV_PROPOSAL_ENGINE_SHADOW] COMPARE for ' + name + ': gasTotal=' + _peGasTotal +
                  ' engineTotal=' + (Number.isFinite(_peEngineTotal) ? _peEngineTotal : '(none)') +
                  ' diff=' + (_peDiff === null ? '(not comparable)' : _peDiff) +
                  (_peDiff === 0 ? ' -- MATCH' : (_peDiff === null ? '' : ' -- MISMATCH')));
              } catch (eCompare) { /* never let the comparison log affect the persistence join below */ }
              var logBody = {
                email: _peEmail,
                brideName: name,
                gas: { subtotal: gasCalc.subtotal, total: gasCalc.total },
                engine: engineSide || { ok: false, reason: 'no engine result' }
              };
              _peNf('https://jv-proposal-engine.calm-hill-fd5b.workers.dev/shadow-log', {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' }, // simple request, no CORS preflight
                body: JSON.stringify(logBody)
              }).catch(function (e) {
                console.log('[JV_PROPOSAL_ENGINE_SHADOW] shadow-log write failed for ' + name + ': ' + (e && e.message));
              });
            } catch (eJoin) { /* never let the persistence join affect anything */ }
          });
        }).catch(function () { /* the real GAS load itself failed -- nothing to compare, no write */ });
      }
    } catch (eShadow) { /* never let the shadow check affect the real load */ }
  }

  if (window.JV_PROPOSAL_CACHE_FASTPATH !== true) return liveLoad_();

  // A brideUid pin means the caller already resolved a SPECIFIC row and the whole
  // point is to load THAT row, not whichever one happens to be cached under this
  // bride's email (the CDN cache is keyed by email, so it cannot disambiguate a
  // duplicate-row bride either). Skip the fastpath entirely and go straight to the
  // authoritative live/warm read. 2026-09-10, HANDOFF-proposal-deeplink-email-not-unique.
  if (opt_brideUid) return liveLoad_();

  // The cache is keyed by EMAIL, but selectBride only has the bride's NAME.
  // Resolve it from the already-loaded bride list (state.brides), which the
  // page fetched before any bride could be clicked. No extra round-trip.
  var email = '';
  try {
    var match = (state.brides || []).filter(function (b) { return b && b.name === name; })[0];
    email = (match && match.email) || '';
  } catch (e) { email = ''; }
  if (!email) return liveLoad_();

  return pbTryCdnFastPath_(email).then(function (cached) {
    if (!cached) return liveLoad_();
    // Cache HIT. Re-wrap in the bridge envelope so the caller cannot tell the
    // difference, then background-refresh from GAS (stale-while-revalidate)
    // so a cache entry that has drifted self-corrects within the same session.
    try {
      liveLoad_().then(function (fresh) {
        var freshData = (fresh && fresh.data) || fresh;
        if (!freshData || freshData.status !== 'ok') return;
        // FAIL LOUD ON A MONEY SURFACE (2026-10-01, Consolidator ask after a
        // live verify pass caught this exact drift: the cached on-load total
        // was $42.51 off the live recalc, and the only way anyone found out
        // was a manual compare -- the self-heal below is silent by design, so
        // if it fires (or if any of its own guards below skip it), an admin
        // can be looking at a wrong price with zero signal either way. This
        // compares the cached total the admin already saw against the real
        // one BEFORE the repaint guards run, so a stale cache is surfaced
        // even in the cases (wrong bride on screen, draft dirty) where the
        // repaint itself is correctly skipped.
        try {
          var _pbCachedTotal = Number(cached && cached.calculation && cached.calculation.total);
          var _pbFreshTotal = Number(freshData.calculation && freshData.calculation.total);
          if (Number.isFinite(_pbCachedTotal) && Number.isFinite(_pbFreshTotal) &&
              Math.abs(_pbCachedTotal - _pbFreshTotal) >= 0.01) {
            showBanner('Price updated to the current total ($' + _pbFreshTotal.toFixed(2) +
              ') -- the saved view shown on open was out of date.', 'warn');
          }
        } catch (eCompare) { /* never let the staleness notice affect the repaint below */ }
        // Only repaint if the bride is still the one on screen AND the user has
        // not started editing, never clobber in-progress work with a background result.
        if (state.currentBride !== name) return;
        if (_draftDirty) return;
        // Repaint via a one-shot bypass so this re-entry goes STRAIGHT to the
        // live path. Calling selectBride() plainly would re-enter this same
        // function, hit the cache again, and fire another background refresh:
        // an infinite loop. Restored in a finally so an exception can never
        // leave the fast-path permanently disabled for the session.
        var prevFlag = window.JV_PROPOSAL_CACHE_FASTPATH;
        window.JV_PROPOSAL_CACHE_FASTPATH = false;
        try { selectBride(name); }
        finally { window.JV_PROPOSAL_CACHE_FASTPATH = prevFlag; }
      }).catch(function () {});
    } catch (e) {}
    return { ok: true, data: cached, _fromCache: true };
  }).catch(function () { return liveLoad_(); });
}

/* WIN B (2026-07-12): warm-retry wrapper for the RECALC routes.
   proposalRecalculate / proposalRecalculateWithAddress chain
   Shopify -> worker -> CRM GAS (cold) -> JV AUTO GAS (cold) -> Maps, so a cold
   pass can 504/timeout at the worker and surface as a "Broke: timed out" alert.
   Both routes are idempotent (read-modify-write of PROPOSAL_CALC_JSON), so ONE
   retry against the now-warm instance is safe and never double-applies. This is
   the same cold-read failure test + single-retry used by pbGetWorkspaceWarm_.
   Drop-in: same (route, payload) -> Promise signature as jvBridge.callAdmin, so
   every existing .then() chain is preserved unchanged. */
/* Issue #8 Slice E — GAS caller short-circuit for proposalBuilder_syncTokenLineItems_
   (2026-10-16). Slice D ships the CF metaobject + D1 writer path; GAS is FROZEN so
   the function body itself cannot be touched. The frontend doesn't call
   proposalBuilder_syncTokenLineItems_ directly — it's an internal GAS helper fanned
   out by proposalRecalculate / proposalRecalculateWithAddress / proposalApproveAndSend /
   proposalUpdateLineItems. Every GAS caller lives inside PROPOSAL.gs; the only
   caller-layer we own is this file (plus its SHOPIFY mirror).

   KILL FLAG: window.JV_PROPOSAL_METAOBJECT_WRITE_ENABLED must be explicitly === true
   for the GAS recalc/send bridge calls to short-circuit. Default (unset) = OFF =
   byte-identical to prior behaviour — every GAS write path (and the internal
   proposalBuilder_syncTokenLineItems_ fan-out) still fires exactly as before. Flag is
   wired via Liquid-rendered metafield into window global, same pattern as
   JV_PROPOSAL_CALC_PERSIST / JV_PROPOSAL_SSOT.

   Slice F will flip the flag on and route the writes through the CF metaobject
   worker; this slice establishes the gate (OFF-by-default, zero behaviour change). */
function jvProp_shouldSkipGasForMetaobject_() {
  try {
    if (typeof window === 'undefined' || window.JV_PROPOSAL_METAOBJECT_WRITE_ENABLED !== true) return false;
    // FAIL-SAFE (Sub-B L3, 2026-10-19): skipping the GAS recalc is only safe once a CF
    // recalculate route exists to replace it -- pbRecalcWarm_ resolves a stub when this
    // returns true, so flipping the write flag alone would silently stop every recalc
    // (stale PROPOSAL_CALC_JSON, stale totals). Require the explicit readiness flag too;
    // until it is set, log loudly and let GAS run exactly as before.
    if (window.JV_PROPOSAL_CF_RECALC_READY !== true) {
      if (!window.__jvPropSkipWarned) {
        window.__jvPropSkipWarned = true;
        console.warn('[JV_PROPOSAL_METAOBJECT_WRITE_ENABLED] ignored: JV_PROPOSAL_CF_RECALC_READY is not set, no CF recalculate route is live yet. GAS recalc still runs.');
      }
      return false;
    }
    return true;
  } catch (_e) { return false; }
}

/* Issue #8 Slice F — proposalApproveAndSend caller-layer CF-shadow wrapper
   (2026-10-16). Depends on Slice E (a8bd7c63b). The send path is the moment a
   proposal becomes "sent to bride" and fans out across THREE systems: GAS
   (acceptance writes, Proposal History sheet, comms log), Xero (invoice create),
   and comms (confirmation email + SMS). Scope limit on this slice: ESTABLISH
   THE CF ROUTE SHELL AND THE CALLER-LAYER WRAPPER ONLY — the real port lands
   in a later slice after Jess's polls on (a) Xero sync-vs-async, (b)
   transactional_email flag, (c) SMS quiet-hours gating.

   KILL FLAG: window.JV_PROPOSAL_SEND_CF_ENABLED must be explicitly === true
   for the shadow CF write to fire. Default (unset) = OFF = byte-identical to
   prior behaviour, zero network traffic to CF, GAS owns the send unchanged.
   When ON (manual Jess flip for soak observation): the wrapper STILL calls
   GAS normally and returns its real response unchanged — the CF POST is
   additive, fire-and-forget, never on the user-visible critical path. Flag
   intentionally mirrors Slice E's naming/pattern.

   Full CF-primary flip (CF owns the send, GAS fallback) is NOT this slice. */
/* SLICE G1 TRI-STATE (2026-10-16):
   window.JV_PROPOSAL_SEND_CF_ENABLED is now a TRI-STATE:
     - unset / false            -> OFF: zero CF traffic, GAS unchanged (default)
     - 'shadow' / true          -> SHADOW: Slice F behaviour (log shadow, GAS owns send)
     - 'primary'                -> PRIMARY: CF /proposals/send-primary is authoritative,
                                   GAS is NOT called. On any CF step failure the response
                                   includes fallbackHint:'gas' — soak policy TBD per Jess.
   Soak threshold (Jess locked, 2026-10-16): 10 shadow sends, 100% D1 ack
   match, before flipping any session to 'primary'. CF-side flag
   JV_PROPOSAL_SEND_CF_WRITES_PRIMARY must also be '1' on the worker.
*/
/* Issue #8 slice 12 -- CANARY for the test bride ONLY (Jess, 2026-10-19: the 10-send soak is waived for jfg@live.com.au and nobody else).
   Every other bride keeps the old behaviour exactly ('off' unless the window flag is set). The canary starts in 'shadow' (GAS still sends,
   the Worker only logs); moving it to 'primary' is a deliberate one-word change to JV_CF_SEND_CANARY_MODE in a later theme push, after the
   Worker flags are on. The Worker's own scope allow-list (JV_PROPOSAL_SEND_CF_SCOPE_EMAILS) and flags are a second, independent gate. */
var JV_CF_SEND_CANARY_EMAILS = ['jfg@live.com.au'];
var JV_CF_SEND_CANARY_MODE = 'shadow';

function jvProp_cfSendMode_(brideEmail) {
  try {
    if (typeof window === 'undefined') return 'off';
    var v = window.JV_PROPOSAL_SEND_CF_ENABLED;
    if (v === 'primary') return 'primary';
    if (v === true || v === 'shadow') return 'shadow';
    var e = String(brideEmail || '').trim().toLowerCase();
    if (e && JV_CF_SEND_CANARY_EMAILS.indexOf(e) !== -1) return JV_CF_SEND_CANARY_MODE === 'primary' ? 'primary' : 'shadow';
    return 'off';
  } catch (_e) { return 'off'; }
}

function jvProp_shouldShadowSendToCf_() {
  var m = jvProp_cfSendMode_();
  return m === 'shadow' || m === 'primary';
}

function jvProp_shouldCfPrimary_() {
  return jvProp_cfSendMode_() === 'primary';
}

function jvProp_cfProposalSendUrl_(mode) {
  // Shadow / primary endpoint — see DEPOLYMENT/jv-proposal-send/worker.js.
  // Base is overridable by window.JV_PROPOSAL_SEND_CF_URL (either the full
  // shadow URL for legacy callers, or just the worker base).
  var base = 'https://jv-proposal-send.calm-hill-fd5b.workers.dev';
  try {
    if (typeof window !== 'undefined' && typeof window.JV_PROPOSAL_SEND_CF_URL === 'string' && window.JV_PROPOSAL_SEND_CF_URL) {
      base = window.JV_PROPOSAL_SEND_CF_URL.replace(/\/proposals\/.*$/, '');
    }
  } catch (_e) {}
  return base + (mode === 'primary' ? '/proposals/send-primary' : '/proposals/send-shadow');
}

function pbApproveAndSendWarm_(name, options) {
  var _sendOpts = options;
  return pbTimed_('send:' + ((_sendOpts && _sendOpts.sendEmail === false) ? 'nosend' : 'send'), function() { return pbApproveAndSendWarmInner_(name, _sendOpts); });
}

function pbApproveAndSendWarmInner_(name, options) {
  var payload = { name: name, options: options || {} };
  // The Send handlers do not put the bride's contact in `options`; the builder state has it (state.workspace.bride).
  var _wsBride = (typeof state !== 'undefined' && state && state.workspace && state.workspace.bride) || {};
  var _sendEmail = String((options && options.brideEmail) || _wsBride.email || '').trim().toLowerCase();
  var _sendPhone = String((options && options.bridePhone) || _wsBride.phone || '').trim();
  var mode = jvProp_cfSendMode_(_sendEmail);
  // Primary needs a recipient: an empty email never POSTs, it stays on GAS exactly as before.
  if (mode === 'primary' && !_sendEmail) mode = 'off';

  // PRIMARY mode (Slice G1): CF /proposals/send-primary is authoritative.
  // GAS is NOT called. The CF worker performs D1 writes, Xero, email, SMS.
  // A step-level failure returns fallbackHint:'gas' but we DO NOT auto-fall-
  // back here (soak policy TBD); the admin sees the failure in UI as a real
  // error, same as any send failure.
  if (mode === 'primary') {
    try {
      var _nfP = (typeof window !== 'undefined' && window._jvNativeFetch) ? window._jvNativeFetch.bind(window) : fetch;
      return _nfP(jvProp_cfProposalSendUrl_('primary'), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          name: name,
          brideEmail: _sendEmail,
          bridePhone: _sendPhone,
          token: (options && options.token) || '',
          firstName: (options && options.firstName) || '',
          lastName: (options && options.lastName) || '',
          weddingDate: (options && options.weddingDate) || null,
          revisionNumber: (options && options.revisionNumber) || 1,
          total: (options && options.total) || null,
          subtotal: (options && options.subtotal) || null,
          gst: (options && options.gst) || null,
          lineItemsJson: (options && options.lineItemsJson) || null,
          proposalUrl: (options && options.proposalUrl) || null,
          bookingTier: (options && options.bookingTier) || null,
          bookingId: (options && options.bookingId) || null,
          depositAmountCents: (options && options.depositAmountCents) || null,
          emailSubject: (options && options.emailSubject) || null,
          emailHtml: (options && options.emailHtml) || null,
          smsBody: (options && options.smsBody) || null,
          sentAt: new Date().toISOString(),
          options: options || {},
          firedBy: (options && options.firedBy) || '',
        }),
      }).then(function(r) { return r.json ? r.json() : r; }).then(function(res) {
        // The Worker answered "I did nothing" (primary flag off, or this bride is outside its scope list): GAS still owns the send.
        // ONLY this explicit answer falls through; a failed or ambiguous CF attempt is surfaced, never retried on GAS (double-send risk).
        if (res && res.primary === false && res.shadow === true) {
          console.log('[JV_PROPOSAL_SEND_CF] worker declined (' + (res.status || 'primary_disabled') + ') -> GAS send');
          return jvBridge.callAdmin('proposalApproveAndSend', payload);
        }
        return res;
      });
    } catch (_e) {
      // Primary mode failed to even fire the fetch — surface as error.
      return Promise.reject(new Error('cf_primary_fetch_failed'));
    }
  }

  // SHADOW mode (Slice F): CF write is fire-and-forget, GAS still owns real send.
  if (mode === 'shadow') {
    try {
      var _nf = (typeof window !== 'undefined' && window._jvNativeFetch) ? window._jvNativeFetch.bind(window) : fetch;
      _nf(jvProp_cfProposalSendUrl_('shadow'), {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          name: name,
          options: options || {},
          firedBy: (options && options.firedBy) || '',
          ts: new Date().toISOString(),
        }),
      }).then(function(r) {
        console.log('[JV_PROPOSAL_SEND_CF_ENABLED] shadow ack ' + (r && r.status));
      }).catch(function(e) {
        console.log('[JV_PROPOSAL_SEND_CF_ENABLED] shadow fail ' + (e && e.message || e));
      });
    } catch (_e) { /* shadow writes never fail the caller */ }
  }

  // GAS is still the real send (shadow or off). Returned Promise is unchanged.
  return jvBridge.callAdmin('proposalApproveAndSend', payload);
}

function pbRecalcWarm_(route, payload) {
  var brideUid = String(state.workspace && state.workspace.brideUid || '').trim();
  if (!brideUid) return Promise.reject(new Error('Cannot save: bride identity not loaded. Hard-refresh the page.'));
  payload.brideUid = brideUid;
  payload.email = String(state.workspace.bride && state.workspace.bride.email || '').trim().toLowerCase();
  if (jvProp_shouldSkipGasForMetaobject_()) {
    // CF metaobject + D1 writer owns the write path. Skip the GAS recalc entirely —
    // this also skips the internal proposalBuilder_syncTokenLineItems_ fan-out
    // (snapshot of PROPOSAL_CALC_JSON into the Proposal Tokens sheet) that we are
    // migrating off. The caller receives a stub resolved status so the UI chain
    // continues; Slice F wires the CF response here.
    console.log('[JV_PROPOSAL_METAOBJECT_WRITE_ENABLED] ' + route + ' skipped — CF primary');
    return Promise.resolve({ status: 'skipped_gas_metaobject_primary', route: route });
  }
  return jvBridge.callAdmin(route, payload).then(function(res) {
    if (!pbIsColdReadFail_(res)) {
      pbCalcPersistShadow_(res);
      return res;
    }
    showBanner('That took a moment (first calc warms the pricing engine). Retrying...', 'warn');
    return jvBridge.callAdmin(route, payload).then(function (retryRes) {
      pbCalcPersistShadow_(retryRes);
      return retryRes;
    });
  });
}

/* /calc/persist FRONTEND CALLER (2026-09-23, Consolidator ask, Piece 2 of the
   D1-first proposal-engine cutover). KILL FLAG: window.JV_PROPOSAL_CALC_PERSIST
   must be explicitly true. Default (unset) = OFF = zero extra bridge calls,
   byte-identical to the old behaviour -- the GAS write path above (whichever
   of proposalRecalculate / proposalRecalculateWithAddress just ran) remains
   the ONLY write of record either way.

   FIRE-AND-FORGET, NEVER BLOCKS, NEVER AFFECTS THE RENDER. This fires AFTER
   a successful recalc response, mirrors the freshly-computed calc to D1 via
   the flag-gated GAS relay bridge_proposal_calcPersist (which itself is dark
   by default server-side too -- double-gated, browser flag AND GAS Script
   Property both have to be on for a real network write to happen). The
   relay never exposes D1_SYNC_SECRET to the browser -- see
   ProposalCalcPersistCaller_2026_09_23.gs's own header for the full auth
   chain (/calc/persist is explicitly "server-to-server only, never
   browser-reachable", this bridge route is the server-to-server caller).

   SCOPE: mirrors calc data ONLY, matching /calc/persist's own narrow scope
   (see that route's header). Does NOT replace proposalBuilder_statusAfterEdit_
   or the admin push notification GAS's real write path also fires -- this is
   a calc-only shadow mirror, not a full write-path replacement. */
function pbCalcPersistShadow_(res) {
  if (window.JV_PROPOSAL_CALC_PERSIST !== true) return;
  try {
    var data = (res && res.data) || res;
    if (!data || data.status !== 'ok' || !data.calculation) return;
    var email = (data.bride && (data.bride.email || data.bride.EMAIL)) || '';
    var brideName = state.currentBride || '';
    if (!email && !brideName) return;
    var body = { calc: data.calculation };
    if (email) body.email = email;
    if (brideName) body.brideName = brideName;
    jvBridge.callAdmin('bridge_proposal_calcPersist', body).then(function (persistRes) {
      console.log('[JV_PROPOSAL_CALC_PERSIST] ' + (brideName || email) + ': ' + JSON.stringify(persistRes));
    }).catch(function (e) {
      console.log('[JV_PROPOSAL_CALC_PERSIST] fetch failed for ' + (brideName || email) + ': ' + (e && e.message));
    });
  } catch (eShadow) { /* never let the persist shadow affect the real recalc flow */ }
}

function pbWorkspaceErrorHtml_(msg) {
  return '<div class="empty-state">' +
    '<i class="fa-solid fa-triangle-exclamation"></i>' +
    '<div>' + escapeHtml(msg) + '</div>' +
    '<button type="button" class="btn-ghost" style="margin-top:14px" onclick="pbRetryWorkspace_()">Retry</button>' +
    '</div>';
}

// Global so the inline Retry button in the workspace error state can reach it.
function pbRetryWorkspace_() {
  if (state.currentBride) selectBride(state.currentBride);
}

function selectBride(name, opt_brideUid, opt_displayName) {
  // opt_brideUid (2026-09-10, HANDOFF-proposal-deeplink-email-not-unique): optional,
  // durable row identity. Not required for normal manual picks from the sidebar list
  // (behaves exactly as before when omitted) -- exists so a caller that already
  // resolved a SPECIFIC row (deep-link, or a future Admin CRM click carrying a real
  // clientId) can pin that exact row through to the GAS workspace load, instead of
  // the two ends independently re-guessing and potentially disagreeing on a
  // duplicate-name/email bride.
  // opt_displayName (2026-09-10, live-verify addendum): optional, cosmetic ONLY --
  // used for the header title text while loading. Does NOT change `name` or
  // state.currentBride (both stay exactly as before, still load-bearing for the GAS
  // lookup / draft-key / comparison logic elsewhere in this function and file). Exists
  // because the Pipeline's direct-uid deep link previously had to pass the bride's EMAIL
  // as the `name` argument (proposalGetWorkspace requires a non-empty brideName even when
  // brideUid does the real resolving) -- which meant the header showed her raw email for
  // the whole loading wait, sometimes 40-95s live. When the caller already knows her real
  // name (the Pipeline card does), this shows it instead, purely for display.
  // #6: warn if there are unsaved changes before switching away from the current bride.
  // Skip the guard when reloading the same bride (artist add/remove, draft restore).
  if (_draftDirty && state.currentBride && state.currentBride !== name) {
    if (!confirm('You have unsaved changes for ' + state.currentBride + '. Switch bride and lose them?')) return;
    try { localStorage.removeItem(draftKey_(state.currentBride)); } catch (e) {}
  }
  // Auto-collapse the bride picker on first bride load this session
  // (unless the user has manually re-expanded it).
  if (!_pbSidebarUserExpanded) {
    var _root = document.querySelector('.jv-proposal-builder');
    if (_root && !_root.classList.contains('pb-sidebar-collapsed')) togglePbSidebar(true);
  }
  state.currentBride = name;
  state.editedLineItems = null;
  state.priceOverrides = {};
  state.lineItemsManuallyEdited = false;
  state.activeStage = 'enquiry';
  state.manualPartyMembers = [];
  state.noTrialRequired = false;
  state.brideWithMe = false;
  state.proposalHistory = [];
  state.studioSchedule = null;
  _draftDirty = false;
  _lastFlushAt = 0;
  if (_draftSaveTimer) { clearTimeout(_draftSaveTimer); _draftSaveTimer = null; }

  document.getElementById('bTitle').textContent = opt_displayName || name;
  document.getElementById('bMeta').textContent = 'Loading proposal workspace...';
  document.getElementById('recalcBtn').disabled = false;
  document.getElementById('sendBtn').disabled = false;

  var ws = document.getElementById('workspace');
  ws.innerHTML = buildPbLoadingSkeleton_();

  // Defer sidebar re-render so name + skeleton paint immediately before the list redraws
  renderStageBar();
  setTimeout(function() { renderBrides(); }, 0);

  var token = ++state.requestToken;

  // Honest elapsed-time indicator (2026-09-10, live-verify addendum). Confirmed live:
  // an archived-bride direct-uid load can genuinely hang at the Cloudflare-worker/GAS
  // hop for 40-95s+ with no error, no response, nothing -- pbGetWorkspaceWarm_ has no
  // client-side timeout (jvBridge has no cancel primitive, and this call's own
  // pbIsColdReadFail_ retry only fires on an actual error response, never on a request
  // that simply never resolves). A genuinely-hanging request is NOT a failure, so this
  // deliberately does NOT show a false "timed out"/error state -- it just keeps the
  // static "Loading proposal workspace..." from reading as possibly-stuck by counting
  // up, same honesty principle as the global-search fix (pbTryImmediateUidDeepLink_'s
  // sibling item 8a). Cleared the moment the promise settles either way.
  var _selBrideStartedAt = Date.now();
  var _selBrideMetaEl = document.getElementById('bMeta');
  var _selBrideElapsedTimer = setInterval(function() {
    if (token !== state.requestToken || !_selBrideMetaEl) { clearInterval(_selBrideElapsedTimer); return; }
    var elapsedSec = Math.round((Date.now() - _selBrideStartedAt) / 1000);
    if (elapsedSec < 5) return; // don't clutter a normal fast load with a counter
    _selBrideMetaEl.textContent = 'Loading proposal workspace... (' + elapsedSec + 's, large enquiries can take longer)';
  }, 1000);

  // Return the promise so callers (e.g. handleEditCurrentProposal's cold-instance
  // recovery) can chain on load completion. 2026-07-12.
  return pbGetWorkspaceWarm_(name, opt_brideUid)
    .then(function(res) {
      clearInterval(_selBrideElapsedTimer);
      if (token !== state.requestToken) return;
      // Cold-read guard: a 504 that survived the warm retry arrives as a bridge
      // failure envelope { ok:false } with no workspace fields. Render a clean,
      // retryable error instead of hydrating the envelope as an empty workspace.
      if (res && res.ok === false) {
        ws.innerHTML = pbWorkspaceErrorHtml_(
          'This enquiry is taking longer than usual to open. The first open runs a live pricing calculation, please retry in a moment.');
        return;
      }
      // Bridge wraps to { ok, data: { status, bride, party, calculation, proposal, ... } }. Defensive single-unwrap.
      var payload = (res && res.data) || res;
      if (!payload || payload.status === 'error') {
        ws.innerHTML = '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation"></i><div>' +
          escapeHtml((payload && payload.message) || 'Failed to load workspace') + '</div></div>';
        return;
      }
      // 2026-10-12: degrade, never block -- repair a malformed saved calc/party shape.
      var _pbSanitizeWarning = pbSanitizeWorkspacePayload_(payload);
      if (_pbSanitizeWarning) showBanner(_pbSanitizeWarning, 'warn', true);
      state.workspace = payload;
      state.workspace.brideUid = String((payload.bride && payload.bride.brideUid) || payload.brideUid || '').trim();
      // Service Catalog CDN fast-path (2026-09-22): shop-wide, identical for every
      // bride, so prefer the instant CDN-served list over whatever this specific
      // proposalGetWorkspace call happened to read live -- same data either way
      // (both derive from proposalBuilder_getServiceCatalog_), this just skips the
      // per-load Sheet read the live path pays. Falls straight through to the
      // GAS-supplied payload.serviceCatalog, unchanged, whenever the CDN cache is
      // missing/empty/malformed.
      var _pbCdnCatalog = pbServiceCatalogFromCdn_();
      if (_pbCdnCatalog && payload.proposalPartyCatalogEnabled) {
        // The CF workspace prices Shopify-owned party services for this bride's
        // wedding year. The shop-wide CDN list has no wedding-year context.
        var _pbPartyCatalog = (payload.serviceCatalog || []).filter(function(svc) {
          return svc.category === 'Bridal Party';
        });
        state.workspace.serviceCatalog = _pbCdnCatalog.filter(function(svc) {
          return svc.category !== 'Bridesmaid' && svc.category !== 'Mother' &&
            svc.category !== 'Bridal Party';
        }).concat(_pbPartyCatalog);
      } else if (_pbCdnCatalog) {
        state.workspace.serviceCatalog = _pbCdnCatalog;
      }
      // SSOT (Phase 3): resolve the rollout flag. Canonical source is the shop-level
      // metafield read in Liquid into window.JV_PROPOSAL_SSOT (reaches the builder no
      // matter WHICH backend served proposalGetWorkspace -- the CRM project or the JV
      // ADMIN island cache-hit path). The GAS response field payload.ssotEnabled is a
      // secondary fallback (only the CRM path emits it; the island path does not). Either
      // being truthy enables the SSOT paths. Default (neither set) = OFF = byte-identical.
      state.ssotEnabled = !!((window.JV_PROPOSAL_SSOT && window.JV_PROPOSAL_SSOT.enabled) || payload.ssotEnabled);
      // RATCHET FIX (2026-07-22): mirror of the backend JV_PROPOSAL_ARTIST_COUNT_CORRECTABLE
      // flag. Default (unset) = OFF = byte-identical to the old monotonic-up behaviour.
      state.correctableArtistCount = !!payload.correctableArtistCount;
      // BUILD-decline-closes-date-to-future-enquiries-2026-07-22: informational only,
      // never auto-declines from the workspace load, Jess decides what to do.
      if (payload.closedDateWarning) {
        showBanner(payload.closedDateWarning, 'warn');
      }
      // 4.1 (2026-07-10): strip manually-added members from the hydrated enquiry party.
      // The GAS workspace return folds manual additions INTO party (tagged isManualAddition)
      // AND returns them separately as manualAdditions -> state.manualPartyMembers. Without
      // this strip the same member is priced twice (party loop + manualAdditions loop) on the
      // local recalc (the ~$4,913 re-edit inflation) AND could re-enter as a genuine enquiry
      // override on the next flush (partyOverrides is mapped from state.workspace.party). Keep
      // manuals in state.manualPartyMembers ONLY; the flush sends partyOverrides + manualAdditions
      // as separate arrays, so nothing is lost.
      if (payload && Array.isArray(payload.party)) {
        state.workspace.party = payload.party.filter(function(p) { return !(p && p.isManualAddition); });
      }
      // Restore bookingTier (preferred) and brideWithMe (back-compat) from stored calc
      var savedCalc = normalizeCalcTier_(payload.calculation || {}); // canonical 'pro_team' -> builder-native 'proTeam'
      if (savedCalc.bookingTier === 'jessica' || savedCalc.bookingTier === 'proTeam') {
        state.bookingTier = savedCalc.bookingTier;
        state.brideWithMe = (savedCalc.bookingTier === 'jessica');
      } else if (typeof savedCalc.brideWithMe === 'boolean') {
        state.brideWithMe = savedCalc.brideWithMe;
        state.bookingTier = savedCalc.brideWithMe ? 'jessica' : 'proTeam';
      } else {
        // JESS RULING 2026-11-03: no stored tier and no stored brideWithMe boolean means UNKNOWN,
        // and unknown defaults to proTeam. Previously this set jessica/true, so 414 of 805 records
        // with no stored tier loaded the admin screen at Jessica pricing while the Cloudflare heal
        // resolved them to proTeam - the screen and the server quoting one bride two numbers.
        // BOTH lines must change together: brideWithMe is what actually prices the screen
        // (jvBridePricing_), bookingTier is the label and the payload field. Flipping only
        // bookingTier would show Jessica money under a proTeam label, which is worse than the
        // original defect, not better.
        state.bookingTier = 'proTeam';
        state.brideWithMe = false;
      }
      state.bookingTierLockedAt = savedCalc.bookingTierLockedAt || '';
      // Move-aware travel origin (2026-07-14): restore the per-quote choice from
      // the workspace's calcJson echo-back (proposalGetWorkspace, ProposalBuilderAPI.gs).
      state.travelOriginOverride = (savedCalc.travelOriginOverride === 'current' || savedCalc.travelOriginOverride === 'future') ? savedCalc.travelOriginOverride : null;
      state.travelOriginLockedAt = savedCalc.travelOriginLockedAt || '';
      state.juniorBridesmaids = Math.max(0, Number(savedCalc.juniorBridesmaids) || 0);
      state.assignedArtists = Array.isArray(savedCalc.assignedArtists) ? savedCalc.assignedArtists : [];
      // M4 (2026-07-09): rehydrate persisted custom appointment locations so the location
      // cards (label + address) reappear on reload. The travel LINE ITEM already round-trips
      // as a custom edited line; this restores the card metadata that was frontend-only.
      state.workspace.locations = Array.isArray(savedCalc.customLocations)
        ? savedCalc.customLocations.map(function(l) {
            return {
              type: 'custom',
              label: (l && l.label) || 'Custom Location',
              address: (l && l.address) || '',
              addressObj: (l && l.addressObj) || null,
              artists: (l && Array.isArray(l.artists)) ? l.artists : [],
              travelBreakdown: (l && Array.isArray(l.travelBreakdown)) ? l.travelBreakdown : []
            };
          })
        : [];
      var bride = payload.bride || {};
      document.getElementById('bMeta').textContent =
        formatDate(bride.weddingDate) + (bride.location ? ' \u00b7 ' + bride.location : '');

      // Header status reflection (2026-09-10, BUILD-proposal-builder-ux-fixes item 10):
      // the header "Review & Send" button previously stayed fully enabled even for a
      // terminal-status proposal (Signed/Void/Expired/etc), visually contradicting
      // Stage 4's own "this proposal has reached a final status" body text. Runs on
      // every workspace load so switching between brides always reflects the CURRENT
      // bride's status, not a stale one left over from whoever was open before.
      pbUpdateHeaderSendButton_(payload.proposal || {});

      // PATH 2: restore localStorage draft if newer than CRM's last save.
      // Drafts are written on every edit; the most recent flush clears them.
      // A surviving draft means the user closed the tab between edits + Save.
      var draft = loadDraft_(name);
      _draftDirty = false;
      _lastFlushAt = 0;
      if (draft && draft.calculation) {
        // Compare draft.ts vs the CRM calc's last-write timestamp if available.
        // Fall back to "always prefer draft if present" \u2014 unflushed work is the
        // user's, even if older than today's CRM mutations. Discard via the UI.
        if (confirm('Unsaved changes found from a previous session \u2014 restore them?\n\n(Cancel to discard and load the saved version from the server.)')) {
          state.workspace.calculation = draft.calculation;
          state.manualPartyMembers = draft.manualPartyMembers || [];
          state.noTrialRequired   = !!draft.noTrialRequired;
          state.brideWithMe       = !!draft.brideWithMe;
          state.bookingTier       = draft.bookingTier || state.bookingTier;
          state.editedLineItems   = draft.editedLineItems || null;
          state.priceOverrides    = draft.priceOverrides || {};
          state.travelSuppressions = draft.travelSuppressions || (draft.calculation && draft.calculation.travelSuppressions) || {};
          state.lineItemsManuallyEdited = !!draft.lineItemsManuallyEdited;
          if (draft.party) state.workspace.party = draft.party;
          _draftDirty = true;
        } else {
          try { localStorage.removeItem(draftKey_(name)); } catch (e) {}
        }
      }

      // Migration: if a legacy session has editedLineItems but no priceOverrides,
      // build the sparse map from the edited items using category+description as bridge.
      // Runs once per workspace load; clears editedLineItems price mutations after migration.
      if (state.editedLineItems && !Object.keys(state.priceOverrides || {}).length) {
        var freshCalc = payload.calculation || {};
        var freshItems = freshCalc.normalizedLineItems || freshCalc.lineItems || [];
        if (freshItems.length > 0) {
          var bridgeMap = {};
          freshItems.forEach(function(fi) {
            if (fi._rowId) bridgeMap[(fi.category || '') + '|' + (fi.description || fi.name || '')] = fi._rowId;
          });
          var migratedOverrides = {};
          state.editedLineItems.forEach(function(ei) {
            if (!ei._userEdited) return;
            var key = (ei.category || '') + '|' + (ei.description || ei.name || '');
            var rid = bridgeMap[key];
            if (!rid) return;
            var ov = {};
            var fi = freshItems.filter(function(f) { return f._rowId === rid; })[0];
            if (fi && Math.abs((ei.unitPrice || ei.rate || 0) - (fi.unitPrice || fi.rate || 0)) > 0.001) {
              ov.rate = ei.unitPrice || ei.rate || 0;
            }
            if (fi && Math.abs((ei.qty || ei.quantity || 1) - (fi.qty || fi.quantity || 1)) > 0.001) {
              ov.qty = ei.qty || ei.quantity || 1;
            }
            if (ov.rate !== undefined || ov.qty !== undefined) migratedOverrides[rid] = ov;
          });
          if (Object.keys(migratedOverrides).length > 0) {
            state.priceOverrides = migratedOverrides;
          }
          // Keep custom-row additions in editedLineItems; clear price mutation entries
          state.editedLineItems = state.editedLineItems.filter(function(ei) { return !!ei.custom; });
          if (!state.editedLineItems.length) state.editedLineItems = null;
        }
      }

      // Restore priceOverrides from workspace if present and no local overrides active
      if (!Object.keys(state.priceOverrides || {}).length && payload.calculation && payload.calculation.priceOverrides) {
        state.priceOverrides = payload.calculation.priceOverrides;
      }
      // Seed sticky travel suppressions from the loaded calc unless a draft already set them.
      if (!Object.keys(state.travelSuppressions || {}).length) {
        state.travelSuppressions = (state.workspace.calculation && state.workspace.calculation.travelSuppressions) || {};
      }

      if (payload._calcWarning) {
        showBanner('Pricing calculation encountered an error. Some line items may not reflect latest rates. Save and reload to retry.', 'warn');
      }
      // History + studio schedule are now folded into the workspace response
      // (eliminates 2 extra GAS round-trips on bride load).
      if (Array.isArray(payload.history)) {
        state.proposalHistory = payload.history;
      }
      if (payload.studioSchedule) {
        state.studioSchedule = payload.studioSchedule;
      }
      // P1d: seed the calc finish-time override from the bride's enquiry finish
      // time when the stored calc has none, so the finish inputs and the start
      // formula agree (the diagnostic found bride.finishTime set but
      // calculation.finishTime empty). Load-only: never clears an existing start
      // override (Invariant 1, override-wins).
      if (state.workspace.calculation && state.workspace.bride) {
        var _bft = String(state.workspace.bride.finishTime || '').trim();
        if (_bft && !String(state.workspace.calculation.finishTime || '').trim()) {
          state.workspace.calculation.finishTime = _bft;
        }
      }
      renderActiveStage();
      renderSavePill_();


      // Auto-calculate wedding travel on first load when the bride has an address
      // but no prior travel data. Fires silently in the background so travel lines
      // appear without the admin having to manually confirm the address in the panel.
      (function() {
        var _calc = state.workspace && state.workspace.calculation;
        var _bride = state.workspace && state.workspace.bride;
        var _addr = _bride && _bride.weddingAddressFull;
        var _hasTravel = _calc && (
          (_calc.weddingTravel && _calc.weddingTravel.length) ||
          (_calc.travel && _calc.travel.weddingTravel && _calc.travel.weddingTravel.length)
        );
        if (!_addr || _hasTravel || !state.currentBride) return;
        var _autoTok = ++state.requestToken;
        console.log('[PB-FREEZE-DIAG] auto-travel-calc firing for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride');
        pbRecalcWarm_('proposalRecalculateWithAddress', {
          name: state.currentBride,
          address: { address: _addr, type: 'wedding' },
          partyOverrides: (state.workspace.party || []).map(function(p) {
            return { role: p.role, serviceType: p.serviceType, qty: p.qty !== undefined ? Number(p.qty) : 1 };
          }),
          locationType: 'wedding'
        }).then(function(res) {
          console.log('[PB-FREEZE-DIAG] auto-travel-calc settled (ok) for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride');
          if (_autoTok !== state.requestToken) return;
          var payload = (res && res.data) || res;
          if (!payload || payload.status === 'error') return;
          var newCalc = payload.calculation || payload;
          if (newCalc && state.workspace) {
            // Preserve timing overrides the admin may have set before this async
            // response landed, same pattern as commitArtistsField_ (Invariant 1).
            var _prevC = state.workspace.calculation || {};
            var _kf = String(_prevC.finishTime         || '').trim();
            var _ks = String(_prevC.estimatedStartTime || '').trim();
            var _ka = _prevC.artistsRequired;
            // MONEY BUG FIX (2026-07-16): proposalRecalculateWithAddress only carries
            // paymentOption forward from CRM's already-stored value, never from an
            // in-memory choice the admin just made (e.g. full_gc). Without this preserve,
            // this silent background auto-travel-calc wipes a freshly picked payment
            // option back to whatever was previously stored (commonly none, which
            // downstream defaults to a real 'stripe' link). See
            // BRIEFS/HANDOVER-full-gc-root-cause-found-2026-07-16.md.
            var _keepPaymentOption = _prevC.paymentOption || null;
            state.workspace.calculation = newCalc;
            if (_kf) state.workspace.calculation.finishTime         = _kf;
            if (_ks) state.workspace.calculation.estimatedStartTime = _ks;
            if (_ka) state.workspace.calculation.artistsRequired    = _ka;
            if (_keepPaymentOption) state.workspace.calculation.paymentOption = _keepPaymentOption;
            recalcAndMerge_();
            renderActiveStage();
            renderSavePill_();
          }
        }).catch(function(err) {
          console.log('[PB-FREEZE-DIAG] auto-travel-calc settled (err) for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride: ' + (err && err.message));
        });
      })();

      // Auto-reconcile a stale stored bride-line price on first load (2026-09-21,
      // Kate Murphy incident). renderActiveStage() above paints state.workspace.calculation
      // exactly as GAS returned it -- the STORED PROPOSAL_CALC_JSON, never re-derived on
      // plain load. If that row's bride-line price was saved before her wedding-year's
      // correct D1 rate existed (or before a pricing fix landed), the builder shows a
      // stale dollar figure until the admin happens to click Recalculate -- confirmed live
      // via JV_diagnoseProposalPricingPath_KateMurphy(): her stored calc showed $350,
      // Recalculate correctly resolved $400 (matching live D1), same mechanism a fresh
      // unsent proposal from before a pricing correction would hit.
      // Never fires on a frozen/sent proposal (jvIsProposalFrozen_ mirrors the exact same
      // GAS-side gate proposalRecalculate_localPricing_ uses) -- a frozen quote's stored
      // price is INTENTIONALLY final and must never silently change on open.
      // Never fires when the bride line already carries an admin priceOverrides entry --
      // an admin who hand-set this line's price must never have it silently overwritten
      // just because she reopened the proposal (override-wins invariant, same rule
      // recalcAndMerge_ already applies everywhere else in this file).
      // Same shape as the travel auto-calc directly above: silent, background, token-
      // guarded, re-renders via the SAME flushToGas_()/proposalRecalculate round-trip
      // manualSave_ already uses -- never computes/writes a price client-side, the
      // server (already fixed to read D1 correctly, commit 50912bf4) stays the single
      // source of truth.
      (function() {
        var _calc = state.workspace && state.workspace.calculation;
        if (!_calc || !Array.isArray(_calc.lineItems) || jvIsProposalFrozen_()) return;
        var _brideLi = null;
        for (var _bi = 0; _bi < _calc.lineItems.length; _bi++) {
          if (_calc.lineItems[_bi].category === 'bride') { _brideLi = _calc.lineItems[_bi]; break; }
        }
        if (!_brideLi) return;
        // Skip if the admin already hand-set this exact line's price -- never overwrite
        // a deliberate override just because the proposal was reopened.
        if (_brideLi._rowId && state.priceOverrides && state.priceOverrides[_brideLi._rowId]) return;
        var _svc = 'Hair & Makeup';
        var _desc = String(_brideLi.description || '');
        if (_desc.indexOf('Hair Only') >= 0) _svc = 'Hair Only';
        else if (_desc.indexOf('Makeup Only') >= 0) _svc = 'Makeup Only';
        var _livePricing = jvBridePricing_(!!state.brideWithMe);
        var _liveUnit = _livePricing && _livePricing[_svc];
        var _storedUnit = Number(_brideLi.unitPrice);
        // Only trigger the round-trip on a genuine disagreement (not float rounding
        // noise) -- a proposal whose stored price already matches the live resolve
        // must be a complete no-op, never a needless recalc on every single open.
        if (typeof _liveUnit !== 'number' || isNaN(_storedUnit) || Math.abs(_liveUnit - _storedUnit) < 0.01) return;
        console.log('[PB-FREEZE-DIAG] auto-price-reconcile flushToGas_ firing for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride');
        flushToGas_().then(function() {
          console.log('[PB-FREEZE-DIAG] auto-price-reconcile flushToGas_ settled (ok) for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride');
          if (token !== state.requestToken) return; // bride switched away mid-flight
          renderActiveStage();
          renderSavePill_();
        }).catch(function(err) {
          console.log('[PB-FREEZE-DIAG] auto-price-reconcile flushToGas_ settled (err) for ' + state.currentBride + ' at +' + (Date.now() - _selBrideStartedAt) + 'ms since selectBride: ' + (err && err.message));
        }); // non-fatal -- stale display self-heals on the next manual Save/Recalculate
      })();

      // Enquiry form doc lazy-fetch (2026-06-10 regression fix). The P1a-3 perf
      // change (2026-06-03) moved the Gmail enquiry search off the main load: on a
      // cache miss proposalGetWorkspace returns enquiryFormData=null plus
      // enquiryFormPending=true. The client never implemented the deferred fetch,
      // so the "Form Submission Data" panel silently vanished for un-cached brides.
      // Fetch it now off the critical path and re-render the enquiry stage when it
      // lands (the route also warms ENQUIRY_FORM_JSON so the next load is a cache hit).
      // The Cloudflare workspace already owns enquiry-form hydration from D1.
      // A pending marker means the mirror has no authoritative form yet; never
      // revive the retired Gmail/GAS search as a hidden fallback.
      if (payload.enquiryFormPending && !payload.enquiryFormData) {
        state.workspace.enquiryFormPending = true;
      }
    })
    .catch(function(err) {
      clearInterval(_selBrideElapsedTimer);
      if (token !== state.requestToken) return;
      // 2026-10-12: a hydrate/render throw (not a transport failure) with a local draft
      // present is almost always the draft poisoning the load -- and the draft survives
      // reloads, so without this the bride stays unopenable on this browser forever.
      // Remove it so the retry loads the clean server version.
      var _hadDraft = false;
      try {
        if (localStorage.getItem(draftKey_(name))) { localStorage.removeItem(draftKey_(name)); _hadDraft = true; }
      } catch (eD) {}
      ws.innerHTML = pbWorkspaceErrorHtml_(getErrorMessage(err) +
        (_hadDraft ? ' An old unsaved local draft for this bride was cleared, please retry.' : ''));
    });
}

/* Merge a server-returned normalized calculation into the live workspace and
   re-render in place, with no second proposalGetWorkspace round-trip. Used by
   the add/remove artist flows so a committed CRM change shows instantly without
   the ~8s reload + selectBride() state reset/race. The calc object has the exact
   shape proposalGetWorkspace returns as `calculation` (same server builder).
   Returns true on success, or false (caller falls back to selectBride) if the
   payload is missing/malformed. */
/* =================================================================
   SSOT (Phase 3): one apply-response merge + one race guard
   Brief: PROPOSAL-BUILDER-SINGLE-SOURCE-OF-TRUTH-REFACTOR + ADDENDUM-A.
   These run ONLY when state.ssotEnabled (backend flag surfaced via the
   workspace response). Flag OFF = the legacy per-handler merges run,
   byte-identical. The goal: every server calc response re-hydrates ALL
   display state (calc, assignedArtists, travel lines, totals, timing,
   priceOverrides) from server truth in ONE place, so the "removed pill
   lingers" / "tier flip shows old allocation" class of bug cannot recur.
   ================================================================= */

// The ONE place a server calc response is applied to display state. Re-seeds
// everything selectBride()/applyWorkspaceCalculation_ would, from the authoritative
// server calc, including state.assignedArtists (so a removed/added/re-allocated
// artist is reflected in the chips AND the travel lines in a single render). Returns
// true on success, false if there was nothing usable to apply (caller falls back to a
// full reload). ADDENDUM-A A1+A2 are fixed by always routing add/remove/tier through here.
function applyCalcResponse_(calc, opts) {
  opts = opts || {};
  if (!calc || typeof calc !== 'object' || !state.workspace) return false;
  normalizeCalcTier_(calc); // canonical 'pro_team' -> builder-native 'proTeam' before any tier read
  state.workspace.calculation = calc;

  // Tier + its derived back-compat flag, from server truth.
  if (calc.bookingTier === 'jessica' || calc.bookingTier === 'proTeam') {
    state.bookingTier = calc.bookingTier;
    state.brideWithMe = (calc.bookingTier === 'jessica');
  } else if (typeof calc.brideWithMe === 'boolean') {
    state.brideWithMe = calc.brideWithMe;
    state.bookingTier = calc.brideWithMe ? 'jessica' : 'proTeam';
  }
  state.bookingTierLockedAt = calc.bookingTierLockedAt || '';
  state.juniorBridesmaids   = Math.max(0, Number(calc.juniorBridesmaids) || 0);

  // Allocation is SERVER-OWNED. Always trust the server's assignedArtists (it runs
  // travelBuildWeddingArtistList, which forces Jessica as artist #1 on jessica tier).
  // This is the A1/A2 fix: the chips + travel lines both derive from this one array.
  state.assignedArtists = Array.isArray(calc.assignedArtists) ? calc.assignedArtists : [];

  // Sticky overrides that round-trip through GAS.
  state.priceOverrides = (calc.priceOverrides && typeof calc.priceOverrides === 'object') ? calc.priceOverrides : (state.priceOverrides || {});
  state.travelOriginOverride = (calc.travelOriginOverride === 'current' || calc.travelOriginOverride === 'future') ? calc.travelOriginOverride : null;
  state.travelOriginLockedAt = calc.travelOriginLockedAt || '';

  // A server commit is the new source of truth: any pending local line-item edit is
  // superseded by the canonical calc, so collapse the legacy edit store (no stale
  // editedLineItems can win over the fresh calc, killing the "which of 3 stores"
  // ambiguity).
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;

  if (opts.clearDraft !== false) {
    _draftDirty = false;
    try { localStorage.removeItem(draftKey_(state.currentBride)); } catch (e) {}
  }
  if (opts.render !== false) {
    renderActiveStage();
    renderSavePill_();
  }
  return true;
}

// Universal stale-response guard for admin route calls that mutate the calc. Bumps
// requestToken before the call and no-ops the response if a newer action superseded it
// (the add/remove-artist calls are the longest, cold GAS to JV AUTO to Maps, up to a
// minute, and previously had NO guard, so a party edit during that minute was clobbered).
// Only used on the SSOT path. Returns a promise resolving to { ok, payload, stale }.
function callAdminGuarded(route, payload) {
  var _tok = ++state.requestToken;
  return jvBridge.callAdmin(route, payload).then(function(res) {
    var data = (res && res.data) || res;
    var stale = (_tok !== state.requestToken);
    return { ok: !(!data || data.status === 'error'), payload: data, stale: stale };
  });
}

function applyWorkspaceCalculation_(calc) {
  if (!calc || typeof calc !== 'object' || !state.workspace) return false;
  normalizeCalcTier_(calc); // canonical 'pro_team' -> builder-native 'proTeam' before any tier read
  state.workspace.calculation = calc;
  // Re-derive the same state fields selectBride() restores from a fresh calc,
  // so the artist panel, tier pill and totals all reflect the change.
  if (calc.bookingTier === 'jessica' || calc.bookingTier === 'proTeam') {
    state.bookingTier = calc.bookingTier;
    state.brideWithMe = (calc.bookingTier === 'jessica');
  } else if (typeof calc.brideWithMe === 'boolean') {
    state.brideWithMe = calc.brideWithMe;
    state.bookingTier = calc.brideWithMe ? 'jessica' : 'proTeam';
  }
  state.bookingTierLockedAt = calc.bookingTierLockedAt || '';
  state.juniorBridesmaids = Math.max(0, Number(calc.juniorBridesmaids) || 0);
  state.assignedArtists = Array.isArray(calc.assignedArtists) ? calc.assignedArtists : [];
  // A server commit is the new source of truth. Local edits were already
  // discarded by the add/remove confirm prompt, so the draft is clean.
  _draftDirty = false;
  try { localStorage.removeItem(draftKey_(state.currentBride)); } catch (e) {}
  renderActiveStage();
  renderSavePill_();
  return true;
}

/* =================================================================
   STAGE BAR
   ================================================================= */
function renderStageBar() {
  var bar = document.getElementById('stageBar');
  var html = '';
  var activeIdx = STAGES.findIndex ? STAGES.findIndex(function(s) { return s.id === state.activeStage; }) : -1;
  if (activeIdx < 0) { for (var j = 0; j < STAGES.length; j++) { if (STAGES[j].id === state.activeStage) { activeIdx = j; break; } } }
  for (var i = 0; i < STAGES.length; i++) {
    var s = STAGES[i];
    var active = s.id === state.activeStage;
    var completed = i < activeIdx;
    var cls = 'stage-pill' + (active ? ' active current' : '') + (completed ? ' completed' : '');
    var ariaAttrs = active ? 'aria-current="step"' : 'aria-label="' + s.name + (completed ? ' (done)' : '') + '"';
    html += '<button class="' + cls + '" onclick="setStage(\'' + s.id + '\')" ' + ariaAttrs + '>' +
      '<div class="stage-count">' + s.count + '</div>' +
      '<div class="stage-name">' + s.name + '</div>' +
      '<div class="stage-note">' + s.note + '</div>' +
      '</button>';
  }
  bar.innerHTML = html;
}

function setStage(id) {
  state.activeStage = id;
  renderStageBar();
  renderActiveStage();

  // Advancing to review (page 3) or send (page 4): persist through the SINGLE
  // canonical path (flushToGas_ then proposalRecalculate) so the bride-facing line
  // items reflect the latest artist count and travel overrides set on page 2.
  //
  // The old code called the DEPRECATED proposalUpdateLineItems route here. That
  // route does NOT regenerate line items. It blindly persists whatever items it
  // is handed (the sanitised DISPLAY line items) and recomputes only the totals,
  // re-applying neither the artist-count override nor the travel priceOverrides,
  // so page 3 showed a stale version that did not match page 2 (this bug). The
  // flush path below regenerates the canonical calc from current client state and
  // updates state.workspace.calculation, then we re-render so page 3 is correct.
  if ((id === 'review' || id === 'send') && _draftDirty && state.currentBride && state.workspace) {
    // Cancel any pending party-recalc debounce: we are committing current state now.
    if (_partyRecalcTimer) { clearTimeout(_partyRecalcTimer); _partyRecalcTimer = null; }
    flushToGas_()
      .then(function() { if (state.activeStage === id) renderActiveStage(); })
      .catch(function() {});
  }
}

function goToSendStage() {
  if (!state.currentBride) return;
  setStage('send');
}

function renderActiveStage() {
  if (!state.workspace) return;
  if (state.activeStage === 'enquiry') renderStageEnquiry();
  else if (state.activeStage === 'builder') renderStageBuilder();
  else if (state.activeStage === 'review') renderStageReview();
  else if (state.activeStage === 'send') renderStageSend();
}

/* =================================================================
   AWAITING RESPONSE PANEL + FOLLOW-UP LOG
   ================================================================= */
function renderAwaitingResponsePanel_(proposal, clientId) {
  var lastActFmt    = proposal.lastActionDate ? formatDate(proposal.lastActionDate) : null;
  var expiryFmt     = proposal.expiryDate     ? formatDate(proposal.expiryDate)     : null;
  var viewCount     = proposal.viewCount      || 0;
  var log           = proposal.followupLog    || [];

  var emailDelivered = !!proposal.emailSentAt;
  var smsDelivered   = !!proposal.smsSentAt;
  var emailLabel = emailDelivered
    ? '<span style="color:#1a7a3e;font-weight:600"><i class="fa-solid fa-circle-check"></i> Delivered ' + escapeHtml(formatDate(proposal.emailSentAt)) + '</span>'
    : '<span style="color:#9e3a2b"><i class="fa-solid fa-triangle-exclamation"></i> Not confirmed</span>';
  var smsLabel = smsDelivered
    ? '<span style="color:#1a7a3e;font-weight:600"><i class="fa-solid fa-circle-check"></i> Queued ' + escapeHtml(formatDate(proposal.smsSentAt)) + '</span>'
    : '<span style="color:var(--muted)">Not sent</span>';

  var statsHtml =
    '<div style="display:flex;gap:20px;flex-wrap:wrap;margin-bottom:16px">' +
      '<div style="text-align:center;min-width:80px">' +
        '<div style="font-size:22px;font-weight:700;color:var(--brand-primary)">' + viewCount + '</div>' +
        '<div style="font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em">Views</div>' +
      '</div>' +
      '<div style="flex:1;min-width:180px">' +
        '<div style="font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px">Email</div>' +
        '<div style="font-size:13px">' + emailLabel + '</div>' +
        '<div style="font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:4px;margin-top:8px">SMS</div>' +
        '<div style="font-size:13px">' + smsLabel + '</div>' +
      '</div>' +
      (lastActFmt
        ? '<div style="flex:1;min-width:160px"><div style="font-size:12px;color:var(--muted);margin-bottom:2px">Last interaction</div><div style="font-size:13px;font-weight:600">' + escapeHtml(lastActFmt) + '</div></div>'
        : '') +
      (expiryFmt
        ? '<div style="flex:1;min-width:160px"><div style="font-size:12px;color:var(--muted);margin-bottom:2px">Expires</div><div style="font-size:13px;font-weight:600">' + escapeHtml(expiryFmt) + '</div></div>'
        : '') +
    '</div>';

  var logHtml = '';
  if (log.length) {
    logHtml = '<div style="border-top:1px solid var(--border-color,#e8ddd3);padding-top:12px;margin-top:4px">' +
      '<div style="font-size:12px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">Follow-up log</div>';
    for (var li = log.length - 1; li >= 0; li--) {
      var e = log[li];
      var safeTs = escapeHtml(String(e.ts || ''));
      var editedNote = e.editedAt ? ' <span style="font-style:italic;color:var(--muted)">(edited ' + escapeHtml(e.editedAt) + ')</span>' : '';
      logHtml +=
        '<div data-log-ts="' + safeTs + '" style="padding:8px 10px;background:var(--brand-bg-soft);border-radius:var(--radius-medium);margin-bottom:6px;font-size:13px">' +
          '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px">' +
            '<div style="font-size:11px;color:var(--muted)">' +
              escapeHtml(e.dateLabel || e.ts || '') +
              (e.author ? ' &middot; ' + escapeHtml(e.author) : '') +
            '</div>' +
            '<div style="display:flex;gap:4px">' +
              '<button class="btn-log-action" title="Edit" onclick="editFollowupNote(' + (clientId || 0) + ',\'' + safeTs + '\')" ' +
                'style="background:none;border:none;cursor:pointer;color:var(--muted);padding:2px 4px;font-size:11px;line-height:1">' +
                '<i class="fa-solid fa-pencil"></i>' +
              '</button>' +
              '<button class="btn-log-action" title="Delete" onclick="deleteFollowupNote(' + (clientId || 0) + ',\'' + safeTs + '\')" ' +
                'style="background:none;border:none;cursor:pointer;color:#9e3a2b;padding:2px 4px;font-size:11px;line-height:1">' +
                '<i class="fa-solid fa-trash"></i>' +
              '</button>' +
            '</div>' +
          '</div>' +
          '<div class="log-note-text">' + escapeHtml(e.note || '') + editedNote + '</div>' +
        '</div>';
    }
    logHtml += '</div>';
  }

  var addNoteHtml =
    '<div style="border-top:1px solid var(--border-color,#e8ddd3);padding-top:12px;margin-top:8px">' +
      '<div style="font-size:12px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px">Add follow-up note</div>' +
      '<textarea id="followupNoteInput" placeholder="e.g. Called Jess, left voicemail. Following up Friday." rows="2" ' +
        'style="width:100%;box-sizing:border-box;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:8px 10px;font-size:13px;font-family:inherit;resize:vertical;background:#fff;color:var(--brand-text)"></textarea>' +
      '<div style="display:flex;justify-content:flex-end;margin-top:6px">' +
        '<button class="btn btn-primary btn-sm" id="followupSaveBtn" onclick="addFollowupNote(' + (clientId || 0) + ')">' +
          '<i class="fa-solid fa-floppy-disk"></i> Save note' +
        '</button>' +
      '</div>' +
    '</div>';

  return '<div class="card" style="border-left:3px solid var(--brand-primary)">' +
    '<div class="card-title"><i class="fa-solid fa-clock-rotate-left"></i> Awaiting Response</div>' +
    statsHtml +
    logHtml +
    addNoteHtml +
  '</div>';
}

function addFollowupNote(clientId) {
  var ta  = document.getElementById('followupNoteInput');
  var btn = document.getElementById('followupSaveBtn');
  if (!ta || !clientId) return;
  var note = ta.value.trim();
  if (!note) { ta.focus(); return; }

  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving...'; }

  jvBridge.callAdmin('bridge_admin_generic', {
    function: 'adminCrm_addProposalFollowupNote',
    args: { clientId: clientId, note: note }
  })
  .then(function(res) {
    var data = (res && res.data) || res;
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save note'; }
    if (data && data.ok) {
      // Patch the in-memory proposal log and re-render the panel
      if (state.workspace && state.workspace.proposal) {
        state.workspace.proposal.followupLog = data.log || [];
        state.workspace.proposal.lastActionDate = (data.entry && data.entry.dateLabel) || state.workspace.proposal.lastActionDate;
      }
      showBanner('Note saved.', 'success');
      renderStageEnquiry();
    } else {
      showBanner((data && data.message) || 'Failed to save note', 'error');
    }
  })
  .catch(function(err) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save note'; }
    showBanner(getErrorMessage(err), 'error');
  });
}

function deleteFollowupNote(clientId, ts) {
  if (!clientId || !ts) return;
  if (!confirm('Delete this log entry? This cannot be undone.')) return;

  jvBridge.callAdmin('bridge_admin_generic', {
    function: 'adminCrm_deleteProposalFollowupNote',
    args: { clientId: clientId, ts: ts }
  })
  .then(function(res) {
    var data = (res && res.data) || res;
    if (data && data.ok) {
      if (state.workspace && state.workspace.proposal) {
        state.workspace.proposal.followupLog = data.log || [];
      }
      showBanner('Entry deleted.', 'success');
      renderStageEnquiry();
    } else {
      showBanner((data && data.message) || 'Failed to delete entry', 'error');
    }
  })
  .catch(function(err) { showBanner(getErrorMessage(err), 'error'); });
}

function editFollowupNote(clientId, ts) {
  if (!clientId || !ts) return;
  var entryEl = document.querySelector('[data-log-ts="' + ts + '"]');
  if (!entryEl) return;
  var noteEl = entryEl.querySelector('.log-note-text');
  if (!noteEl) return;

  // Already in edit mode?
  if (entryEl.querySelector('.log-edit-ta')) return;

  var currentText = (state.workspace && state.workspace.proposal && state.workspace.proposal.followupLog || [])
    .filter(function(e) { return String(e.ts) === ts; })
    .map(function(e) { return e.note; })[0] || noteEl.textContent;

  noteEl.style.display = 'none';
  var ta = document.createElement('textarea');
  ta.className = 'log-edit-ta';
  ta.value = currentText;
  ta.rows = 2;
  ta.style.cssText = 'width:100%;box-sizing:border-box;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:6px 8px;font-size:13px;font-family:inherit;resize:vertical;background:#fff;color:var(--brand-text);margin-top:4px';
  entryEl.appendChild(ta);

  var btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;margin-top:4px';
  var saveBtn = document.createElement('button');
  saveBtn.className = 'btn btn-primary btn-sm';
  saveBtn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save';
  var cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn btn-ghost btn-sm';
  cancelBtn.textContent = 'Cancel';
  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(saveBtn);
  entryEl.appendChild(btnRow);
  ta.focus();

  cancelBtn.addEventListener('click', function() {
    ta.remove(); btnRow.remove(); noteEl.style.display = '';
  });

  saveBtn.addEventListener('click', function() {
    var newNote = ta.value.trim();
    if (!newNote) { ta.focus(); return; }
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i>';

    jvBridge.callAdmin('bridge_admin_generic', {
      function: 'adminCrm_editProposalFollowupNote',
      args: { clientId: clientId, ts: ts, note: newNote }
    })
    .then(function(res) {
      var data = (res && res.data) || res;
      if (data && data.ok) {
        if (state.workspace && state.workspace.proposal) {
          state.workspace.proposal.followupLog = data.log || [];
        }
        showBanner('Entry updated.', 'success');
        renderStageEnquiry();
      } else {
        saveBtn.disabled = false;
        saveBtn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save';
        showBanner((data && data.message) || 'Failed to save', 'error');
      }
    })
    .catch(function(err) {
      saveBtn.disabled = false;
      saveBtn.innerHTML = '<i class="fa-solid fa-floppy-disk"></i> Save';
      showBanner(getErrorMessage(err), 'error');
    });
  });
}

/* =================================================================
   STAGE 1 -- ENQUIRY DETAILS
   ================================================================= */
function renderStageEnquiry() {
  var ws = state.workspace;
  var bride = ws.bride || {};
  var party = ws.party || [];
  var refs = ws.referenceLinks || {};
  var proposal = ws.proposal || {};

  var partyText = party.map(function(p) {
    var roleLabel = p.displayLabel || p.role;
    return (p.qty > 1 ? p.qty + 'x ' : '') + roleLabel + (p.serviceType ? ' (' + p.serviceType + ')' : '');
  }).join(', ') || '\u2014';

  var linksHtml = '';
  if (refs.inspirationFolder) {
    linksHtml += '<div class="detail-item"><label>Inspiration Folder</label><div class="value"><a href="' + escapeHtml(refs.inspirationFolder) + '" target="_blank"><i class="fa-solid fa-folder-open"></i> Open Folder</a></div></div>';
  }
  if (refs.agreementLink) {
    linksHtml += '<div class="detail-item"><label>Agreement</label><div class="value"><a href="' + escapeHtml(refs.agreementLink) + '" target="_blank"><i class="fa-solid fa-file-contract"></i> View Agreement</a></div></div>';
  }
  if (refs.invoiceFolder) {
    linksHtml += '<div class="detail-item"><label>Invoice Folder</label><div class="value"><a href="' + escapeHtml(refs.invoiceFolder) + '" target="_blank"><i class="fa-solid fa-file-invoice-dollar"></i> View Invoices</a></div></div>';
  }

  var statusHtml = '';
  if (proposal.status) {
    var cleanStatus = sanitiseProposalStatus(proposal.status);
    statusHtml = '<div class="detail-item"><label>Proposal Status</label><div class="value"><span class="status-badge ' + proposalBadgeClass(cleanStatus) + '">' + escapeHtml(cleanStatus) + '</span></div></div>';
  }

  // Full address with suburb/state/postcode
  var fullAddress = bride.address || '';
  if (bride.suburb || bride.state || bride.postcode) {
    var addrParts = [bride.suburb, bride.state, bride.postcode].filter(function(p) { return !!p; });
    if (fullAddress && addrParts.length) fullAddress += ', ';
    fullAddress += addrParts.join(' ');
  }

  var awaitingPanel = '';
  var ps0 = String(proposal.status || '').toUpperCase();
  if (ps0 === 'SENT' || ps0 === 'ACTIVE') {
    awaitingPanel = renderAwaitingResponsePanel_(proposal, state.workspace.clientId);
  }

  // WI-2 (2026-06-20): "Create New Proposal" choice card.
  // Show when a proposal exists (including VOID) so Jess can always create a new one.
  // For VOID proposals only the "Create New" button is shown (can't edit a void proposal).
  var createNewPanel = '';
  var hasExistingProposal = ps0 && ps0 !== 'DECLINED' && ps0 !== '';
  if (hasExistingProposal) {
    // Item 3c (BUILD-item3-reenquiry-surfacing-2026-07-19): a same-date re-enquiry
    // (isReEnquiry AND no alt-date slot) offers a third choice alongside the existing
    // Edit/Create New buttons -- add the new details as a package option instead of
    // starting a whole new proposal. A different-date re-enquiry (hasAltDateSlot) is
    // handled separately below -- see altDateCard_ -- it is a genuinely separate
    // wedding and gets its OWN independent send, never folded into this proposal
    // as a variant (Jess's decision, confirmed 2026-07-22: Option B, one row, two
    // independent sends -- see ProposalAltDateBuildAndSend_2026_07_22.gs).
    var offerPackageOption = !!ws.isReEnquiry && !ws.altDateSlot;
    createNewPanel = renderProposalChoiceCard_(proposal, bride, offerPackageOption);
  }

  // Item 2 (SESSION-proposal-builder-dupe-prevention-2026-07-21.md): the "spin off a
  // proposal for a different wedding date" card. Shown whenever the row carries a
  // stored alt-date slot, independent of hasExistingProposal above -- a bride can
  // have BOTH a normal proposal for her primary date AND a dormant alt-date slot
  // waiting to be built into its own proposal.
  var altDateCard = ws.altDateSlot ? renderAltDateCard_(ws.altDateSlot) : '';

  document.getElementById('workspace').innerHTML =
    createNewPanel +
    altDateCard +
    awaitingPanel +
    '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-user"></i> Bride Details</div>' +
      '<div class="detail-grid">' +
        '<div class="detail-item"><label>Name</label><div class="value">' + escapeHtml(bride.name) + '</div></div>' +
        '<div class="detail-item"><label>Email</label><div class="value">' + escapeHtml(bride.email || '\u2014') + '</div></div>' +
        '<div class="detail-item"><label>Phone</label><div class="value">' + escapeHtml(bride.phone || '\u2014') + '</div></div>' +
        '<div class="detail-item"><label>Wedding Date<span class="field-type-tag field-type-tag--editable">Editable</span></label><div class="value" id="weddingDateFieldWrap">' + renderWeddingDateDisplay_(bride) + '</div></div>' +
        '<div class="detail-item"><label>Ceremony / Finish Time<span class="field-type-tag field-type-tag--editable">Editable</span></label><div class="value">' +
          '<select id="finishTimeInput" onchange="saveFinishTime(this.value)" ' +
          'style="width:100%;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium,1rem);padding:4px 10px;font-size:inherit;font-family:inherit;background:var(--brand-bg-white,#fff);color:var(--brand-text,#2f2524);">' +
          // P1d: read the single source (calc.finishTime), falling back to the raw
          // enquiry value, so Stage-1 and the Stage-2 timing input stay in sync.
          buildFinishTimeOptions_((ws.calculation && ws.calculation.finishTime) || bride.finishTime || '') +
          '</select>' +
        '</div></div>' +
        '<div class="detail-item"><label>Venue / Location<span class="field-type-tag field-type-tag--computed" title="As submitted at enquiry. The venue used for travel pricing is set separately on Stage 2 (Wedding Day Team card).">As submitted</span></label><div class="value">' + escapeHtml(bride.location || '\u2014') + '</div></div>' +
        '<div class="detail-item"><label>Address</label><div class="value">' + escapeHtml(fullAddress || '\u2014') + '</div></div>' +
        (bride.leadSource ? '<div class="detail-item"><label>How They Found Us</label><div class="value">' + escapeHtml(bride.leadSource) + '</div></div>' : '') +
        statusHtml +
      '</div>' +
    '</div>' +
    '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-users"></i> Bridal Party & Services</div>' +
      '<div class="detail-grid">' +
        (bride.servicesRequested ? '<div class="detail-item" style="grid-column:1/-1"><label>Services Requested (original)</label><div class="value">' + escapeHtml(bride.servicesRequested) + '</div></div>' : '') +
        '<div class="detail-item" style="grid-column:1/-1"><label>Party Composition</label><div class="value">' + escapeHtml(partyText) + '</div></div>' +
      '</div>' +
    '</div>' +
    (linksHtml ? '<div class="card"><div class="card-title"><i class="fa-solid fa-link"></i> Reference Links</div><div class="detail-grid">' + linksHtml + '</div></div>' : '') +
    buildEnquirySourceCard_() +
    renderProposalHistoryHtml_() +
    // Studio schedule card — async loaded for the wedding date
    '<div class="card" id="studioScheduleCard">' +
      '<div class="card-title"><i class="fa-solid fa-calendar-day"></i> Studio Schedule: ' + escapeHtml(formatDate(bride.weddingDate)) + '</div>' +
      '<div id="studioScheduleCardBody" style="color:var(--muted);font-size:13px;text-align:center;padding:16px 0">' +
        '<i class="fa-solid fa-spinner fa-spin"></i><span style="margin-left:8px">Loading schedule…</span>' +
      '</div>' +
    '</div>' +
    '<div style="margin-top:8px;padding:14px 16px;border:1px solid rgba(201,64,64,0.2);border-radius:var(--radius-medium);background:rgba(201,64,64,0.03);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap">' +
      '<div style="font-size:13px;color:var(--muted)"><strong style="color:#c94040">Can\'t take this booking?</strong> Close the enquiry and send the bride a polite decline.</div>' +
      '<button class="btn btn-danger btn-sm" onclick="openDeclineContextModal()" style="white-space:nowrap">' +
        '<i class="fa-solid fa-circle-xmark"></i> Mark Unavailable &amp; Notify' +
      '</button>' +
    '</div>';

  // The narrower D1 schedule read is opt-in separately from the other builder reads.
  // Its coverage metadata is rendered below so missing GAS-only availability facts stay visible.
  var preloadedSchedule = state.studioSchedule;
  if (window.JV_PB_DECLINE_SCHEDULE_CF_ENABLED === true && bride.weddingDate) {
    pbCfBuilderRead_('decline-schedule', { date: bride.weddingDate }, function(b) {
      return b && b.ok === true && Array.isArray(b.artistsSummary) && b.coverage;
    }).then(function(sched) {
      var panel = document.getElementById('studioScheduleCardBody');
      if (!panel) return;
      if (sched) {
        panel.innerHTML = _buildDeclineScheduleHtml_(sched);
        return;
      }
      return jvBridge.callAdmin('bridge_admin_generic', { function: 'proposalGetDeclineSchedule', args: { dateStr: bride.weddingDate } })
        .then(function(res) {
          var fallbackSchedule = (res && res.data) || res;
          panel.innerHTML = _buildDeclineScheduleHtml_(fallbackSchedule);
        });
    }).catch(function() {
      var panel = document.getElementById('studioScheduleCardBody');
      if (panel) panel.innerHTML = '<span style="color:var(--muted);font-size:13px">Could not load schedule.</span>';
    });
  } else if (preloadedSchedule && preloadedSchedule.ok !== false) {
    var panel = document.getElementById('studioScheduleCardBody');
    if (panel) panel.innerHTML = _buildDeclineScheduleHtml_(preloadedSchedule);
  } else if (bride.weddingDate) {
    jvBridge.callAdmin('bridge_admin_generic', { function: 'proposalGetDeclineSchedule', args: { dateStr: bride.weddingDate } })
      .then(function(res) {
        var panel = document.getElementById('studioScheduleCardBody');
        if (!panel) return;
        var sched = (res && res.data) || res;
        panel.innerHTML = _buildDeclineScheduleHtml_(sched);
      })
      .catch(function() {
        var panel = document.getElementById('studioScheduleCardBody');
        if (panel) panel.innerHTML = '<span style="color:var(--muted);font-size:13px">Could not load schedule.</span>';
      });
  }
}

/* ── WI-2 (2026-06-20): Create New Proposal — choice card + handler ─────── */

/**
 * Renders the "Edit or Create New" choice card shown at the top of step-01 when
 * the bride already has an active proposal. Returns an HTML string.
 */
function renderProposalChoiceCard_(proposal, bride, offerPackageOption) {
  var status = sanitiseProposalStatus(String(proposal.status || ''));
  var rawStatus = String(proposal.status || '').toUpperCase();
  var isVoid = rawStatus === 'VOID';
  var totalStr = '';
  if (state.workspace && state.workspace.calculation && state.workspace.calculation.total) {
    totalStr = ' (A$' + parseFloat(state.workspace.calculation.total).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ')';
  }
  var editBtn = isVoid ? '' :
    '<button class="btn btn-primary btn-sm" onclick="handleEditCurrentProposal()">' +
      '<i class="fa-solid fa-pencil"></i> Edit Current Proposal' +
    '</button>';
  // Item 3c (BUILD-item3-reenquiry-surfacing-2026-07-19): same-date re-enquiry only --
  // she re-submitted the enquiry form with the SAME wedding date, so instead of a whole
  // new proposal, Jess can add her updated details as a package option on the existing
  // one (the bride then sees a toggle between options in her viewer).
  var packageOptionBtn = (!isVoid && offerPackageOption)
    ? '<button class="btn btn-ghost btn-sm" onclick="handleReEnquiryAsPackageOption_()">' +
        '<i class="fa-solid fa-layer-group"></i> Add as Package Option' +
      '</button>'
    : '';
  var reEnquiryNote = offerPackageOption
    ? ' She re-enquired again for the <strong>same date</strong> -- you can fold her new details into this proposal as a package option instead of starting over.'
    : '';
  var description = isVoid
    ? 'A voided proposal exists for ' + escapeHtml(bride.name || 'this bride') + ' (<span class="status-badge ' + proposalBadgeClass(status) + '">' + escapeHtml(status) + '</span>). Create a new one to replace it.'
    : 'A proposal is already in progress for ' + escapeHtml(bride.name || 'this bride') + ' (<span class="status-badge ' + proposalBadgeClass(status) + '">' + escapeHtml(status) + '</span>' + escapeHtml(totalStr) + ').' + reEnquiryNote + ' Choose what to do:';
  return (
    '<div class="card" style="border:1.5px solid var(--brand-primary);margin-bottom:16px">' +
      '<div class="card-title" style="color:var(--brand-primary)">' +
        '<i class="fa-solid fa-file-invoice"></i> Existing Proposal' +
      '</div>' +
      '<p style="margin:0 0 12px;font-size:13.5px;color:var(--brand-text)">' + description + '</p>' +
      '<div style="display:flex;gap:10px;flex-wrap:wrap">' +
        editBtn +
        packageOptionBtn +
        '<button class="btn ' + (isVoid ? 'btn-primary' : 'btn-ghost') + ' btn-sm" onclick="handleCreateNewProposal()">' +
          '<i class="fa-solid fa-plus"></i> Create New Proposal' +
        '</button>' +
      '</div>' +
    '</div>'
  );
}

/**
 * Item 2 (SESSION-proposal-builder-dupe-prevention-2026-07-21.md): "spin off a
 * proposal for a different wedding date" card. altSlot is the parsed
 * PROPOSAL_ALT_DATE_JSON blob (see enquiryApi_storeAltDateSlot_ in
 * BridalEnquiryAPI.gs for the write side, ProposalAltDateBuildAndSend_2026_07_22.gs
 * for the build/send backend). This is a SEPARATE, independently-tracked proposal
 * from the one shown above; sending it never changes the primary date's status,
 * sent date, or Xero quote.
 */
function renderAltDateCard_(altSlot) {
  var dateLabel = altSlot.weddingDateFormatted || formatDate(altSlot.weddingDate) || 'an unconfirmed date';
  var venueLabel = altSlot.venueName || altSlot.weddingLocation || '';
  var altStatus = String(altSlot.altProposalStatus || '').trim();
  var hasBeenSent = altStatus === 'Sent';
  var totalStr = (altSlot.altCalcJson && typeof altSlot.altCalcJson.total === 'number')
    ? 'A$' + parseFloat(altSlot.altCalcJson.total).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  var statusBadge = hasBeenSent
    ? '<span class="status-badge ' + proposalBadgeClass('Sent') + '">Sent ' + escapeHtml(altSlot.altProposalSentDate ? formatDate(altSlot.altProposalSentDate) : '') + '</span>'
    : '<span class="status-badge">Not yet sent</span>';

  var primaryDateLabel = formatDate((state.workspace && state.workspace.bride && state.workspace.bride.weddingDate) || '') || 'primary date';
  var description = 'She also re-enquired for a <strong>different wedding date</strong>, <strong>' +
    escapeHtml(dateLabel) + '</strong>' + (venueLabel ? ' at ' + escapeHtml(venueLabel) : '') +
    '. This is tracked separately from the proposal above. Sending it will not change the status of her ' +
    escapeHtml(primaryDateLabel) + ' proposal.';

  var calcBtn = '<button class="btn btn-ghost btn-sm" onclick="handleAltDateRecalculate_()">' +
    '<i class="fa-solid fa-calculator"></i> ' + (altSlot.altCalcJson ? 'Recalculate' : 'Calculate Pricing') +
    '</button>';
  var sendBtn = altSlot.altCalcJson
    ? '<button class="btn btn-primary btn-sm" onclick="handleAltDateSend_()">' +
        '<i class="fa-solid fa-paper-plane"></i> ' + (hasBeenSent ? 'Re-send Proposal' : 'Build & Send Proposal') +
      '</button>'
    : '';
  var viewLinkBtn = (hasBeenSent && altSlot.altProposalToken)
    ? '<a class="btn btn-ghost btn-sm" href="https://jessicavegas.com/pages/your-bridal-beauty-package?token=' +
        encodeURIComponent(altSlot.altProposalToken) + '" target="_blank">' +
        '<i class="fa-solid fa-arrow-up-right-from-square"></i> View Proposal' +
      '</a>'
    : '';

  return (
    '<div class="card" style="border:1.5px solid var(--brand-primary);margin-bottom:16px">' +
      '<div class="card-title" style="color:var(--brand-primary)">' +
        '<i class="fa-solid fa-calendar-days"></i> Different-Date Re-enquiry ' + statusBadge +
      '</div>' +
      '<p style="margin:0 0 12px;font-size:13.5px;color:var(--brand-text)">' + description + '</p>' +
      (totalStr ? '<p style="margin:0 0 12px;font-size:13.5px;color:var(--brand-text)">Calculated total: <strong>' + totalStr + '</strong></p>' : '') +
      '<div style="display:flex;gap:10px;flex-wrap:wrap">' +
        calcBtn +
        sendBtn +
        viewLinkBtn +
      '</div>' +
    '</div>'
  );
}

/**
 * Calculates (or recalculates) pricing for the alt-date slot via
 * proposalAltDateRecalculate. Does not send anything, purely builds the calc so
 * Jess can review the total before sending. Reloads the workspace on success so
 * the card re-renders with the fresh total and enabled Send button.
 */
function handleAltDateRecalculate_() {
  var bride = (state.workspace && state.workspace.bride) || {};
  if (!bride.name) { showToast('Bride name missing', 'error'); return; }
  showLoadingOverlay('Calculating pricing for the alt date...');
  jvBridge.callAdmin('proposalAltDateRecalculate', { name: bride.name })
    .then(function(res) {
      hideLoadingOverlay();
      var data = (res && res.data) || res;
      if (!res || res.ok === false || !data || data.status === 'error') {
        showToast((data && data.message) || (res && res.error) || 'Could not calculate the alt-date proposal', 'error');
        return;
      }
      showToast('Alt-date pricing calculated.', 'success');
      selectBride(bride.name);
    })
    .catch(function() {
      hideLoadingOverlay();
      showToast('Could not calculate the alt-date proposal. Please try again.', 'error');
    });
}

/**
 * Builds and sends the alt-date proposal via proposalAltDateSend, mints its own
 * token, its own Proposal Tokens row, and its own email, entirely independent of
 * the primary date's proposal state (see ProposalAltDateBuildAndSend_2026_07_22.gs).
 */
function handleAltDateSend_() {
  var bride = (state.workspace && state.workspace.bride) || {};
  var altSlot = (state.workspace && state.workspace.altDateSlot) || {};
  if (!bride.name) { showToast('Bride name missing', 'error'); return; }
  if (!altSlot.altCalcJson) { showToast('Calculate the alt-date pricing before sending.', 'error'); return; }
  if (!confirm('Send the proposal for her alternate date now? This sends a separate email/link and does not affect her existing proposal.')) return;
  showLoadingOverlay('Sending the alt-date proposal...');
  pbCfBuilderWrite_('altdate-send', {
    brideUid: bride.brideUid || '',
    email: bride.email || '',
    calcJson: altSlot.altCalcJson,
    resend: String(altSlot.altProposalStatus || '').toUpperCase() === 'SENT'
  }, pbAcceptCfAltDateSend_)
    .then(function(result) {
      hideLoadingOverlay();
      if (result && result.refusal) {
        showToast(result.refusal.error || 'Could not build the alt-date proposal', 'error');
        return;
      }
      var data = result && result.cf;
      if (!data) {
        showToast('Could not confirm the Cloudflare alt-date outcome. Do not retry until the workspace is refreshed.', 'error');
        return;
      }
      if (data.needsEmail === true) {
        showBanner('The alt-date proposal was built, but bride email is disabled or was not queued. Nothing was sent.', 'warn', true);
      } else {
        showToast(data.isResend ? 'Alt-date proposal re-send queued.' : 'Alt-date proposal email queued.', 'success');
      }
      selectBride(bride.name);
    })
    .catch(function() {
      hideLoadingOverlay();
      showToast('Could not send the alt-date proposal. Please try again.', 'error');
    });
}

/**
 * "Add as Package Option" (Item 3c, same-date re-enquiry only). Reuses the existing
 * package-variant builder (pvAddVariant_) so the re-enquired details become a
 * selectable option on the SAME proposal rather than a new one. pvAddVariant_ already
 * pushes the new variant slot AND enters the page-2 builder for it, so Jess lands
 * straight on an editable option pre-populated from the base package -- she then
 * adjusts party/services from the re-enquiry compare panel and Saves the option.
 */
function handleReEnquiryAsPackageOption_() {
  if (typeof pvAddVariant_ !== 'function') { showToast('Package options are unavailable right now.', 'error'); return; }
  pvAddVariant_();
  showToast('New package option started -- adjust it to match her re-enquiry, then Save Option.', 'success');
}

/**
 * Shared reopen-for-edit call, used by ALL THREE "Edit Proposal" entry points
 * (Stage 1 handleEditCurrentProposal, Stage 3/4 editProposal). Added 2026-09-08,
 * BRIEFS/BUILD-proposal-builder-triple-edit-entry-unify-2026-09-08.md.
 *
 * Before this fix, only Stage 1 had this cold-start recovery logic, Stage 3/4's
 * editProposal() treated ANY failed response as a hard failure with a plain
 * error toast, no reload-and-check. Given proposalReopenForEdit is NOT
 * idempotent (it bumps QUOTE_REVISION_COUNT and flips SENT/READY to
 * DRAFT_EDIT), a false-failure toast on Stage 3/4 risked an admin retrying and
 * creating a real double-reopen/revision-count issue that Stage 1's version
 * was specifically built to avoid. One implementation now, not two that could
 * silently diverge in reliability.
 *
 * @param {string} brideName
 * @param {Object} [opts]
 * @param {Function} [opts.onBusy]    called with true/false around the call,
 *   for UI-specific busy-state handling (spinner text, disabled button), each
 *   call site's own concern, not this shared function's.
 * @param {Function} [opts.onColdRetry]  called once when a cold-read is
 *   detected, before the workspace reload starts (e.g. to show a
 *   caller-specific "checking..." banner).
 * @param {Function} [opts.onSuccess]  called with the resolved response on a
 *   genuine (non-cold-read) success.
 * @param {Function} [opts.onFailure]  called with the resolved response (or
 *   error) on a genuine (non-cold-read) failure.
 * @returns {Promise} resolves after the workspace reload (cold-read path) or
 *   the initial call (success/failure path) settles.
 */
/* REOPEN-THEN-RELOAD RACE FIX (2026-10-12, jfg builder-open root cause).
   There is NO short client timeout on this path (bridge TIMEOUT_MS 150s, overlay
   170s, relay 55s for proposalReopenForEdit + proposalGetWorkspace). What sent the
   admin back to the "Existing Proposal" card was the RELOAD after a reopen:
   (a) when JV_PROPOSAL_CACHE_FASTPATH is on, selectBride() served the CDN-cached
       PRE-reopen workspace (status READY) -> Existing Proposal card again, and
   (b) on a cold-read failure envelope from the reopen, selectBride() reloaded ONCE,
       immediately, while GAS was still finishing the write (12-29s+ observed) ->
       workspace still READY -> Existing Proposal card again.
   Fix: always reload LIVE (never the cache) after a reopen, and on an ambiguous
   reopen poll the live workspace for up to PB_REOPEN_WAIT_MS until the status
   actually reads DRAFT_EDIT, with a visible elapsed-seconds "still opening" state.
   Read-only polling (proposalGetWorkspace), never re-fires the non-idempotent
   reopen. Independent of c3eb587d3 (malformed-data/draft recovery), which only
   runs inside selectBride's own then/catch. */
var PB_REOPEN_WAIT_MS = 45000;
var PB_REOPEN_POLL_GAP_MS = 5000;

function pbSelectBrideLive_(name) {
  // Same one-shot cache bypass the stale-while-revalidate repaint uses;
  // pbGetWorkspaceWarm_ reads the flag synchronously inside selectBride.
  var prevFlag = window.JV_PROPOSAL_CACHE_FASTPATH;
  window.JV_PROPOSAL_CACHE_FASTPATH = false;
  try { return selectBride(name); }
  finally { window.JV_PROPOSAL_CACHE_FASTPATH = prevFlag; }
}

function pbSetOverlayText_(msg) {
  try {
    var o = document.getElementById('jvLoadingOverlay');
    var t = o && o.querySelector('.loader > div:last-child');
    if (t) t.textContent = msg;
  } catch (e) {}
}

function pbAwaitReopenLanded_(brideName) {
  var started = Date.now();
  var tick = setInterval(function() {
    var sec = Math.round((Date.now() - started) / 1000);
    var msg = 'Still opening your proposal... (' + sec + 's, the server is slow right now, please keep this page open)';
    pbSetOverlayText_(msg);
    var meta = document.getElementById('bMeta');
    if (meta) meta.textContent = msg;
  }, 1000);
  function done(v) { clearInterval(tick); return v; }
  function attempt() {
    var bride = (state.brides || []).find(function(b) { return b && b.name === brideName; }) || {};
    return pbCfBuilderRead_('workspace', { email: bride.email || '', brideUid: bride.brideUid || '' }, function(b) {
      return b && b.ok === true && b.status === 'loaded' && b.bride;
    }).then(function(res) {
      var p = (res && res.data) || res;
      var st = String((p && p.proposal && p.proposal.status) || '').trim().toUpperCase();
      if (st === 'DRAFT_EDIT') return true;
      if (Date.now() - started >= PB_REOPEN_WAIT_MS) return false;
      return new Promise(function(r) { setTimeout(r, PB_REOPEN_POLL_GAP_MS); }).then(attempt);
    }, function() {
      if (Date.now() - started >= PB_REOPEN_WAIT_MS) return false;
      return new Promise(function(r) { setTimeout(r, PB_REOPEN_POLL_GAP_MS); }).then(attempt);
    });
  }
  return attempt().then(done, function() { return done(false); });
}

function pbReopenProposalForEdit_(brideName, opts) {
  opts = opts || {};
  var onBusy = typeof opts.onBusy === 'function' ? opts.onBusy : function() {};
  var onSuccess = typeof opts.onSuccess === 'function' ? opts.onSuccess : function() {};
  var onFailure = typeof opts.onFailure === 'function' ? opts.onFailure : function() {};
  var bride = (state.workspace && state.workspace.bride) || {};

  onBusy(true);
  return pbCfBuilderWrite_('state-actions', {
    action: 'reopen',
    brideUid: bride.brideUid || '',
    email: bride.email || ''
  }, pbAcceptCfReopen_)
    .then(function(result) {
      onBusy(false);
      if (result && result.refusal) {
        onFailure(result.refusal);
        return result.refusal;
      }
      var cf = result && result.cf;
      if (!cf) {
        var unavailable = { ok: false, error: 'Cloudflare proposal editing is unavailable. Nothing was changed.' };
        onFailure(unavailable);
        return unavailable;
      }
      onSuccess(cf);
      return cf;
    })
    .catch(function(err) {
      onBusy(false);
      onFailure(err);
    });
}

/**
 * "Edit Current", proceed to the normal DRAFT_EDIT flow (proposalReopenForEdit).
 */
function handleEditCurrentProposal() {
  var bride = (state.workspace && state.workspace.bride) || {};
  if (!bride.name) { showToast('Bride name missing', 'error'); return; }
  // ALREADY-IN-EDIT GUARD (2026-10-12, jfg root cause, confirmed in code): the
  // choice card renders "Edit Current Proposal" for ANY non-empty, non-DECLINED
  // status (renderStageEnquiry hasExistingProposal), including DRAFT_EDIT, and this
  // handler used to call proposalReopenForEdit unconditionally. GAS rejects that
  // ("must be in SENT or READY status ... Current status: DRAFT_EDIT", PROPOSAL.gs
  // proposalReopenForEdit), onFailure toasted it and the admin was left on the
  // choice card forever. A DRAFT_EDIT proposal is already open for editing: skip
  // the reopen and go straight to the builder stage.
  var _curStatus = String((state.workspace && state.workspace.proposal && state.workspace.proposal.status) || '').trim().toUpperCase();
  if (_curStatus === 'DRAFT_EDIT') {
    setStage('builder');
    return;
  }
  showLoadingOverlay('Opening current proposal for editing...');
  pbReopenProposalForEdit_(bride.name, {
    onSuccess: function(res) {
      // Cloudflare is authoritative for the reopen. Do not immediately reload the
      // legacy workspace: for non-canary brides it can still report the stale SENT
      // status and push the admin back onto the choice card even though D1 is already
      // DRAFT_EDIT. Reopen changes state only; the calculation already in memory is
      // the package the admin chose to edit, so apply the confirmed status locally.
      if (state.workspace && state.workspace.proposal) {
        state.workspace.proposal.status = 'DRAFT_EDIT';
        state.workspace.proposal.revisionCount = res.revisionCount || 0;
      }
      showBanner('Proposal reopened for editing (Rev ' + (res.revisionCount || '') + ')', 'success');
      setStage('builder');
    },
    onFailure: function(res) {
      showToast((res && (res.message || res.error)) || 'Could not reopen proposal. Please try again.', 'error');
    },
    onBusy: function(busy) { if (!busy) hideLoadingOverlay(); }
  });
}

/**
 * "Create New" — show the combined choice dialog: void old? + copy or fresh?
 * Uses a single modal with three explicit buttons (Option 2 UX from build plan).
 */
function handleCreateNewProposal() {
  var bride = (state.workspace && state.workspace.bride) || {};
  var brideName = bride.name || '';
  if (!brideName) { showToast('Bride name missing', 'error'); return; }
  var ps = String((state.workspace && state.workspace.proposal && state.workspace.proposal.status) || '').toUpperCase();
  var isSent = ps === 'SENT' || ps === 'ACTIVE' || ps === 'ACCEPTED'; // widened 2026-09-17: an already-signed bride also needs the void-warning (state/findings/OPEN-send-path-accepted-token-reuse-2026-09-16.md)

  // Build modal HTML
  var voidWarning = isSent
    ? '<div style="margin-bottom:12px;padding:10px 12px;background:rgba(201,64,64,0.06);border-radius:var(--radius-medium,1rem);border:1px solid rgba(201,64,64,0.2);font-size:13px;color:#c94040">' +
        '<strong>Note:</strong> The existing sent proposal will be <strong>voided</strong> — the old link will stop working. The bride will receive a new link when you send the new proposal.' +
      '</div>'
    : '';

  var modalHtml =
    '<div style="text-align:left">' +
      voidWarning +
      '<p style="margin:0 0 14px;font-size:14px;color:var(--brand-text)">Choose how to start the new proposal:</p>' +
      '<div style="display:flex;flex-direction:column;gap:10px">' +
        '<button class="btn btn-primary" style="justify-content:flex-start;text-align:left" onclick="confirmCreateNew(\'fresh\')">' +
          '<i class="fa-solid fa-rotate-right" style="margin-right:8px"></i>' +
          '<span><strong>Rebuild from her enquiry</strong><br>' +
          '<span style="font-size:12px;font-weight:400;opacity:0.9">A fresh, clean proposal recomputed from her enquiry details (party, services, venue).</span></span>' +
        '</button>' +
        '<button class="btn btn-ghost" style="justify-content:flex-start;text-align:left" onclick="confirmCreateNew(\'blank\')">' +
          '<i class="fa-solid fa-file" style="margin-right:8px"></i>' +
          '<span><strong>Start blank</strong><br>' +
          '<span style="font-size:12px;font-weight:400;opacity:0.65">Empty proposal so you can build it by hand from the catalog.</span></span>' +
        '</button>' +
        '<button class="btn btn-ghost" style="justify-content:flex-start;text-align:left" onclick="confirmCreateNew(\'copy\')">' +
          '<i class="fa-solid fa-copy" style="margin-right:8px"></i>' +
          '<span><strong>Create a copy</strong><br>' +
          '<span style="font-size:12px;font-weight:400;opacity:0.65">Start from the current package (deduped and sanitised).</span></span>' +
        '</button>' +
        '<button class="btn btn-ghost" style="color:var(--muted);margin-top:4px" onclick="closeProposalModal()">' +
          'Cancel' +
        '</button>' +
      '</div>' +
    '</div>';

  showProposalModal('Create New Proposal', modalHtml, { hidePrimaryBtn: true });

  // Store context for the confirmCreateNew handler.
  window._createNewContext = { brideName: brideName, voidOld: isSent };
}

/**
 * Called when the user picks copy or fresh from the modal.
 */
function confirmCreateNew(startMode) {
  closeProposalModal();
  var ctx = window._createNewContext || {};
  var brideName = ctx.brideName;
  var voidOld   = !!ctx.voidOld;
  if (!brideName) { showToast('Bride name missing', 'error'); return; }

  var modeLabel = startMode === 'copy' ? 'Copying current package...'
                : startMode === 'blank' ? 'Creating a blank proposal...'
                : 'Rebuilding from her enquiry...';
  showLoadingOverlay(modeLabel);

  // The D1 state-action route fully supports the blank variant. Copy and
  // fresh remain on their existing path until their calculator/sanitiser
  // dependencies have Cloudflare equivalents.
  if (startMode === 'blank') {
    var wsBride = (state.workspace && state.workspace.bride) || {};
    pbCfBuilderWrite_('state-actions', {
      action: 'void_and_new',
      startMode: 'blank',
      brideUid: wsBride.brideUid || '',
      email: wsBride.email || ''
    }, pbAcceptCfVoidAndNew_).then(function(result) {
      if (result && result.refusal) { pbShowCfWriteRefusal_(result.refusal); return; }
      if (!result || !result.cf) {
        hideLoadingOverlay();
        showBanner('Could not create the blank proposal through Cloudflare. Nothing was changed.', 'error');
        return;
      }
      hideLoadingOverlay();
      showToast('New blank proposal created', 'success');
      selectBride(brideName);
    }).catch(function() {
      hideLoadingOverlay();
      showBanner('Could not confirm the Cloudflare blank-proposal outcome. Reload before trying again.', 'error');
    });
    return;
  }

  jvBridge.callAdmin('proposalVoidAndNew', { name: brideName, startMode: startMode, voidOld: voidOld })
    .then(function(res) {
      hideLoadingOverlay();
      if (!res || res.status === 'error') {
        showToast((res && res.message) || 'Could not create new proposal', 'error');
        return;
      }
      showToast('New proposal created', 'success');
      // Reload workspace so the builder loads the new token/calc.
      selectBride(brideName);
    })
    .catch(function(err) {
      hideLoadingOverlay();
      showToast('Error creating new proposal', 'error');
    });
}

// Normalise a date string to a comparable "yyyy-MM-dd" token. Handles the three
// formats this codebase actually produces: ISO ("2026-11-20" / "2026-11-20T..."),
// Australian "DD/MM/YYYY", and free text like "20 November 2026". Returns '' when
// the value can't be parsed confidently, so callers can treat "unknown" as "don't
// flag a mismatch" rather than guessing.
function normaliseDateForCompare_(val) {
  var s = String(val == null ? '' : val).trim();
  if (!s) return '';
  // ISO yyyy-MM-dd (optionally with time)
  var iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return iso[1] + '-' + iso[2] + '-' + iso[3];
  // Australian DD/MM/YYYY (or D/M/YYYY)
  var au = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (au) {
    var d = ('0' + au[1]).slice(-2);
    var m = ('0' + au[2]).slice(-2);
    return au[3] + '-' + m + '-' + d;
  }
  // Fallback: let the engine parse text dates ("20 November 2026", "Sat 20 Mar 2026").
  // Avoid this path for slash/dash numeric formats (handled above) where JS would
  // misread DD/MM as MM/DD.
  var parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    var yy = parsed.getFullYear();
    var mm = ('0' + (parsed.getMonth() + 1)).slice(-2);
    var dd = ('0' + parsed.getDate()).slice(-2);
    return yy + '-' + mm + '-' + dd;
  }
  return '';
}

/**
 * ITEM 1 (2026-08-04) -- Option B, chosen by Jess: parse and SHOW, never auto-fill.
 *
 * Scans the bride's / planner's free-text (CRM DREAM_DETAILS) for people-count and
 * role signals and renders them as a READ-ONLY prompt beside the party builder.
 *
 * HARD CONTRACT -- do not "improve" this into a pre-fill:
 *   - It returns an HTML string and nothing else. It has no side effects.
 *   - It NEVER writes to state.manualPartyMembers, state.workspace.party,
 *     state.editedLineItems, or any calculation field.
 *   - Every number here is a HINT for a human to act on, never an input to pricing.
 * This project has repeatedly been burned by inferred data presented as fact; the
 * whole point of Option B is that a parse error can never become a wrong quote.
 *
 * @returns {string} HTML for the hints panel, or '' when there is nothing to show.
 */
function buildDreamDetailHintsHtml_() {
  var src  = (state.workspace && state.workspace.enquirySource) || {};
  var text = String(src.dreamDetails || '').trim();
  if (!text) return '';

  var lower = text.toLowerCase();
  var hints = [];

  // Spelled-out numbers, so "fourteen people" is caught as well as "14 people".
  var WORD_NUMS = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
    nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
    fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20
  };
  var NUM_PATTERN = '(\\d{1,2}|' + Object.keys(WORD_NUMS).join('|') + ')';

  function toNum(raw) {
    var s = String(raw || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(WORD_NUMS, s) ? WORD_NUMS[s] : (parseInt(s, 10) || 0);
  }

  // "<n> <noun>" for each people-noun we care about. Collect the LARGEST match per
  // noun rather than the first, so "2 or 3 bridesmaids" surfaces the upper figure.
  var NOUNS = [
    { re: 'people|guests|of us|in the (?:bridal )?party',  one: 'person',     many: 'people' },
    { re: 'bridesmaids?',                                  one: 'bridesmaid', many: 'bridesmaids' },
    { re: 'groomsmen|groomsman',                           one: 'groomsman',  many: 'groomsmen' },
    { re: 'men|males|guys|gentlemen',                      one: 'man',        many: 'men' },
    { re: 'mothers?|mums?|moms?',                          one: 'mother',     many: 'mothers' },
    { re: 'flower ?girls?',                                one: 'flower girl',many: 'flower girls' },
    { re: 'page ?boys?',                                   one: 'page boy',   many: 'page boys' }
  ];

  for (var n = 0; n < NOUNS.length; n++) {
    var rx = new RegExp(NUM_PATTERN + '\\s+(?:' + NOUNS[n].re + ')\\b', 'g');
    var best = 0, m;
    while ((m = rx.exec(lower)) !== null) {
      var v = toNum(m[1]);
      if (v > best) best = v;
    }
    if (best > 0) hints.push({ count: best, label: (best === 1 ? NOUNS[n].one : NOUNS[n].many) });
  }

  // Mentions worth flagging even without a number attached to them.
  var FLAGS = [
    { re: /\bnon[- ]?binary\b|\bthey\/them\b/,                          label: 'Mentions a non-binary guest' },
    { re: /\bgroomsm(?:e|a)n\b|\bmen'?s grooming\b|\bmale\b|\bgroom\b/, label: "Mentions men / men's grooming" },
    { re: /\bplanner\b|\bwedding planner\b|\bcoordinator\b/,            label: 'Submitted via a planner' },
    { re: /\bmother of the (?:bride|groom)\b|\bmob\b|\bmog\b/,          label: 'Mentions mother of the bride / groom' }
  ];
  var flags = [];
  for (var f = 0; f < FLAGS.length; f++) {
    if (FLAGS[f].re.test(lower)) flags.push(FLAGS[f].label);
  }

  // PRICE-BREAKDOWN REQUEST (2026-08-04). The highest-value signal in this panel, and
  // the reason it was extended. Fern's planner wrote "pricing considerations breaking
  // down the costs like: Hair & Makeup $xx / Hair only or Makeup only $xx / Men's
  // Grooming $xx" -- an explicit request for multiple priced options. The proposal
  // builder HAS that feature (Package Options, page 3, up to 3 packages with a
  // bride-facing toggle) and it works, but nothing on screen ever connects the request
  // to the tool: the message sits on page 1, the control sits below the totals block on
  // page 3, and three separate sessions read the enquiry and still hand-built options in
  // a document instead. This prompt exists to close that gap at the moment of need.
  //
  // Still SHOW-ONLY, per Option B: it creates no package, changes no price, and clicks
  // nothing. It tells the admin there is a job to do and where to do it.
  var breakdown = dreamDetailsBreakdownRequest_(lower);

  if (hints.length === 0 && flags.length === 0 && !breakdown) return '';

  var html = '<div style="border:1px solid var(--brand-primary,#c99591);' +
    'background:var(--brand-bg-soft,#faf7f4);border-radius:var(--radius-medium,1rem);' +
    'padding:12px 14px;margin-bottom:12px">' +
    '<div style="font-weight:600;font-size:13px;margin-bottom:6px;color:var(--brand-primary-deep,#a9706c)">' +
      '<i class="fa-solid fa-quote-left" style="margin-right:6px"></i>Her message mentions' +
    '</div>' +
    '<div style="font-size:12px;color:var(--muted);margin-bottom:8px">' +
      'Read from her own words. Nothing here has been added to the quote. Enter anyone you want included below.' +
    '</div>';

  if (hints.length) {
    html += '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:' + (flags.length ? '8px' : '0') + '">';
    for (var h = 0; h < hints.length; h++) {
      html += '<span style="display:inline-block;padding:4px 10px;background:#fff;' +
        'border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-pill,3rem);' +
        'font-size:12.5px;font-weight:600;color:var(--brand-text,#2f2524)">' +
        hints[h].count + ' ' + escapeHtml(hints[h].label) + '</span>';
    }
    html += '</div>';
  }

  if (flags.length) {
    html += '<ul style="margin:0;padding-left:18px;font-size:12.5px;color:var(--brand-text,#2f2524)">';
    for (var g = 0; g < flags.length; g++) {
      html += '<li>' + escapeHtml(flags[g]) + '</li>';
    }
    html += '</ul>';
  }

  // The price-breakdown prompt is rendered LAST and visually separated, because it is a
  // call to action rather than another observation. It names the service types she listed
  // and points at Package Options by name so the admin knows the tool exists and where.
  if (breakdown) {
    var svcNote = breakdown.services.length
      ? ' She lists: ' + breakdown.services.join(', ') + '.'
      : '';
    html += '<div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border-color,#e8ddd3);' +
      'font-size:12.5px;color:var(--brand-text,#2f2524)">' +
      '<strong style="color:var(--brand-primary-deep,#a9706c)">' +
        'She asked for a price breakdown' +
        (breakdown.count ? ' (' + breakdown.count + ' options)' : '') +
      '</strong>' +
      escapeHtml(svcNote) +
      ' You can quote each one as its own package: <strong>Package Options</strong>, ' +
      'at the bottom of the Review page (up to 3, she picks in her viewer).' +
      '</div>';

    // NAME THE GAP, do not quietly drop it (2026-08-04). The builder has no Men's
    // Grooming service: party services are exactly two checkboxes, Hair and Makeup, so a
    // groomsman prices on the same rate card as a bridesmaid. Listing it above without
    // saying so would send Jess to build packages for a request she cannot fully answer,
    // and she would find out from the planner instead. A limitation that is named is
    // handled; a limitation that is silent is a surprise. Remove this line only when
    // Men's Grooming genuinely exists as its own priced service.
    if (breakdown.services.indexOf("Men's Grooming") >= 0) {
      html += '<div style="margin-top:8px;padding:8px 10px;background:#fff;' +
        'border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium,1rem);' +
        'font-size:12px;color:var(--brand-text,#2f2524)">' +
        '<strong>Heads up:</strong> there is no Men&#39;s Grooming service yet. ' +
        'Quote the men as Hair Only or Makeup Only, and say what is included in your message to her.' +
        '</div>';
    }
  }

  html += '</div>';
  return html;
}

/**
 * Detects an explicit request for separate prices per service type.
 *
 * Deliberately CONSERVATIVE: it fires only when the message shows a real intent to have
 * costs broken out ("breaking down the costs", "price list", "how much for X and Y"),
 * NOT merely because two service names appear. A bride writing "I want hair and makeup"
 * is stating what she wants, not asking for a comparison, and must not trigger this.
 * A false positive here nags the admin about a job that does not exist, which is how a
 * useful prompt becomes noise that gets ignored.
 *
 * Returns null when not detected, else { count, services }. Read-only, no side effects.
 *
 * @param {string} lower already-lowercased message text.
 * @returns {{count:number, services:string[]}|null}
 * @private
 */
function dreamDetailsBreakdownRequest_(lower) {
  if (!lower) return null;

  // NORMALISE APOSTROPHES FIRST (fix 2026-08-04, browser-verified defect).
  // Shipped v1 matched only the straight ASCII apostrophe (men'?s), so a message written
  // with a CURLY apostrophe -- which is what Google Docs, iOS/Android keyboards, Word and
  // most email clients actually produce -- silently failed the Men's Grooming test. On
  // the live planner enquiry that turned "4 options" into "3 options" and dropped the one
  // service the builder cannot quote, i.e. it hid exactly the gap this prompt exists to
  // expose, while still reading as a confident, complete list. Map every curly/typographic
  // apostrophe to the straight form, then match once. Cheap, and it protects every
  // possessive pattern in this function, not just this one.
  lower = lower.replace(/[‘’ʼ՚＇`´]/g, "'");

  // Intent: she is asking about cost in a way that implies itemisation.
  var askIntent =
       /break(?:ing|s)?[- ]?down the cost|breakdown of (?:the )?cost|broken down/.test(lower)
    || /itemis|itemiz|price list|pricing (?:consideration|breakdown|option|structure)/.test(lower)
    || /cost(?:s)? (?:for|of) each|separate price|price(?:s|d)? (?:for )?each/.test(lower)
    || /\bhow much (?:is|for|would|does)\b/.test(lower)
    || /\$xx|\$x{2,}|\$\s*\?/.test(lower);   // the "Hair & Makeup: $xx" template shape

  // Service types she named. Order is the order we quote them in.
  var SERVICES = [
    { re: /hair\s*(?:&|and|\+)\s*make ?up|make ?up\s*(?:&|and|\+)\s*hair/, label: 'Hair & Makeup' },
    { re: /hair[- ]only|only hair|just hair/,                             label: 'Hair Only' },
    { re: /make ?up[- ]only|only make ?up|just make ?up/,                 label: 'Makeup Only' },
    { re: /men'?s grooming|mens grooming|groom(?:sm[ae]n)? grooming|barber/, label: "Men's Grooming" }
  ];
  var found = [];
  for (var i = 0; i < SERVICES.length; i++) {
    if (SERVICES[i].re.test(lower)) found.push(SERVICES[i].label);
  }

  // "hair only or makeup only" is one phrase naming two priceable things; the regexes
  // above already catch both, so no special-casing is needed here.

  // Fire when there is explicit intent AND at least two distinct service types to
  // compare. Either alone is too weak: intent without services could be a general
  // "how much do you charge", and services without intent is just a description.
  if (!askIntent || found.length < 2) return null;

  return { count: found.length, services: found };
}

function buildEnquirySourceCard_() {
  var src = (state.workspace && state.workspace.enquirySource) || {};
  var bride = (state.workspace && state.workspace.bride) || {};

  // Determine source label
  var sourceLabel = '\u2014';
  if (src.bookingSource === 'branded_form') {
    sourceLabel = 'JV Bridal Enquiry Form';
  } else if (src.moxieClientId) {
    sourceLabel = 'Moxie';
  } else if (src.leadSource2) {
    sourceLabel = src.leadSource2;
  } else if (src.leadSource) {
    sourceLabel = src.leadSource;
  }

  var sourceIcon = src.moxieClientId ? 'fa-solid fa-m' : 'fa-solid fa-file-lines';

  var html = '<div class="card">' +
    '<div class="card-title"><i class="' + sourceIcon + '"></i> Enquiry Source Data</div>' +
    '<div class="detail-grid">' +
      '<div class="detail-item"><label>Enquiry Source</label><div class="value"><span class="status-badge status-active">' + escapeHtml(sourceLabel) + '</span></div></div>' +
      (src.enquiryDate ? '<div class="detail-item"><label>Enquiry Date</label><div class="value">' + formatDate(src.enquiryDate) + '</div></div>' : '') +
      (src.leadSource ? '<div class="detail-item"><label>How They Found Us</label><div class="value">' + escapeHtml(src.leadSource) + '</div></div>' : '') +
      (src.venueName ? '<div class="detail-item"><label>Venue Name<span class="field-type-tag field-type-tag--computed" title="As submitted at enquiry. The venue used for travel pricing is set separately on Stage 2 (Wedding Day Team card).">As submitted</span></label><div class="value">' + escapeHtml(src.venueName) + '</div></div>' : '') +
      (src.trialRequired ? '<div class="detail-item"><label>Trial Required?</label><div class="value">' + escapeHtml(src.trialRequired) + '</div></div>' : '');

  // Moxie-specific fields
  if (src.moxieClientId) {
    html += '<div class="detail-item"><label>Moxie Client ID</label><div class="value"><code>' + escapeHtml(src.moxieClientId) + '</code></div></div>';
    if (src.moxieAgreementId) {
      html += '<div class="detail-item"><label>Moxie Agreement</label><div class="value"><code>' + escapeHtml(src.moxieAgreementId) + '</code></div></div>';
    }
  }

  // Notes (CRM NOTES column). NOT the bride's own message -- see Dream Details below.
  if (src.notes) {
    html += '<div class="detail-item" style="grid-column:1/-1"><label>Notes</label><div class="value" style="white-space:pre-line">' + escapeHtml(src.notes) + '</div></div>';
  }

  // ITEM 2 (2026-08-04): the bride's / planner's own free-text message
  // (CRM DREAM_DETAILS, ProposalBuilderAPI.gs:2456 -> src.dreamDetails). It reached
  // the frontend but was rendered nowhere, and line above was mislabelled
  // "Notes / Dream Details", which hid the gap.
  //
  // NO INNER SCROLL BOX (fixed 2026-08-04, same day, browser-verified defect).
  // The first version of this block used max-height:260px + overflow-y:auto to stop a
  // long message dominating the panel. On Fern's planner enquiry that clipped the list
  // mid-way at "Hair & Makeup: $xx", putting "Men's Grooming: $xx" -- the single most
  // consequential line in the whole enquiry -- below a fold INSIDE a fold, behind a
  // nested scroll nobody thinks to perform. A message the admin must act on must never
  // be height-capped: the page scrolls, the message does not. If a future change wants
  // to tame long messages, collapse them with a visible "Show more" control that states
  // there IS more, never a silent inner scroll.
  if (src.dreamDetails) {
    html += '<div class="detail-item" style="grid-column:1/-1">' +
      '<label>Dream Details (her own words)</label>' +
      '<div class="value" style="white-space:pre-line;' +
      'background:var(--brand-bg-soft,#faf7f4);border:1px solid var(--border-color,#e8ddd3);' +
      'border-radius:var(--radius-medium,1rem);padding:12px 14px;line-height:1.55">' +
      escapeHtml(src.dreamDetails) + '</div></div>';
  }

  // Original form data summary
  html += '<div class="detail-item" style="grid-column:1/-1"><label>Original Request</label><div class="value">' +
    (bride.servicesRequested ? escapeHtml(bride.servicesRequested) : '\u2014') + '</div></div>';

  html += '</div>'; // close detail-grid

  // --- Enquiry Submission Evidence ---
  var evidence = (state.workspace && state.workspace.enquiryEvidence) || {};
  var artifacts = evidence.artifacts || [];

  html += '<div style="border-top:1px solid var(--border-color, #e8ddd3); margin-top:16px; padding-top:16px">' +
    '<div style="font-weight:600; font-size:0.85rem; color:var(--text-muted, #8b7d6b); margin-bottom:8px; text-transform:uppercase; letter-spacing:0.5px">' +
    '<i class="fa-solid fa-magnifying-glass-chart" style="margin-right:6px"></i>Submission Evidence</div>';

  // Primary source label
  html += '<div style="margin-bottom:10px; font-size:0.9rem; color:var(--text-secondary, #6b5e50)">' +
    escapeHtml(evidence.primaryLabel || 'No original submission artifact found') + '</div>';

  if (artifacts.length > 0) {
    html += '<div style="display:flex; flex-wrap:wrap; gap:8px">';
    for (var a = 0; a < artifacts.length; a++) {
      var art = artifacts[a];
      html += '<a href="' + escapeHtml(art.url) + '" target="_blank" rel="noopener" ' +
        'style="display:inline-flex; align-items:center; gap:6px; padding:6px 12px; ' +
        'background:var(--bg-subtle, #faf7f4); border:1px solid var(--border-color, #e8ddd3); ' +
        'border-radius:6px; font-size:0.82rem; color:var(--brand-primary-deep); text-decoration:none; ' +
        'transition:background 0.15s">' +
        '<i class="' + escapeHtml(art.icon) + '"></i> ' + escapeHtml(art.label) + ' <i class="fa-solid fa-arrow-up-right-from-square" style="font-size:0.7rem; opacity:0.6"></i>' +
        '</a>';
    }
    html += '</div>';
  } else {
    html += '<div style="font-size:0.82rem; color:var(--text-muted, #a89888); font-style:italic">' +
      '<i class="fa-solid fa-circle-info" style="margin-right:4px"></i>' +
      'No linked submission artifacts found for this bride. Check CRM row directly for additional context.</div>';
  }

  html += '</div>'; // close evidence section

  // --- Parsed Form Submission Data ---
  // Item 3b (BUILD-item3-reenquiry-surfacing-2026-07-19): this section used
  // to HIDE ITSELF ENTIRELY whenever the cached form blob's wedding date disagreed with
  // the bride's current CRM cells, which is exactly what happens on a re-enquiry with a
  // different date, i.e. the moment Jess most needs to see the submission data. The
  // panel must never silently vanish. When there are two submissions (a previous + a
  // latest), render a SIDE-BY-SIDE compare grid (previous | latest) with any field whose
  // value differs between the two highlighted, so the diff is obvious at a glance. When
  // there is only one, render it as a single column regardless of any date mismatch.
  var formData = (state.workspace && state.workspace.enquiryFormData) || null;
  var formDataPrevious = (state.workspace && state.workspace.enquiryFormDataPrevious) || null;

  // Field list shared by both single + compare rendering, so the two stay in lockstep.
  // BUILD-proposal-builder-ux-fixes item 9 (F-06): weddingVenue carries an optional
  // fieldTag ('computed' -> the field-type-tag--computed CSS class) so
  // renderFormSubmissionBlock_ can mark it "as submitted" -- this is ANOTHER of the
  // four venue values found on Stage 1 that does NOT drive travel pricing (the
  // authoritative one is on Stage 2's Wedding Day Team card, marked "Used for pricing").
  // Not applied to renderFormSubmissionCompare_ (the previous/latest diff view) this
  // pass -- that view already uses a "Changed" tag for a different purpose and adding a
  // second tag there needs its own layout pass, flagged in the results file as remaining scope.
  var FORM_SUBMISSION_FIELDS_ = [
    { key: 'weddingDate', label: 'Wedding Date' },
    { key: 'weddingVenue', label: 'Venue', fieldTag: 'computed' },
    { key: 'finishTime', label: 'Finish Time' },
    { key: '_address', label: 'Address' },
    { key: 'trialSameAddress', label: 'Trial Same Address?' },
    { key: 'servicesRequested', label: 'Services Requested', wide: true },
    { key: '_partyMembers', label: 'Bridal Party', wide: true },
    { key: 'notes', label: 'Bride\'s Notes', wide: true, pre: true },
    { key: 'referralSource', label: 'Referral Source' }
  ];

  function formFieldValue_(fd, key) {
    if (!fd) return '';
    if (key === '_address') return [fd.postalAddress, fd.suburb, fd.state, fd.postcode].filter(Boolean).join(', ');
    if (key === '_partyMembers') {
      if (!fd.partyMembers || !fd.partyMembers.length) return '';
      return fd.partyMembers.map(function(pm) { return pm.role + ': ' + pm.count; }).join(' | ');
    }
    return fd[key] != null ? String(fd[key]) : '';
  }

  function renderFormSubmissionBlock_(fd, labelSuffix) {
    var out = '<div class="detail-grid">';
    FORM_SUBMISSION_FIELDS_.forEach(function(f) {
      var v = formFieldValue_(fd, f.key);
      if (!v) return;
      var tagHtml = f.fieldTag === 'computed'
        ? '<span class="field-type-tag field-type-tag--computed" title="As submitted at enquiry. The venue used for travel pricing is set separately on Stage 2 (Wedding Day Team card).">As submitted</span>'
        : '';
      out += '<div class="detail-item"' + (f.wide ? ' style="grid-column:1/-1"' : '') + '>' +
        '<label>' + escapeHtml(f.label) + tagHtml + '</label>' +
        '<div class="value"' + (f.pre ? ' style="white-space:pre-line"' : '') + '>' + escapeHtml(v) + '</div>' +
      '</div>';
    });
    out += '</div>';
    return '<div style="margin-bottom:14px">' +
      '<div style="font-size:0.72rem; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; color:var(--brand-primary-deep, #a9706c); margin-bottom:6px">' + escapeHtml(labelSuffix) + '</div>' +
      out + '</div>';
  }

  // Side-by-side compare: one row per field, previous | latest, changed values
  // highlighted on BOTH sides so the diff reads regardless of which column caught the eye.
  function renderFormSubmissionCompare_(prevFd, latestFd) {
    var rows = '';
    FORM_SUBMISSION_FIELDS_.forEach(function(f) {
      var pv = formFieldValue_(prevFd, f.key);
      var lv = formFieldValue_(latestFd, f.key);
      if (!pv && !lv) return;
      var changed = pv !== lv;
      var cellStyle = changed
        ? 'background:#fdf3e3; border:1px solid #e8c67a; border-radius:6px; padding:6px 8px; font-weight:600;'
        : 'padding:6px 8px;';
      rows += '<div class="detail-item" style="grid-column:1/-1; display:grid; grid-template-columns:120px 1fr 1fr; gap:8px; align-items:start;' +
          (f.wide ? '' : '') + '">' +
        '<label style="padding-top:6px">' + escapeHtml(f.label) +
          (changed ? ' <span style="color:#a9706c; font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em;">Changed</span>' : '') +
        '</label>' +
        '<div class="value" style="' + cellStyle + (f.pre ? ' white-space:pre-line;' : '') + '">' + (pv ? escapeHtml(pv) : '<span style="opacity:0.4">-</span>') + '</div>' +
        '<div class="value" style="' + cellStyle + (f.pre ? ' white-space:pre-line;' : '') + '">' + (lv ? escapeHtml(lv) : '<span style="opacity:0.4">-</span>') + '</div>' +
      '</div>';
    });
    return '<div style="display:grid; grid-template-columns:120px 1fr 1fr; gap:8px 8px; align-items:center; margin-bottom:8px;">' +
        '<div></div>' +
        '<div style="font-size:0.72rem; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; color:var(--text-muted, #8b7d6b)">Previous submission</div>' +
        '<div style="font-size:0.72rem; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; color:var(--brand-primary-deep, #a9706c)">Latest submission</div>' +
      '</div>' +
      '<div style="display:grid; gap:6px;">' + rows + '</div>';
  }

  if (formData) {
    html += '<div style="border-top:1px solid var(--border-color, #e8ddd3); margin-top:16px; padding-top:16px">' +
      '<div style="font-weight:600; font-size:0.85rem; color:var(--text-muted, #8b7d6b); margin-bottom:10px; text-transform:uppercase; letter-spacing:0.5px">' +
      '<i class="fa-solid fa-file-lines" style="margin-right:6px"></i>Form Submission Data' +
      (formDataPrevious ? ' <span style="font-weight:400; text-transform:none; letter-spacing:0; font-size:0.78rem">(2 submissions - changed fields highlighted)</span>' : '') +
      '</div>';

    if (formDataPrevious) {
      html += renderFormSubmissionCompare_(formDataPrevious, formData);
    } else {
      html += renderFormSubmissionBlock_(formData, 'Submission');
    }

    html += '</div>';
  }

  html += '</div>'; // close card
  return html;
}

/* =================================================================
   STAGE 2 -- PACKAGE BUILDER
   ================================================================= */
// P3e: sticky running summary for Stage 2. Stays pinned while scrolling the long
// builder so "where am I / what's the total" is always answered. Values refresh
// live via refreshSummary() on line-item edits and on every recalc re-render.
function buildStickySummary_(subtotal, gst, total, calc) {
  calc = calc || {};
  var artists = calc.artistsRequired || calc.artistsNeeded || 1;
  var start = fmtTime12h_(String(calc.estimatedStartTime || '').trim());
  function item(label, val, id, emphasis) {
    return '<div class="pss-item">' +
      '<span class="pss-label">' + label + '</span>' +
      '<span class="pss-val' + (emphasis ? ' pss-total' : '') + '" id="' + id + '">' + escapeHtml(String(val)) + '</span>' +
    '</div>';
  }
  return '<div class="pb-sticky-summary" id="pbStickySummary" role="status" aria-label="Running proposal summary">' +
      item('Total inc GST', formatCurrency(total), 'pbStickyTotal', true) +
      '<div class="pss-sep"></div>' +
      item('GST', formatCurrency(gst), 'pbStickyGst', false) +
      item('Artists', artists, 'pbStickyArtists', false) +
      item('Est. start', start || 'TBC', 'pbStickyStart', false) +
    '</div>';
}

function renderStageBuilder() {
  var ws = state.workspace;
  // LOAD-TIME TRIGGER FIX (2026-11-02): renderStageBuilder reads ws.calculation as-is and
  // never recalculates on its own -- every recalcAndMerge_() call site in this file is
  // inside an edit handler (party +/-, tier toggle, Recalculate button, etc.), none of
  // which fire on initial stage render. That left a real admin seeing the correct
  // "rendering from sent snapshot" notice next to a misleading $0.00 total until they
  // clicked something. Populate it once, up front, when eligible and not already done.
  if (shouldUseSnapshotFallback_() && !(ws.calculation && ws.calculation._fromSnapshot)) {
    recalcAndMerge_(); // mutates ws.calculation in place (see its own JSDoc)
  }
  var calc = ws.calculation || {};
  // Restore noTrialRequired BEFORE rendering the checkbox.
  // Saved calc takes priority; fall back to enquiry form preference for first-time proposals.
  if (typeof calc.noTrialRequired === 'boolean') {
    state.noTrialRequired = calc.noTrialRequired;
  } else if (ws.enquirySource && ws.enquirySource.trialRequired !== 'Yes') {
    // Bride said "no trial required" on enquiry form (or field is blank) — pre-tick the checkbox.
    state.noTrialRequired = true;
  }
  // Base items from GAS calc (stamped with _rowIds). Initialize editedLineItems for
  // structural ops (add/remove) only when not already set; don't clobber a previously-
  // built structural list that may have custom rows added this session.
  var calcItems = JSON.parse(JSON.stringify(calc.normalizedLineItems || calc.lineItems || []));
  // Cap wedding-day travel lines to artistsRequired so the table matches the artist
  // count the admin set. GAS may return more lines than the current selection.
  proposalCapWeddingTravelLines_(calc);
  if (!state.editedLineItems) {
    state.editedLineItems = calcItems;
  }
  // Apply sparse priceOverrides on top to produce display items. Overrides whose
  // _rowId no longer appears in calcItems are silently dropped (party member removed).
  var overrides = state.priceOverrides || {};
  var items = calcItems.map(function(it) {
    var ov = it._rowId ? overrides[it._rowId] : null;
    if (!ov) return it;
    var rate = ov.rate !== undefined ? ov.rate : (it.unitPrice || it.rate || 0);
    var qty  = ov.qty  !== undefined ? ov.qty  : (it.qty || it.quantity || 1);
    return Object.assign({}, it, {
      unitPrice: rate, rate: rate, qty: qty, quantity: qty,
      total: rate * qty, _userEdited: true
    });
  });
  // Append any custom rows added via addLineItem()/addCatalogItem() that are NOT
  // already represented in `items`. Once a custom/catalogue row round-trips
  // through a save it becomes part of calc.lineItems (and still carries
  // custom:true, which is never cleared), so without this dedup it renders
  // once as a base item and a second time here (the 2026-07-03 duplication bug).
  var _seenKeys = {};
  items.forEach(function(it) { _seenKeys[_proposalLineItemKey_(it)] = true; });
  (state.editedLineItems || []).filter(function(li) { return !!li.custom; }).forEach(function(li) {
    var k = _proposalLineItemKey_(li);
    if (_seenKeys[k]) return;
    _seenKeys[k] = true;
    items.push(li);
  });
  // Drop any sticky-suppressed travel line (defensive: the calc engines already exclude
  // them, but a freshly-loaded GAS calc may still carry one until the GAS side is folded in).
  items = items.filter(function(it) { return !isTravelSuppressed_(it); });
  // Drop zero-quantity rows so a delete/zero-out registers immediately in the
  // rendered list, not just as a $0 total that keeps taking up a row.
  items = items.filter(function(it) {
    var q = it.qty !== undefined ? it.qty : it.quantity;
    return q === undefined || q === null || Number(q) !== 0;
  });
  _currentDisplayItems = items;

  // P2i: reconcile the travel chip with the actual travel line items. The chip
  // previously read calc.travelCost (the auto travel.fee), which a manual override
  // does NOT change, so the chip and the line items drifted apart after an override.
  // Deriving travelCost from the displayed travel line items keeps the chip in
  // agreement with the lines in every case (override, add-artist, normal).
  // _weddingTravelTotal is used as the override input default below so re-applying
  // the default value is a no-op rather than double-counting.
  var _travelLineTotal = 0, _weddingTravelTotal = 0, _hasTravelLine = false;
  // P2j: detect an ACTIVE manual wedding-travel override so the panel can surface a
  // discoverable "Clear override" action (and auto-open). An override is active if a
  // "manual override" custom row exists, or a priceOverride is set on a wedding-travel
  // _rowId. Used only for the UI affordance; the clear logic re-derives independently.
  var _hasManualTravelOverride = false;
  items.forEach(function(it) {
    var _d = String(it.description || it.name || '').toLowerCase();
    var _c = String(it.category || '').toLowerCase();
    if (_d.indexOf('manual override') >= 0) _hasManualTravelOverride = true;
    if (_c.indexOf('travel') < 0 && _d.indexOf('travel') < 0) return;
    _hasTravelLine = true;
    _travelLineTotal += Number(it.total || 0);
    if (_d.indexOf('trial') < 0 && _d.indexOf('spray') < 0) _weddingTravelTotal += Number(it.total || 0);
  });
  calcItems.forEach(function(it) {
    if (!it._rowId || !overrides[it._rowId]) return;
    var _d = String(it.description || it.name || '').toLowerCase();
    var _c = String(it.category || '').toLowerCase();
    if ((_c.indexOf('travel') >= 0 || _d.indexOf('travel') >= 0) &&
        _d.indexOf('trial') < 0 && _d.indexOf('spray') < 0) _hasManualTravelOverride = true;
  });
  if (_hasTravelLine && calc) calc.travelCost = _travelLineTotal;

  // Party composition cards
  var party = ws.party || [];
  var roleIcons = {
    'bride': 'fa-gem', 'bridesmaid': 'fa-heart', 'bridesmaids': 'fa-heart',
    'mother': 'fa-hand-holding-heart', 'mothers': 'fa-hand-holding-heart',
    'flowergirl': 'fa-wand-magic-sparkles', 'flowergirls': 'fa-wand-magic-sparkles',
    'guest': 'fa-user', 'guests': 'fa-user',
    // ITEM 4 (2026-08-04): icon only. Role still prices as 'guest' -- see the
    // <option value="Groomsman"> comment in the manual party panel.
    'groomsman': 'fa-user-tie', 'groomsmen': 'fa-user-tie'
  };
  var partyHtml = '';
  if (party.length) {
    partyHtml = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-users"></i> Party Composition ' +
        '<span id="partySyncIndicator" style="display:none;font-size:0.8em;color:#b08968;margin-left:8px;">' +
          '<i class="fa-solid fa-spinner fa-spin"></i> syncing line items...' +
        '</span>' +
      '</div>' +
      '<div class="party-cards">';
    for (var p = 0; p < party.length; p++) {
      var member = party[p];
      var icon = roleIcons[String(member.role || '').toLowerCase()] || 'fa-user';
      var svc = member.serviceType || 'Hair & Makeup';
      partyHtml +=
        '<div class="party-card">' +
          '<div class="role-icon"><i class="fa-solid ' + icon + '"></i></div>' +
          '<div class="role-name">' + escapeHtml(member.role) + '</div>' +
          '<div class="role-qty">' +
            '<button class="qty-btn" onclick="adjustPartyQty(' + p + ',-1)">-</button>' +
            '<span class="qty-val" id="pqty' + p + '">' + (typeof member.qty === 'number' ? member.qty : 1) + '</span>' +
            '<button class="qty-btn" onclick="adjustPartyQty(' + p + ',1)">+</button>' +
          '</div>' +
          '<select onchange="updatePartyService(' + p + ',this.value)">' +
            '<option value="Hair & Makeup"' + (svc === 'Hair & Makeup' ? ' selected' : '') + '>Hair & Makeup</option>' +
            '<option value="Hair Only"' + (svc === 'Hair Only' ? ' selected' : '') + '>Hair Only</option>' +
            '<option value="Makeup Only"' + (svc === 'Makeup Only' ? ' selected' : '') + '>Makeup Only</option>' +
          '</select>' +
        '</div>';
    }
    partyHtml +=
      '</div>' +
      '<label style="display:flex;align-items:center;gap:8px;margin-top:12px;cursor:pointer;font-size:13px;color:var(--brand-text)">' +
        '<input type="checkbox" id="noTrialToggle"' + (state.noTrialRequired ? ' checked' : '') + ' onchange="toggleNoTrial(this.checked)" style="width:15px;height:15px;accent-color:var(--brand-primary);cursor:pointer">' +
        '<span>No trial required, remove trial line items</span>' +
      '</label>' +
      '<label style="display:flex;align-items:center;gap:8px;margin-top:10px;font-size:13px;color:var(--brand-text)">' +
        '<span style="flex:1">Junior bridesmaids (age 10-14), 25% off base rate (max 2)</span>' +
        '<input type="number" min="0" step="1" id="juniorBmInput" value="' + (Number(state.juniorBridesmaids) || 0) + '" onchange="setJuniorBridesmaids(this.value)" style="width:64px;padding:6px 8px;border:1px solid var(--brand-bg-soft);border-radius:8px;accent-color:var(--brand-primary)">' +
      '</label>' +
      juniorNoEligibleHint_() +
    '</div>';
  }
  // P3c: tier + timing + payment were pulled OUT of the party-composition card so
  // the builder reads in the natural quote order (who -> tier -> price -> travel ->
  // payment). They render as their own cards in the assembly below.
  // 2026-07-12: Booking Tier and Timing split into two cards. Timing (finish + est
  // start) is now its own slim card, separate from the Booking Tier toggle, per the
  // Wedding Day Team simplification brief. The artist count already moved to the
  // Team card, so this Timing card is finish-time + est-start only.
  var tierHtml = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-star"></i> Booking Tier</div>' +
      buildBookingTierControl_() +
    '</div>';
  var timingHtml = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-clock"></i> Wedding-Day Timing</div>' +
      buildTimingControl_() +
    '</div>';
  // Travel Origin card only renders once a future address is actually configured
  // (state.travelOriginFutureAddressSet, loaded once at init) - hidden otherwise so
  // the control never shows up as a confusing no-op before Jess has set anything up.
  var travelOriginHtml = state.travelOriginFutureAddressSet
    ? ('<div class="card">' +
        '<div class="card-title"><i class="fa-solid fa-house-circle-check"></i> Travel Origin</div>' +
        buildTravelOriginControl_() +
      '</div>')
    : '';
  var tierTimingHtml = tierHtml + timingHtml + travelOriginHtml;
  var paymentHtml = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-credit-card"></i> Payment</div>' +
      buildPaymentPlanControl_() +
    '</div>';

  // ITEM 1 (2026-08-04): read-only "what her message mentions" panel.
  // Jess chose Option B: parse and SHOW, NEVER auto-fill. This writes to nothing --
  // not the party builder, not state.manualPartyMembers, not a line item. It exists so
  // Jess enters people deliberately after seeing what the free text asked for.
  // Rationale: "Rebuild from enquiry" uses only the structured fields
  // (brideServices/bridesmaids/mothers/flowergirls). For Fern's planner submission those
  // were 1/0/0/0 while the real request -- 14 people, 2 men, 1 non-binary -- lived only
  // in DREAM_DETAILS, so the rebuild produced a 1-bride proposal for a 14-person wedding.
  var dreamHintsHtml = buildDreamDetailHintsHtml_();

  // P.4: Manual party members panel
  var calcManualAdditions = (calc && calc.manualAdditions) || [];
  // Sync to local state on first load if calcJson has them and local state is empty
  if (calcManualAdditions.length > 0 && state.manualPartyMembers.length === 0) {
    state.manualPartyMembers = JSON.parse(JSON.stringify(calcManualAdditions));
  }
  var manualMembers = state.manualPartyMembers;
  var manualPartyHtml = '<div class="card" id="manualPartyCard">' +
    '<div class="card-title" style="display:flex;align-items:center;justify-content:space-between">' +
      '<span><i class="fa-solid fa-user-plus"></i> Additional Party Members</span>' +
    '</div>' +
    '<div style="font-size:13px;color:var(--muted);margin-bottom:12px">Add guests, mums, or flower girls not in the original enquiry.</div>' +
    dreamHintsHtml;
  if (manualMembers.length > 0) {
    manualPartyHtml += '<div class="party-cards" style="margin-bottom:12px">';
    for (var mp = 0; mp < manualMembers.length; mp++) {
      var mm = manualMembers[mp];
      var mmSvc = manualMemberSvc_(mm);
      var mmQty = Math.max(1, (typeof mm.qty === 'number' && mm.qty > 0) ? mm.qty : 1);
      var mmIcon = roleIcons[String(mm.role || '').toLowerCase()] || 'fa-user';
      manualPartyHtml +=
        '<div class="party-card party-card--manual">' +
          '<button class="party-card__remove" onclick="removeManualPartyMember(' + mp + ')" title="Remove">' +
            '<i class="fa-solid fa-xmark"></i>' +
          '</button>' +
          '<div class="role-icon"><i class="fa-solid ' + mmIcon + '"></i></div>' +
          '<div class="role-name">' + escapeHtml(mm.name || mm.role || 'Guest') + '</div>' +
          '<div class="role-sub">' + escapeHtml(mm.role || 'Guest') + '</div>' +
          '<div class="role-qty">' +
            '<button class="qty-btn" onclick="adjustManualQty(' + mp + ',-1)">-</button>' +
            '<span class="qty-val">' + mmQty + '</span>' +
            '<button class="qty-btn" onclick="adjustManualQty(' + mp + ',1)">+</button>' +
          '</div>' +
          '<select onchange="updateManualService(' + mp + ',this.value)">' +
            '<option value="Hair & Makeup"' + (mmSvc === 'Hair & Makeup' ? ' selected' : '') + '>Hair &amp; Makeup</option>' +
            '<option value="Hair Only"' + (mmSvc === 'Hair Only' ? ' selected' : '') + '>Hair Only</option>' +
            '<option value="Makeup Only"' + (mmSvc === 'Makeup Only' ? ' selected' : '') + '>Makeup Only</option>' +
          '</select>' +
        '</div>';
    }
    manualPartyHtml += '</div>';
  }
  manualPartyHtml +=
    '<details id="addMemberPanel" style="border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);overflow:hidden">' +
      '<summary style="padding:10px 14px;cursor:pointer;font-size:14px;font-weight:600;background:var(--brand-bg-soft);list-style:none;display:flex;align-items:center;gap:8px">' +
        '<i class="fa-solid fa-plus" style="color:var(--brand-primary)"></i> Add party member' +
      '</summary>' +
      '<div style="padding:14px;display:flex;flex-direction:column;gap:10px">' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">' +
          '<div>' +
            '<label style="font-size:12px;font-weight:600;margin-bottom:4px;display:block">Name</label>' +
            '<input type="text" id="manualMemberName" placeholder="e.g. Mum" ' +
              'style="width:100%;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:8px 12px;font-size:14px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
          '</div>' +
          '<div>' +
            '<label style="font-size:12px;font-weight:600;margin-bottom:4px;display:block">Role</label>' +
            '<select id="manualMemberRole" style="width:100%;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:8px 12px;font-size:14px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
              '<option value="Bridesmaid">Bridesmaid</option>' +
              '<option value="Mother of Bride">Mother of Bride</option>' +
              '<option value="Mother of Groom">Mother of Groom</option>' +
              '<option value="Flower Girl">Flower Girl</option>' +
              // ITEM 4 (2026-08-04): Fern's 3 groomsmen had to be entered as "Guest", so the
              // line read "3 x Groomsmen (Guest) Hair Only" to a planner who had asked
              // specifically about men's grooming. COSMETIC ONLY: every role classifier
              // (localPricingCategory_ above, and cat_() in ProposalBuilderAPI.gs at :4634
              // and :4949) matches only bridesmaid/bride/flower/mother and falls through to
              // 'guest', so "Groomsman" prices exactly as "Guest" does today. Do NOT add a
              // classifier branch for it -- that would be a pricing change, not a label.
              // The Men's Grooming service itself comes from the Service Catalog sheet.
              '<option value="Groomsman">Groomsman</option>' +
              '<option value="Guest">Guest</option>' +
              '<option value="Other">Other</option>' +
            '</select>' +
          '</div>' +
        '</div>' +
        '<div>' +
          '<label style="font-size:12px;font-weight:600;margin-bottom:4px;display:block">Services</label>' +
          '<div style="display:flex;gap:16px">' +
            '<label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:14px">' +
              '<input type="checkbox" id="manualSvcHair" checked> Hair' +
            '</label>' +
            '<label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:14px">' +
              '<input type="checkbox" id="manualSvcMakeup" checked> Makeup' +
            '</label>' +
          '</div>' +
        '</div>' +
        '<button class="btn btn-primary btn-sm" onclick="addManualPartyMember()">' +
          '<i class="fa-solid fa-plus"></i> Add to Party' +
        '</button>' +
      '</div>' +
    '</details>' +
  '</div>';

  var hasAnyOverride = Object.keys(overrides).length > 0;
  var overrideMenuHtml = hasAnyOverride
    ? '<div style="display:flex;justify-content:flex-end;margin-bottom:6px">' +
        '<button class="btn btn-ghost btn-sm" onclick="resetAllOverrides_()" ' +
          'title="Clear your manual price overrides and restore the calculated prices. Added items and other edits are kept." ' +
          'style="font-size:11px;color:var(--muted);padding:2px 8px">' +
          '<i class="fa-solid fa-rotate-left"></i> Reset price overrides' +
        '</button>' +
      '</div>'
    : '';

  var rowsHtml = '';
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    var desc = it.description || it.name || '';
    var subtitle = it.subtitle || '';
    var qty = it.qty || it.quantity || 1;
    var rate = it.unitPrice || it.rate || 0;
    var total = (Number(rate) * Number(qty)).toFixed(2);
    // GST is applied to the proposal-wide subtotal (subtotal * 1.10), so every
    // line is effectively taxable unless it's a bonus/discount/comp item.
    // Default taxable=true for service categories rather than relying on the
    // explicit `taxable` flag, which is only set on travel items historically.
    var catLower = String(it.category || '').toLowerCase();
    var isBonusOrDiscount = (catLower === 'bonus' || catLower === 'discount');
    var isTaxable = it.taxable === false ? false : !isBonusOrDiscount;
    var gstTag = isTaxable ? '<span class="li-gst-tag">+GST</span>' : '';

    var isUserEdited = !!it._userEdited;
    var rowId = it._rowId || '';
    // Rose dot + Reset link for overridden rows; lock icon for legacy _userEdited rows without _rowId
    var overrideIndicator = '';
    if (isUserEdited && rowId) {
      overrideIndicator = ' <span class="li-override-dot" title="Custom price set" ' +
        'style="display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--brand-primary);margin-left:4px;vertical-align:middle"></span>' +
        ' <button class="btn-link-inline li-reset-btn" onclick="resetRowOverride_(\'' + rowId + '\')" ' +
          'title="Reset to calculated price" style="font-size:11px;color:var(--muted);text-decoration:underline;cursor:pointer;background:none;border:none;padding:0 0 0 2px">Reset</button>';
    } else if (isUserEdited) {
      overrideIndicator = '<i class="fa-solid fa-lock li-lock-icon" title="Price manually set"></i>';
    }
    var descCell = '<div class="li-row-desc"><span>' + escapeHtml(desc) + gstTag + overrideIndicator + '</span>';
    if (subtitle) {
      descCell += '<span class="li-row-subtitle">' + escapeHtml(subtitle) + '</span>';
    }
    descCell += '</div>';

    rowsHtml += '<tr data-li-row="' + i + '" data-li-taxable="' + (isTaxable ? '1' : '0') + '"' +
      (rowId ? ' data-row-id="' + rowId + '"' : '') +
      (isUserEdited ? ' class="li-row-locked"' : '') + '>' +
      '<td>' + descCell + '</td>' +
      '<td><input type="number" min="0" value="' + qty + '" onchange="updateLineItem(' + i + ', \'qty\', this.value)"></td>' +
      '<td><input type="number" min="0" step="0.01" value="' + Number(rate).toFixed(2) + '" onchange="updateLineItem(' + i + ', \'rate\', this.value)"></td>' +
      '<td id="li-total-' + i + '">' + formatAudGst(total, isTaxable) + '</td>' +
      '<td class="row-actions"><button class="remove-row-btn" onclick="removeLineItem(' + i + ')" title="Remove"><i class="fa-solid fa-trash-can"></i></button></td>' +
      '</tr>';
  }

  var subtotal = calcSubtotal(items);
  var gst = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;

  var flagsHtml = '';
  var nonTravelFlags = [];
  var hasLocalEstimate = false;
  // Internal engine telemetry that should NOT surface on the admin quote surface.
  // These are recalc/cache breadcrumbs, not admin-actionable warnings (2026-07-12).
  var _internalFlagPrefixes = ['LOCAL_ESTIMATE', 'LOCAL_RECALCULATE', 'CACHE_', 'DEBUG_', 'TRACE_'];
  function _isInternalFlag_(fl) {
    var s = String(fl || '');
    for (var _i = 0; _i < _internalFlagPrefixes.length; _i++) {
      if (s.indexOf(_internalFlagPrefixes[_i]) >= 0) return true;
    }
    return false;
  }
  if (calc.flags && calc.flags.length > 0) {
    for (var f = 0; f < calc.flags.length; f++) {
      if (String(calc.flags[f]).indexOf('LOCAL_ESTIMATE') >= 0) {
        hasLocalEstimate = true;
      } else if (!_isInternalFlag_(calc.flags[f])) {
        nonTravelFlags.push(calc.flags[f]);
      }
    }
    if (nonTravelFlags.length > 0) {
      flagsHtml = '<div class="flag-list">';
      for (var nf = 0; nf < nonTravelFlags.length; nf++) {
        flagsHtml += '<span class="flag-tag"><i class="fa-solid fa-flag"></i> ' + escapeHtml(nonTravelFlags[nf]) + '</span>';
      }
      flagsHtml += '</div>';
    }
  }

  // Travel & Address panel -- multi-location cards
  var travelOverrideHtml = '';

  if (hasLocalEstimate) {
    travelOverrideHtml +=
      '<div class="travel-warn-banner">' +
        '<i class="fa-solid fa-triangle-exclamation"></i>' +
        '<div><strong>Travel estimate is approximate.</strong> Search for the venue below to calculate the real travel fee, ' +
        'or manually enter an override.</div>' +
      '</div>';
  }

  // Detect which location types are relevant
  var hasTrial = !!(ws.bride && ws.bride.trialAddressFull) ||
    !!(calc.travelBreakdown && calc.travelBreakdown.filter(function(tb) {
      return String(tb.description || '').indexOf('Trial') >= 0;
    }).length > 0);
  var hasSprayTan = !!(calc.lineItems && calc.lineItems.filter(function(li) {
    var d = String(li.description || li.name || '').toLowerCase();
    return d.indexOf('spray tan') >= 0 || d.indexOf('spray_tan') >= 0;
  }).length > 0);

  // Pre-resolve addresses by type from selectedAddress, else CRM value
  var weddingSelectedAddr = '';
  var trialSelectedAddr = '';
  var sprayTanSelectedAddr = '';
  if (calc.selectedAddress) {
    var saType = calc.selectedAddress.type || 'wedding';
    var saAddr = calc.selectedAddress.address || '';
    if (saType === 'trial') { trialSelectedAddr = saAddr; }
    else if (saType === 'spray_tan') { sprayTanSelectedAddr = saAddr; }
    else { weddingSelectedAddr = saAddr; }
  }
  if (!weddingSelectedAddr) weddingSelectedAddr = (ws.bride && ws.bride.weddingAddressFull) || (ws.bride && ws.bride.location) || '';
  if (!trialSelectedAddr && hasTrial) trialSelectedAddr = (ws.bride && ws.bride.trialAddressFull) || '';

  // Split breakdown rows by type
  var weddingBreakdown = (calc.travelBreakdown || []).filter(function(tb) {
    return String(tb.description || '').indexOf('Trial') < 0;
  });
  var trialBreakdown = (calc.travelBreakdown || []).filter(function(tb) {
    return String(tb.description || '').indexOf('Trial') >= 0;
  });

  // -- Wedding Day Team card (2026-07-12 consolidation) --
  // ONE card, rendered as its own top-level card: venue search (top) -> artists
  // (Auto + stepper) -> automatic travel -> Adjust (lock/manual/retry) ->
  // per-artist breakdown -> assign specific artists (roster dropdown + chips).
  // Replaces the old wedding-location card + the separate Assigned-Artists panel
  // + the standalone manual-override details. Every control reuses the SAME
  // handlers the scattered controls used, so the calc engines and field names are
  // untouched (Invariant 8). Trial / spray-tan / custom cards stay in the panel below.
  travelOverrideHtml += buildWeddingDayTeamCard_(calc, weddingSelectedAddr, _weddingTravelTotal, _hasManualTravelOverride);

  // FU2 (2026-07-19): wedding-address failure Retry banner. When the last WEDDING
  // travel pick failed (relay timeout / no line items), Layer A silently reverts the
  // venue and keeps stale totals; without this the admin has no idea it failed and no
  // way to retry THAT pick (the trial path already had this). On-brand warn surface,
  // pill Retry, rendered directly under the Wedding Day Team card.
  if (_pendingTravelRetry && _pendingTravelRetry.type === 'wedding') {
    var _wrAddr = escapeHtml((_pendingTravelRetry.addr && (_pendingTravelRetry.addr.placeName || _pendingTravelRetry.addr.address)) || 'the wedding location');
    travelOverrideHtml +=
      '<div class="wdt-warn" style="align-items:center;justify-content:space-between;gap:12px;margin-top:10px">' +
        '<div style="display:flex;align-items:flex-start;gap:8px">' +
          '<i class="fa-solid fa-triangle-exclamation"></i>' +
          '<div>Travel to ' + _wrAddr + ' could not be calculated (the request timed out). The travel fee shown may be stale.</div>' +
        '</div>' +
        '<button class="btn btn-primary btn-sm" onclick="retryLocationTravelCalc()" title="Retry wedding travel calculation" style="flex:none"><i class="fa-solid fa-rotate-right"></i> Retry</button>' +
      '</div>';
  }

  // -- Appointment Locations & Travel panel (trial / spray-tan / custom only) --
  travelOverrideHtml +=
    '<div class="travel-override-panel" style="' + (hasLocalEstimate ? '' : 'border-color: var(--line);') + '">' +
      '<div class="card-title" style="font-size:15px;margin-bottom:12px"><i class="fa-solid fa-route"></i> Other appointment locations</div>';

  // -- Trial Location card (conditional) --
  if (hasTrial) {
    var trialArtistChips = calc.trialTravel ? [calc.trialTravel.artist] : [];
    var _tlMode = calc.trialLocationMode || (calc.trialTravel ? 'in_home' : '');
    travelOverrideHtml +=
      '<div class="location-card" id="locationCard-trial">' +
        '<div class="location-card-header"><i class="fa-solid fa-location-dot"></i><span class="location-type-label">Trial Location</span></div>' +
        buildTrialLocationModeControl_(_tlMode, calc);
    if (trialArtistChips.length > 0) {
      travelOverrideHtml +=
        '<div class="location-artists">' +
          '<div class="location-artists-label">Artists</div>' +
          '<div class="location-artist-chips">' +
            trialArtistChips.map(function(a) { return '<span class="location-artist-chip">' + escapeHtml(a) + '</span>'; }).join('') +
            '<span class="override-link" onclick="toggleArtistOverride(\'trial\')">Override</span>' +
          '</div>' +
          '<div class="artist-override-panel" id="artistOverride-trial" style="display:none">' +
            '<input type="text" id="artistOverrideInput-trial" list="artist-roster-list-trial" placeholder="Type an artist name or pick from the list" value="' + escapeHtml(trialArtistChips.join(', ')) + '" autocomplete="off">' +
            '<datalist id="artist-roster-list-trial" class="artist-roster-list"></datalist>' +
            '<button class="btn btn-ghost btn-sm" onclick="applyArtistOverride(\'trial\')">Recalculate</button>' +
            '<span class="override-link" onclick="toggleArtistOverride(\'trial\')">Cancel</span>' +
          '</div>' +
        '</div>';
    }
    if (trialSelectedAddr) {
      travelOverrideHtml +=
        '<div class="travel-selected-addr" id="trialSelectedAddr">' +
          '<i class="fa-solid fa-location-dot"></i>' +
          '<span class="addr-text" id="trialAddrText">' + escapeHtml(trialSelectedAddr) + '</span>' +
          '<span class="addr-clear" onclick="clearLocationAddress(\'trial\')" title="Clear address"><i class="fa-solid fa-xmark"></i></span>' +
        '</div>';
    }
    travelOverrideHtml +=
        '<div class="address-search-wrap">' +
          '<i class="fa-solid fa-magnifying-glass search-icon"></i>' +
          '<input type="text" id="addressSearch-trial" placeholder="Search trial location..." ' +
            'autocomplete="off" oninput="onLocationAddressInput(this.value, \'trial\')">' +
          '<div class="address-results" id="addressResults-trial"></div>' +
        '</div>';
    // P1a (2026-07-18): trial-travel failure banner + Retry. When the last trial recalc
    // failed (relay timeout / no line items), Layer A silently reverts the address and
    // keeps stale totals; without this the admin has no idea it failed and no way to
    // retry (the wedding path has a persistent Retry button under Adjust travel). On-brand
    // warn surface, pill Retry button, no sharp corners.
    if (_pendingTrialTravelRetry) {
      var _ptAddr = escapeHtml(_pendingTrialTravelRetry.placeName || _pendingTrialTravelRetry.address || 'the trial location');
      travelOverrideHtml +=
        '<div class="wdt-warn" style="align-items:center;justify-content:space-between;gap:12px">' +
          '<div style="display:flex;align-items:flex-start;gap:8px">' +
            '<i class="fa-solid fa-triangle-exclamation"></i>' +
            '<div>Trial travel to ' + _ptAddr + ' could not be calculated (the request timed out). The trial fee shown may be stale.</div>' +
          '</div>' +
          '<button class="btn btn-primary btn-sm" onclick="retryTrialTravelCalc()" title="Retry trial travel calculation" style="flex:none"><i class="fa-solid fa-rotate-right"></i> Retry</button>' +
        '</div>';
    }
    if (trialTravelModeSuppressed_(calc)) {
      /* BUG FIX (2026-09-15): in_studio/tbc suppress the FEE, so the per-row km/fee
         breakdown would show figures for travel that is not being charged. Replaced
         with Jess's wording, which states why the fee is unknown and what resolves it. */
      travelOverrideHtml += '<div class="travel-breakdown"><div class="travel-breakdown-row">' +
        '<span class="tb-label">' + escapeHtml(TRIAL_TRAVEL_TBC_LABEL_) + '</span>' +
        '</div></div>';
    } else if (trialBreakdown.length > 0) {
      travelOverrideHtml += '<div class="travel-breakdown' + (travelLockState_('trial', calc) ? ' is-locked' : '') + '" id="travelBreakdown-trial"><div class="travel-breakdown-title">Trial Travel' + renderTravelLockToggle_('trial', calc) + '</div>';
      for (var tt = 0; tt < trialBreakdown.length; tt++) {
        var ttItem = trialBreakdown[tt];
        travelOverrideHtml +=
          '<div class="travel-breakdown-row">' +
            '<span class="tb-label">' + escapeHtml(ttItem.description || 'Travel') + '</span>' +
            '<span class="tb-km">' + (ttItem.distanceKm ? ttItem.distanceKm + ' km' : '') + '</span>' +
            '<span class="tb-fee">' + formatCurrency(ttItem.total || 0) + '</span>' +
          '</div>';
      }
      travelOverrideHtml += '</div>';
    }
    travelOverrideHtml += '</div>';
  }

  // -- Spray Tan Location card (conditional) --
  if (hasSprayTan) {
    var sprayTanArtistChips = calc.sprayTanTravel ? ['Jessica'] : ['Jessica'];
    travelOverrideHtml +=
      '<div class="location-card" id="locationCard-spray_tan">' +
        '<div class="location-card-header"><i class="fa-solid fa-location-dot"></i><span class="location-type-label">Spray Tan Location</span></div>' +
        // FU2 (2026-07-19): spray-tan address failure Retry banner (mirrors trial + wedding).
        ((_pendingTravelRetry && (_pendingTravelRetry.type === 'spray_tan' || _pendingTravelRetry.type === 'spray'))
          ? '<div class="wdt-warn" style="align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px">' +
              '<div style="display:flex;align-items:flex-start;gap:8px">' +
                '<i class="fa-solid fa-triangle-exclamation"></i>' +
                '<div>Spray tan travel to ' + escapeHtml((_pendingTravelRetry.addr && (_pendingTravelRetry.addr.placeName || _pendingTravelRetry.addr.address)) || 'the spray tan location') + ' could not be calculated (the request timed out). The travel fee shown may be stale.</div>' +
              '</div>' +
              '<button class="btn btn-primary btn-sm" onclick="retryLocationTravelCalc()" title="Retry spray tan travel calculation" style="flex:none"><i class="fa-solid fa-rotate-right"></i> Retry</button>' +
            '</div>'
          : '') +
        '<div class="location-artists">' +
          '<div class="location-artists-label">Artists</div>' +
          '<div class="location-artist-chips">' +
            sprayTanArtistChips.map(function(a) { return '<span class="location-artist-chip">' + escapeHtml(a) + '</span>'; }).join('') +
            '<span class="override-link" onclick="toggleArtistOverride(\'spray_tan\')">Override</span>' +
          '</div>' +
          '<div class="artist-override-panel" id="artistOverride-spray_tan" style="display:none">' +
            '<input type="text" id="artistOverrideInput-spray_tan" list="artist-roster-list-spray_tan" placeholder="Type an artist name or pick from the list" value="' + escapeHtml(sprayTanArtistChips.join(', ')) + '" autocomplete="off">' +
            '<datalist id="artist-roster-list-spray_tan" class="artist-roster-list"></datalist>' +
            '<button class="btn btn-ghost btn-sm" onclick="applyArtistOverride(\'spray_tan\')">Recalculate</button>' +
            '<span class="override-link" onclick="toggleArtistOverride(\'spray_tan\')">Cancel</span>' +
          '</div>' +
        '</div>';
    if (sprayTanSelectedAddr) {
      travelOverrideHtml +=
        '<div class="travel-selected-addr" id="sprayTanSelectedAddr">' +
          '<i class="fa-solid fa-location-dot"></i>' +
          '<span class="addr-text" id="sprayTanAddrText">' + escapeHtml(sprayTanSelectedAddr) + '</span>' +
          '<span class="addr-clear" onclick="clearLocationAddress(\'spray_tan\')" title="Clear address"><i class="fa-solid fa-xmark"></i></span>' +
        '</div>';
    }
    travelOverrideHtml +=
        '<div class="address-search-wrap">' +
          '<i class="fa-solid fa-magnifying-glass search-icon"></i>' +
          '<input type="text" id="addressSearch-spray_tan" placeholder="Search spray tan location..." ' +
            'autocomplete="off" oninput="onLocationAddressInput(this.value, \'spray_tan\')">' +
          '<div class="address-results" id="addressResults-spray_tan"></div>' +
        '</div>' +
      '</div>';
  }

  // -- Custom location cards --
  var customLocs = (state.workspace && state.workspace.locations) ? state.workspace.locations.filter(function(l) { return l.type === 'custom'; }) : [];
  for (var ci = 0; ci < customLocs.length; ci++) {
    var cloc = customLocs[ci];
    var cIdx = state.workspace.locations.indexOf(cloc);
    travelOverrideHtml +=
      '<div class="location-card" id="locationCard-custom-' + ci + '">' +
        '<div class="location-card-header">' +
          '<i class="fa-solid fa-location-dot"></i>' +
          '<span class="location-type-label">' + escapeHtml(cloc.label || 'Custom Location') + '</span>' +
          '<span class="location-card-remove" onclick="removeCustomLocation(' + cIdx + ')" title="Remove"><i class="fa-solid fa-xmark"></i> Remove</span>' +
        '</div>';
    if (cloc.address) {
      travelOverrideHtml +=
        '<div class="travel-selected-addr">' +
          '<i class="fa-solid fa-location-dot"></i>' +
          '<span class="addr-text">' + escapeHtml(cloc.address) + '</span>' +
          '<span class="addr-clear" onclick="clearCustomLocationAddress(' + cIdx + ')" title="Clear address"><i class="fa-solid fa-xmark"></i></span>' +
        '</div>';
    }
    if (cloc.artists && cloc.artists.length > 0) {
      travelOverrideHtml +=
        '<div class="location-artists">' +
          '<div class="location-artists-label">Artists</div>' +
          '<div class="location-artist-chips">' +
            cloc.artists.map(function(a) { return '<span class="location-artist-chip">' + escapeHtml(a) + '</span>'; }).join('') +
            '<span class="override-link" onclick="toggleArtistOverride(\'custom_' + ci + '\')">Override</span>' +
          '</div>' +
          '<div class="artist-override-panel" id="artistOverride-custom_' + ci + '" style="display:none">' +
            '<input type="text" id="artistOverrideInput-custom_' + ci + '" list="artist-roster-list-custom_' + ci + '" placeholder="Type an artist name or pick from the list" value="' + escapeHtml((cloc.artists || []).join(', ')) + '" autocomplete="off">' +
            '<datalist id="artist-roster-list-custom_' + ci + '" class="artist-roster-list"></datalist>' +
            '<button class="btn btn-ghost btn-sm" onclick="applyArtistOverride(\'custom_' + ci + '\')">Recalculate</button>' +
            '<span class="override-link" onclick="toggleArtistOverride(\'custom_' + ci + '\')">Cancel</span>' +
          '</div>' +
        '</div>';
    }
    travelOverrideHtml +=
        '<div class="address-search-wrap">' +
          '<i class="fa-solid fa-magnifying-glass search-icon"></i>' +
          '<input type="text" id="addressSearch-custom_' + ci + '" placeholder="Search venue or address..." ' +
            'autocomplete="off" oninput="onLocationAddressInput(this.value, \'custom_' + ci + '\')">' +
          '<div class="address-results" id="addressResults-custom_' + ci + '"></div>' +
        '</div>';
    if (cloc.travelBreakdown && cloc.travelBreakdown.length > 0) {
      travelOverrideHtml += '<div class="travel-breakdown"><div class="travel-breakdown-title">Travel</div>';
      for (var tb2 = 0; tb2 < cloc.travelBreakdown.length; tb2++) {
        var tbi = cloc.travelBreakdown[tb2];
        travelOverrideHtml +=
          '<div class="travel-breakdown-row">' +
            '<span class="tb-label">' + escapeHtml(tbi.description || 'Travel') + '</span>' +
            '<span class="tb-km">' + (tbi.distanceKm ? tbi.distanceKm + ' km' : '') + '</span>' +
            '<span class="tb-fee">' + formatCurrency(tbi.total || 0) + '</span>' +
          '</div>';
      }
      travelOverrideHtml += '</div>';
    }
    travelOverrideHtml += '</div>';
  }

  // -- Add Appointment Location button --
  travelOverrideHtml +=
    '<button class="btn btn-soft btn-sm" id="addLocationBtn" onclick="addCustomLocation()" style="margin-bottom:12px">' +
      '<i class="fa-solid fa-plus"></i> Add Appointment Location' +
    '</button>';

  // -- Manual fee override moved into the Wedding Day Team card's "Adjust" (2026-07-12) --
  // The wedding-travel manual override / retry / clear now live inside
  // buildWeddingDayTeamCard_ so there is ONE place for artists + travel. This
  // standalone panel is removed. _weddingTravelTotal + _hasManualTravelOverride
  // are passed into the Team card above.

  travelOverrideHtml += '</div>';

  // P3c quote order: who (party) -> what (tier/timing) -> price (line items) ->
  // logistics (travel/locations) -> payment. P3e sticky summary pinned on top.
  // Variant mode: show a banner at the top and save/cancel at the bottom.
  var _vIdx = state.editingVariantIdx;
  var _variantBanner = '';
  var _variantFooter = '';
  if (_vIdx != null) {
    var _vLabel = pvGetVariantLabel_(_vIdx);
    _variantBanner =
      '<div style="background:var(--brand-primary,#c99591);color:#fff;padding:12px 16px;border-radius:var(--radius-large,1.5rem);margin-bottom:16px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px">' +
        '<div style="display:flex;align-items:center;gap:10px;flex:1;min-width:0">' +
          '<i class="fa-solid fa-layer-group" style="flex-shrink:0"></i>' +
          '<div style="flex:1;min-width:0">' +
            '<div style="font-size:11px;opacity:0.85;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:4px">Package option name</div>' +
            '<input id="pvVariantLabelInput" type="text" value="' + escapeHtml(_vLabel) + '" ' +
              'style="background:rgba(255,255,255,0.2);color:#fff;border:1.5px solid rgba(255,255,255,0.5);border-radius:var(--radius-pill,3rem);padding:5px 14px;font-size:14px;font-weight:700;font-family:inherit;width:100%;max-width:260px;outline:none" ' +
              'placeholder="Option name..." ' +
              'onfocus="this.style.background=\'rgba(255,255,255,0.3)\'" ' +
              'onblur="this.style.background=\'rgba(255,255,255,0.2)\'">' +
            '<div style="font-size:11px;opacity:0.75;margin-top:3px">Edit party, services, tier, timing and travel below. Save when done.</div>' +
          '</div>' +
        '</div>' +
        '<div style="display:flex;gap:8px;flex-shrink:0">' +
          '<button class="btn btn-ghost btn-sm" onclick="pvCancelVariantBuilder_()" style="background:rgba(255,255,255,0.2);color:#fff;border-color:rgba(255,255,255,0.4)">' +
            '<i class="fa-solid fa-xmark"></i> Cancel' +
          '</button>' +
          '<button class="btn btn-ghost btn-sm" onclick="pvSaveAndExitVariantBuilder_()" style="background:#fff;color:var(--brand-primary,#c99591);border-color:#fff;font-weight:700">' +
            '<i class="fa-solid fa-check"></i> Save Option' +
          '</button>' +
        '</div>' +
      '</div>';
    _variantFooter =
      '<div style="margin-top:18px;display:flex;gap:10px;align-items:center">' +
        '<button class="btn btn-ghost" onclick="pvCancelVariantBuilder_()"><i class="fa-solid fa-xmark"></i> Cancel</button>' +
        '<button class="btn btn-primary" onclick="pvSaveAndExitVariantBuilder_()"><i class="fa-solid fa-check"></i> Save Option</button>' +
      '</div>';
  }

  document.getElementById('workspace').innerHTML =
    pbSnapshotFallbackNoticeHtml_() +
    _variantBanner +
    buildStickySummary_(subtotal, gst, total, calc) +
    partyHtml +
    manualPartyHtml +
    tierTimingHtml +
    travelOverrideHtml +
    '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-list-check"></i> Line Items</div>' +
      overrideMenuHtml +
      '<table class="line-items-table">' +
        '<thead><tr>' +
          '<th>Description</th><th style="width:90px">Qty</th><th style="width:110px">Rate</th><th style="width:110px">Total</th><th style="width:40px"></th>' +
        '</tr></thead>' +
        '<tbody id="lineItemsBody">' + rowsHtml + '</tbody>' +
      '</table>' +
      buildSuppressedTravelRestoreHtml_(calc) +
      '<div class="svc-picker-bar">' +
        '<button class="svc-picker-btn" onclick="toggleServiceCatalog()"><i class="fa-solid fa-grid-2"></i> Add from Catalog</button>' +
        '<button class="svc-picker-btn" onclick="toggleCustomItem()"><i class="fa-solid fa-pen"></i> Custom Item</button>' +
      '</div>' +
      '<div id="svcCatalogPanel" style="display:none"></div>' +
      '<div id="svcCustomPanel" style="display:none">' +
        '<div class="svc-custom-row">' +
          '<input type="text" id="newItemDesc" placeholder="Description...">' +
          '<input type="number" id="newItemQty" placeholder="Qty" style="width:70px;flex:none" value="1" min="1">' +
          '<input type="number" id="newItemRate" placeholder="Rate" style="width:90px;flex:none" step="0.01" min="0">' +
          '<button class="btn btn-ghost btn-sm" onclick="addLineItem()"><i class="fa-solid fa-plus"></i> Add</button>' +
        '</div>' +
      '</div>' +
      '<div class="summary-strip">' +
        '<div class="summary-item"><div class="label">Subtotal</div><div class="amount" id="sumSubtotal">' + formatCurrency(subtotal) + '</div></div>' +
        '<div class="summary-item"><div class="label">GST (10%)</div><div class="amount" id="sumGst">' + formatCurrency(gst) + '</div></div>' +
        '<div class="summary-item"><div class="label">Total</div><div class="amount total" id="sumTotal">' + formatCurrency(total) + '</div></div>' +
      '</div>' +
      '<div class="info-strip">' +
        // Artists / Est. Start / Travel readouts removed 2026-07-12: they now live
        // authoritatively in the Wedding Day Team card (artists + travel) and the
        // Timing card (est start), so the numbers appear in exactly one place.
        '<div class="info-item">Deposit (50%): <strong>' + formatCurrency(total / 2) + '</strong></div>' +
      '</div>' +
      // Package notes (2026-08-04). Rendered AFTER the summary strip, deliberately
      // outside the priced table, so it is visually obvious these are not services and
      // carry no cost. Neutral styling per Jess: this highlights something already in
      // the T&Cs, it is not a warning banner.
      //
      // FLAGGED OFF until the GAS half lands. The client half (storage, per-variant
      // save/restore, flush payload, UI) is complete, but GAS does not yet persist
      // packageNotes onto PROPOSAL_CALC_JSON, return it on workspace load, or carry it
      // into the token snapshot the signed agreement reads. With the UI live and the
      // backend absent, a note would appear to save and then silently vanish on reload,
      // and would never reach the agreement -- a visible-but-broken feature, which is
      // worse than no feature. Flip window.JV_PB_PACKAGE_NOTES = true (or delete this
      // gate) in the SAME change that lands the GAS side.
      // Full remaining scope: BRIEFS/HANDOVER-package-notes-gas-half-2026-08-04.md
      (window.JV_PB_PACKAGE_NOTES === true ? buildPackageNotesHtml_(calc) : '') +
      buildServiceDurationBanner_(calc) +
      buildIntegrityFlags_(calc) +
      flagsHtml +
    '</div>' +
    paymentHtml +
    '<div id="availabilityPanel" style="margin-top:12px"></div>' +
    _variantFooter +
    renderProposalHistoryHtml_();

  loadAvailabilityCheck();
  pbApplySnapshotFallbackUiState_();
}

/* =====================================================================
   WEDDING DAY TEAM CARD (2026-07-12): consolidates the artist count,
   automatic travel, venue search, and roster assignment into ONE card.
   Every control reuses an EXISTING handler, so the three calc engines and
   all field names are untouched (Invariant 8 holds by construction):
     - venue search  -> onLocationAddressInput(v, 'wedding') / clearLocationAddress
     - artist count  -> commitArtistsField_(n) / clearTimingField('artistsRequired')
     - travel adjust -> toggleTravelLock / applyTravelOverride / retryTravelCalc /
                        clearTravelOverrideAndPersist_
     - assign artist -> openAddArtistPicker() / removeAssignedArtist(name)
   Layout is Option A (venue at the top). Trial / spray-tan / custom location
   cards render separately below in the "Other appointment locations" panel.
   ===================================================================== */
function buildWeddingDayTeamCard_(calc, weddingSelectedAddr, weddingTravelTotal, hasManualTravelOverride) {
  calc = calc || {};
  var visibleWedding = proposalVisibleWeddingTravel_(calc);
  var artistCount = Number(calc.artistsRequired || calc.artistsNeeded || 1);
  var availTier = calc.availability && calc.availability.pricingTier;
  var availLabel = availTier === 'jessica' ? 'Jessica available'
                 : (availTier ? 'Pro Team only' : '');
  var isKnownVenue = !!(calc.selectedAddress && calc.selectedAddress.knownVenue)
                  || !!(calc.selectedAddresses && calc.selectedAddresses.wedding && calc.selectedAddresses.wedding.knownVenue);
  var isTbc = !!(calc.weddingLocationTbc || calc.travelTBC);
  var _cardOpen = '<div class="card" id="weddingDayTeamCard">' +
    '<div class="card-title"><i class="fa-solid fa-user-group"></i> Wedding Day Team</div>' +
    '<div class="wdt-note">Venue, artists and travel for the wedding day. All calculated for you.</div>';

  // --- Venue at the top (Option A) ---
  // Pricing-authority marker (2026-09-10, BUILD-proposal-builder-ux-fixes item 9, F-06):
  // this is the ONE venue value that actually drives travel pricing (calc.selectedAddress,
  // falling back to bride.weddingAddressFull/location) -- distinct from the three OTHER
  // venue values shown elsewhere on Stage 1 (Bride Details "Venue / Location", Enquiry
  // Source "Venue Name", Form Submission "Venue"), which are as-submitted reference data
  // only and never feed the travel calculation. Confirmed live: those three can disagree
  // with each other and with this one, with nothing on screen saying which one prices.
  var venueHtml = '';
  if (weddingSelectedAddr) {
    venueHtml +=
      '<div class="wdt-venue-set" id="weddingSelectedAddr">' +
        '<i class="fa-solid fa-location-dot"></i>' +
        '<span class="addr-text" id="weddingAddrText">' + escapeHtml(weddingSelectedAddr) + '</span>' +
        '<span class="wdt-known" style="background:var(--brand-primary-deep);color:#fff" title="This is the venue used to calculate travel pricing. The Venue/Location, Enquiry Source, and Form Submission values on Stage 1 are as-submitted reference data only and do not affect pricing.">Used for pricing</span>' +
        (isKnownVenue ? '<span class="wdt-known">Known venue</span>' : '') +
        '<span class="wdt-venue-x addr-clear" onclick="clearLocationAddress(\'wedding\')" title="Clear address"><i class="fa-solid fa-xmark"></i></span>' +
      '</div>';
  }
  venueHtml +=
    '<div class="address-search-wrap">' +
      '<i class="fa-solid fa-magnifying-glass search-icon"></i>' +
      '<input type="text" id="addressSearch-wedding" placeholder="' +
        (weddingSelectedAddr ? 'Change venue or search a new address...' : 'Search venue or address...') + '" ' +
        'autocomplete="off" oninput="onLocationAddressInput(this.value, \'wedding\')">' +
      '<div class="address-results" id="addressResults-wedding"></div>' +
    '</div>';

  // --- Artists needed (Auto pill + stepper) ---
  var artistsHtml =
    '<div class="wdt-row">' +
      '<div class="wdt-label"><strong>Artists needed</strong>' +
        '<span class="wdt-desc">From party size and finish time' +
          (availLabel ? ' <span class="wdt-pill wdt-pill-avail">' + escapeHtml(availLabel) + '</span>' : '') +
        '</span>' +
      '</div>' +
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<button type="button" class="wdt-pill wdt-pill-revert" onclick="wdtRevertArtists_()" title="Revert to the auto-calculated count">Auto</button>' +
        '<div class="wdt-stepper">' +
          '<button type="button" aria-label="fewer artists" onclick="wdtStepArtists_(-1)"' + (artistCount <= 1 ? ' disabled' : '') + '>&minus;</button>' +
          '<span class="wdt-val" id="wdtArtistVal">' + artistCount + '</span>' +
          '<button type="button" aria-label="more artists" onclick="wdtStepArtists_(1)"' + (artistCount >= 12 ? ' disabled' : '') + '>+</button>' +
        '</div>' +
      '</div>' +
    '</div>';

  // --- Travel (automatic) + Adjust ---
  var travelHtml = '';
  if (isTbc) {
    travelHtml +=
      '<div class="wdt-row">' +
        '<div class="wdt-label"><strong>Travel</strong><span class="wdt-desc">Per artist at $0.95/km ex GST, return</span></div>' +
      '</div>' +
      '<div class="wdt-tbc">' +
        '<strong>Wedding-day travel: TBC</strong>' +
        '<div style="opacity:0.85;margin-top:2px">Added once the bride confirms her getting-ready address. The total and 50% booking fee are pre-travel. The post-booking address chase still fires.</div>' +
      '</div>';
  } else if (visibleWedding.length > 0) {
    var totalFee = 0, totalKm = 0, anyEstimate = false, dest = '';
    for (var i = 0; i < visibleWedding.length; i++) {
      totalFee += Number(visibleWedding[i].fee || 0);
      totalKm  += Math.round(visibleWedding[i].roundTripKm || 0);
      if (visibleWedding[i].isEstimate) anyEstimate = true;
      if (!dest && visibleWedding[i].toAddress) dest = String(visibleWedding[i].toAddress).split(',')[0].trim();
    }
    var perArtistKm = visibleWedding.length ? Math.round(totalKm / visibleWedding.length) : 0;
    var nA = visibleWedding.length;
    travelHtml +=
      '<div class="wdt-row">' +
        '<div class="wdt-label"><strong>Travel</strong><span class="wdt-desc">' + wdtTravelRateLabel_(totalFee, totalKm) + '</span></div>' +
      '</div>' +
      '<div class="wdt-travel' + (anyEstimate ? ' wdt-estimate' : '') + '">' +
        '<div>' +
          '<div class="wdt-t-main">' + nA + ' artist' + (nA > 1 ? 's' : '') + ' travelling' + (dest ? ' to ' + escapeHtml(dest) : '') + '</div>' +
          '<div class="wdt-t-sub">' + (perArtistKm ? perArtistKm + ' km return each, ' : '') + (anyEstimate ? 'estimated from the venue' : 'from the venue') + '</div>' +
        '</div>' +
        '<div class="wdt-t-amt">' + (anyEstimate ? 'approx ' : '') + formatCurrency(totalFee) + '</div>' +
      '</div>';
    if (anyEstimate) {
      travelHtml +=
        '<div class="wdt-warn">' +
          '<i class="fa-solid fa-triangle-exclamation"></i>' +
          '<div>Travel is approximate. Search the venue above to calculate the real fee, or set a manual fee under Adjust.</div>' +
        '</div>';
    }
    // Per-artist breakdown (collapsed) + Adjust (lock / manual / retry).
    travelHtml += '<details class="wdt-adjust"><summary>Per-artist breakdown</summary><div style="margin-top:8px">';
    for (var w = 0; w < visibleWedding.length; w++) {
      var it = visibleWedding[w];
      var km = Math.round(it.roundTripKm || 0);
      var d2 = (it.toAddress || '').split(',')[0].trim();
      var lbl = (it.isEstimate && d2 ? 'Approximate Travel to ' + d2 : 'Wedding Day Travel')
        + _TRAVEL_DASH + (it.artist || 'Artist') + (km ? ' (' + km + 'km return)' : '');
      travelHtml +=
        '<div class="travel-breakdown-row">' +
          '<span class="tb-label">' + escapeHtml(lbl) + '</span>' +
          '<span class="tb-km">' + (km ? km + ' km' : '') + '</span>' +
          '<span class="tb-fee">' + formatCurrency(it.fee || 0) + '</span>' +
        '</div>';
    }
    travelHtml += '</div></details>';
  } else if (calc.travelKm && calc.travelCost) {
    travelHtml +=
      '<div class="wdt-row"><div class="wdt-label"><strong>Travel</strong><span class="wdt-desc">' + wdtTravelRateLabel_(calc.travelCost, calc.travelKm) + '</span></div></div>' +
      '<div class="wdt-travel"><div><div class="wdt-t-main">Total travel</div><div class="wdt-t-sub">' + calc.travelKm + ' km</div></div>' +
        '<div class="wdt-t-amt">' + formatCurrency(calc.travelCost) + '</div></div>';
  } else {
    travelHtml +=
      '<div class="wdt-row"><div class="wdt-label"><strong>Travel</strong><span class="wdt-desc">Per artist at $0.95/km ex GST, return</span></div></div>' +
      '<div class="wdt-empty">Set the venue above and travel is calculated for each artist automatically.</div>';
  }

  // Adjust: lock this fee / manual override / retry (demoted off the default surface).
  var lockOn = travelLockState_('wedding', calc);
  travelHtml +=
    '<details class="wdt-adjust"' + (hasManualTravelOverride ? ' open' : '') + '>' +
      '<summary>Adjust travel manually</summary>' +
      '<div style="margin-top:10px;display:flex;flex-direction:column;gap:10px">' +
        '<label class="toggle" onclick="toggleTravelLock(\'wedding\')" style="display:inline-flex;align-items:center;gap:8px;cursor:pointer;font-size:13px">' +
          '<span class="track" style="width:38px;height:22px;border-radius:999px;position:relative;flex:none;background:' + (lockOn ? 'var(--brand-primary)' : 'var(--line)') + '">' +
            '<span style="position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.2);transition:transform .3s var(--ease-reveal);transform:translateX(' + (lockOn ? '16px' : '0') + ')"></span>' +
          '</span>' +
          'Lock this fee so a recalc won\'t change it' +
        '</label>' +
        '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">' +
          '<label style="font-size:13px;color:var(--brand-text)">Manual fee override ($)</label>' +
          '<input type="number" id="travelFeeOverride" min="0" step="1" placeholder="e.g. 120" value="' + (weddingTravelTotal || '') + '" style="width:110px;padding:6px 8px;border:1px solid var(--line);border-radius:8px;font-family:inherit">' +
          '<button class="btn btn-primary btn-sm" onclick="applyTravelOverride()" title="Apply manual override"><i class="fa-solid fa-check"></i> Apply</button>' +
          '<button class="btn btn-ghost btn-sm" onclick="retryTravelCalc()" title="Retry standard calculation"><i class="fa-solid fa-rotate-right"></i> Retry</button>' +
        '</div>' +
        (hasManualTravelOverride
          ? '<button class="btn btn-ghost btn-sm" onclick="clearTravelOverrideAndPersist_()" title="Remove the manual override and restore the calculated travel fee" style="align-self:flex-start"><i class="fa-solid fa-xmark"></i> Clear override</button>'
          : '') +
      '</div>' +
    '</details>';

  // --- Assign specific artists (roster dropdown + chips) ---
  var assignHtml =
    '<div class="wdt-divider"></div>' +
    '<div class="wdt-row wdt-first">' +
      '<div class="wdt-label"><strong>Assign specific artists</strong>' +
        '<span class="wdt-desc">Optional. Uses each artist\'s exact distance instead of the estimate. The bride never sees these names.</span>' +
      '</div>' +
    '</div>' +
    '<button type="button" class="wdt-selector-btn" onclick="openAddArtistPicker()">' +
      '<span class="wdt-sel-placeholder">Select from your Pro Team&hellip;</span>' +
      '<i class="fa-solid fa-chevron-down"></i>' +
    '</button>';

  // Assigned chips (suppressed removed + capped, same set the travel lines use).
  var assigned = (state.assignedArtists && state.assignedArtists.length) ? state.assignedArtists
               : (Array.isArray(calc.assignedArtists) ? calc.assignedArtists : []);
  if (Array.isArray(calc.assignedArtists)) state.assignedArtists = calc.assignedArtists;
  var _sup = state.travelSuppressions || {};
  var _cap = Number(calc && calc.artistsRequired) || 0;
  assigned = assigned.filter(function(a) { return !_sup[travelId_('wedding', (a && a.artistName) || 'Artist')]; });
  if (_cap > 0 && assigned.length > _cap) assigned = assigned.slice(0, _cap);
  if (assigned.length > 0) {
    assignHtml += '<div class="wdt-chips">';
    for (var c = 0; c < assigned.length; c++) {
      var a = assigned[c];
      var nm = a.artistName || 'Unassigned';
      var initials = String(nm).trim().split(/\s+/).map(function(p) { return p.charAt(0); }).join('').slice(0, 2).toUpperCase();
      var meta = (a.suburb ? a.suburb : '') + (a.roundTripKm ? (a.suburb ? ' · ' : '') + a.roundTripKm + 'km' : '');
      assignHtml +=
        '<span class="wdt-chip">' +
          '<span class="wdt-chip-av">' + escapeHtml(initials || nm.charAt(0)) + '</span>' +
          '<span>' + escapeHtml(nm) + (meta ? '<span class="wdt-chip-meta"> ' + escapeHtml(meta) + '</span>' : '') + '</span>' +
          '<button type="button" class="wdt-chip-x" title="Remove artist" onclick="removeAssignedArtist(\'' + escapeHtml(String(nm)).replace(/'/g, "\\'") + '\')"><i class="fa-solid fa-xmark"></i></button>' +
          (a.manualAdded ? '' : '<span class="wdt-chip-auto" title="Calculator-assigned">auto</span>') +
        '</span>';
    }
    assignHtml += '</div>';
  } else {
    assignHtml += '<div class="wdt-empty">Not assigned yet. Travel is estimated from the venue. Assign artists to use their exact distances.</div>';
  }

  return _cardOpen + venueHtml + artistsHtml + travelHtml + assignHtml + '</div>';
}

// Stepper handlers for the Wedding Day Team card. These wrap the EXISTING artist
// override path so the calc/travel behaviour is identical to typing in the old
// Artists field: commitArtistsField_ writes calc.artistsRequired, re-caps wedding
// travel, and fires a GAS travel calc when the count increases.
var _wdtArtistDebounce = null;
function wdtStepArtists_(delta) {
  var calc = (state.workspace && state.workspace.calculation) || {};
  // Use the last optimistic value if a debounce is pending, so rapid +/+/+ steps
  // from what the admin currently SEES, not the last-committed server value.
  var cur = (_wdtArtistDebounce && _wdtArtistPending_ != null)
    ? _wdtArtistPending_
    : Number(calc.artistsRequired || calc.artistsNeeded || 1);
  var next = Math.max(1, Math.min(12, cur + delta));
  if (next === cur) return;
  // Optimistic UI: update the visible number + stepper button disabled-state
  // instantly so the click feels immediate even though the GAS travel recalc
  // (cold GAS + JV AUTO + Maps) can take several seconds. Debounce the expensive
  // recalc so holding +/+/+ fires ONE call, not one per click (kills the pile-up
  // of concurrent cold recalcs that tripped the 14-22s slow alerts). Pricing is
  // unchanged: it's the SAME commitArtistsField_ recalc, just coalesced.
  _wdtArtistPending_ = next;
  var _valEl = document.getElementById('wdtArtistVal');
  if (_valEl) _valEl.textContent = next;
  var _card = document.getElementById('weddingDayTeamCard');
  if (_card) {
    var _btns = _card.querySelectorAll('.wdt-stepper button');
    if (_btns[0]) _btns[0].disabled = (next <= 1);
    if (_btns[1]) _btns[1].disabled = (next >= 12);
  }
  if (_wdtArtistDebounce) clearTimeout(_wdtArtistDebounce);
  _wdtArtistDebounce = setTimeout(function() {
    _wdtArtistDebounce = null;
    var _n = _wdtArtistPending_;
    _wdtArtistPending_ = null;
    commitArtistsField_(String(_n));
  }, 600);
}
var _wdtArtistPending_ = null;

// "Auto" pill: revert the artist count to the formula-derived value (same as
// clearing the old Artists input). clearTimingField re-renders.
function wdtRevertArtists_() {
  clearTimingField('artistsRequired');
}

/* ── SERVICE DURATION BANNER (TBC finish time) ──────── */
/* ── ASSIGNED ARTISTS PANEL (admin-only — bride never sees these names) ── */
function buildAssignedArtistsPanel_(calc) {
  var artists = (state.assignedArtists && state.assignedArtists.length) ? state.assignedArtists
              : (Array.isArray(calc.assignedArtists) ? calc.assignedArtists : []);
  // Keep state in sync so add/remove handlers see the latest list
  if (Array.isArray(calc.assignedArtists)) state.assignedArtists = calc.assignedArtists;

  // Show only the artists actually being quoted: drop deleted (suppressed) ones and
  // cap to the artist count, so this panel agrees with the wedding-travel lines and
  // "N artists required". The full list stays in state so raising the count restores.
  var _sup = state.travelSuppressions || {};
  var _cap = Number(calc && calc.artistsRequired) || 0;
  artists = artists.filter(function(a) {
    return !_sup[travelId_('wedding', (a && a.artistName) || 'Artist')];
  });
  if (_cap > 0 && artists.length > _cap) artists = artists.slice(0, _cap);

  var rowsHtml = '';
  if (artists.length === 0) {
    rowsHtml = '<div style="padding:8px 4px;color:var(--muted,#8b7d6b);font-size:13px;font-style:italic">No artists assigned yet — recalculate the wedding address to populate, or add an artist below.</div>';
  } else {
    for (var i = 0; i < artists.length; i++) {
      var a = artists[i];
      var meta = (a.suburb ? a.suburb + ' · ' : '') +
                 (a.roundTripKm ? a.roundTripKm + 'km · ' : '') +
                 (typeof a.fee === 'number' ? formatCurrency(a.fee) : '');
      var removeBtn = '<button title="Remove artist" onclick="removeAssignedArtist(\'' + escapeHtml(a.artistName).replace(/'/g, "\\'") + '\')" style="border:none;background:transparent;color:#c94040;cursor:pointer;padding:4px 8px;font-size:14px"><i class="fa-solid fa-xmark"></i></button>';
      rowsHtml +=
        '<div style="display:flex;align-items:center;gap:8px;padding:6px 4px;border-bottom:1px solid rgba(232,221,211,0.4)">' +
          '<div style="flex:1;font-size:13px;color:var(--brand-text,#2f2524)"><strong>' + (i + 1) + '. ' + escapeHtml(a.artistName || 'Unassigned') + '</strong>' +
          (meta ? '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:2px">' + escapeHtml(meta) + '</div>' : '') +
          '</div>' +
          removeBtn +
        '</div>';
    }
  }

  return '<div style="margin-top:14px;padding:12px 14px;background:#fff;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-medium,1rem)">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">' +
      '<div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--brand-text,#2f2524)">' +
        '<i class="fa-solid fa-user-group" style="margin-right:6px;color:var(--brand-primary)"></i>Assigned Artists (admin only, not shown to bride)' +
      '</div>' +
      '<button onclick="openAddArtistPicker()" class="btn btn-ghost btn-sm" style="white-space:nowrap"><i class="fa-solid fa-plus"></i> Add artist</button>' +
    '</div>' +
    rowsHtml +
  '</div>';
}

// Phase 4: roster the Add-Artist picker renders from (CDN team_member metaobject
// preferred, GAS fallback). Indexed so confirmAddArtist can pass the full meta.
var _addArtistRoster = [];

function _addArtist_assignedNames_() {
  var cur = (state.assignedArtists && state.assignedArtists.length)
    ? state.assignedArtists
    : ((state.workspace && state.workspace.calculation && state.workspace.calculation.assignedArtists) || []);
  var map = {};
  (cur || []).forEach(function(a) {
    var n = String((a && (a.artistName || a.artist)) || '').toLowerCase();
    if (n) map[n] = true;
  });
  return map;
}

function openAddArtistPicker() {
  if (!state.currentBride) return;
  // Post-send lock: changing artists changes the quoted price (matches the
  // server-side gate in proposalAddArtistToWedding).
  if (state.bookingTierLockedAt) {
    showBanner('Proposal already sent. Unlock the tier first to change artists.', 'warn');
    return;
  }

  // Prefer the CDN team_member roster (instant; injected as window.JV_TEAM_ROSTER).
  var cdn = (typeof window !== 'undefined' && Array.isArray(window.JV_TEAM_ROSTER)) ? window.JV_TEAM_ROSTER : null;
  if (cdn && cdn.length) {
    var assigned = _addArtist_assignedNames_();
    var available = cdn
      .filter(function(a) { return a && a.artistName && !assigned[String(a.artistName).toLowerCase()]; })
      .sort(function(a, b) { return String(a.displayName || a.artistName).localeCompare(String(b.displayName || b.artistName)); });
    _showAddArtistModal(available);
    return;
  }

  // The filtered D1 roster is authoritative when the embedded roster is absent.
  var bride = (state.workspace && state.workspace.bride) || {};
  pbCfBuilderRead_('list-artists-for-add', {
    name: state.currentBride,
    email: bride.email || ''
  }, pbAcceptCfArtistsForAdd_)
    .then(function(payload) {
      if (!payload) {
        showBanner('Could not load the artist roster through Cloudflare.', 'error');
        return;
      }
      _showAddArtistModal(payload.available || []);
    })
    .catch(function(err) {
      showBanner(getErrorMessage(err), 'error');
    });
}

function _showAddArtistModal(available) {
  var existing = document.getElementById('addArtistOverlay');
  if (existing) existing.remove();

  // Index the roster so the row click passes the full artist meta (suburb,
  // place_id) through to confirmAddArtist -> proposalAddArtistToWedding.
  _addArtistRoster = available || [];

  var optsHtml = '';
  if (_addArtistRoster.length === 0) {
    optsHtml = '<div style="padding:16px;text-align:center;color:var(--muted,#8b7d6b);font-size:13px">No more active artists available to add (all are already on this wedding).</div>';
  } else {
    for (var i = 0; i < _addArtistRoster.length; i++) {
      var a = _addArtistRoster[i];
      var label = a.displayName || a.artistName;
      var skillsArr = Array.isArray(a.skills) ? a.skills : [];
      var skillsTxt = skillsArr.map(function(s) {
        s = String(s).toLowerCase();
        return s === 'makeup' ? 'Makeup' : s === 'hair' ? 'Hair' : s === 'lead' ? 'Lead' : s;
      }).join(' · ');
      var metaLine = [a.suburb || '', a.role || '', skillsTxt].filter(Boolean).join(' · ');
      var avatar = a.photo
        ? '<img src="' + escapeHtml(a.photo) + '" alt="" style="width:34px;height:34px;border-radius:50%;object-fit:cover;flex:none">'
        : '<div style="width:34px;height:34px;border-radius:50%;flex:none;background:var(--brand-bg-soft,#f6f1ee);color:var(--brand-primary);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700">' + escapeHtml(a.initials || String(label).charAt(0)) + '</div>';
      optsHtml += '<div onclick="confirmAddArtist(' + i + ')" style="display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;border-bottom:1px solid rgba(232,221,211,0.4);transition:background 0.15s" onmouseover="this.style.background=\'var(--brand-bg-soft,#f6f1ee)\'" onmouseout="this.style.background=\'\'">' +
        avatar +
        '<div style="flex:1;min-width:0">' +
          '<div style="font-weight:600;font-size:13px;color:var(--brand-text,#2f2524)">' + escapeHtml(label) + '</div>' +
          (metaLine ? '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:2px">' + escapeHtml(metaLine) + '</div>' : '') +
        '</div>' +
        '</div>';
    }
  }

  var overlay = document.createElement('div');
  overlay.id = 'addArtistOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(31,23,22,0.4);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px';
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:var(--radius-large,1.5rem);padding:20px;max-width:420px;width:100%;max-height:80vh;overflow-y:auto;box-shadow:0 16px 48px rgba(169,112,108,0.2)">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">' +
        '<div style="font-size:15px;font-weight:700;color:var(--brand-text,#2f2524)">Add Artist to Wedding</div>' +
        '<button onclick="document.getElementById(\'addArtistOverlay\').remove()" style="border:none;background:transparent;cursor:pointer;font-size:18px;color:var(--muted,#8b7d6b)"><i class="fa-solid fa-xmark"></i></button>' +
      '</div>' +
      '<div style="font-size:12px;color:var(--muted,#8b7d6b);margin-bottom:10px">Pick from active roster. Wedding-day travel is calculated from their home suburb at $0.95/km round trip. The artist\'s name is admin-only — the bride sees "Artist 2", "Artist 3", etc.</div>' +
      '<div style="border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-medium,1rem);overflow:hidden">' +
        optsHtml +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });
}

function confirmAddArtist(ref) {
  // ref is the roster index (Phase 4). Back-compat: a string artist name still works.
  var item = (typeof ref === 'number' && _addArtistRoster && _addArtistRoster[ref]) ? _addArtistRoster[ref]
           : (typeof ref === 'string' ? { artistName: ref } : null);
  if (!item || !item.artistName) return;
  var artistName = item.artistName;
  if (_draftDirty && !confirm('You have unsaved changes. Adding this artist will reload the workspace and those changes will be lost. Continue?')) return;
  var overlay = document.getElementById('addArtistOverlay');
  if (overlay) overlay.remove();
  // This call chains CRM GAS to JV AUTO GAS to Google Maps for a live distance,
  // so it can take up to a minute on a cold instance. Show a blocking overlay
  // so the admin gets honest feedback instead of a silent freeze.
  showLoadingOverlay('Calculating travel for ' + artistName + '. This can take up to a minute.');
  var _addMeta = { suburb: item.suburb || '', placeId: item.placeId || '', displayName: item.displayName || artistName };

  // Cloudflare is authoritative. A missing route is an explicit failure, never a GAS retry.
  if (window.JV_PB_READS_CF !== true || !window._pbAdminProxyBase) {
    hideLoadingOverlay();
    showBanner('Cloudflare proposal editing is unavailable. Nothing was changed.', 'error');
    return;
  }
  pbCfBuilderWrite_('add-artist', { name: state.currentBride, artistName: artistName, artistMeta: _addMeta }, pbAcceptCfAddArtist_)
    .then(function(result) {
      if (result && result.refusal) { pbShowCfWriteRefusal_(result.refusal); return; }
      var cf = result && result.cf;
      if (!cf) { hideLoadingOverlay(); showBanner('Could not add artist through Cloudflare. Nothing was changed.', 'error'); return; }
      hideLoadingOverlay();
      if (cf.noop) { showBanner(cf.note || (artistName + ' is already on this wedding'), 'success'); selectBride(state.currentBride); return; }
      showBanner(artistName + ' added (' + cf.added.roundTripKm + 'km · ' + formatCurrency(cf.added.fee) + ')', 'success');
      var applied = state.ssotEnabled ? applyCalcResponse_(cf.calculation) : applyWorkspaceCalculation_(cf.calculation);
      if (!applied) selectBride(state.currentBride);
      else _recomputeTravelOnChange_();
    });
}

function removeAssignedArtist(artistName) {
  if (state.bookingTierLockedAt) {
    showBanner('Proposal already sent. Unlock the tier first to change artists.', 'warn');
    return;
  }
  if (_draftDirty && !confirm('You have unsaved changes. Removing this artist will reload the workspace and those changes will be lost. Continue?')) return;
  if (!confirm('Remove ' + artistName + ' from this wedding? Their travel fee will be removed and the artist count decreases by 1.')) return;
  showLoadingOverlay('Removing ' + artistName + ' and updating the quote...');

  if (window.JV_PB_READS_CF !== true || !window._pbAdminProxyBase) {
    hideLoadingOverlay();
    showBanner('Cloudflare proposal editing is unavailable. Nothing was changed.', 'error');
    return;
  }
  pbCfBuilderWrite_('remove-artist', { name: state.currentBride, artistName: artistName }, pbAcceptCfRemoveArtist_)
    .then(function(result) {
      if (result && result.refusal) { pbShowCfWriteRefusal_(result.refusal); return; }
      var cf = result && result.cf;
      if (!cf) { hideLoadingOverlay(); showBanner('Could not remove artist through Cloudflare. Nothing was changed.', 'error'); return; }
      hideLoadingOverlay();
      if (cf.noop) { showBanner(cf.note || (artistName + ' is not on this wedding'), 'success'); selectBride(state.currentBride); return; }
      showBanner(artistName + ' removed (saved ' + formatCurrency(cf.removed.fee) + ')', 'success');
      var applied = state.ssotEnabled ? applyCalcResponse_(cf.calculation) : applyWorkspaceCalculation_(cf.calculation);
      if (!applied) selectBride(state.currentBride);
      else _recomputeTravelOnChange_();
    });
}

// ── Generic proposal modal (used by Create New Proposal) ─────────────────────
// These were referenced by handleCreateNewProposal but never defined, so the
// "Create New Proposal" button silently threw a ReferenceError and did nothing.
function showProposalModal(title, html, opts) {
  opts = opts || {};
  closeProposalModal();
  var overlay = document.createElement('div');
  overlay.id = 'proposalModalOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(31,23,22,0.4);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px';
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:var(--radius-large,1.5rem);padding:22px;max-width:460px;width:100%;max-height:85vh;overflow-y:auto;box-shadow:0 16px 48px rgba(169,112,108,0.2)">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">' +
        '<div style="font-size:16px;font-weight:700;color:var(--brand-text,#2f2524)">' + escapeHtml(title || '') + '</div>' +
        '<button onclick="closeProposalModal()" style="border:none;background:transparent;cursor:pointer;font-size:18px;color:var(--muted,#8b7d6b)"><i class="fa-solid fa-xmark"></i></button>' +
      '</div>' +
      '<div>' + html + '</div>' +
    '</div>';
  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) closeProposalModal(); });
}

function closeProposalModal() {
  var o = document.getElementById('proposalModalOverlay');
  if (o) o.remove();
}

function buildServiceDurationBanner_(calc) {
  // Only show when finish time is TBC or missing, otherwise estimatedStartTime
  // covers the same info more usefully.
  var ftRaw = String(calc.finishTimeRaw || '').trim().toUpperCase();
  var isTbc = ftRaw === 'TBC' || (calc.finishTimeProvided === false && !calc.estimatedStartTime);
  if (!isTbc) return '';
  // Bug 2b (2026-07-19, Jess rule): prefer the backend-computed label ("Start time
  // TBC - X hours required") so the phrasing is exact and computed once, in one
  // place. Fall back to a client-side estimate for cached data from before this fix.
  var label = String(calc.startTimeTbcLabel || '').trim();
  if (!label) {
    var mins = Number(calc.totalServiceMinutes || 0);
    if (mins <= 0) return '';
    var artistCount = Number(calc.artistsRequired || calc.artistsNeeded || 1);
    var hours = Math.ceil(mins / artistCount / 60);
    label = 'Start time TBC - ' + hours + ' hours required';
  }
  return '<div class="duration-banner" style="margin-top:12px;padding:12px 14px;background:var(--brand-bg-soft,#f6f1ee);border-left:3px solid var(--brand-primary,#c99591);border-radius:var(--radius-medium,1rem);font-size:14px;line-height:1.5;color:var(--brand-text,#2f2524);">' +
    '<strong>' + escapeHtml(label) + '</strong> ' +
    '<span style="opacity:0.8;">Final artist count and price are confirmed once the bride locks in her finish time.</span>' +
    '</div>';
}

/* ── DATA INTEGRITY FLAGS ───────────────────────────── */
function buildIntegrityFlags_(calc) {
  var issues = [];
  var items = calc.lineItems || [];

  // $0 line items
  items.forEach(function(it) {
    var price = Number(it.unitPrice || it.rate || 0);
    if (price === 0 && (it.description || '').toLowerCase().indexOf('complimentary') < 0) {
      issues.push('<i class="fa-solid fa-triangle-exclamation"></i> $0 line item: <em>' + escapeHtml(it.description || 'Unknown') + '</em>');
    }
  });

  // Travel fee checks
  var km = Number(calc.travelKm || 0);
  var travelFee = Number(calc.travelCost || 0);
  var artistCount = Number(calc.artistsRequired || calc.artistsNeeded || 1);
  var kmPerArtist = artistCount > 0 ? km / artistCount : km;
  var hasTravelLineItem = items.some(function(it) {
    return (it.description || '').toLowerCase().indexOf('travel') > -1;
  });
  var hasAddress = !!(calc.selectedAddresses && calc.selectedAddresses.wedding) ||
                   !!(calc.selectedAddress && calc.selectedAddress.address);

  if (!hasTravelLineItem && hasAddress) {
    issues.push('<i class="fa-solid fa-triangle-exclamation"></i> Wedding address is set but no travel fee line item — travel may be missing from the quote');
  }
  if (!hasTravelLineItem && km > 0) {
    issues.push('<i class="fa-solid fa-triangle-exclamation"></i> ' + Math.round(km) + ' km recorded but no travel fee line item in the quote');
  }
  if (hasTravelLineItem && kmPerArtist > 500 && travelFee === 0) {
    issues.push('<i class="fa-solid fa-triangle-exclamation"></i> ' + Math.round(kmPerArtist) + ' km/artist but travel fee is $0 — check travel calculation');
  }

  if (!issues.length) return '';
  return '<div class="travel-warn-banner" style="margin-top:8px;gap:6px;flex-direction:column;align-items:flex-start">' +
    issues.map(function(i) { return '<div>' + i + '</div>'; }).join('') +
    '</div>';
}

/* ── TRAVEL FEE LOCK ──────────────────────────────────
   Lock a travel fee (wedding / trial / spray_tan) so subsequent recalculations
   don't overwrite a value Jess has committed. Independent per side. */
function travelLockState_(type, calc) {
  var locks = (calc && calc.travelLocks) || {};
  return !!locks[type];
}

function renderTravelLockToggle_(type, calc) {
  var isLocked = travelLockState_(type, calc);
  var label = isLocked ? 'Locked' : 'Lock fee';
  var title = isLocked
    ? 'Unlock — allow recalculation to update this travel fee'
    : 'Lock fee — protect this travel fee from recalculation';
  var lockSvg = isLocked
    ? '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>'
    : '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 7.5-2"/></svg>';
  return '<button type="button" class="travel-lock-toggle' + (isLocked ? ' is-locked' : '') +
    '" onclick="toggleTravelLock(\'' + type + '\')" title="' + title + '" aria-pressed="' + (isLocked ? 'true' : 'false') + '">' +
    lockSvg + '<span>' + label + '</span></button>';
}

// PATH 2: local-only edit. Travel lock state lives in calc.travelLocks; flushed on Save.
function toggleTravelLock(type) {
  if (!state.workspace || !state.workspace.calculation) return;
  var calc = state.workspace.calculation;
  if (!calc.travelLocks) calc.travelLocks = {};
  calc.travelLocks[type] = !calc.travelLocks[type];
  markDirty_();
  renderStageBuilder();
}

/* ---- Proposal Builder Cloudflare reads via the cors-relay 'proposal-builder' entity ----
   Requires window.JV_PB_READS_CF === true and window._pbAdminProxyBase.
   pbCfBuilderRead_(action, payload, accept) resolves to the parsed body ONLY when the HTTP call succeeded, the body parsed,
   and the caller's accept(body) predicate says the payload is genuinely usable. Every other outcome resolves null. It never
   rejects. Callers fail visibly and do not retry through GAS. */
function pbCfBuilderRead_(action, payload, accept) {
  if (window.JV_PB_READS_CF !== true || !window._pbAdminProxyBase) return Promise.resolve(null);
  try {
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    return nf(String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-builder/' + action, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify(payload || {})
    })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (b) { try { return (b && typeof b === 'object' && !b.fallback && accept(b)) ? b : null; } catch (_) { return null; } })
      .catch(function () { return null; });
  } catch (_) { return Promise.resolve(null); }
}

/* ---- Proposal Builder Cloudflare writes: the three D1 routes via the SAME cors-relay entity. Both the frontend gate and
   jv-crm-mirror route flag must be enabled. A disabled/unavailable route is a visible failure and never a GAS retry.

   CORRECTED BEHAVIOUR: a CF write response is one of three outcomes, not two.
     1. USABLE  -- ok:true and accept(body) says the payload is genuinely usable  -> use it, do NOT call GAS.
     2. REFUSED -- ok:false, a `refused` reason code is present, AND there is no `fallback:'gas'` alongside it. This is an
        EXPLICIT BUSINESS BLOCK (frozen/signed/tier_locked/sent_price_honoured/bad_mode/etc, see proposal-builder-edits.mjs
        REFUSAL_TEXT) -- the backend is saying "this change is not allowed," not "I couldn't check." This is FINAL: show the
        refusal to the admin and STOP. Retrying via GAS would silently attempt the same blocked write a second time through a
        different path, which is exactly the "two systems disagree" failure Jess's decision exists to prevent.
     3. MISS    -- everything else: flag off, no proxy base, non-200, network throw, malformed JSON, a legacy
        `fallback:'gas'` response, or an incomplete payload. The caller shows an error and makes no write.
   pbClassifyCfWriteOutcome_ is the pure decision function (unit-tested in tests/proposal-builder-cf-write-refusal.test.mjs
   -- keep both in sync if this logic changes). */
function pbClassifyCfWriteOutcome_(ok200, body, accept) {
  if (!ok200 || !body || typeof body !== 'object') return { cf: null, refusal: null };
  if (body.fallback === 'gas') return { cf: null, refusal: null };
  if (body.ok === false && body.refused) return { cf: null, refusal: body };
  if (body.ok === true) {
    try { if (accept(body)) return { cf: body, refusal: null }; } catch (_) { /* fall through to miss */ }
  }
  return { cf: null, refusal: null };
}
function pbCfBuilderWrite_(action, payload, accept) {
  if (window.JV_PB_READS_CF !== true || !window._pbAdminProxyBase) return Promise.resolve({ cf: null, refusal: null });
  try {
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    return nf(String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-builder/' + action, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify(payload || {})
    })
      .then(function (r) {
        if (!r || !r.ok) return { ok200: false, body: null };
        return r.json().then(function (b) { return { ok200: true, body: b }; }, function () { return { ok200: false, body: null }; });
      })
      .then(function (res) { return pbClassifyCfWriteOutcome_(res.ok200, res.body, accept); })
      .catch(function () { return { cf: null, refusal: null }; });
  } catch (_) { return Promise.resolve({ cf: null, refusal: null }); }
}
/* Shared helper: show a refusal banner the same way every write call site does. */
function pbShowCfWriteRefusal_(refusal) {
  hideLoadingOverlay();
  showBanner((refusal && refusal.error) || 'This change is not allowed right now.', 'error');
}

/* add-artist: usable only when ok:true AND either an idempotent noop replay (backend already resynced the snapshot) OR a real
   add carrying both the added-artist summary and the full recomputed calc. A refusal (ok:false, e.g. frozen/signed/tier_locked/
   travel_not_cached) returns false -> GAS decides. */
function pbAcceptCfAddArtist_(b) {
  return !!(b && b.ok === true && (b.noop === true || (b.added && b.calculation && typeof b.calculation === 'object')));
}
/* remove-artist: same shape as add, with removed{} instead of added{}. */
function pbAcceptCfRemoveArtist_(b) {
  return !!(b && b.ok === true && (b.noop === true || (b.removed && b.calculation && typeof b.calculation === 'object')));
}
/* set-trial-location: usable when ok:true AND either an idempotent noop OR a real change carrying the recomputed calc. */
function pbAcceptCfTrialLocation_(b) {
  return !!(b && b.ok === true && (b.noop === true || (b.calc && typeof b.calc === 'object')));
}
function pbAcceptCfReopen_(b) {
  return !!(b && b.ok === true && b.status === 'ok' && String(b.to || '').toUpperCase() === 'DRAFT_EDIT');
}
function pbAcceptCfVoidAndNew_(b) {
  return !!(b && b.ok === true && b.status === 'ok' && b.action === 'void_and_new' &&
    b.startMode === 'blank' && String(b.to || '').toUpperCase() === 'READY');
}
function pbAcceptCfUnlockTier_(b) {
  return !!(b && b.ok === true && b.status === 'ok' && b.action === 'unlock_tier' &&
    b.calculation && typeof b.calculation === 'object');
}
function pbAcceptCfUnlockTravelOrigin_(b) {
  return !!(b && b.ok === true && b.status === 'ok' && b.action === 'unlock_travel_origin');
}
function pbAcceptCfArtistsForAdd_(b) {
  return !!(b && b.status === 'ok' && Array.isArray(b.available));
}
function pbAcceptCfClosedDates_(b) {
  return !!(b && b.ok === true && Array.isArray(b.dates));
}
function pbAcceptCfClosedDateReopen_(b) {
  return !!(b && b.ok === true && b.dateIso);
}
function pbAcceptCfAltDateSend_(b) {
  return !!(b && b.ok === true && b.token && b.proposalUrl && b.email && typeof b.email === 'object');
}
function pbAcceptCfVoid_(b) {
  if (!b || b.ok !== true) return false;
  if (b.noop === true) return String(b.status || '').toUpperCase() === 'VOID';
  return b.action === 'void' && String(b.to || '').toUpperCase() === 'VOID';
}

/* accepted-summary: a signed money document. D1 names fields it cannot supply in missing[]; any non-empty missing[]
   (add-ons / accepted total not recorded at sign) means the figures may be the BASE quote, so we do NOT use it. */
function pbAcceptCfAcceptedSummary_(b) {
  var s = b && b.summary;
  return !!(b && b.ok === true && Array.isArray(b.missing) && b.missing.length === 0 &&
    s && Array.isArray(s.lineItems) && s.lineItems.length > 0 && isFinite(Number(s.acceptedTotal)) && Number(s.acceptedTotal) > 0);
}

/* check-availability: D1 infers EBM (no CLIENT TYPE column), so it can UNDER-report. A reported conflict is shown (safe
   direction). A "clear" result is trusted ONLY when the worker explicitly says clearTrusted===true (flag on + bride_partners
   fresh + no ambiguous same-day row + no conflicts); the worker fails closed, so any doubt gives clearTrusted:false. An absent
   or non-true clearTrusted is NEVER a trusted clear and falls through to the authoritative GAS check. */
function pbAcceptCfAvailability_(b) {
  if (!b || b.status !== 'ok') return false;
  var conflict = b.hasConflicts === true &&
    (Array.isArray(b.sameDayWeddings) ? b.sameDayWeddings.length : 0) + (Array.isArray(b.ebmEvents) ? b.ebmEvents.length : 0) > 0;
  var trustedClear = b.clearTrusted === true && b.hasConflicts === false;
  return !!(conflict || trustedClear);
}

/* ── SAME-DAY AVAILABILITY CHECK ──────────────────────
   PATH 2 polish: cache the rendered HTML on state.workspace.availabilityCache
   keyed by weddingDate. renderStageBuilder() fires on every edit; the wedding
   date doesn't change between renders, so the GAS call is wasteful. Cache
   evicts naturally when state.workspace is replaced on selectBride. In-flight
   guard prevents duplicate concurrent calls (mirrors flush race protection). */
function loadAvailabilityCheck() {
  var panel = document.getElementById('availabilityPanel');
  if (!panel || !state.currentBride || !state.workspace) return;
  var weddingDate = state.workspace.bride && state.workspace.bride.weddingDate;
  if (!weddingDate) { panel.innerHTML = ''; return; }

  var cache = state.workspace.availabilityCache;
  if (cache && cache.weddingDate === weddingDate && typeof cache.html === 'string') {
    panel.innerHTML = cache.html;
    return;
  }
  if (state.workspace._availabilityInFlight === weddingDate) return;
  state.workspace._availabilityInFlight = weddingDate;

  panel.innerHTML = '<div style="color:var(--muted);font-size:13px;padding:8px 0"><i class="fa-solid fa-spinner fa-spin"></i> Checking date availability...</div>';

  // A1 2026-11-02 defect 4: availability-spinner terminal state. Diagnostic
  // observed the spinner remaining visible with no clear/conflict/error ever
  // rendered, cascading into "page looks still loading" plus hiding any real
  // underlying error. Behind window.JV_PB_AVAILABILITY_TERMINAL_STATE
  // (default OFF), add a hard 15s timeout and render a visible fail-closed
  // banner on timeout/error/empty instead of silently blanking the panel.
  var terminalStateOn_ = window.JV_PB_AVAILABILITY_TERMINAL_STATE === true && pbJfgOnlyScope_();
  var failClosedBanner_ = function (msg) {
    return '<div class="travel-warn-banner" style="flex-direction:column;align-items:flex-start;gap:4px;background:rgba(179,39,39,0.07);border-color:rgba(179,39,39,0.4);font-size:13px">' +
      '<div style="font-weight:600"><i class="fa-solid fa-triangle-exclamation" style="color:#b32727"></i> Availability check could not complete</div>' +
      '<div>' + escapeHtml(String(msg || 'Unknown reason.')) + ' Treat the date as unknown until this is re-run.</div>' +
      '</div>';
  };
  var withTimeout_ = function (p, ms, label) {
    var t;
    return Promise.race([
      p.then(function (v) { if (t) clearTimeout(t); return v; },
             function (e) { if (t) clearTimeout(t); throw e; }),
      new Promise(function (_resolve, reject) {
        t = setTimeout(function () { reject(new Error(label + ' timeout after ' + ms + 'ms')); }, ms);
      })
    ]);
  };

  var pipeline_ = pbCfBuilderRead_('check-availability', { name: state.currentBride, weddingDate: weddingDate }, pbAcceptCfAvailability_)
    .then(function(cfRes) { return cfRes || jvBridge.callAdmin('proposalCheckAvailability', { name: state.currentBride, weddingDate: weddingDate }); });
  if (terminalStateOn_) {
    pipeline_ = withTimeout_(pipeline_, 15000, 'availability');
  }

  pipeline_
    .then(function(res) {
      var d = (res && res.data) || res;
      if (!d || d.status === 'error') {
        if (terminalStateOn_) {
          var errMsg = (d && d.message) ? d.message : 'The upstream service returned no result.';
          var errHtml = failClosedBanner_(errMsg);
          panel.innerHTML = errHtml;
          cacheAvailabilityHtml_(weddingDate, errHtml);
        } else {
          panel.innerHTML = '';
          cacheAvailabilityHtml_(weddingDate, '');
        }
        return;
      }

      var weddings = d.sameDayWeddings || [];
      var ebm = d.ebmEvents || [];
      var committed = d.totalArtistsCommitted || 0;
      var html;

      if (!d.hasConflicts) {
        html = '<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:rgba(76,175,80,0.08);border:1px solid rgba(76,175,80,0.25);border-radius:8px;font-size:13px;color:#2e7d32">' +
          '<i class="fa-solid fa-circle-check"></i> No other bookings on this date — date is clear.</div>';
      } else {
        html = '<div class="travel-warn-banner" style="flex-direction:column;align-items:flex-start;gap:6px;background:rgba(255,152,0,0.07);border-color:rgba(255,152,0,0.4)">' +
          '<div style="font-weight:600;font-size:14px"><i class="fa-solid fa-triangle-exclamation" style="color:#e65100"></i> Date Conflict — ' + committed + ' artist' + (committed !== 1 ? 's' : '') + ' already committed</div>';

        if (weddings.length) {
          html += '<div style="font-size:13px;font-weight:600;margin-top:4px">Other Weddings</div>';
          weddings.forEach(function(w) {
            html += '<div style="font-size:13px;padding-left:8px">' +
              '<strong>' + escapeHtml(w.name) + '</strong> · ' + w.artistsRequired + ' artist' + (w.artistsRequired !== 1 ? 's' : '') +
              (w.artists && w.artists.length ? ' (' + w.artists.map(function(a) { return escapeHtml(a); }).join(', ') + ')' : '') +
              '</div>';
          });
        }
        if (ebm.length) {
          html += '<div style="font-size:13px;font-weight:600;margin-top:4px">EBM Events</div>';
          ebm.forEach(function(e) {
            html += '<div style="font-size:13px;padding-left:8px">' +
              '<strong>' + escapeHtml(e.name) + '</strong> · ' + e.artistsRequired + ' artist' + (e.artistsRequired !== 1 ? 's' : '') +
              (e.artists && e.artists.length ? ' (' + e.artists.map(function(a) { return escapeHtml(a); }).join(', ') + ')' : '') +
              '</div>';
          });
        }
        html += '</div>';
      }

      panel.innerHTML = html;
      cacheAvailabilityHtml_(weddingDate, html);
    })
    .catch(function(err) {
      if (terminalStateOn_) {
        var msg = (err && err.message) ? err.message : 'Unexpected error while checking availability.';
        var errHtml2 = failClosedBanner_(msg);
        panel.innerHTML = errHtml2;
        // Do NOT cache a fail-closed banner on exception: a transient network
        // blip should re-attempt on the next render, not stick as "unknown"
        // for the whole workspace session.
      } else {
        panel.innerHTML = '';
      }
    })
    .then(function() {
      if (state.workspace && state.workspace._availabilityInFlight === weddingDate) {
        state.workspace._availabilityInFlight = null;
      }
    });
}

function cacheAvailabilityHtml_(weddingDate, html) {
  if (!state.workspace) return;
  state.workspace.availabilityCache = { weddingDate: weddingDate, html: html };
}

/* =================================================================
   WEDDING DATE CHANGE - enquiry/quote stage (booked:false only)
   BRIEFS/BUILD-proposal-builder-agent-role1-wedding-date-change-2026-08-24.md
   Calls the shared role-8 endpoint (bridge_admin_weddingDateChange, CRM
   WeddingDateChange.gs) with booked:false, then optionally chains into the
   existing proposalRecalculate save path so the date-change and the recalc
   read as one continuous action. This page never has a booked bride loaded
   (Proposal Builder is enquiry/quote stage only), so booked is always false
   here; the booked:true / calendar-move path is role 3's (Admin CRM) surface.
   ================================================================= */
var _wdcInFlight = false;

function renderWeddingDateDisplay_(bride) {
  var iso = String((bride && bride.weddingDate) || '').trim();
  if (!iso) {
    return '<button type="button" class="btn-ghost btn-sm" onclick="openWeddingDateEditor_()" style="padding:4px 10px">' +
      '<i class="fa-solid fa-calendar-plus"></i> Set wedding date</button>';
  }
  return '<span class="wd-display" onclick="openWeddingDateEditor_()" style="cursor:pointer;border-bottom:1px dashed var(--muted,#9a918a)" title="Click to change">' +
    escapeHtml(formatDate(iso)) + '</span>';
}

function openWeddingDateEditor_() {
  var wrap = document.getElementById('weddingDateFieldWrap');
  if (!wrap || _wdcInFlight) return;
  var bride = (state.workspace && state.workspace.bride) || {};
  var iso = String(bride.weddingDate || '').trim();

  wrap.innerHTML =
    '<div style="display:flex;flex-direction:column;gap:6px">' +
      '<div style="display:flex;align-items:center;gap:6px">' +
        '<input type="date" id="weddingDateInput" value="' + escapeHtml(iso) + '" ' +
          'style="border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium,1rem);padding:4px 10px;font-size:13px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
        '<button type="button" class="btn-ghost btn-sm" onclick="cancelWeddingDateEdit_()" style="padding:4px 8px">Cancel</button>' +
      '</div>' +
      '<label style="display:flex;align-items:center;gap:6px;font-size:12px;color:var(--muted);cursor:pointer">' +
        '<input type="checkbox" id="weddingDateRecalcToggle" checked style="margin:0"> Recalculate pricing now?' +
      '</label>' +
      '<button type="button" class="btn-primary btn-sm" onclick="commitWeddingDateChange_()" style="align-self:flex-start;padding:5px 14px">Save date</button>' +
      '<div id="weddingDateStatusLine" style="font-size:12px"></div>' +
    '</div>';

  var input = document.getElementById('weddingDateInput');
  if (input) input.focus();
}

function cancelWeddingDateEdit_() {
  var wrap = document.getElementById('weddingDateFieldWrap');
  if (!wrap || !state.workspace) return;
  wrap.innerHTML = renderWeddingDateDisplay_(state.workspace.bride || {});
}

function _wdcSetStatus_(msg, tone) {
  var line = document.getElementById('weddingDateStatusLine');
  if (!line) return;
  var colors = { ok: '#2e7d32', warn: '#e65100', error: '#c94040' };
  line.style.color = colors[tone] || 'var(--muted)';
  line.innerHTML = msg;
}

function commitWeddingDateChange_() {
  if (_wdcInFlight) return;
  if (!state.currentBride || !state.workspace) return;
  var bride = state.workspace.bride || {};
  var brideUid = String(state.workspace.brideUid || '').trim();
  if (!brideUid) {
    _wdcSetStatus_('Cannot save: bride identity not loaded. Hard-refresh the page.', 'error');
    return;
  }
  var email = String(bride.email || '').trim();
  if (!email) {
    _wdcSetStatus_('No email on file for this enquiry, cannot change the date safely.', 'error');
    return;
  }

  var input = document.getElementById('weddingDateInput');
  var recalcToggle = document.getElementById('weddingDateRecalcToggle');
  var newDateIso = input ? String(input.value || '').trim() : '';
  if (!newDateIso) {
    _wdcSetStatus_('Pick a date first.', 'error');
    return;
  }
  var oldDateIso = String(bride.weddingDate || '').trim();
  var wantsRecalc = !!(recalcToggle && recalcToggle.checked);

  if (newDateIso === oldDateIso) {
    // Nothing changed, collapse back to the display state without a round-trip.
    cancelWeddingDateEdit_();
    return;
  }

  _wdcInFlight = true;
  var saveBtn = document.querySelector('#weddingDateFieldWrap .btn-primary');
  if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }
  if (input) input.disabled = true;
  if (recalcToggle) recalcToggle.disabled = true;
  _wdcSetStatus_('<i class="fa-solid fa-spinner fa-spin"></i> Saving new date...', null);

  var oldTotal = Number((state.workspace.calculation && state.workspace.calculation.total) || 0);

  jvBridge.callAdmin('bridge_admin_weddingDateChange', {
    brideUid: brideUid,
    brideEmail: email,
    newDateIso: newDateIso,
    booked: false
  }).then(function(res) {
    var data = (res && res.data) || res;
    if (!data || data.ok === false) {
      _wdcInFlight = false;
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save date'; }
      if (input) input.disabled = false;
      if (recalcToggle) recalcToggle.disabled = false;
      _wdcSetStatus_(escapeHtml((data && data.error) || 'Could not save the new date.'), 'error');
      return;
    }

    // CRM write succeeded. Update local state immediately so the display and the
    // availability-check cache key move to the new date even before a reload.
    state.workspace.bride.weddingDate = newDateIso;

    if (!wantsRecalc) {
      _wdcInFlight = false;
      _wdcSetStatus_('<i class="fa-solid fa-circle-check"></i> Date updated to ' + escapeHtml(formatDate(newDateIso)) +
        '. Pricing left unchanged, use Recalculate when ready.', 'ok');
      // Re-render the read-only display; leave the rest of the page (quote, line
      // items, availability panel) untouched per the toggle-OFF requirement. The
      // existing manual Recalculate button reads state.currentBride fresh each
      // time it's clicked, so it still works correctly on the new date afterward.
      var wrapOff = document.getElementById('weddingDateFieldWrap');
      if (wrapOff) wrapOff.innerHTML = renderWeddingDateDisplay_(state.workspace.bride);
      var scheduleTitleOff = document.querySelector('#studioScheduleCard .card-title');
      if (scheduleTitleOff) {
        scheduleTitleOff.innerHTML = '<i class="fa-solid fa-calendar-day"></i> Studio Schedule: ' + escapeHtml(formatDate(newDateIso));
      }
      _recomputeTravelOnChange_();
      return;
    }

    _wdcSetStatus_('<i class="fa-solid fa-spinner fa-spin"></i> Date saved, recalculating pricing for the new date...', null);
    // The date is already saved. Force a fresh Cloudflare travel calculation
    // in the following Save; the engine checks that its D1 date has caught up.
    _travelAutoRequired = true;

    // Chain into the same recalculate path the manual Recalculate button uses
    // (proposalRecalculate via flushToGas_ then pbRecalcWarm_), so this reuses the
    // existing PROPOSAL_CALC_JSON / Proposal Tokens sync (proposalBuilder_syncTokenLineItems_)
    // rather than a second parallel write path.
    flushToGas_().then(function() {
      _wdcInFlight = false;
      var newTotal = Number((state.workspace.calculation && state.workspace.calculation.total) || 0);
      var delta = newTotal - oldTotal;
      var deltaLabel = delta === 0 ? 'no change' :
        (delta > 0 ? '+$' + delta.toFixed(2) : '-$' + Math.abs(delta).toFixed(2));
      // Full reload (selectBride) so every surface fed by the workspace, the
      // Wedding Date field itself, the availability-check cache (re-keyed on the
      // new weddingDate), the Studio Schedule card, line items and quote total,
      // repaints from the server's canonical post-recalc state rather than a
      // partial client patch.
      selectBride(state.currentBride).then(function() {
        _recomputeTravelOnChange_();
        showBanner('Wedding date updated to ' + formatDate(newDateIso) + '. Pricing recalculated (total ' + deltaLabel + ').',
          delta === 0 ? 'success' : 'warn');
      });
    }).catch(function(err) {
      _wdcInFlight = false;
      _wdcSetStatus_('Date saved, but recalculation failed: ' + escapeHtml(getErrorMessage(err)) +
        '. Use the Recalculate button to retry, the date change itself is safe.', 'warn');
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save date'; }
      if (input) input.disabled = false;
      if (recalcToggle) recalcToggle.disabled = false;
    });
  }).catch(function(err) {
    _wdcInFlight = false;
    if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save date'; }
    if (input) input.disabled = false;
    if (recalcToggle) recalcToggle.disabled = false;
    _wdcSetStatus_(escapeHtml(getErrorMessage(err)), 'error');
  });
}

function calcSubtotal(items) {
  var s = 0;
  for (var i = 0; i < items.length; i++) {
    var qty = Number(items[i].qty || items[i].quantity || 0);
    var rate = Number(items[i].unitPrice || items[i].rate || 0);
    s += qty * rate;
  }
  return Math.round(s * 100) / 100;
}

/*
 * A1 2026-11-02 defect 1: snapshot-total vs recalc hard invariant.
 *
 * Diagnostic (BRIEFS/DIAGNOSTIC-a1-proposal-builder-e2e-2026-11-02.md) found
 * the live Builder rendered AUD 640.00 for a canonical AUD 2.00 D1 snapshot
 * because recalcAndMerge_ regenerates party-service lines on top of the stored
 * custom line. Clicking Save would overwrite D1. This gate centralises the
 * check: if the loaded ACTIVE snapshot total (proposal.tokenTotal, surfaced
 * by Worker Lane 1 d19d058ef) differs from the currently-rendered recalc
 * total by more than half a cent, the function returns a mismatch descriptor
 * callers use to disable Save/Preview/Publish/Send/Re-send and render a
 * banner surfacing both totals so the admin never silently persists a wrong
 * amount.
 *
 * Behind dark flag window.JV_PB_TOTAL_MISMATCH_GUARD (default OFF). Also
 * returns null when tokenTotal is absent (older Worker, no snapshot), so no
 * false alarms before Lane 1 is live. All values compared in dollars.
 */
function pbTotalMismatchGate_(renderedTotal) {
  if (window.JV_PB_TOTAL_MISMATCH_GUARD !== true || !pbJfgOnlyScope_()) return null;
  var ws = state && state.workspace;
  if (!ws || !ws.proposal) return null;
  var snapshotTotal = ws.proposal.tokenTotal;
  if (snapshotTotal == null) return null;
  var snap = Number(snapshotTotal);
  var cur = Number(renderedTotal);
  if (!isFinite(snap) || !isFinite(cur)) return null;
  // Half-a-cent tolerance to absorb GST rounding; mismatches here are
  // dollars-scale, not sub-cent.
  var mismatch = Math.abs(snap - cur) > 0.005;
  return {
    mismatch: mismatch,
    snapshotTotal: snap,
    renderedTotal: cur,
    tokenStatus: ws.proposal.tokenStatus || null,
    token: ws.proposal.token || null,
  };
}

/*
 * Render the mismatch banner block (returns HTML string, or '' when the gate
 * is OFF or there is no mismatch). Callers prepend this before outward
 * buttons and skip outward-button rendering when the gate returns mismatch.
 */
function pbTotalMismatchBanner_(gate) {
  if (!gate || !gate.mismatch) return '';
  var fmt = function (v) {
    return 'A$' + (Number(v) || 0).toFixed(2);
  };
  return '<div class="travel-warn-banner" style="flex-direction:column;align-items:flex-start;gap:6px;background:rgba(179,39,39,0.07);border-color:rgba(179,39,39,0.4);margin:12px 0">' +
    '<div style="font-weight:600;font-size:14px"><i class="fa-solid fa-triangle-exclamation" style="color:#b32727"></i> Snapshot total does not match recalculated total</div>' +
    '<div style="font-size:13px">Stored snapshot (' + escapeHtml(gate.tokenStatus || 'ACTIVE') + '): <strong>' + fmt(gate.snapshotTotal) + '</strong></div>' +
    '<div style="font-size:13px">Currently rendered: <strong>' + fmt(gate.renderedTotal) + '</strong></div>' +
    '<div style="font-size:12px;color:var(--muted)">Save, Preview, Publish, Send and Re-send are blocked until the two match. Investigate the recalc divergence; do not persist.</div>' +
    '</div>';
}

function updateLineItem(idx, field, value) {
  // Use _currentDisplayItems for _rowId lookup (set during renderStageBuilder).
  var displayItems = _currentDisplayItems || [];
  var it = displayItems[idx];
  if (!it) return;
  var rowId = it._rowId || '';
  var newQty = (field === 'qty') ? (Number(value) || 0) : null;

  if (rowId) {
    // Sparse override: write only this row's changed field. Survives recalcs.
    var entry = (state.priceOverrides || {})[rowId] || {};
    if (field === 'qty')  entry.qty  = Number(value) || 0;
    if (field === 'rate') entry.rate = Number(value) || 0;
    if (!state.priceOverrides) state.priceOverrides = {};
    state.priceOverrides[rowId] = entry;
  } else {
    // Fallback for custom rows (no _rowId yet): find the actual row in
    // editedLineItems by its stable identity, NOT by `idx` (2026-07-03 fix
    // -- `idx` is a position in `displayItems`, a different array with
    // different indexing than editedLineItems, so the old `eli[idx]` could
    // silently mutate the wrong row).
    var eli = state.editedLineItems;
    if (!eli) return;
    var matchKey = _proposalLineItemKey_(it);
    var eliItem = null;
    for (var m = 0; m < eli.length; m++) {
      if (it._localId && eli[m]._localId === it._localId) { eliItem = eli[m]; break; }
      if (!it._localId && _proposalLineItemKey_(eli[m]) === matchKey) { eliItem = eli[m]; break; }
    }
    if (!eliItem) return;
    if (field === 'qty')  { eliItem.qty = Number(value) || 0; eliItem.quantity = eliItem.qty; }
    if (field === 'rate') { eliItem.unitPrice = Number(value) || 0; eliItem.rate = eliItem.unitPrice; }
    // Recompute total after either mutation. Without this, a qty/rate edit on a
    // custom/catalogue row (no _rowId yet) leaves a stale total that flushToGas_
    // persists as-is (2026-08-24, Angela Crnogorac proposal: qty bumped to 6 but
    // total stayed at the qty-1 value).
    eliItem.total = (eliItem.unitPrice || eliItem.rate || 0) * (eliItem.qty || eliItem.quantity || 0);
    eliItem._userEdited = true;
  }

  // A row zeroed to qty 0 must disappear from the table, not just show a $0
  // total in place. Full re-render (same path removeLineItem uses) so the
  // renderStageBuilder qty===0 filter drops it immediately.
  if (newQty === 0) {
    state.lineItemsManuallyEdited = true;
    markDirty_();
    renderStageBuilder();
    return;
  }

  // Compute the display values for the in-place DOM update.
  var dispRate = (rowId && state.priceOverrides[rowId] && state.priceOverrides[rowId].rate !== undefined)
    ? state.priceOverrides[rowId].rate
    : (it.unitPrice || it.rate || 0);
  var dispQty  = (rowId && state.priceOverrides[rowId] && state.priceOverrides[rowId].qty  !== undefined)
    ? state.priceOverrides[rowId].qty
    : (it.qty || it.quantity || 1);
  var newTotal = dispRate * dispQty;

  // Update total cell in place (no full re-render to preserve input focus).
  var totalCell = document.getElementById('li-total-' + idx);
  if (totalCell) {
    var row = totalCell.closest('tr');
    var isTaxable = row ? row.getAttribute('data-li-taxable') === '1' : true;
    totalCell.innerHTML = formatAudGst(newTotal.toFixed(2), isTaxable);
    if (row) {
      row.classList.add('li-row-locked');
      // Add rose dot + Reset link if not already present.
      var descSpan = row.querySelector('.li-row-desc span');
      if (descSpan && rowId && !descSpan.querySelector('.li-override-dot')) {
        var dot = document.createElement('span');
        dot.className = 'li-override-dot';
        dot.title = 'Custom price set';
        dot.style.cssText = 'display:inline-block;width:7px;height:7px;border-radius:50%;background:var(--brand-primary);margin-left:4px;vertical-align:middle';
        descSpan.appendChild(dot);
        var resetBtn = document.createElement('button');
        resetBtn.className = 'btn-link-inline li-reset-btn';
        resetBtn.title = 'Reset to calculated price';
        resetBtn.style.cssText = 'font-size:11px;color:var(--muted);text-decoration:underline;cursor:pointer;background:none;border:none;padding:0 0 0 2px';
        resetBtn.textContent = 'Reset';
        resetBtn.setAttribute('onclick', 'resetRowOverride_(\'' + rowId + '\')');
        descSpan.appendChild(resetBtn);
      } else if (descSpan && !rowId && !descSpan.querySelector('.li-lock-icon')) {
        var icon = document.createElement('i');
        icon.className = 'fa-solid fa-lock li-lock-icon';
        icon.title = 'Price manually set';
        descSpan.appendChild(icon);
      }
    }
  }

  state.lineItemsManuallyEdited = true;
  markDirty_();
  refreshSummary(_currentDisplayItems.map(function(di, k) {
    if (k !== idx) return di;
    return Object.assign({}, di, { unitPrice: dispRate, rate: dispRate, qty: dispQty, total: newTotal });
  }));
}

function resetRowOverride_(rowId) {
  if (!rowId || !state.priceOverrides) return;
  // P2j: if this is the wedding-day travel row, route through the full sweep + flush.
  // applyTravelOverride auto-persists a travel override to GAS, so the inline Reset
  // must un-persist (a bare delete leaves the override stored, and it returns on reload).
  var di = (_currentDisplayItems || []).filter(function(it) { return it._rowId === rowId; })[0];
  if (di) {
    var d = String(di.description || di.name || '').toLowerCase();
    var c = String(di.category || '').toLowerCase();
    if ((c.indexOf('travel') >= 0 || d.indexOf('travel') >= 0) &&
        d.indexOf('trial') < 0 && d.indexOf('spray') < 0) {
      clearTravelOverrideAndPersist_();
      return;
    }
  }
  delete state.priceOverrides[rowId];
  markDirty_();
  renderStageBuilder();
}

function resetAllOverrides_() {
  state.priceOverrides = {};
  markDirty_();
  renderStageBuilder();
}

function removeLineItem(idx) {
  // `idx` is a position in `_currentDisplayItems` (the rendered/merged array),
  // which is NOT the same indexing as `state.editedLineItems` (2026-07-03 fix).
  // Resolve the actual row first, then remove by its stable identity
  // (_rowId once persisted, _localId/key before that) instead of by index.
  var item = _currentDisplayItems && _currentDisplayItems[idx];
  if (!item) return;

  // Travel lines are calc-derived (not in editedLineItems), so the normal splice can't
  // remove them. Mark the line suppressed instead so it stays gone across recalcs until
  // restored, then re-derive locally. (Per-line independent: only this line is removed.)
  var travelKey = travelLineKey_(item);
  if (travelKey) {
    if (!state.travelSuppressions) state.travelSuppressions = {};
    state.travelSuppressions[travelKey] = true;
    state.lineItemsManuallyEdited = true;
    recalcAndMerge_();
    renderStageBuilder();
    markDirty_();
    return;
  }

  if (item._rowId) {
    // Calc-derived row (party line, or a custom/catalogue row that already
    // round-tripped through a save). Zero it via priceOverrides, same channel
    // updateLineItem uses, so removal survives a recalc the same way an
    // override does. renderStageBuilder's qty===0 filter drops it from view.
    if (!state.priceOverrides) state.priceOverrides = {};
    var ov = state.priceOverrides[item._rowId] || {};
    ov.qty = 0;
    state.priceOverrides[item._rowId] = ov;
    state.lineItemsManuallyEdited = true;
    markDirty_();
    renderStageBuilder();
    return;
  }

  // Pre-save custom row (no _rowId yet): remove it from editedLineItems by its
  // stable local key, not by the display index.
  if (!state.editedLineItems) return;
  var removeKey = _proposalLineItemKey_(item);
  var pos = -1;
  for (var i = 0; i < state.editedLineItems.length; i++) {
    var eli = state.editedLineItems[i];
    if (item._localId && eli._localId === item._localId) { pos = i; break; }
    if (!item._localId && _proposalLineItemKey_(eli) === removeKey) { pos = i; break; }
  }
  if (pos >= 0) state.editedLineItems.splice(pos, 1);
  state.lineItemsManuallyEdited = true;
  markDirty_();
  renderStageBuilder();
}

// Restore a previously-deleted travel line. Clears the sticky key; calcLocalEstimate_'s
// structured-array reconciliation rebuilds the line from calc.weddingTravel / trialTravel
// / sprayTanTravel at the original fee.
function restoreTravelLine_(key) {
  if (state.travelSuppressions && state.travelSuppressions[key]) {
    delete state.travelSuppressions[key];
  }
  recalcAndMerge_();
  renderStageBuilder();
  markDirty_();
}

// Builds the "Removed travel" strip listing each suppressed travel line with a Restore
// button. Labels + fees are read from the always-intact structured travel arrays so the
// strip is accurate even after the line was stripped from the line-item list.
function buildSuppressedTravelRestoreHtml_(calc) {
  var sup = state.travelSuppressions || {};
  if (!Object.keys(sup).length) return '';
  var rows = [], matched = {};
  // Key off the STABLE travel id (type + real artist), the same key the delete
  // handler records. This makes a deleted line match its structured entry, so
  // "Removed travel" shows exactly one restorable row (no orphan pile-up).
  var consider = function(type, artist, label, fee) {
    var key = travelId_(type, artist || 'Artist');
    if (key && sup[key] && !matched[key]) {
      matched[key] = true;
      rows.push({ key: key, label: label, fee: fee || 0 });
    }
  };
  var wt = (calc && (calc.weddingTravel || (calc.travel && calc.travel.weddingTravel))) || [];
  wt.forEach(function(t) { consider('wedding', t.artist, 'Wedding Day Travel' + _TRAVEL_DASH + (t.artist || 'Artist'), t.fee); });
  var tt = calc && (calc.trialTravel || (calc.travel && calc.travel.trialTravel));
  if (tt) consider('trial', trialTravelModeSuppressed_(calc) ? '' : tt.artist, trialTravelModeSuppressed_(calc) ? TRIAL_TRAVEL_TBC_LABEL_ : ('Trial Travel' + _TRAVEL_DASH + (tt.artist || 'Artist')), trialTravelModeSuppressed_(calc) ? null : tt.fee);
  var stt = calc && (calc.sprayTanTravel || (calc.travel && calc.travel.sprayTanTravel));
  if (stt) consider('spray', stt.artist, 'Spray Tan Travel', stt.fee);
  // Orphan keys with no structured match: still offer a generic restore.
  Object.keys(sup).forEach(function(k) {
    if (matched[k]) return;
    matched[k] = true;
    rows.push({ key: k, label: k.replace(/\b\w/g, function(c) { return c.toUpperCase(); }), fee: 0 });
  });
  if (!rows.length) return '';
  var esc = function(s) { return String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'"); };
  var html = '<div class="removed-travel-strip" style="margin-top:10px;padding:10px 14px;' +
    'background:var(--brand-bg-soft,#f6f1ee);border-radius:var(--radius-medium,1rem);font-size:13px;">' +
    '<div style="font-weight:600;color:var(--brand-text,#2f2524);margin-bottom:6px;">' +
      '<i class="fa-solid fa-eye-slash"></i> Removed travel</div>';
  rows.forEach(function(r) {
    html += '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:4px 0;">' +
      '<span style="color:var(--brand-text,#2f2524);">' + escapeHtml(r.label) +
        (r.fee ? ', ' + formatCurrency(r.fee) : '') + '</span>' +
      '<button class="btn btn-ghost btn-sm" onclick="restoreTravelLine_(\'' + esc(r.key) + '\')">' +
        '<i class="fa-solid fa-rotate-left"></i> Restore</button>' +
    '</div>';
  });
  html += '</div>';
  return html;
}

// Stable client-side identity for a custom/catalogue row before it has been
// through a save round-trip (that is when GAS stamps a real _rowId). Lets
// removeLineItem/qty-zero and the dedup check below target the exact row
// instead of a raw array/display index.
function _proposalLocalRowId_() {
  return 'local_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

// Matching key used to detect "this custom/catalogue row already exists":
// _rowId when stamped (post-save), else category+description as the fallback
// bridge (mirrors the server-side key in proposalBuilder_stampRowIds_).
function _proposalLineItemKey_(li) {
  if (!li) return '';
  if (li._rowId) return 'id:' + li._rowId;
  return 'k:' + String(li.category || '').toLowerCase() + '|' + String(li.description || li.name || '').toLowerCase();
}

// MONEY/DATA BUG FIX (2026-07-21): proposalRecalculateWithAddress has no mechanism
// to accept a client-sent editedLineItems payload (see BRIEFS/BRIEF-proposal-builder-
// lineitems-not-committing-2026-07-21.md) -- it always returns a server-rebuilt
// lineItems array, and all three callers of that route (selectLocationAddress,
// applyArtistOverride, commitArtistsField_) then unconditionally null out
// state.editedLineItems. If the admin added a custom/catalogue line item on Page 2
// and then, before a flush fired, changed an address or bumped the artist count,
// that line item was silently dropped -- never sent to the server, never in
// PROPOSAL_CALC_JSON, absent from Page 3 and the bride preview.
// Fix: re-append any custom (custom:true) rows from the about-to-be-cleared
// editedLineItems cache onto the fresh server calc BEFORE nulling it out. This never
// touches the server's freshly-computed travel/pricing lines (the reason the null-out
// exists in the first place, commit d078cdbf 2026-05-18) -- it only restores rows the
// server calculator doesn't know how to produce (manual additions), skipping any that
// are already present in the fresh calc (matched by the same _rowId/category+description
// key the server uses in proposalBuilder_stampRowIds_).
function _pbReappendCustomLineItems_(newCalc, prevEditedLineItems) {
  if (!newCalc || !Array.isArray(prevEditedLineItems) || !prevEditedLineItems.length) return;
  var customRows = prevEditedLineItems.filter(function(li) { return li && li.custom; });
  if (!customRows.length) return;
  var freshItems = (newCalc.lineItems || newCalc.normalizedLineItems || []).slice();
  var freshKeys = {};
  freshItems.forEach(function(li) { freshKeys[_proposalLineItemKey_(li)] = true; });
  var appended = false;
  customRows.forEach(function(li) {
    var key = _proposalLineItemKey_(li);
    if (freshKeys[key]) return; // already present in the server's fresh calc
    freshItems.push(li);
    freshKeys[key] = true;
    appended = true;
  });
  if (!appended) return;
  newCalc.lineItems = freshItems;
  newCalc.normalizedLineItems = freshItems.slice();
  var newSubtotal = 0;
  freshItems.forEach(function(li) { newSubtotal += Number(li.total || 0); });
  newCalc.subtotal = Math.round(newSubtotal * 100) / 100;
  newCalc.gst      = Math.round(newCalc.subtotal * 0.10 * 100) / 100;
  newCalc.total    = Math.round((newCalc.subtotal + newCalc.gst) * 100) / 100;
}

/* =================================================================
   PACKAGE NOTES (2026-08-04) -- non-priced free-text disclosures.

   Jess: "add notes like 'Destination Wedding: Accommodation to be priced closer to the
   date (for 1 x night prior to wedding)'. Its not an included service but a note that
   there will be an additional cost added on to the package that they are agreeing to."

   THE ONE HARD RULE: a note is NOT a line item. It has no qty, no rate, no total, and it
   MUST NEVER be pushed into a lineItems array. Every subtotal/GST/total in this builder is
   derived by summing lineItems (calcLocalEstimate_, pvSaveAndExitVariantBuilder_), so a
   note living in that array would be summed and would corrupt the quote. Notes travel in
   calc.packageNotes and render in their own block.

   SCOPE: notes added while editing a package option belong to THAT package (saved onto the
   variant at Save Option). Notes added on the base proposal apply to the whole proposal.
   Both are stored the same way; which one you are editing is simply which calc is active.

   TONE: neutral, per Jess -- "its in our t's and c's anyway so its just highlighting the
   fact". Not a warning. No red, no alarm icon.
   ================================================================= */

/**
 * Renders the package-notes block for the active calculation.
 * @param {Object} calc the active calculation object.
 * @returns {string} HTML.
 * @private
 */
function buildPackageNotesHtml_(calc) {
  var notes = (calc && Array.isArray(calc.packageNotes)) ? calc.packageNotes : [];
  var inVariant = (state.editingVariantIdx != null);
  var scopeLabel = inVariant ? 'this package option' : 'the whole proposal';

  var html = '<div class="card" style="margin-top:14px">' +
    '<div class="card-title" style="display:flex;align-items:center;justify-content:space-between">' +
      '<span><i class="fa-solid fa-note-sticky"></i> Notes</span>' +
    '</div>' +
    '<div style="font-size:13px;color:var(--muted);margin-bottom:10px">' +
      'Free-text notes shown with the package. Not a service, no price attached. ' +
      'Applies to <strong>' + escapeHtml(scopeLabel) + '</strong>.' +
    '</div>';

  if (notes.length) {
    html += '<div style="display:flex;flex-direction:column;gap:8px;margin-bottom:10px">';
    for (var i = 0; i < notes.length; i++) {
      html += '<div style="display:flex;gap:8px;align-items:flex-start;padding:10px 12px;' +
        'background:var(--brand-bg-soft,#faf7f4);border:1px solid var(--border-color,#e8ddd3);' +
        'border-radius:var(--radius-medium,1rem)">' +
        '<div style="flex:1;font-size:13px;line-height:1.5;white-space:pre-line">' +
          escapeHtml(String(notes[i].text || '')) +
        '</div>' +
        '<button class="btn btn-ghost btn-sm" onclick="removePackageNote(' + i + ')" ' +
          'title="Remove note" style="flex:none">' +
          '<i class="fa-solid fa-xmark"></i>' +
        '</button>' +
        '</div>';
    }
    html += '</div>';
  }

  html += '<div style="display:flex;gap:8px;align-items:flex-start">' +
      '<textarea id="newPackageNote" rows="2" placeholder="e.g. Destination Wedding: ' +
        'Accommodation to be priced closer to the date (for 1 x night prior to wedding)" ' +
        'style="flex:1;border:1px solid var(--border-color,#e8ddd3);' +
        'border-radius:var(--radius-medium,1rem);padding:8px 12px;font-size:13px;' +
        'font-family:inherit;background:#fff;color:var(--brand-text);resize:vertical"></textarea>' +
      '<button class="btn btn-ghost btn-sm" onclick="addPackageNote()" style="flex:none">' +
        '<i class="fa-solid fa-plus"></i> Add note' +
      '</button>' +
    '</div>' +
    '</div>';

  return html;
}

/**
 * Adds a note to the active calculation. Local-only per Path 2: persists on Save.
 */
function addPackageNote() {
  if (!state.workspace || !state.workspace.calculation) return;
  var el = document.getElementById('newPackageNote');
  var text = el ? String(el.value || '').trim() : '';
  if (!text) return;

  var calc = state.workspace.calculation;
  if (!Array.isArray(calc.packageNotes)) calc.packageNotes = [];
  calc.packageNotes.push({
    // Index-independent id so a later reorder/delete cannot silently re-target a note.
    id: 'note_' + calc.packageNotes.length + '_' + String(text.length),
    text: text,
    scope: (state.editingVariantIdx != null) ? 'package' : 'proposal'
  });

  // Deliberately NO recalcAndMerge_ here: notes carry no money, so there is nothing to
  // re-derive, and calling it would be a misleading signal that totals changed.
  markDirty_();
  renderActiveStage();
}

/**
 * Removes a note by index from the active calculation.
 * @param {number} idx
 */
function removePackageNote(idx) {
  if (!state.workspace || !state.workspace.calculation) return;
  var calc = state.workspace.calculation;
  if (!Array.isArray(calc.packageNotes)) return;
  if (idx < 0 || idx >= calc.packageNotes.length) return;
  calc.packageNotes.splice(idx, 1);
  markDirty_();
  renderActiveStage();
}

function addLineItem() {
  var desc = document.getElementById('newItemDesc').value.trim();
  var qty = Number(document.getElementById('newItemQty').value) || 1;
  var rate = Number(document.getElementById('newItemRate').value) || 0;
  if (!desc) { showBanner('Enter a description for the line item.', 'warn'); return; }
  if (!state.editedLineItems) state.editedLineItems = [];
  var newItem = {
    description: desc, name: desc,
    qty: qty, quantity: qty,
    unitPrice: rate, rate: rate,
    total: qty * rate,
    custom: true,
    _localId: _proposalLocalRowId_()
  };
  // Dedup: don't add a second copy of a row that already exists in the
  // current display/edited list (matches by _rowId if stamped, else by
  // category+description, the same bridge key the server uses).
  var key = _proposalLineItemKey_(newItem);
  var already = (_currentDisplayItems || []).concat(state.editedLineItems)
    .some(function(li) { return _proposalLineItemKey_(li) === key; });
  if (already) {
    showBanner('That line item is already on the proposal.', 'warn');
    return;
  }
  state.editedLineItems.push(newItem);
  state.lineItemsManuallyEdited = true;
  markDirty_(); // M2 (2026-07-09): flag unsaved so the added item persists + the Unsaved pill shows
  renderStageBuilder();
}

/* -- Service Catalog Picker -- */

function toggleServiceCatalog() {
  var panel = document.getElementById('svcCatalogPanel');
  var customPanel = document.getElementById('svcCustomPanel');
  if (!panel) return;
  if (panel.style.display === 'none') {
    panel.style.display = 'block';
    if (customPanel) customPanel.style.display = 'none';
    renderServiceCatalog_();
  } else {
    panel.style.display = 'none';
  }
}

function toggleCustomItem() {
  var panel = document.getElementById('svcCustomPanel');
  var catPanel = document.getElementById('svcCatalogPanel');
  if (!panel) return;
  if (panel.style.display === 'none') {
    panel.style.display = 'block';
    if (catPanel) catPanel.style.display = 'none';
  } else {
    panel.style.display = 'none';
  }
}

function renderServiceCatalog_() {
  var panel = document.getElementById('svcCatalogPanel');
  if (!panel) return;

  var catalog = (state.workspace && state.workspace.serviceCatalog) || [];
  if (!catalog.length) {
    panel.innerHTML = '<div class="svc-catalog-panel"><p style="color:var(--muted);font-size:13px">No service catalog available.</p></div>';
    return;
  }

  // Group by category
  var groups = {};
  var groupOrder = [];
  for (var i = 0; i < catalog.length; i++) {
    var cat = catalog[i].category || 'Other';
    if (!groups[cat]) { groups[cat] = []; groupOrder.push(cat); }
    groups[cat].push(catalog[i]);
  }

  var html = '<div class="svc-catalog-panel">';
  for (var g = 0; g < groupOrder.length; g++) {
    var groupName = groupOrder[g];
    var items = groups[groupName];
    html += '<div class="svc-cat-label">' + escapeHtml(groupName) + '</div>';
    for (var j = 0; j < items.length; j++) {
      var svc = items[j];
      var rateStr = svc.rate > 0 ? formatCurrency(svc.rate) : (svc.rate === 0 ? 'Complimentary' : formatCurrency(svc.rate));
      var taxStr = svc.taxable ? ' +10% GST' : '';
      html += '<div class="svc-catalog-item" onclick="addCatalogItem(\'' + svc.id + '\')">' +
        '<div style="flex:1">' +
          '<div class="svc-desc">' + escapeHtml(svc.description) + '</div>' +
          (svc.subtitle ? '<div class="svc-sub">' + escapeHtml(svc.subtitle) + '</div>' : '') +
        '</div>' +
        '<div class="svc-rate">' + rateStr + '<span class="svc-tax">' + taxStr + '</span></div>' +
      '</div>';
    }
  }
  html += '</div>';
  panel.innerHTML = html;
}

function addCatalogItem(svcId) {
  var catalog = (state.workspace && state.workspace.serviceCatalog) || [];
  var svc = null;
  for (var i = 0; i < catalog.length; i++) {
    if (catalog[i].id === svcId) { svc = catalog[i]; break; }
  }
  if (!svc) return;

  if (!state.editedLineItems) state.editedLineItems = [];

  // Dedup guard (fixes the Updo-duplication bug, 2026-07-03): if this exact
  // catalogue item is already present (matched by _rowId once persisted, else
  // by category+description), bump its quantity instead of adding a second
  // row. Without this, re-clicking the same catalogue item, or a row that
  // already round-tripped through a save and is now part of the base calc
  // list, produces a second copy on the next render/reload.
  var candidateKey = 'k:' + String((svc.category || '').toLowerCase()) + '|' + String(svc.description || '').toLowerCase();
  var pool = (_currentDisplayItems || []).concat(state.editedLineItems);
  var existingLi = null;
  for (var e = 0; e < pool.length; e++) {
    if (_proposalLineItemKey_(pool[e]) === candidateKey) { existingLi = pool[e]; break; }
  }
  if (existingLi) {
    if (existingLi._rowId) {
      var ov = (state.priceOverrides || {})[existingLi._rowId] || {};
      var curQty = ov.qty !== undefined ? ov.qty : (existingLi.qty || existingLi.quantity || 1);
      ov.qty = curQty + 1;
      if (!state.priceOverrides) state.priceOverrides = {};
      state.priceOverrides[existingLi._rowId] = ov;
    } else {
      var eliMatch = state.editedLineItems.filter(function(li) { return _proposalLineItemKey_(li) === candidateKey; })[0];
      if (eliMatch) {
        eliMatch.qty = (eliMatch.qty || 1) + 1;
        eliMatch.quantity = eliMatch.qty;
        eliMatch.total = eliMatch.qty * (eliMatch.unitPrice || eliMatch.rate || 0);
      }
    }
    state.lineItemsManuallyEdited = true;
    markDirty_(); // M2 (2026-07-09): flag unsaved on catalogue qty bump
    renderStageBuilder();
    showBanner('Increased quantity: ' + svc.description, 'success');
    return;
  }

  state.editedLineItems.push({
    description: svc.description,
    subtitle: svc.subtitle || '',
    name: svc.description,
    qty: 1,
    quantity: 1,
    unitPrice: svc.rate,
    rate: svc.rate,
    total: svc.rate,
    catalogId: svc.id,
    taxable: svc.taxable || false,
    // custom:true marks this as a kept add-on so it survives a party recalc (server
    // carry-forward) AND re-renders in the line-item list (the display re-injects custom
    // rows). Catalogue extras like hairstyle upgrades / spray tan would otherwise vanish
    // on the next recalc, exactly like hand-typed lines did (2026-06-23).
    custom: true,
    _localId: _proposalLocalRowId_(),
    type: (svc.category || '').toLowerCase() === 'travel' ? 'travel'
        : (svc.category || '').toLowerCase() === 'bonus' ? 'bonus'
        : (svc.category || '').toLowerCase() === 'discount' ? 'discount'
        : 'service'
  });
  state.lineItemsManuallyEdited = true;
  markDirty_(); // M2 (2026-07-09): flag unsaved so the catalogue item persists + the Unsaved pill shows
  renderStageBuilder();
  showBanner('Added: ' + svc.description, 'success');
}

var _partyRecalcTimer = null;
var _partyRecalcInFlight = false;

function adjustPartyQty(idx, delta) {
  var party = state.workspace.party || [];
  if (!party[idx]) return;
  party[idx].qty = Math.max(0, (typeof party[idx].qty === 'number' ? party[idx].qty : 1) + delta);
  var el = document.getElementById('pqty' + idx);
  if (el) el.textContent = party[idx].qty;
  schedulePartyRecalc_();
}

function updatePartyService(idx, value) {
  var party = state.workspace.party || [];
  if (!party[idx]) return;
  party[idx].serviceType = value;
  schedulePartyRecalc_();
}

// PATH 2: party changes are instant + local. No debounce, no GAS round-trip.
// Persisted by flushToGas_() on Save / Generate Preview / Send.
// priceOverrides survive party changes by construction (applied on top of fresh
// calc items in renderStageBuilder). No confirm modal needed.
function schedulePartyRecalc_() {
  if (!state.workspace) return;
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  renderActiveStage();
  _recomputeTravelOnChange_();
}

var _travelAutoTimer = null;
var _travelAutoSeq = 0;
var _travelAutoStatus = 'ready';
var _travelAutoRequired = false;
function _travelAutoAddress_(item) {
  if (typeof item === 'string') return item.trim();
  return String(item && (item.address || item.fullAddress || item.formattedAddress) || '').trim();
}
function _recomputeTravelOnChange_() {
  if (_travelAutoTimer) clearTimeout(_travelAutoTimer);
  var seq = ++_travelAutoSeq;
  var ws = state.workspace || {};
  var calc = ws.calculation || {};
  var selected = calc.selectedAddresses && calc.selectedAddresses.wedding || calc.selectedAddress;
  var venue = _travelAutoAddress_(selected) || _travelAutoAddress_(ws.bride && ws.bride.weddingAddressFull) || _travelAutoAddress_(ws.bride && ws.bride.location);
  var email = String(ws.bride && ws.bride.email || '').trim().toLowerCase();
  var brideUid = String(ws.brideUid || '').trim();
  if (!state.currentBride || calc._fromSnapshot) {
    _travelAutoStatus = 'ready';
    return;
  }
  _travelAutoRequired = true;
  if (!venue || !email || !brideUid) {
    _travelAutoStatus = 'error';
    showBanner(!brideUid ? 'Cannot calculate travel: bride identity not loaded. Hard-refresh the page.' : 'A wedding venue address and bride email are needed to calculate travel.', 'error');
    return;
  }
  _travelAutoStatus = 'pending';
  _travelAutoTimer = setTimeout(function() {
    _travelAutoTimer = null;
    var payload = {
      brideName: state.currentBride, brideUid: brideUid, email: email, weddingDate: String(ws.bride && ws.bride.weddingDate || '').slice(0, 10), venueAddress: venue,
      venuePlaceId: selected && selected.placeId || '',
      partyOverrides: (ws.party || []).filter(function(p) { return !(p && p.isManualAddition); }).map(function(p) {
        return { role: p.role, serviceType: p.serviceType, qty: p.qty !== undefined ? Number(p.qty) : 1 };
      }),
      manualAdditions: state.manualPartyMembers || [],
      noTrialRequired: !!state.noTrialRequired,
      bookingTier: state.bookingTier || 'proTeam',
      travelOriginOverride: state.travelOriginOverride,
      timingOverrides: { finishTime: String(calc.finishTime || '').trim(),
        artistsRequired: Number(calc.artistsRequired) || '', estimatedStartTime: String(calc.estimatedStartTime || '').trim() },
      timingLocks: calc.timingLocks || {}, travelLocks: calc.travelLocks || {},
      travelSuppressions: state.travelSuppressions || calc.travelSuppressions || {},
      editedLineItems: state.editedLineItems || null,
      priceOverrides: state.priceOverrides || {},
      juniorBridesmaids: Number(state.juniorBridesmaids) || 0,
      customLocations: Array.isArray(ws.locations) ? ws.locations.filter(function(l) { return l && l.type === 'custom'; }) : [],
      customLocationsPresent: Array.isArray(ws.locations)
    };
    jvBridge.callAdmin('proposalTravelAutoPreview', payload).then(function(res) {
      if (seq !== _travelAutoSeq || state.workspace !== ws) return;
      var data = res && res.data || res;
      if (!data || data.ok !== true || !data.calculation || !data.calculation.travel ||
          !Array.isArray(data.calculation.travel.weddingTravel) || !data.calculation.travel.weddingTravel.length) {
        throw new Error('Automatic travel quote needs review.');
      }
      _travelAutoStatus = 'ready';
      applyCalcResponse_(data.calculation, { clearDraft: false });
    }).catch(function(err) {
      if (seq !== _travelAutoSeq) return;
      _travelAutoStatus = 'error';
      showBanner((err && err.message) || 'Automatic travel quote needs review.', 'error');
    });
  }, 400);
}

function runPartyRecalc_() {
  // Path 2 no-op: replaced by schedulePartyRecalc_() doing local recalc only.
  // Kept for any straggler callers.
  schedulePartyRecalc_();
}

// partyChangeModal and partyChangeRecalculate_/partyChangeKeepPrices_ removed 2026-05-31.
// Sparse priceOverrides survive party changes automatically; no confirm needed.

function refreshSummary(items) {
  var subtotal = calcSubtotal(items);
  var gst = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;
  var el;
  el = document.getElementById('sumSubtotal'); if (el) el.textContent = formatCurrency(subtotal);
  el = document.getElementById('sumGst');      if (el) el.textContent = formatCurrency(gst);
  el = document.getElementById('sumTotal');    if (el) el.textContent = formatCurrency(total);
  // P3e: keep the sticky running summary in sync on every line-item edit.
  el = document.getElementById('pbStickyGst');   if (el) el.textContent = formatCurrency(gst);
  el = document.getElementById('pbStickyTotal'); if (el) el.textContent = formatCurrency(total);
  var calc = (state.workspace && state.workspace.calculation) || {};
  el = document.getElementById('pbStickyArtists'); if (el) el.textContent = (calc.artistsRequired || calc.artistsNeeded || 1);
  el = document.getElementById('pbStickyStart');   if (el) el.textContent = fmtTime12h_(String(calc.estimatedStartTime || '').trim()) || 'TBC';
}

/* -- Address Search & Travel Actions -- */
var _addressSearchTimer = null;
var _addressSearchResults = [];

// -- Multi-location address search --
var _activeLocationSearch = 'wedding';

function onLocationAddressInput(value, locationType) {
  _activeLocationSearch = locationType || 'wedding';
  clearTimeout(_addressSearchTimer);
  var panelId = 'addressResults-' + _activeLocationSearch;
  var panel = document.getElementById(panelId);
  if (!panel) return;

  var q = String(value || '').trim();
  if (q.length < 3) {
    panel.classList.remove('open');
    panel.innerHTML = '';
    return;
  }

  panel.innerHTML = '<div class="address-searching"><i class="fa-solid fa-spinner fa-spin"></i> Searching...</div>';
  panel.classList.add('open');

  var capturedType = _activeLocationSearch;
  // G2 route #1 (2026-10-14, SCOPE-proposal-admin-mutations-cf-port-2026-10-14.md):
  // When window.JV_PROPOSAL_ADDR_SEARCH_CF is true (emitted by sections/jv-proposal-
  // builder.liquid from shop metafield custom.proposal_addr_search_cf_enabled),
  // route this single query to the cors-relay's native jv_address_search handler --
  // one Shopify -> Cloudflare -> Google Places hop. Default (flag off/unset) keeps
  // the existing jvBridge.callAdmin('proposalSearchAddress') GAS proxy path, which
  // itself proxies to JV AUTO -> Google Places (two GAS hops). CF-only no-soak per
  // Jess's Q3 decision: nothing to mirror, GAS is dormant on this route the moment
  // the flag flips. jvBridge.callAdminRelay returns the relay body UNWRAPPED
  // ({status, results}); callAdmin wraps the GAS response in {ok, data: ...}; the
  // response handler below branches by flag to pick the right unwrap.
  var _addrSearchViaCf = (typeof window !== 'undefined' && window.JV_PROPOSAL_ADDR_SEARCH_CF === true);
  _addressSearchTimer = setTimeout(function() {
    var _addrSearchPromise = _addrSearchViaCf
      ? jvBridge.callAdminRelay('jv_address_search', { query: q, searchType: 'venue' })
      : jvBridge.callAdmin('proposalSearchAddress', { query: q, type: 'venue' });
    _addrSearchPromise
      .then(function(res) {
        var p = document.getElementById('addressResults-' + capturedType);
        if (!p) return;
        // Branch on which transport we actually used: CF path returns the relay body
        // ({status, results}) directly; GAS path returns {ok, data: {status, results}}.
        // Defensive single-unwrap in both cases.
        var payload = _addrSearchViaCf ? res : ((res && res.data) || res);
        if (!payload || payload.status === 'error' || !payload.results || payload.results.length === 0) {
          p.innerHTML = '<div class="address-searching">No results found</div>';
          return;
        }
        _addressSearchResults = payload.results;
        var html = '';
        for (var i = 0; i < payload.results.length; i++) {
          var r = payload.results[i];
          html += '<div class="address-result-item" onclick="selectLocationAddress(' + i + ',\'' + capturedType + '\')">';
          if (r.placeName) {
            html += '<div class="place-name">' + escapeHtml(r.placeName) + '</div>';
            html += '<div class="place-addr">' + escapeHtml(r.address) + '</div>';
          } else {
            html += '<div class="place-name">' + escapeHtml(r.address) + '</div>';
          }
          if (r.suburb || r.state) {
            html += '<div class="place-addr">' + escapeHtml([r.suburb, r.state, r.postCode].filter(Boolean).join(', ')) + '</div>';
          }
          html += '</div>';
        }
        p.innerHTML = html;
      })
      .catch(function(err) {
        var p = document.getElementById('addressResults-' + capturedType);
        if (p) { p.innerHTML = '<div class="address-searching">Search failed -- try again</div>'; }
      });
  }, 400);
}

/* P1a (2026-07-18): pending trial-travel retry state. When a trial-travel recalc
   fails (relay timeout / {ok:false} / no calculation), Layer A silently reverts the
   address and keeps stale totals. The wedding path has a persistent Retry button
   (retryTravelCalc, rendered at the Adjust-travel section); the trial path had none,
   so the admin never saw the failure. We now stash the failed trial address here and
   render an on-brand error plus Retry affordance in the Trial Location card. Cleared
   on a successful trial recalc. Frontend-only; no bearing on the money preserve. */
var _pendingTrialTravelRetry = null;
/* FU2 (2026-07-19): generic pending-retry for the WEDDING + SPRAY-TAN address
   picks, mirroring _pendingTrialTravelRetry. A failed non-trial pick used to
   show a transient banner then silently revert the venue with no way to retry
   THAT pick; now we stash { type, addr } and render an on-brand Retry banner in
   the matching location card. Cleared on a successful recalc of that type.
   Frontend-only; the _keepPaymentOption money-preserve is untouched. */
var _pendingTravelRetry = null;

/* WIN A (2026-07-12): fire-and-forget venue travel-cache warm.
   The instant a venue is picked we kick off a background warm of that venue
   against every active roster artist (bridge_venue_warm to JV AUTO warm_venue).
   We do NOT await it; the normal recalc below still runs. Its whole job is to
   make the NEXT recalc (e.g. after the admin steps the artist count) a pure
   cache-hit instead of a fresh ~20s live-Maps pass. Best-effort: any failure is
   swallowed (the lazy per-quote cache path still works).

   FU1 (2026-07-19): optional priorityArtist name (the trial artist) is
   forwarded so JV AUTO warms their leg first, ahead of the rest of the
   roster. Without this, a big roster can leave the trial artist's cache
   entry unwarmed by the time the recalc fires a moment later, since this
   call is fire-and-forget, not awaited. */
/* Dual-path (2026-11-02): window.JV_VENUE_WARM_CF_ENABLED gates the NEW
   Cloudflare venue-warm route (jv-crm-mirror POST /venue-warm) in place of
   the legacy GAS bridge_venue_warm call below. Defaults to false/unset,
   so nothing changes until Jess explicitly flips it. Still fire-and-forget
   either way; never awaited, never blocks the pick flow. */
function warmVenueCache_(addr, priorityArtist) {
  try {
    if (!addr) return;
    var address = addr.address || addr.placeName || '';
    if (!address) return;
    var body = { address: address, placeId: addr.placeId || '' };
    if (priorityArtist) body.priorityArtist = priorityArtist;
    if (window.JV_VENUE_WARM_CF_ENABLED === true) {
      warmVenueCacheCf_(body);
      return;
    }
    jvBridge.callAdmin('bridge_venue_warm', body)
      .catch(function() { /* best-effort, ignore */ });
  } catch (e) { /* never let a warm failure touch the pick flow */ }
}

/* NEW Cloudflare path for warmVenueCache_, flag-gated OFF by default (see
   window.JV_VENUE_WARM_CF_ENABLED above). Calls jv-crm-mirror's POST
   /venue-warm route (DEPOLYMENT/jv-crm-mirror/venue-warm.mjs), which
   upserts travel_route_cache rows for the active artist roster. That
   Worker is not yet routed to a public domain, so window.JV_VENUE_WARM_CF_URL
   must be set before this flag is ever flipped true in production; left
   unset, this is a harmless no-op fetch failure swallowed below. */
function warmVenueCacheCf_(body) {
  try {
    var base = window.JV_VENUE_WARM_CF_URL;
    if (!base) return;
    fetch(base.replace(/\/$/, '') + '/venue-warm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).catch(function() { /* best-effort, ignore */ });
  } catch (e) { /* never let a warm failure touch the pick flow */ }
}

function selectLocationAddress(idx, locationType) {
  var addr = _addressSearchResults[idx];
  if (!addr) return;

  var type = locationType || 'wedding';
  var panel = document.getElementById('addressResults-' + type);
  if (panel) { panel.classList.remove('open'); panel.innerHTML = ''; }
  var input = document.getElementById('addressSearch-' + type);
  if (input) input.value = '';

  // WIN A: start warming the travel cache for this venue across the roster NOW,
  // in the background, so the subsequent artist-stepper recalc is a cache-hit.
  // FU1 (2026-07-19): for a trial pick, tell the warm which artist to prioritise
  // (the last-known trial artist chip, defaulting to 'Jessica' to match the
  // server-side default in TravelCalculator.gs) so their leg warms first.
  var _warmPriorityArtist = (type === 'trial')
    ? ((state.workspace && state.workspace.calculation && state.workspace.calculation.trialTravel && state.workspace.calculation.trialTravel.artist) || 'Jessica')
    : null;
  warmVenueCache_(addr, _warmPriorityArtist);

  showBanner('Calculating travel to ' + (addr.placeName || addr.address) + '...', 'info');
  showLoadingOverlay('Calculating travel. This can take up to a minute.');

  var partyOverrides = (state.workspace && state.workspace.party || []).filter(function(p) { return !(p && p.isManualAddition); }).map(function(p) { // 4.1: never send a manual member as an enquiry override
    return { role: p.role, serviceType: p.serviceType, qty: p.qty !== undefined ? Number(p.qty) : 1 };
  });

  // Custom location -- store result on the location object, not on calculation
  var isCustom = String(type).indexOf('custom_') === 0;
  var customIdx = isCustom ? parseInt(String(type).replace('custom_', ''), 10) : -1;
  var apiType = isCustom ? 'custom' : type;
  var capturedAddr = addr;

  // FIX (2026-07-23): manual additions (e.g. a flowergirl added via Additional Party
  // Members) were never sent on an address recalc, and proposalRecalculateWithAddress had
  // no code path to preserve or re-apply them, so they silently vanished from the priced
  // total the moment a venue address was re-selected in the same edit session. Mirrors the
  // manualAdditions field already sent by flushToGas_() for the plain recalculate route.
  var _adTok = ++state.requestToken;
  pbRecalcWarm_('proposalRecalculateWithAddress', { name: state.currentBride, address: addr, partyOverrides: partyOverrides, manualAdditions: state.manualPartyMembers || [], locationType: apiType })
    .then(function(res) {
      if (_adTok !== state.requestToken) { hideLoadingOverlay(); return; } // newer edit in flight
      hideLoadingOverlay();
      // Bridge wraps to { ok, data: { status, calculation, message } }. Defensive single-unwrap.
      var payload = (res && res.data) || res;
      // Also catch the bridge-WRAPPER failure shape { ok:false, error } (rate limit,
      // timeout, caught exception) which has no .status key and no .calculation.
      if (!payload || payload.status === 'error' || payload.ok === false || !payload.calculation) {
        // P1a (2026-07-18): trial-travel failure was silent + unretryable. Stash the
        // failed trial address so the Trial Location card can render a persistent error
        // plus Retry affordance (mirrors the wedding Retry button). Non-trial paths keep
        // the transient banner only, as before.
        if (type === 'trial') {
          _pendingTrialTravelRetry = capturedAddr;
          renderActiveStage();
        } else if (type === 'wedding' || type === 'spray_tan' || type === 'spray') {
          // FU2: stash the failed non-trial pick so the location card can render a
          // persistent Retry (was a transient banner + silent revert before).
          _pendingTravelRetry = { type: type, addr: capturedAddr };
          renderActiveStage();
        }
        showBanner((payload && (payload.message || payload.error)) || 'Travel calculation failed', 'error');
        return;
      }
      if (type === 'trial') _pendingTrialTravelRetry = null; // trial recalc succeeded
      if (_pendingTravelRetry && _pendingTravelRetry.type === type) _pendingTravelRetry = null; // FU2: non-trial recalc succeeded
      if (isCustom && customIdx >= 0 && state.workspace && state.workspace.locations) {
        var cloc = state.workspace.locations[customIdx];
        if (cloc) {
          cloc.address = capturedAddr.address || '';
          cloc.addressObj = capturedAddr;
          cloc.travelBreakdown = (payload.calculation && payload.calculation.travelBreakdown) || [];
          // Add custom travel line item
          var travelFee = (payload.calculation && payload.calculation.travelCost) || 0;
          if (travelFee > 0) {
            if (!state.editedLineItems) {
              state.editedLineItems = JSON.parse(JSON.stringify((state.workspace.calculation && (state.workspace.calculation.normalizedLineItems || state.workspace.calculation.lineItems)) || []));
            }
            var desc = (cloc.label || 'Custom Location') + ' \u2014 Travel Fee';
            var existing = -1;
            for (var ei = 0; ei < state.editedLineItems.length; ei++) {
              if (String(state.editedLineItems[ei].description || '').indexOf(desc) >= 0) { existing = ei; break; }
            }
            var li = { description: desc, name: desc, unitPrice: travelFee, rate: travelFee, qty: 1, quantity: 1, total: travelFee, custom: true };
            if (existing >= 0) { state.editedLineItems[existing] = li; } else { state.editedLineItems.push(li); }
          }
        }
        showBanner('Travel updated -- ' + (capturedAddr.placeName || capturedAddr.suburb || 'address selected'), 'success');
        renderActiveStage();
      } else {
        // MONEY BUG FIX (2026-07-16): proposalRecalculateWithAddress only carries
        // paymentOption forward from CRM's already-stored value, so a venue-address
        // pick after the admin has chosen a payment option (e.g. full_gc) would
        // otherwise silently revert it to a stripe default. See
        // BRIEFS/HANDOVER-full-gc-root-cause-found-2026-07-16.md.
        var _newCalc4704 = payload.calculation;
        if (!_newCalc4704 || !state.workspace) {
          if (type === 'trial') { _pendingTrialTravelRetry = capturedAddr; renderActiveStage(); }
          else if (type === 'wedding' || type === 'spray_tan' || type === 'spray') { _pendingTravelRetry = { type: type, addr: capturedAddr }; renderActiveStage(); }
          showBanner('Travel calculation failed', 'error');
          return;
        }
        var _keepPaymentOption4704 = (state.workspace.calculation && state.workspace.calculation.paymentOption) || null;
        state.workspace.calculation = _newCalc4704;
        if (_keepPaymentOption4704) state.workspace.calculation.paymentOption = _keepPaymentOption4704;
        // DATA BUG FIX (2026-07-21): re-append any custom Page-2 line items before
        // clearing the cache -- see _pbReappendCustomLineItems_ for full context.
        _pbReappendCustomLineItems_(state.workspace.calculation, state.editedLineItems);
        state.editedLineItems = null;
        state.lineItemsManuallyEdited = false;
        showBanner('Travel updated -- ' + (capturedAddr.placeName || capturedAddr.suburb || 'address selected'), 'success');
        renderActiveStage();
      }
      _recomputeTravelOnChange_();
    })
    .catch(function(err) {
      hideLoadingOverlay();
      if (type === 'trial') { _pendingTrialTravelRetry = capturedAddr; renderActiveStage(); }
      else if (type === 'wedding' || type === 'spray_tan' || type === 'spray') { _pendingTravelRetry = { type: type, addr: capturedAddr }; renderActiveStage(); }
      showBanner(getErrorMessage(err), 'error');
    });
}

/* P1a (2026-07-18): re-run the last failed trial-travel recalc. Mirrors the wedding
   retryTravelCalc button. It replays selectLocationAddress' recalc against the stashed
   trial address object directly (the address-results list has been cleared by then, so
   we can't go back through the index). On a fresh attempt the failure paths above will
   re-stash + re-render the banner if it fails again; a success clears the pending state. */
function retryTrialTravelCalc() {
  var addr = _pendingTrialTravelRetry;
  if (!addr) return;
  // Reuse the canonical pick path so the recalc, warm, overlay, banner + money-preserve
  // logic are all byte-identical to the original trial pick. Prime the results buffer so
  // selectLocationAddress(0, 'trial') resolves to the stashed address.
  _addressSearchResults = [addr];
  showBanner('Retrying trial travel calculation...', 'info');
  selectLocationAddress(0, 'trial');
}

/* FU2 (2026-07-19): re-run the last failed WEDDING / SPRAY-TAN travel pick.
   Mirrors retryTrialTravelCalc exactly, it replays selectLocationAddress against
   the stashed { type, addr } so the recalc, warm, overlay, banner and money
   preserve are byte-identical to the original pick. A repeat failure re-stashes and
   re-renders the banner; a success clears _pendingTravelRetry for that type. */
function retryLocationTravelCalc() {
  var pend = _pendingTravelRetry;
  if (!pend || !pend.addr) return;
  _addressSearchResults = [pend.addr];
  showBanner('Retrying travel calculation...', 'info');
  selectLocationAddress(0, pend.type);
}

/* Trial-location control (BUILD-2026-08-30, Part A). Renders the In Home / In
   Studio / TBC segmented control. One click owns both the location AND its
   travel consequence -- the whole point of this control is that they can no
   longer drift apart (Jess: "no way to change... if they originally selected
   an in-home trial"). Server-authoritative: the route
   (proposalSetTrialLocationMode, TrialLocationControl_2026_08_30.gs) computes
   and persists the calc directly, this just applies the returned calc, the
   same pattern selectLocationAddress uses for its server round-trip. */
function buildTrialLocationModeControl_(currentMode, calc) {
  var opts = [
    { mode: 'in_home',   label: 'In Home' },
    { mode: 'in_studio', label: 'In Studio' },
    { mode: 'tbc',       label: 'TBC' }
  ];
  var html = '<div class="trial-location-mode-control" role="group" aria-label="Trial location">';
  for (var i = 0; i < opts.length; i++) {
    var isActive = currentMode === opts[i].mode;
    html += '<button type="button" class="trial-location-mode-btn' + (isActive ? ' is-active' : '') + '" ' +
      'onclick="setTrialLocationMode_(\'' + opts[i].mode + '\')" ' +
      (isActive ? 'aria-pressed="true"' : 'aria-pressed="false"') + '>' +
      escapeHtml(opts[i].label) + '</button>';
  }
  html += '</div>';
  if (currentMode === 'tbc') {
    html += '<div class="wdt-warn" style="margin-top:8px">' +
      '<i class="fa-solid fa-clock"></i>' +
      '<div>Trial location is TBC. No travel fee is charged yet, but this bride is tracked ' +
      'so the fee is not forgotten once her trial location firms up.</div>' +
      '</div>';
  }
  return html;
}

function setTrialLocationMode_(mode) {
  if (!state.currentBride) return;
  var prevMode = (state.workspace && state.workspace.calculation && state.workspace.calculation.trialLocationMode) || '';
  if (prevMode === mode) return; // no-op, already this mode

  showLoadingOverlay(mode === 'in_home' ? 'Restoring trial travel...' : 'Updating trial location...');

  if (window.JV_PB_READS_CF !== true || !window._pbAdminProxyBase) {
    hideLoadingOverlay();
    showBanner('Cloudflare proposal editing is unavailable. Nothing was changed.', 'error');
    return;
  }
  pbCfBuilderWrite_('set-trial-location', { name: state.currentBride, mode: mode }, pbAcceptCfTrialLocation_)
    .then(function(result) {
      if (result && result.refusal) { pbShowCfWriteRefusal_(result.refusal); return; }
      var cf = result && result.cf;
      if (!cf) { hideLoadingOverlay(); showBanner('Could not update trial location through Cloudflare. Nothing was changed.', 'error'); return; }
      hideLoadingOverlay();
      if (cf.calc && state.workspace) { state.workspace.calculation = cf.calc; }
      var label = mode === 'in_home' ? 'In Home' : (mode === 'in_studio' ? 'In Studio' : 'TBC');
      showBanner('Trial location set to ' + label + '.', 'success');
      renderActiveStage();
    });
}

function clearLocationAddress(locationType) {
  // P3 (2026-07-09): clearing an address must also flush the persisted travel fee for
  // that location, not just blank the UI. Previously the fee lingered in
  // PROPOSAL_CALC_JSON until a later recalc, so the admin saw the address gone but the
  // fee still on the proposal. Now: clear the stored address (legacy + multi-address
  // map), suppress that location's travel line(s) so they drop from totals, and mark
  // dirty so the change persists on the next Save.
  var type = locationType || 'wedding';
  var calc = state.workspace && state.workspace.calculation;
  if (calc) {
    var sa = calc.selectedAddress;
    if (sa && (sa.type || 'wedding') === type) {
      calc.selectedAddress = null;
    }
    if (calc.selectedAddresses && calc.selectedAddresses[type]) {
      delete calc.selectedAddresses[type];
    }
  }
  // Suppress the travel line(s) for this location type so the fee is removed from the
  // total (sticky delete, keyed by stable travel id — same channel as a manual delete).
  if (!state.travelSuppressions) state.travelSuppressions = {};
  var prevItems = (state.editedLineItems) ||
                  (calc && (calc.normalizedLineItems || calc.lineItems)) || [];
  prevItems.forEach(function(li) {
    if (String(li && li._travelType || '') === type) {
      var k = travelLineKey_(li);
      if (k) state.travelSuppressions[k] = true;
    }
  });
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

// Legacy wrappers -- keep for backward compatibility
function onAddressInput(value) { onLocationAddressInput(value, 'wedding'); }
function selectAddress(idx) { selectLocationAddress(idx, 'wedding'); }
function clearSelectedAddress() { clearLocationAddress('wedding'); }

function retryTravelCalc() {
  if (!state.currentBride) return;
  showBanner('Retrying travel calculation...', 'info');
  recalculate();
}

function applyTravelOverride() {
  var feeInput = document.getElementById('travelFeeOverride');
  var raw = feeInput ? String(feeInput.value).trim() : '';
  var fee = Number(raw);
  if (raw !== '' && (isNaN(fee) || fee < 0)) {
    showBanner('Enter a valid travel fee amount.', 'warn');
    return;
  }

  // P2i: the override targets the WEDDING-DAY travel line(s) and routes through the
  // sparse priceOverrides map (P2a), the SAME channel the CRM re-applies after every
  // recalc, keyed by a stable _rowId, round-tripped through PROPOSAL_CALC_JSON.
  //
  // The old code matched the FIRST line whose description contained "travel" AND had
  // a _rowId. But display items (calc.normalizedLineItems) carried NO _rowId, so that
  // match silently failed and it fell into the else branch, APPENDING a custom
  // "Travel Fee (manual override)" row. normalizeLineItemsForDisplay_ then relabelled
  // that row into a phantom "Wedding Day Travel Fee - Pro Team Artist N" line (the P2i
  // bug: it accumulated on every Apply, persisted, and the chip stayed inconsistent).
  // The server fix (normalize now preserves _rowId) makes the priceOverrides path
  // below actually hit the real wedding-travel line, in place, with no new rows.
  //
  // Scope: wedding-day travel only. Trial/spray travel are separate figures and are
  // left untouched (matches "exactly ONE travel figure changes"). The input defaults
  // to the current wedding-travel total (see renderStageBuilder) so re-applying the
  // default is a no-op rather than double-counting.
  var displayItems = _currentDisplayItems || [];
  var weddingTravelRowIds = [];
  for (var i = 0; i < displayItems.length; i++) {
    var d   = String(displayItems[i].description || displayItems[i].name || '').toLowerCase();
    var cat = String(displayItems[i].category || '').toLowerCase();
    var isTravel = (cat.indexOf('travel') >= 0 || d.indexOf('travel') >= 0);
    if (!isTravel) continue;
    if (d.indexOf('trial') >= 0 || d.indexOf('spray') >= 0) continue; // not wedding-day travel
    if (displayItems[i]._rowId) weddingTravelRowIds.push(displayItems[i]._rowId);
  }

  if (!state.priceOverrides) state.priceOverrides = {};

  // CLEAR: blank or 0 reverts to the auto travel calc. P2j: the override can live in
  // THREE channels (priceOverrides, a legacy "manual override" editedLineItems row, or
  // a baked-in calc.lineItems row after a flush). clearTravelOverride_ sweeps all three
  // and flushToGas_ un-persists from PROPOSAL_CALC_JSON, so the revert is total.
  if (raw === '' || fee === 0) {
    clearTravelOverrideAndPersist_();
    showBanner('Travel override cleared. Standard travel calculation restored.', 'success');
    return;
  }

  if (weddingTravelRowIds.length) {
    // Override the FIRST wedding-travel line to the entered fee; collapse any
    // additional wedding-travel lines to $0 so the wedding-travel total equals fee
    // (one clean figure, no phantom per-artist rows). Idempotent: re-applying just
    // rewrites the same _rowId entries.
    state.priceOverrides[weddingTravelRowIds[0]] = { rate: fee, qty: 1 };
    for (var k = 1; k < weddingTravelRowIds.length; k++) {
      state.priceOverrides[weddingTravelRowIds[k]] = { rate: 0, qty: 0 };
    }
    // Defensive: clear any stale manual-override custom row from the old buggy path.
    if (state.editedLineItems) {
      state.editedLineItems = state.editedLineItems.filter(function(li) {
        return String(li.description || li.name || '').toLowerCase().indexOf('manual override') < 0;
      });
      if (!state.editedLineItems.length) state.editedLineItems = null;
    }
  } else {
    // No auto wedding-travel line exists yet (travel not calculated). Add ONE custom
    // row so the admin can still set a fee. Idempotent: strip any prior override row
    // first so repeated Applies never accumulate.
    if (!state.editedLineItems) {
      var calc0 = (state.workspace && state.workspace.calculation) || {};
      state.editedLineItems = JSON.parse(JSON.stringify(calc0.normalizedLineItems || calc0.lineItems || []));
    }
    state.editedLineItems = state.editedLineItems.filter(function(li) {
      return String(li.description || li.name || '').toLowerCase().indexOf('manual override') < 0;
    });
    state.editedLineItems.push({
      description: 'Wedding Day Travel Fee (manual override)', name: 'Wedding Day Travel Fee (manual override)',
      qty: 1, quantity: 1, unitPrice: fee, rate: fee, total: fee, category: 'travel', custom: true
    });
  }

  // Clear stale LOCAL_ESTIMATE flag. The travel chip is reconciled from the travel
  // line items in renderStageBuilder, so it stays in agreement with the override.
  if (state.workspace && state.workspace.calculation && state.workspace.calculation.flags) {
    state.workspace.calculation.flags = state.workspace.calculation.flags.filter(function(f) {
      return String(f).indexOf('LOCAL_ESTIMATE') < 0;
    });
  }

  markDirty_();
  renderStageBuilder();
  // Persist immediately so the override lands in PROPOSAL_CALC_JSON before any
  // address recalc reads it back (proposalRecalculateWithAddress re-applies stored
  // priceOverrides). One round-trip on a deliberate Apply.
  showLoadingOverlay('Saving travel override...');
  flushToGas_()
    .then(function() { hideLoadingOverlay(); renderStageBuilder(); })
    .catch(function() { hideLoadingOverlay(); });
  showBanner('Wedding travel fee set to $' + fee.toFixed(2) + ' (persists through recalc).', 'success');
}

// P2j: a wedding-day travel override can land in THREE channels:
//   1. state.priceOverrides keyed by the travel line's _rowId (P2i normal path)
//   2. a legacy "manual override" custom row in state.editedLineItems (old else-branch)
//   3. calc.lineItems / calc.normalizedLineItems, after a flush bakes editedLineItems
//      into the calc server-side (GAS replaces the whole list, then state.editedLineItems
//      is nulled, so the row is left stranded in the calc).
// The pre-P2j clear only swept channel 1, so an override that reached channel 3
// "resisted undo" (Reset / blank-Apply / Recalculate could not reach it). This sweeps
// all three and leaves state in a shape that the next flush un-persists cleanly while
// restoring the auto-calculated travel fee, without dropping genuine custom rows.
function clearTravelOverride_() {
  var isManualOverrideRow = function(li) {
    return String(li.description || li.name || '').toLowerCase().indexOf('manual override') >= 0;
  };
  var isWeddingTravel = function(li) {
    var d = String(li.description || li.name || '').toLowerCase();
    var c = String(li.category || '').toLowerCase();
    return (c.indexOf('travel') >= 0 || d.indexOf('travel') >= 0) &&
           d.indexOf('trial') < 0 && d.indexOf('spray') < 0;
  };

  // Channel 1: drop every wedding-travel priceOverride.
  //
  // MONEY BUG (2026-08-07, Cowork round 5 Check D): this used to sweep ONLY the
  // _rowIds present in _currentDisplayItems. After a venue change the travel line's
  // description changes and that list can be stale, so the stored override key
  // survived the sweep: the panel collapsed and the "Clear override" button vanished
  // (both driven by local UI state) while the Line Items table and the PERSISTED
  // total still carried $158.00. An admin clearing a mistaken manual price would
  // believe it had reverted to auto and could send at the wrong price.
  //
  // Sweep by IDENTITY instead of by what happens to be rendered: any override whose
  // key is a derived travel row id (see proposalBuilder_stampRowIds_ in the CRM,
  // which now mints "travel-wedding-<artist>"), plus any wedding-travel row found in
  // EITHER the display list or the live calc. Belt-and-braces so a clear cannot be
  // defeated by a stale render.
  if (state.priceOverrides) {
    var _sweepIds = {};
    var _collect = function(arr) {
      (arr || []).forEach(function(it) {
        if (it && it._rowId && isWeddingTravel(it)) _sweepIds[it._rowId] = true;
      });
    };
    _collect(_currentDisplayItems);
    if (state.workspace && state.workspace.calculation) {
      _collect(state.workspace.calculation.lineItems);
      _collect(state.workspace.calculation.normalizedLineItems);
    }
    _collect(state.editedLineItems);
    Object.keys(state.priceOverrides).forEach(function(k) {
      // Derived wedding-travel ids are self-identifying, so a stale render cannot hide
      // them. Trial/spray travel keep their own overrides and are deliberately spared.
      if (/^travel-wedding-/.test(String(k))) _sweepIds[k] = true;
    });
    Object.keys(_sweepIds).forEach(function(k) { delete state.priceOverrides[k]; });
  }

  var calc = (state.workspace && state.workspace.calculation) || null;

  // Channel 3: strip any baked-in "manual override" row from the calc itself.
  //
  // MONEY BUG (2026-08-07, Cowork round 6): this only removed rows literally
  // DESCRIBED "manual override", a legacy shape. The row an Apply actually produces
  // is an ordinary wedding-travel line carrying _userEdited and the overridden
  // total. So after a clear it survived here, and because flushToGas_ sends
  // calc.lineItems, the server was handed $158 again and re-persisted it. Result:
  // the panel said cleared, the Line Items table and the stored total kept $158.00,
  // and it only self-corrected on the NEXT cold reopen (when the workspace rebuilds
  // travel from the travel data block). In that window an admin could send at a
  // price they believed they had reverted.
  //
  // Restore the calculated fee on any _userEdited wedding-travel row instead of
  // deleting the row (deleting it would lose the line entirely). calc.travel holds
  // the authoritative per-artist fee, the same source the cold-load rebuild uses,
  // which is why the cold load already produced the right answer.
  if (calc) {
    var _authFee = null;
    try {
      var _wt = (calc.travel && calc.travel.weddingTravel) || calc.weddingTravel || [];
      if (_wt && _wt.length && _wt[0] && _wt[0].fee != null) _authFee = Number(_wt[0].fee);
    } catch (_feeErr) { _authFee = null; }

    ['lineItems', 'normalizedLineItems'].forEach(function(key) {
      if (!Array.isArray(calc[key])) return;
      calc[key] = calc[key].filter(function(li) { return !isManualOverrideRow(li); });
      calc[key].forEach(function(li) {
        if (!li || !isWeddingTravel(li)) return;
        li._userEdited = false;
        if (_authFee != null && !isNaN(_authFee)) {
          var q = Number(li.qty != null ? li.qty : 1) || 1;
          li.unitPrice = _authFee;
          li.rate      = _authFee;
          li.total     = Math.round(_authFee * q * 100) / 100;
        }
      });
    });

    // Keep the scalars in step with the rows we just reset, so the total shown
    // (and flushed) matches the lines immediately rather than after a reopen.
    if (Array.isArray(calc.lineItems)) {
      var _sub = 0;
      calc.lineItems.forEach(function(li) { _sub += Number((li && li.total) || 0); });
      calc.subtotal = Math.round(_sub * 100) / 100;
      calc.gst      = Math.round(calc.subtotal * 0.10 * 100) / 100;
      calc.total    = Math.round((calc.subtotal + calc.gst) * 100) / 100;
    }
  }

  // Channel 2: drop the override row from editedLineItems and clear _userEdited on any
  // wedding-travel row, preserving genuine user-added custom rows.
  if (state.editedLineItems) {
    state.editedLineItems = state.editedLineItems.filter(function(li) { return !isManualOverrideRow(li); });
    state.editedLineItems.forEach(function(li) {
      if (isWeddingTravel(li) && li._userEdited) li._userEdited = false;
    });
  }

  // Pick the flush channel so the override un-persists AND the auto travel fee is
  // restored, without losing legitimately user-added custom rows.
  var editedHasCustom = state.editedLineItems &&
    state.editedLineItems.some(function(li) { return !!li.custom; });
  var bakedHasCustom = calc && Array.isArray(calc.lineItems) &&
    calc.lineItems.some(function(li) { return !!li.custom; });
  if (editedHasCustom) {
    // Keep the structural snapshot (override already stripped); the flush replaces calc.
    if (!state.editedLineItems.length) state.editedLineItems = null;
  } else if (bakedHasCustom) {
    // Genuine custom rows live only in the calc now; send the cleaned list so they survive.
    state.editedLineItems = calc.lineItems.slice();
  } else {
    // Nothing but the override remained; drop the snapshot so GAS recomputes the auto
    // travel fee fresh on the next flush.
    state.editedLineItems = null;
  }

  // Clear the stale LOCAL_ESTIMATE flag so the travel chip reconciles to the auto figure.
  if (calc && calc.flags) {
    calc.flags = calc.flags.filter(function(f) { return String(f).indexOf('LOCAL_ESTIMATE') < 0; });
  }
}

// P2j: the single, obvious revert action. Sweeps every override channel, re-renders,
// and flushes so the clear un-persists from PROPOSAL_CALC_JSON (matches P2a's
// priceOverrides round-trip; an accidental Apply is fully UI-recoverable). Used by
// the manual-fee blank-Apply, the "Clear override" button, and the inline travel Reset.
function clearTravelOverrideAndPersist_() {
  clearTravelOverride_();
  markDirty_();
  renderStageBuilder();
  flushToGas_().then(function() { renderStageBuilder(); }).catch(function() {});
}

// -- Artist Override helpers --
/**
 * Lazy-load active artist roster on first use. Cached in state.allActiveArtists.
 * Populates the <datalist> for artist-override autocomplete pickers.
 * Resolves silently on error — the picker degrades to free text.
 */
function ensureActiveArtistRoster_() {
  if (state.allActiveArtists && state.allActiveArtists.length) return Promise.resolve(state.allActiveArtists);
  // Issue #8 builder route port (2026-10-25): D1 roster read via cors-relay entity 'proposal-builder'
  // (secret injected server-side) instead of GAS proposalListAllActiveArtists. Opt-in only:
  // window.JV_PB_READS_CF must be true (default OFF) AND jv-crm-mirror PROPOSAL_BUILDER_READS_EXTRA_ENABLED on.
  // Any non-ok / {fallback:'gas'} / network failure falls through to the unchanged GAS call.
  var cfTry = Promise.resolve(null);
  if (window.JV_PB_READS_CF === true && window._pbAdminProxyBase) {
    var _raNf = (window._jvNativeFetch || window.fetch).bind(window);
    cfTry = _raNf(String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-builder/list-active-artists', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify({})
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      // length > 0 is deliberate: an ok-but-EMPTY roster must fall through to GAS, not paint an
      // empty artist picker. Reachable when D1 team_members has no active rows. (Review 2026-10-25.)
      .then(function (b) { return (b && b.status === 'ok' && Array.isArray(b.artists) && b.artists.length > 0) ? { data: b } : null; })
      .catch(function () { return null; });
  }
  return cfTry
    .then(function (cfRes) { return cfRes || jvBridge.callAdmin('proposalListAllActiveArtists', {}); })
    .then(function(res) {
      var payload = (res && res.data) || res;
      if (payload && payload.status === 'ok' && Array.isArray(payload.artists)) {
        state.allActiveArtists = payload.artists;
        // Populate every artist-override datalist that's been rendered
        document.querySelectorAll('datalist.artist-roster-list').forEach(function(dl) {
          dl.innerHTML = state.allActiveArtists.map(function(a) {
            return '<option value="' + escapeHtml(a.artistName) + '">' +
              (a.suburb ? escapeHtml(a.suburb) : '') + '</option>';
          }).join('');
        });
        return state.allActiveArtists;
      }
      return [];
    })
    .catch(function() { return []; });
}

function toggleArtistOverride(locationType) {
  var panel = document.getElementById('artistOverride-' + locationType);
  if (!panel) return;
  panel.style.display = panel.style.display === 'none' ? 'flex' : 'none';
  // First open → fetch roster for autocomplete
  if (panel.style.display !== 'none') ensureActiveArtistRoster_();
}

function applyArtistOverride(locationType) {
  var input = document.getElementById('artistOverrideInput-' + locationType);
  var names = String(input ? input.value : '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
  if (!names.length) return;

  // Roster validation — warn if any typed name doesn't match a real active artist.
  // Catches typos that would silently break the GAS travel calc (artist not found → no travel fee).
  // Allows admin override on explicit confirm (some legacy proposals use "Pro Team Artist N" placeholders).
  if (state.allActiveArtists && state.allActiveArtists.length) {
    var rosterLower = {};
    state.allActiveArtists.forEach(function(a) { rosterLower[String(a.artistName).toLowerCase()] = true; });
    var notInRoster = names.filter(function(n) {
      var lower = n.toLowerCase();
      // Allow generic placeholders that the system understands
      if (lower === 'jessica' || lower === 'jessica vegas' || lower.indexOf('pro team artist') === 0) return false;
      return !rosterLower[lower];
    });
    if (notInRoster.length) {
      var msg = 'These names are not on the active roster:\n  - ' + notInRoster.join('\n  - ') +
        '\n\nTravel calc may not work correctly for unmatched names. Continue anyway?';
      if (!confirm(msg)) return;
    }
  }

  // Resolve address from location type
  var addressObj = null;
  var calc = (state.workspace && state.workspace.calculation) || {};
  if (locationType === 'wedding' || locationType === 'trial' || locationType === 'spray_tan') {
    var sa = calc.selectedAddress;
    if (sa && (sa.type || 'wedding') === locationType) {
      addressObj = { address: sa.address, placeName: sa.placeName || '' };
    }
    if (!addressObj) {
      showBanner('Set an address for this location first', 'warn');
      return;
    }
  } else if (String(locationType).indexOf('custom_') === 0) {
    var cidx = parseInt(String(locationType).replace('custom_', ''), 10);
    var locs = (state.workspace && state.workspace.locations) || [];
    var customLoc = locs[cidx];
    if (!customLoc || !customLoc.addressObj) {
      showBanner('Set an address for this location first', 'warn');
      return;
    }
    addressObj = customLoc.addressObj;
  }

  showBanner('Recalculating travel with overridden artists...', 'info');

  var apiType = (String(locationType).indexOf('custom_') === 0) ? 'custom' : locationType;
  var partyOverrides = (state.workspace && state.workspace.party || []).filter(function(p) { return !(p && p.isManualAddition); }).map(function(p) { // 4.1: never send a manual member as an enquiry override
    return { role: p.role, serviceType: p.serviceType, qty: p.qty !== undefined ? Number(p.qty) : 1 };
  });

  // FIX (2026-07-23): see the identical fix + comment in selectLocationAddress above,
  // this second call site into proposalRecalculateWithAddress had the same gap.
  var _aoTok = ++state.requestToken;
  pbRecalcWarm_('proposalRecalculateWithAddress', { name: state.currentBride, address: addressObj, partyOverrides: partyOverrides, manualAdditions: state.manualPartyMembers || [], locationType: apiType, artistOverrides: names })
    .then(function(res) {
      if (_aoTok !== state.requestToken) return; // newer edit in flight
      // Bridge wraps to { ok, data: { status, calculation, message } }. Defensive single-unwrap.
      var payload = (res && res.data) || res;
      // Also catch the bridge-WRAPPER failure shape { ok:false, error } (rate limit,
      // timeout, caught exception) which has no .status key and no .calculation.
      if (!payload || payload.status === 'error' || payload.ok === false || !payload.calculation) {
        showBanner((payload && (payload.message || payload.error)) || 'Recalculation failed', 'error');
        return;
      }
      // MONEY BUG FIX (2026-07-16): proposalRecalculateWithAddress only carries
      // paymentOption forward from CRM's already-stored value, so an artist override
      // recalc after the admin has chosen a payment option (e.g. full_gc) would
      // otherwise silently revert it to a stripe default. See
      // BRIEFS/HANDOVER-full-gc-root-cause-found-2026-07-16.md.
      var _newCalc5046 = payload.calculation;
      if (!_newCalc5046 || !state.workspace) {
        showBanner('Recalculation failed', 'error');
        return;
      }
      var _keepPaymentOption5046 = (state.workspace.calculation && state.workspace.calculation.paymentOption) || null;
      state.workspace.calculation = _newCalc5046;
      if (_keepPaymentOption5046) state.workspace.calculation.paymentOption = _keepPaymentOption5046;
      // DATA BUG FIX (2026-07-21): re-append any custom Page-2 line items before
      // clearing the cache -- see _pbReappendCustomLineItems_ for full context.
      _pbReappendCustomLineItems_(state.workspace.calculation, state.editedLineItems);
      // Flush cached edited line items so the bottom table picks up the new wedding
      // travel row from the fresh calc (artist override changes the artist's home
      // suburb → different fee). Matches the address-pick flow at selectLocationAddress.
      state.editedLineItems = null;
      state.lineItemsManuallyEdited = false;
      showBanner('Travel updated with artist override', 'success');
      var panel2 = document.getElementById('artistOverride-' + locationType);
      if (panel2) panel2.style.display = 'none';
      renderActiveStage();
    })
    .catch(function(err) { showBanner(getErrorMessage(err), 'error'); });
}

// -- Custom Location helpers --
function addCustomLocation() {
  // P3 (2026-07-09): in-app modal instead of the off-brand native window.prompt().
  var html =
    '<p style="font-size:13px;color:var(--muted,#8b7d6b);margin:0 0 12px 0;line-height:1.5">' +
      'Give this appointment location a name, for example "Hotel Touch-up" or "Pre-event Blow-dry".' +
    '</p>' +
    '<input id="customLocLabelInput" type="text" maxlength="80" ' +
      'placeholder="Location name" ' +
      'style="width:100%;padding:10px 12px;border:1px solid var(--brand-bg-soft,#f6f1ee);' +
      'border-radius:var(--radius-medium,1rem);font-size:14px;box-sizing:border-box;' +
      'accent-color:var(--brand-primary,#c99591)" />' +
    '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:16px">' +
      '<button class="btn btn-ghost btn-sm" onclick="closeProposalModal()">Cancel</button>' +
      '<button class="btn btn-primary btn-sm" onclick="confirmAddCustomLocation_()">Add location</button>' +
    '</div>';
  showProposalModal('Add appointment location', html);
  // Focus + Enter-to-submit for keyboard flow.
  setTimeout(function() {
    var el = document.getElementById('customLocLabelInput');
    if (el) {
      el.focus();
      el.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); confirmAddCustomLocation_(); }
      });
    }
  }, 50);
}

function confirmAddCustomLocation_() {
  var el = document.getElementById('customLocLabelInput');
  var label = el ? String(el.value || '').trim() : '';
  if (!label) { if (el) el.focus(); return; }

  if (!state.workspace.locations) state.workspace.locations = [];
  state.workspace.locations.push({
    type: 'custom',
    label: label,
    address: '',
    addressObj: null,
    artists: [],
    travelBreakdown: []
  });

  closeProposalModal();
  markDirty_(); // P3: adding a location is an unsaved change
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

function removeCustomLocation(idx) {
  if (!state.workspace || !state.workspace.locations) return;
  var removed = state.workspace.locations.splice(idx, 1)[0];
  if (state.editedLineItems && removed && removed.label) {
    var desc = removed.label + ' \u2014 Travel Fee';
    state.editedLineItems = state.editedLineItems.filter(function(li) {
      return String(li.description || '').indexOf(desc) < 0;
    });
  }
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

function clearCustomLocationAddress(idx) {
  if (!state.workspace || !state.workspace.locations) return;
  var loc = state.workspace.locations[idx];
  if (loc) { loc.address = ''; loc.addressObj = null; loc.travelBreakdown = []; }
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

// Close address dropdowns when clicking outside
document.addEventListener('click', function(e) {
  var wraps = document.querySelectorAll('.address-search-wrap');
  for (var w = 0; w < wraps.length; w++) {
    if (!wraps[w].contains(e.target)) {
      var results = wraps[w].querySelector('.address-results');
      if (results) results.classList.remove('open');
    }
  }
});

/* =================================================================
   STAGE 3 -- REVIEW
   ================================================================= */
function renderStageReview() {
  var ws = state.workspace;
  var calc = ws.calculation || {};
  var bride = ws.bride || {};
  var proposal = ws.proposal || {};
  var items = state.editedLineItems || calc.normalizedLineItems || calc.lineItems || [];

  var subtotal = calcSubtotal(items);
  var gst = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;

  // A1 2026-11-02 defect 1: snapshot-vs-recalc total hard invariant. Flag
  // defaults OFF; this is a no-op until window.JV_PB_TOTAL_MISMATCH_GUARD
  // is flipped true AND the Worker is surfacing proposal.tokenTotal. If
  // mismatched, replace the Review stage with the banner + a back button
  // so Generate Preview / Approve & Send / Re-send cannot be clicked.
  var mismatchGate_review_ = pbTotalMismatchGate_(total);
  if (mismatchGate_review_ && mismatchGate_review_.mismatch) {
    var mismatchHtml_review_ = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-list-check"></i> Review</div>' +
      pbTotalMismatchBanner_(mismatchGate_review_) +
      '<div style="margin-top:8px"><button class="btn btn-ghost" onclick="setStage(\'builder\')">' +
      '<i class="fa-solid fa-arrow-left"></i> Back to Package Builder</button></div>' +
      '</div>';
    document.getElementById('workspace').innerHTML = mismatchHtml_review_ + renderProposalHistoryHtml_();
    return;
  }

  // Check if already sent. Matches proposalReopenForEdit's own guard exactly
  // (ProposalBuilderAPI.gs: currentStatus.toUpperCase() !== 'SENT' && !== 'READY')
  // -- case-insensitive against SENT/READY, everything else counts as "sent" state
  // (VOID, EXPIRED, DECLINED, DEPOSIT PAID, CALC_FAILED, DRAFT_EDIT all included).
  // Previously this only excluded '' and the literal string 'READY', so e.g. VOID or
  // Deposit Paid still fell into the "not sent" branch below and offered a Send flow
  // that doesn't apply -- see HANDOFF-technical-stability-item3-... 2026-07-28.
  var statusUpper_ = String(proposal.status || '').trim().toUpperCase();
  var isSent = statusUpper_ !== '' && statusUpper_ !== 'READY';

  var html = '';

  // Post-send status card
  if (isSent) {
    var statusClass = statusUpper_ === 'EXPIRED' || statusUpper_ === 'VOID' ? 'expired'
                    : proposal.viewCount > 0 ? 'viewed'
                    : (statusUpper_ === 'ACCEPTED' || statusUpper_ === 'SIGNED') ? 'accepted'
                    : 'sent';
    var statusIcon = statusClass === 'expired' ? 'fa-clock' : statusClass === 'viewed' ? 'fa-eye' : statusClass === 'accepted' ? 'fa-circle-check' : 'fa-paper-plane';
    var statusLabel = proposal.viewCount > 0 && statusUpper_ !== 'ACCEPTED' && statusUpper_ !== 'SIGNED' && statusUpper_ !== 'EXPIRED'
      ? 'Viewed (' + proposal.viewCount + ' time' + (proposal.viewCount > 1 ? 's' : '') + ')'
      : proposal.status;

    var revNum = proposal.revisionCount || 0;
    var revBadgeReview = revNum > 1 ? '<span class="status-badge badge-ready" style="margin-left:6px;font-size:11px;vertical-align:middle">Rev ' + revNum + '</span>' : '';
    html += '<div class="proposal-status-card">' +
      '<div class="status-header">' +
        '<div class="status-icon ' + statusClass + '"><i class="fa-solid ' + statusIcon + '"></i></div>' +
        '<div><div style="font-weight:700;font-size:16px;color:var(--brand-dark)">' + escapeHtml(statusLabel) + revBadgeReview + '</div>' +
        '<div style="font-size:12px;color:var(--muted)">Proposal for ' + escapeHtml(bride.name) + '</div></div>' +
      '</div>' +
      '<div class="status-details">' +
        '<div class="status-detail-item"><label>Sent</label><div class="value">' + escapeHtml(proposal.sentDate || '\u2014') + '</div></div>' +
        '<div class="status-detail-item"><label>Views</label><div class="value">' + (proposal.viewCount || 0) + '</div></div>' +
        '<div class="status-detail-item"><label>Expiry</label><div class="value">' + escapeHtml(proposal.expiryDate || '\u2014') + '</div></div>' +
        (revNum > 1 ? '<div class="status-detail-item"><label>Revision</label><div class="value">' + revNum + '</div></div>' : '') +
        (proposal.xeroQuoteId ? '<div class="status-detail-item"><label>Xero Quote</label><div class="value">' + escapeHtml(proposal.xeroQuoteId) + '</div></div>' : '') +
      '</div>' +
      // Edit Proposal only where proposalReopenForEdit will actually accept it
      // (SENT/READY, case-insensitive) -- otherwise it dead-ends on the backend's
      // own guard error. Same fix as the Send stage below and the earlier
      // DRAFT_EDIT-only fix; this extends it to VOID/EXPIRED/DECLINED/Deposit
      // Paid/CALC_FAILED, which all had the identical dead-end. See
      // HANDOFF-technical-stability-item3-... 2026-07-28.
      '<div class="status-actions">' +
        (proposalIsAccepted_(proposal.status) ?
          '<button class="btn btn-ghost btn-sm" id="downloadAcceptedBtn" onclick="downloadAcceptedSummary(this)"><i class="fa-solid fa-file-arrow-down"></i> Download Signed Summary</button>' : '') +
        (statusUpper_ === 'VOID' ? '' : '<button class="btn btn-ghost btn-sm" onclick="resendProposal()"><i class="fa-solid fa-rotate-right"></i> Re-send</button>') +
        (statusUpper_ === 'VOID' || statusUpper_ === 'DECLINED' || proposalIsAccepted_(proposal.status) ? '' :
          '<button class="btn btn-ghost btn-sm" id="reactivateProposalBtn" onclick="reactivateProposalLink()"><i class="fa-solid fa-link"></i> Reactivate Link</button>') +
        (statusUpper_ === 'SENT' || statusUpper_ === 'READY' || statusUpper_ === 'DRAFT_EDIT'
          ? '<button class="btn btn-ghost btn-sm" id="editProposalBtn" onclick="editProposal()"><i class="fa-solid fa-pen-to-square"></i> Edit Proposal</button>' : '') +
        (statusUpper_ === 'VOID' ? '' : '<button class="btn btn-danger btn-sm" onclick="voidProposal()"><i class="fa-solid fa-ban"></i> Void Proposal</button>') +
        (statusUpper_ === 'VOID' || statusUpper_ === 'DECLINED' ? '' : '<button class="btn btn-danger btn-sm" onclick="openDeclineContextModal()"><i class="fa-solid fa-circle-xmark"></i> Mark Unavailable</button>') +
      '</div>' +
    '</div>';
  }

  // "Preview Bride's View" button -- link to the live proposal page
  //
  // BUG 2 FIX (2026-08-04, Fern report: "its not saving/refreshing when i add items"):
  // once a token existed, this branch rendered ONLY a static <a href> to the last-baked
  // snapshot -- the one control that calls proposalGeneratePreview (generatePreviewUrl,
  // which flushes pending edits then re-bakes the token row) only rendered in the ELSE
  // branch, i.e. only before a token existed. Add a "Refresh Preview" button here too,
  // wired to the SAME generatePreviewUrl() (already flushes-then-generates, see that
  // function) so a token-holding proposal can always be re-baked. The token itself is
  // NOT reminted -- proposalBuilder_writeToken_ upserts in place -- so the link stays
  // the exact URL a bride/planner may already hold; only its contents change.
  var proposalToken = proposal.token || '';
  if (proposalToken) {
    var proposalViewerBase = (typeof JV_PROPOSAL_VIEWER_URL !== 'undefined' ? JV_PROPOSAL_VIEWER_URL : 'https://jessicavegas.com/pages/your-bridal-beauty-package');
    var proposalViewUrl = proposalViewerBase + '?token=' + encodeURIComponent(proposalToken);
    html += '<div style="margin-bottom:14px;display:flex;align-items:center;gap:12px;flex-wrap:wrap" id="previewBtnWrap">' +
      '<a href="' + escapeHtml(proposalViewUrl) + '" target="_blank" class="btn btn-ghost btn-sm" style="text-decoration:none">' +
        '<i class="fa-solid fa-eye"></i> Preview Bride\'s View' +
      '</a>' +
      '<button class="btn btn-ghost btn-sm" id="refreshPreviewBtn" onclick="generatePreviewUrl()">' +
        '<i class="fa-solid fa-rotate"></i> Refresh Preview' +
      '</button>' +
      '<button class="btn btn-ghost btn-sm" onclick="copyExistingProposalLink()">' +
        '<i class="fa-solid fa-copy"></i> Copy Link' +
      '</button>' +
      '<span style="font-size:12px;color:var(--muted)">Same link every time -- Refresh Preview saves your latest edits into it first</span>' +
    '</div>';
  } else {
    html += '<div style="margin-bottom:14px;display:flex;align-items:center;gap:12px" id="previewBtnWrap">' +
      '<button class="btn btn-ghost btn-sm" id="generatePreviewBtn" onclick="generatePreviewUrl()">' +
        '<i class="fa-solid fa-eye"></i> Generate Preview' +
      '</button>' +
      '<span style="font-size:12px;color:var(--muted)">Creates the proposal page so you can preview the bride\'s view before sending</span>' +
    '</div>';
  }

  // Moxie-format preview panel
  var previewItemsHtml = '';
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    var desc = it.description || it.name || '';
    var subtitle = it.subtitle || '';
    var qty = it.qty || it.quantity || 1;
    var rate = it.unitPrice || it.rate || 0;
    var itTotal = Number(qty) * Number(rate);
    var taxable = it.taxable || false;
    var itemType = it.type || 'service';
    var isBonus = itemType === 'bonus' || itTotal === 0 && desc.toLowerCase().indexOf('bonus') >= 0;

    var priceHtml = '';
    if (isBonus || (itTotal === 0 && desc.toLowerCase().indexOf('tbc') >= 0)) {
      priceHtml = itTotal === 0 ? '<span class="moxie-item-bonus">' + (desc.indexOf('TBC') >= 0 ? 'A$0.00' : '') + '</span>' : formatAudGst(itTotal, taxable);
    } else {
      priceHtml = '<span class="moxie-item-price">' + formatAudGst(itTotal, taxable) + '</span>';
    }

    var extraClass = isBonus ? ' bonus-item' : '';
    previewItemsHtml += '<div class="moxie-preview-item' + extraClass + '">' +
      '<div class="moxie-item-left">' +
        '<div class="moxie-item-title"><span class="moxie-item-num">' + (i + 1) + '.</span> ' + escapeHtml(desc) + '</div>' +
        (subtitle ? '<div class="moxie-item-subtitle">' + escapeHtml(subtitle) + '</div>' : '') +
      '</div>' +
      '<div class="moxie-item-right">' + priceHtml + '</div>' +
    '</div>';
  }

  html += '<div class="preview-panel">' +
    '<div class="preview-header">' +
      '<h3>Your Bridal Beauty Package</h3>' +
      '<p>Prepared for ' + escapeHtml(bride.name) + ' \u00b7 ' + formatDate(bride.weddingDate) + '</p>' +
    '</div>' +
    '<div class="preview-body">' +
      '<div class="preview-section">' +
        '<h4>Package Details</h4>' +
        '<div class="moxie-preview-list">' + previewItemsHtml + '</div>' +
        '<div class="preview-totals">' +
          '<div class="line"><span>Subtotal (ex GST)</span><span>A' + formatCurrency(subtotal) + '</span></div>' +
          '<div class="line"><span>GST (10%)</span><span>A' + formatCurrency(gst) + '</span></div>' +
          '<div class="line grand"><span>Total (inc GST)</span><span>A' + formatCurrency(total) + '</span></div>' +
        '</div>' +
      '</div>' +
    '</div>' +
  '</div>';

  // Package Options card -- variant builder lives on page 3 (2026-07-02)
  html += buildVariantBuilderCard_();

  // Navigation buttons
  html += '<div style="margin-top:18px;display:flex;gap:10px;align-items:center">' +
    '<button class="btn btn-ghost" onclick="setStage(\'builder\')">' +
      '<i class="fa-solid fa-arrow-left"></i> Edit Package' +
    '</button>' +
    '<button class="btn btn-primary" onclick="setStage(\'send\')">' +
      'Looks Good, Proceed to Send <i class="fa-solid fa-arrow-right"></i>' +
    '</button>' +
  '</div>';

  document.getElementById('workspace').innerHTML = pbSnapshotFallbackNoticeHtml_() + html + renderProposalHistoryHtml_();
  pbApplySnapshotFallbackUiState_();
}

/* =================================================================
   STAGE 4 -- SEND
   ================================================================= */
function renderStageSend() {
  var ws = state.workspace;
  var calc = ws.calculation || {};
  var bride = ws.bride || {};
  var proposal = ws.proposal || {};
  var items = state.editedLineItems || calc.normalizedLineItems || calc.lineItems || [];

  var subtotal = calcSubtotal(items);
  var gst = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;

  // A1 2026-11-02 defect 1: snapshot-vs-recalc total hard invariant. If the
  // flag is OFF or there's no snapshot to compare against, this is null and
  // nothing changes. If a mismatch is detected, render the banner and SKIP
  // every outward control on this stage. See pbTotalMismatchGate_.
  var mismatchGate_send_ = pbTotalMismatchGate_(total);
  if (mismatchGate_send_ && mismatchGate_send_.mismatch) {
    var mismatchHtml_send_ = '<div class="card">' +
      '<div class="card-title"><i class="fa-solid fa-paper-plane"></i> Send Proposal</div>' +
      pbTotalMismatchBanner_(mismatchGate_send_) +
      '<div style="margin-top:8px"><button class="btn btn-ghost" onclick="setStage(\'review\')">' +
      '<i class="fa-solid fa-arrow-left"></i> Back to Review</button></div>' +
      '</div>';
    document.getElementById('workspace').innerHTML = mismatchHtml_send_;
    return;
  }

  // DRAFT_EDIT can mean either "reopened after a real send" or "first proposal
  // still being edited after a preview token was minted". Only the former should
  // render the resend/void panel; otherwise Jess loses the real first-send button.
  var statusUpperSend_ = String(proposal.status || '').trim().toUpperCase();
  // A1 2026-11-02 sent-evidence scoping (diagnostic defect 3):
  // the historical check treated ANY row in state.proposalHistory as proof the
  // current DRAFT_EDIT was already sent, so 13 legacy JFG rows pushed the
  // current fresh token into re-send mode (Email+SMS checked by default). Fix:
  // scope sent evidence to the CURRENT token/version only. Behind dark flag
  // window.JV_PB_SENT_EVIDENCE_SCOPED (default OFF) so the fix is dormant
  // until the Worker's additive proposal.token field is live, and until Jess
  // flips the flag (see BRIEFS/PLAN-sub-a1-2026-11-02.md).
  var historyRows_ = state.proposalHistory || [];
  var currentToken_ = (proposal && proposal.token) ? String(proposal.token) : '';
  var scopedHasHistory_ = currentToken_
    ? historyRows_.some(function (h) {
        if (!h) return false;
        // History rows arrive from both GAS (eventData.token) and the CF
        // workspace (eventData.token on the normalised shape). Match either.
        var evt = (h.eventData && h.eventData.token) || h.token || '';
        return evt && String(evt) === currentToken_;
      })
    : false;
  var hasSentEvidence_ = !!proposal.sentDate || !!proposal.emailSentAt || (
    window.JV_PB_SENT_EVIDENCE_SCOPED === true && pbJfgOnlyScope_()
      ? scopedHasHistory_
      : historyRows_.length > 0
  );
  var isSent = statusUpperSend_ !== '' && statusUpperSend_ !== 'READY' &&
    (statusUpperSend_ !== 'DRAFT_EDIT' || hasSentEvidence_);

  var html = '';

  // Confirmation summary
  html += '<div class="card">' +
    '<div class="card-title"><i class="fa-solid fa-paper-plane"></i> Send Proposal</div>';

  if (isSent) {
    var isVoid = statusUpperSend_ === 'VOID';
    // Terminal/non-editable states: proposalReopenForEdit only accepts SENT/READY,
    // so Void, Expired, Declined, Deposit Paid, Calc Failed all hit the same dead-end
    // Edit button did before this fix (2026-07-28, see HANDOFF-technical-stability-
    // item3-...). Treat them the same read-only way as Void, just with the correct
    // label instead of always saying "voided".
    // SIGNED / ACCEPTED added 2026-08-26 (LANE-proposal-builder-live-issues-2026-08-26.md
    // Item 1): a signed proposal was hitting this exact dead-end too, because the
    // signing writer (ProposalSignatureAPI.gs, owned by the Payments agent) stamps
    // PROPOSAL_STATUS literally as 'Signed', not 'ACCEPTED' -- a cross-agent status-
    // string mismatch this file can't fix at the source, but both values are covered
    // here defensively so the Edit button never appears on either.
    // 2026-09-10: this set now lives in the shared pbIsTerminalStatus_ helper (also
    // used by the header button, item 10) -- kept as the same exact values, just
    // de-duplicated rather than re-typed a second time.
    var isOtherTerminal = !isVoid && pbIsTerminalStatus_(statusUpperSend_);
    if (isVoid || isOtherTerminal) {
      // Voided / other terminal state -- show read-only state, no action buttons.
      var terminalLabel = isVoid ? 'VOIDED' : statusUpperSend_;
      var terminalVerb = isVoid ? 'been voided' : 'reached a final status (' + escapeHtml(proposal.status) + ')';
      html += '<div style="padding:12px 0;font-size:14px">' +
        '<span class="badge badge-expired" style="margin-right:8px">' + escapeHtml(terminalLabel) + '</span>' +
        'This proposal has ' + terminalVerb + ' for <strong>' + escapeHtml(bride.name) + '</strong>.' +
      '</div>' +
      '<div style="margin-top:8px">' +
        '<button class="btn btn-ghost" onclick="setStage(\'review\')">' +
          '<i class="fa-solid fa-arrow-left"></i> Back to Review' +
        '</button>' +
      '</div>';
    } else {
      // Already sent -- show status, re-send / void, and edit options.
      // isDraftEditAlready: proposalReopenForEdit only accepts SENT/READY (see
      // ProposalBuilderAPI.gs) -- a proposal already in DRAFT_EDIT is already open
      // for editing, so showing "Edit Proposal" here just re-hits that guard and
      // shows Jess a confusing "Current status: DRAFT_EDIT" error for no reason
      // (recurring in the live error log, see BRIEFS/HANDOFF-proposal-builder-
      // real-user-errors-2026-07-25.md). Hide the button in that state instead.
      var isDraftEditAlready = statusUpperSend_ === 'DRAFT_EDIT';
      var revCount = proposal.revisionCount || 0;
      var revBadge = revCount > 1
        ? '<span class="status-badge badge-ready" style="margin-left:8px;font-size:11px">Rev ' + revCount + '</span>'
        : '';
      // Persistent, on-page sent confirmation (2026-09-30, HANDOFF-proposal-
      // builder-no-persistent-sent-confirmation-2026-09-30.md). Jess's own
      // words: "It doesn't actually show any indication it's been sent on the
      // UI ... I usually have to go into my Gmail sent box to clarify." This
      // reads the SAME durable server fields the workspace payload already
      // carries (proposal.emailSentAt / smsSentAt, from the CRM columns
      // PROPOSAL_EMAIL_SENT / PROPOSAL_SMS_SENT) so it is correct on first
      // send, on a reload, and days later -- not a client-only flag that a
      // toast or a refresh would lose. Only shown for a genuine send (not the
      // "already reopened for editing" case above it).
      var _sentSummaryCard = '';
      if (!isDraftEditAlready) {
        var _channelSummary = pbSendChannelSummary_(proposal);
        var _proposalToken = proposal.token || '';
        var _viewerBase = (typeof JV_PROPOSAL_VIEWER_URL !== 'undefined' ? JV_PROPOSAL_VIEWER_URL : 'https://jessicavegas.com/pages/your-bridal-beauty-package');
        var _viewerUrl = _proposalToken ? (_viewerBase + '?token=' + encodeURIComponent(_proposalToken)) : '';
        _sentSummaryCard = '<div class="banner banner-success" style="margin-bottom:10px" role="status">' +
          '<i class="fa-solid fa-circle-check"></i>' +
          '<span>Sent to <strong>' + escapeHtml(bride.email || bride.name || '') + '</strong>' +
            (_channelSummary ? '  |  ' + escapeHtml(_channelSummary) : '') +
          '</span>' +
          (_viewerUrl
            ? '<button type="button" class="btn btn-ghost btn-sm" style="margin-left:auto;white-space:nowrap" onclick="copyExistingProposalLink()"><i class="fa-solid fa-copy"></i> Copy Link</button>' +
              '<a href="' + escapeHtml(_viewerUrl) + '" target="_blank" class="btn btn-ghost btn-sm" style="text-decoration:none;white-space:nowrap"><i class="fa-solid fa-eye"></i> View as Bride</a>'
            : '') +
        '</div>';
      }
      html += _sentSummaryCard +
      '<div style="padding:12px 0;color:var(--muted);font-size:14px">' +
        (isDraftEditAlready
          ? '<span class="status-badge badge-ready">EDITING</span> This proposal is already reopened for editing for <strong>' + escapeHtml(bride.name) + '</strong>.' + revBadge
          : 'This proposal has already been sent to <strong>' + escapeHtml(bride.name) + '</strong>.' + revBadge) +
      '</div>' +
      '<div style="display:flex;gap:10px;margin-top:8px;flex-wrap:wrap">' +
        '<button class="btn btn-ghost" onclick="resendProposal()"><i class="fa-solid fa-rotate-right"></i> Re-send Proposal</button>' +
        (isDraftEditAlready ? '' : '<button class="btn btn-ghost" id="editProposalBtn" onclick="editProposal()"><i class="fa-solid fa-pen-to-square"></i> Edit Proposal</button>') +
        '<button class="btn btn-danger btn-sm" onclick="voidProposal()"><i class="fa-solid fa-ban"></i> Void Proposal</button>' +
        '<button class="btn btn-ghost" onclick="setStage(\'review\')">' +
          '<i class="fa-solid fa-arrow-left"></i> Back to Review' +
        '</button>' +
      '</div>';
    }
  } else {
    // Not yet sent (or DRAFT_EDIT) -- show confirmation and send options
    var isDraftEdit = proposal.status === 'DRAFT_EDIT';
    var revCountDraft = proposal.revisionCount || 0;
    var revBadgeDraft = revCountDraft > 1
      ? '<span class="status-badge badge-ready" style="margin-left:6px;font-size:11px">Rev ' + revCountDraft + '</span>'
      : '';
    html += '<div style="padding:12px 0;font-size:14px;line-height:1.6">' +
      (isDraftEdit ? '<div style="margin-bottom:8px"><span class="status-badge badge-ready">EDITING</span>' + revBadgeDraft + ' <span style="color:var(--muted);font-size:13px">Revision in progress</span></div>' : '') +
      'You are about to send this proposal to:<br>' +
      '<strong style="font-size:16px;color:var(--brand-dark)">' + escapeHtml(bride.name) + '</strong><br>' +
      '<span style="color:var(--muted)">' + escapeHtml(bride.email || 'No email on file') + '</span><br>' +
      '<span style="font-size:15px;font-weight:600;color:var(--brand-dark);margin-top:6px;display:inline-block">' +
        'Total: A' + formatCurrency(total) + ' inc GST' +
      '</span>' +
    '</div>' +
    '<div class="send-options" style="margin-top:12px">' +
      '<label class="send-option"><input type="checkbox" id="optEmail" checked> Send email to bride</label>' +
      '<label class="send-option"><input type="checkbox" id="optSms" checked> Send SMS notification</label>' +
    '</div>' +
    '<div style="margin-top:18px;display:flex;gap:10px;flex-wrap:wrap">' +
      '<button class="btn btn-ghost" onclick="setStage(\'review\')">' +
        '<i class="fa-solid fa-arrow-left"></i> Go Back' +
      '</button>' +
      '<button class="btn btn-ghost" id="previewEmailBtn" onclick="showEmailPreviewModal()">' +
        '<i class="fa-solid fa-envelope-open-text"></i> Preview Email' +
      '</button>' +
      '<button class="btn btn-ghost" id="publishOnlyBtn" onclick="publishOnly()">' +
        '<i class="fa-solid fa-link"></i> Publish (No Notification)' +
      '</button>' +
      '<button class="btn btn-ghost" id="publishCopyBtn" onclick="publishAndCopyLink()">' +
        '<i class="fa-solid fa-copy"></i> Publish &amp; Copy Link' +
      '</button>' +
      '<button class="btn btn-primary" id="approveBtn" onclick="approveAndSend()">' +
        '<i class="fa-solid fa-paper-plane"></i> Approve & Send Proposal' +
      '</button>' +
    '</div>';
  }

  html += '</div>';

  document.getElementById('workspace').innerHTML = pbSnapshotFallbackNoticeHtml_() + html + renderProposalHistoryHtml_();
  pbApplySnapshotFallbackUiState_();
}

/* =================================================================
   ACTIONS
   ================================================================= */
// PATH 2: Recalculate is now a local-only operation. It re-derives pricing from
// state without a GAS round-trip. The "Save" button persists changes.
function recalculate() {
  if (!state.workspace) return;
  var fresh = recalcAndMerge_();
  // Clear structural edits (add/remove rows) but preserve priceOverrides;
  // they reapply on top of fresh calc items in renderStageBuilder().
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  // FIX (2026-08-25, Check 1's "Recalculate no-op" finding in
  // BRIEFS/RESULTS-COWORK-pricing-fixes-live-verify-2026-08-25-part2.md):
  // renderActiveStage() only repaints renderStageBuilder() when
  // state.activeStage === 'builder' -- if the admin recalculates from a
  // different stage (e.g. mid-navigation, or a future stage-tracking bug),
  // the recalc SILENTLY updates state.workspace.calculation correctly but
  // never repaints anything visible, no error, no banner. Recalculate is
  // specifically a Builder-stage action -- force the correct renderer
  // directly rather than trusting whatever stage happens to be active, so
  // this can never again look identical to "did nothing" from the state the
  // admin was actually looking at. Belt-and-braces on top of the existing
  // ITEM 3 (2026-08-04) banner-detail fix below, not a replacement for it.
  renderStageBuilder();

  // ITEM 3 (2026-08-04): the button was never silent -- this banner already fired and
  // auto-dismisses after 5s. The defect was that "Recalculated locally" says nothing
  // observable, so a recalc that genuinely changed numbers looked identical to one that
  // did nothing, and the admin re-clicked. Naming the resulting figures makes the effect
  // checkable at a glance. Deliberately still LOCAL ONLY: no GAS call is added here,
  // that would reintroduce the ~8s latency the Path 2 client-owned rebuild removed.
  var svcCount = (fresh && Array.isArray(fresh.lineItems)) ? fresh.lineItems.length : 0;
  var total    = (fresh && Number(fresh.total)) || 0;
  var detail   = svcCount
    ? svcCount + ' line item' + (svcCount === 1 ? '' : 's') + ', ' + formatCurrency(total)
    : '';
  showBanner(
    'Recalculated locally' + (detail ? ' · ' + detail : '') + '. Click Save to persist.',
    'info'
  );
}

// PATH 2: Save Line Items now commits editedLineItems into calculation.lineItems
// locally and marks dirty. flushToGas_() persists on Save / Generate Preview.
function saveLineItems() {
  if (!state.workspace || !state.editedLineItems) return;
  if (!state.workspace.calculation) state.workspace.calculation = {};
  state.workspace.calculation.lineItems = state.editedLineItems.slice();
  state.workspace.calculation.normalizedLineItems = state.editedLineItems.slice();
  // Re-derive totals
  var subtotal = 0;
  state.editedLineItems.forEach(function(li) {
    subtotal += Number(li.total || ((li.qty || li.quantity || 1) * (li.unitPrice || li.rate || 0)));
  });
  state.workspace.calculation.subtotal = subtotal;
  state.workspace.calculation.gst = Math.round(subtotal * 0.10 * 100) / 100;
  state.workspace.calculation.total = state.workspace.calculation.subtotal + state.workspace.calculation.gst;
  state.lineItemsManuallyEdited = true;
  markDirty_();
  showBanner('Line items staged — click Save to persist', 'info');
}

// PATH 2: flushToGas_() persists the full client state, then proposalGeneratePreview
// creates the token. Two GAS calls total — but the flush replaces the legacy
// proposalUpdateLineItems precursor and is the SINGLE persistence path.
function generatePreviewUrl() {
  if (!state.currentBride) return;
  var btn = document.getElementById('generatePreviewBtn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving + generating...';
  }
  showLoadingOverlay('Saving and generating preview...');

  var doGenerate = function() {
    // FIX (2026-09-04, frozen-pricing display bug, HANDOFF-proposal-builder-agent-
    // frozen-pricing-display-and-payto-copy-2026-09-04.md): flushToGas_() already
    // replaced state.workspace.calculation with the real, frozen, GAS-canonical
    // numbers (its own success handler does this correctly) -- but nothing here
    // ever repainted the builder's own on-screen stage from that corrected value.
    // If an earlier edit-driven recalcAndMerge_() call had left the DOM showing a
    // live-D1-rate phantom price (calcLocalEstimate_ / proposalOverlayBookingTierLocal_
    // pull current wedding-year rates unconditionally, with no frozen-quote check),
    // that phantom stayed on screen forever after Generate Preview, since this
    // function only opens the bride's preview URL and never calls renderActiveStage().
    // Repainting here matches what manualSave_() already correctly does after its
    // own flush.
    renderActiveStage();
    jvBridge.callAdmin('proposalGeneratePreview', {
      name: state.currentBride,
      email: (state.workspace && state.workspace.bride && state.workspace.bride.email) || '',
      brideUid: (state.workspace && state.workspace.bride && state.workspace.bride.brideUid) || ''
    })
      .then(function(res) {
        hideLoadingOverlay();
        var payload = (res && res.data) || res;
        // Guard 1: explicit error status OR a worker/bridge failure envelope
        // ({ ok:false } from a 504 "Upstream failed" etc.) which has no `status`.
        if (!payload || payload.status === 'error' || payload.ok === false) {
          if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-eye"></i> Generate Preview'; }
          showBanner((payload && (payload.message || payload.error)) || 'Preview generation failed (the save may have timed out). Please try again.', 'error');
          return;
        }
        // Guard 2: never open a tab with an empty/undefined token. A missing token
        // means the mint did not complete, so `...?token=` / `?token=undefined` would
        // load an invalid bride-facing URL. Fail loudly instead of opening garbage.
        if (!payload.previewUrl && !payload.token) {
          if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-eye"></i> Generate Preview'; }
          showBanner('Preview did not generate a valid link (no token returned). Please try again.', 'error');
          return;
        }
        state.workspace.proposal.token = payload.token || '';
        showBanner('Preview generated -- opening in new tab', 'success');
        var wrap = document.getElementById('previewBtnWrap');
        if (wrap) {
          var _viewerBase = (typeof JV_PROPOSAL_VIEWER_URL !== 'undefined' ? JV_PROPOSAL_VIEWER_URL : 'https://jessicavegas.com/pages/your-bridal-beauty-package');
          var url = payload.previewUrl || (_viewerBase + '?token=' + encodeURIComponent(payload.token));
          wrap.innerHTML = '<a href="' + escapeHtml(url) + '" target="_blank" class="btn btn-ghost btn-sm" style="text-decoration:none">' +
            '<i class="fa-solid fa-eye"></i> Preview Bride\'s View' +
          '</a>' +
          '<span style="font-size:12px;color:var(--muted)">Opens the proposal page as the bride will see it</span>';
          window.open(url, '_blank');
        }
      })
      .catch(function(err) {
        hideLoadingOverlay();
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-eye"></i> Generate Preview'; }
        showBanner(getErrorMessage(err), 'error');
      });
  };

  flushToGas_()
    .then(doGenerate)
    .catch(function(err) {
      hideLoadingOverlay();
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-eye"></i> Generate Preview'; }
      showBanner('Failed to save before preview: ' + getErrorMessage(err), 'error');
    });
}

// PATH 2: flush full state, then do the send action. Single unified path.
function approveAndSend() {
  if (!state.currentBride) return;
  var btn = document.getElementById('approveBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving...'; }
  showLoadingOverlay('Saving and sending proposal...');
  flushToGas_()
    .then(doApproveAndSend)
    .catch(function(err) {
      hideLoadingOverlay();
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-check-circle"></i> Approve & Send Proposal'; }
      showBanner('Failed to save before send: ' + getErrorMessage(err), 'error');
    });
}

function publishOnly() {
  var btn = document.getElementById('publishOnlyBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving + publishing...'; }
  showLoadingOverlay('Saving and publishing proposal...');

  var doPublish = function() {
    pbApproveAndSendWarm_(state.currentBride, { sendEmail: false, sendSms: false, firedBy: (window.jvAuth && window.jvAuth.email) || '' })
      .then(function(res) {
        hideLoadingOverlay();
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-link"></i> Publish (No Notification)'; }
        var payload = (res && res.data) || res;
        if (!payload || payload.status === 'error') {
          showBanner((payload && payload.message) || 'Publish failed', 'error');
          return;
        }
        showBanner('Proposal published — link is active. No notification sent.', 'success');
        state.workspace.proposal.status = 'SENT';
        state.workspace.proposal.sentDate = new Date().toISOString().split('T')[0];
        state.workspace.proposal.token = payload.token || '';
        renderStageSend();
        // Instant + reliable (2026-07-28): patch the sidebar now, don't wait on the
        // refetch below (see patchBrideLocal_ note). loadBrides() still runs to
        // reconcile everything else.
        patchBrideLocal_(state.currentBride, { proposalStatus: 'SENT', sentDate: state.workspace.proposal.sentDate });
        loadBrides();
      })
      .catch(function(err) {
        hideLoadingOverlay();
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-link"></i> Publish (No Notification)'; }
        showBanner(getErrorMessage(err), 'error');
      });
  };

  flushToGas_()
    .then(doPublish)
    .catch(function(err) {
      hideLoadingOverlay();
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-link"></i> Publish (No Notification)'; }
      showBanner('Failed to save before publish: ' + getErrorMessage(err), 'error');
    });
}

// Build the bride-facing proposal URL from a token.
function proposalViewerUrl_(token) {
  var base = (typeof JV_PROPOSAL_VIEWER_URL !== 'undefined' ? JV_PROPOSAL_VIEWER_URL : 'https://jessicavegas.com/pages/your-bridal-beauty-package');
  return base + '?token=' + encodeURIComponent(token || '');
}

// Copy a URL to the clipboard (with a legacy fallback) and confirm via the banner.
function copyProposalLink_(url) {
  if (!url) { showBanner('No proposal link to copy', 'error'); return; }
  var ok = function() { showBanner('Proposal link copied to clipboard', 'success'); };
  var fallback = function() {
    try {
      var ta = document.createElement('textarea');
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta); ok();
    } catch (e) { showBanner('Copy failed. Link: ' + url, 'info'); }
  };
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url).then(ok).catch(fallback);
  } else { fallback(); }
}

// Copy the already-generated proposal link (used once a token exists).
function copyExistingProposalLink() {
  var tok = (state.workspace && state.workspace.proposal && state.workspace.proposal.token) || '';
  if (!tok) { showBanner('Generate or publish the proposal first', 'error'); return; }
  copyProposalLink_(proposalViewerUrl_(tok));
}

// Publish the proposal (link goes live, NO email/SMS) and copy the link to the
// clipboard so it can be sent manually. Mirrors publishOnly() + an auto-copy.
function publishAndCopyLink() {
  if (!state.currentBride) return;
  var btn = document.getElementById('publishCopyBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Publishing...'; }
  var resetBtn = function() {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-link"></i> Publish &amp; Copy Link'; }
  };
  var doIt = function() {
    pbApproveAndSendWarm_(state.currentBride, { sendEmail: false, sendSms: false, firedBy: (window.jvAuth && window.jvAuth.email) || '' })
      .then(function(res) {
        resetBtn();
        var payload = (res && res.data) || res;
        if (!payload || payload.status === 'error') { showBanner((payload && payload.message) || 'Publish failed', 'error'); return; }
        state.workspace.proposal.status = 'SENT';
        state.workspace.proposal.sentDate = new Date().toISOString().split('T')[0];
        state.workspace.proposal.token = payload.token || '';
        copyProposalLink_(payload.previewUrl || proposalViewerUrl_(payload.token));
        renderStageSend();
        patchBrideLocal_(state.currentBride, { proposalStatus: 'SENT', sentDate: state.workspace.proposal.sentDate });
        loadBrides();
      })
      .catch(function(err) { resetBtn(); showBanner(getErrorMessage(err), 'error'); });
  };
  flushToGas_().then(doIt).catch(function(err) {
    resetBtn();
    showBanner('Failed to save before publish: ' + getErrorMessage(err), 'error');
  });
}

function doApproveAndSend() {
  var btn = document.getElementById('approveBtn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending...';
  }

  var options = {
    sendEmail: document.getElementById('optEmail') ? document.getElementById('optEmail').checked : true,
    sendSms: document.getElementById('optSms') ? document.getElementById('optSms').checked : true,
    firedBy: (window.jvAuth && window.jvAuth.email) || ''
  };

  pbApproveAndSendWarm_(state.currentBride, options)
    .then(function(res) {
      hideLoadingOverlay();
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<i class="fa-solid fa-check-circle"></i> Approve & Send Proposal';
      }
      // Bridge wraps to { ok, data: { status, token, xeroQuoteId, message } }. Defensive single-unwrap.
      var payload = (res && res.data) || res;
      if (!payload || payload.status === 'error') {
        showBanner((payload && payload.message) || 'Send failed', 'error');
        return;
      }
      // M1 (2026-07-09): the server can return status:'ok' but emailSent:false when the
      // bride email hard-failed. In that case the server intentionally did NOT mark the
      // proposal Sent (it held it as READY) and fired an admin alert. Do not claim
      // success or flip the local status to SENT — warn so Jess retries. Only treat a
      // false emailSent as a failure when email was actually requested (optEmail on).
      var _emailWasRequested = options.sendEmail !== false;
      if (_emailWasRequested && payload.emailSent === false) {
        showBanner('Saved, but the proposal email did not send. It is still marked Ready. Please retry Send. (' + (payload.emailError || 'email error') + ')', 'error');
        loadBrides(); // refresh sidebar so the still-Ready status shows
        if (state.currentBride) loadProposalHistory_(state.currentBride);
        return;
      }
      // Update local state
      state.workspace.proposal.status = 'SENT';
      state.workspace.proposal.sentDate = new Date().toISOString().split('T')[0];
      state.workspace.proposal.token = payload.token || '';
      state.workspace.proposal.xeroQuoteId = payload.xeroQuoteId || '';
      // FIX (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-confirmation-
      // 2026-09-30.md): optimistically stamp the channel-sent timestamps the
      // instant we know they genuinely went out (email already confirmed above --
      // emailSent:false returned early -- and a deferred send's channels are
      // still pending, so leave those blank rather than claim a false timestamp;
      // the next workspace reload picks up the real PROPOSAL_EMAIL_SENT /
      // PROPOSAL_SMS_SENT value from the CRM row regardless). This is what
      // finally lets the page ITSELF say what happened, not just a 5s toast.
      var _sendStamp = pbNowStamp_();
      if (!payload.deferredJobId) {
        if (_emailWasRequested) state.workspace.proposal.emailSentAt = _sendStamp;
        if (options.sendSms !== false) state.workspace.proposal.smsSentAt = _sendStamp;
      }
      renderStageSend();
      renderSavePill_();
      patchBrideLocal_(state.currentBride, { proposalStatus: 'SENT', sentDate: state.workspace.proposal.sentDate });
      loadBrides(); // Refresh sidebar
      var _sentChannels = pbSendChannelSummary_(state.workspace.proposal);
      var _sentToLabel = (state.workspace.bride && state.workspace.bride.email) || 'the bride';
      showBanner((payload.deferredJobId
        ? 'Proposal sent to ' + _sentToLabel + '. Finishing background tasks...'
        : 'Sent to ' + _sentToLabel + (_sentChannels ? '  (' + _sentChannels + ')' : '')), 'success', true);
      // Deferred sends append history in the background, so give the minute-drain
      // a short head start before refreshing that panel.
      if (state.currentBride) {
        if (payload.deferredJobId) {
          setTimeout(function() { if (state.currentBride) loadProposalHistory_(state.currentBride); }, 8000);
        } else {
          loadProposalHistory_(state.currentBride);
        }
      }
    })
    .catch(function(err) {
      hideLoadingOverlay();
      var resetApproveBtn = function() {
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = '<i class="fa-solid fa-check-circle"></i> Approve & Send Proposal';
        }
      };
      if (pbClassifySendOutcome_(err) === 'unknown') {
        pbResolveSendOutcome_(state.currentBride, resetApproveBtn);
        return;
      }
      resetApproveBtn();
      showBanner(getErrorMessage(err), 'error');
    });
}

function resendProposal() {
  if (!state.currentBride) return;
  showResendModal_();
}

// Extend the real Cloudflare proposal-snapshot expiry without sending any email or SMS.
// There is deliberately no GAS fallback: the similarly named GAS action updates a field
// that the bride-facing sign path does not read.
function reactivateProposalLink() {
  if (!state.currentBride) return;
  var proposal = (state.workspace && state.workspace.proposal) || {};
  if (!proposal.token) {
    showBanner('This proposal does not have a link to reactivate.', 'error');
    return;
  }

  showLoadingOverlay('Reactivating proposal link...');
  pbCfBuilderWrite_('reactivate', { token: proposal.token, newExpiryDays: 30 }, function(b) {
    return !!(b && b.ok === true && b.viewerUrl);
  }).then(function(result) {
    if (result && result.refusal) {
      pbShowCfWriteRefusal_(result.refusal);
      return;
    }
    var cf = result && result.cf;
    if (!cf) {
      hideLoadingOverlay();
      showBanner("Not available right now. The Reactivate feature isn't turned on yet.", 'warn');
      return;
    }

    hideLoadingOverlay();
    proposal.expiryDate = cf.newExpiryDate || proposal.expiryDate;
    renderActiveStage();
    showReactivateLinkModal_(cf);
  });
}

function showReactivateLinkModal_(result) {
  var previousFocus = document.activeElement;
  var expiryLabel = result.newExpiryDate ? formatDate(result.newExpiryDate) : '30 days from today';
  var overlay = document.createElement('div');
  overlay.id = 'reactivateLinkOverlay';
  overlay.setAttribute('role', 'presentation');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.55);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
  overlay.innerHTML =
    '<div role="dialog" aria-modal="true" aria-labelledby="reactivateLinkTitle" style="background:var(--brand-bg-white);border-radius:var(--radius-large);padding:28px;max-width:520px;width:100%;box-shadow:var(--shadow-card);display:flex;flex-direction:column;gap:18px">' +
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<i class="fa-solid fa-link" aria-hidden="true" style="color:var(--brand-primary);font-size:18px"></i>' +
        '<h2 id="reactivateLinkTitle" style="font-size:17px;font-weight:600;color:var(--brand-dark);margin:0">Proposal Link Reactivated</h2>' +
      '</div>' +
      '<p style="font-size:14px;color:var(--brand-text);line-height:1.6;margin:0">The proposal can now be signed until <strong>' + escapeHtml(expiryLabel) + '</strong>. No email or SMS was sent.</p>' +
      '<div style="display:flex;flex-direction:column;gap:7px">' +
        '<label for="reactivatedProposalUrl" style="font-size:13px;font-weight:600;color:var(--brand-text)">Proposal link</label>' +
        '<input id="reactivatedProposalUrl" type="text" readonly value="' + escapeHtml(result.viewerUrl) + '" style="width:100%;min-height:44px;border:1px solid rgba(47,37,36,0.15);border-radius:var(--radius-medium);padding:10px 12px;font-size:13px;color:var(--brand-text);background:var(--brand-bg-soft);box-sizing:border-box">' +
      '</div>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap">' +
        '<button id="reactivateCloseBtn" class="btn btn-ghost btn-sm" style="min-height:44px">Close</button>' +
        '<button id="reactivateCopyBtn" class="btn btn-primary btn-sm" style="min-height:44px"><i class="fa-solid fa-copy" aria-hidden="true"></i> Copy Link</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);

  var closeBtn = document.getElementById('reactivateCloseBtn');
  var copyBtn = document.getElementById('reactivateCopyBtn');
  var focusable = [closeBtn, copyBtn];
  var close = function() {
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
    var replacement = document.getElementById('reactivateProposalBtn');
    if (replacement) replacement.focus();
    else if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
  };
  var onKeydown = function(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  closeBtn.addEventListener('click', close);
  copyBtn.addEventListener('click', function() { copyProposalLink_(result.viewerUrl); });
  overlay.addEventListener('click', function(e) { if (e.target === overlay) close(); });
  document.addEventListener('keydown', onKeydown);
  copyBtn.focus();
}

function showResendModal_() {
  var bride = (state.workspace && state.workspace.bride) || {};
  var brideEmail = bride.email || '';
  var brideName = state.currentBride || '';

  var overlay = document.createElement('div');
  overlay.id = 'resendOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.55);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:16px;padding:28px;max-width:480px;width:100%;box-shadow:0 8px 40px rgba(47,37,36,0.18);display:flex;flex-direction:column;gap:18px">' +
      '<div style="display:flex;align-items:center;gap:10px">' +
        '<i class="fa-solid fa-rotate-right" style="color:var(--brand-primary);font-size:18px"></i>' +
        '<span style="font-size:17px;font-weight:700;color:#1e1716">Re-send Proposal</span>' +
      '</div>' +
      '<div style="background:#f6f1ee;border-radius:10px;padding:12px 14px;font-size:13px;color:#2f2524;line-height:1.6">' +
        '<strong>' + escapeHtml(brideName) + '</strong><br>' +
        '<span style="color:#7e706c">' + escapeHtml(brideEmail || 'No email on file') + '</span>' +
      '</div>' +
      '<div style="display:flex;flex-direction:column;gap:6px">' +
        '<label style="font-size:13px;font-weight:600;color:#2f2524">Personal note <span style="font-weight:400;color:#9a918a">(optional)</span></label>' +
        '<textarea id="resendNote" rows="3" placeholder="Add a personal message — e.g. \'I\'ve updated the pricing as discussed and added the extra bridesmaid. Let me know if you have any questions!\'" ' +
          'style="width:100%;border:1.5px solid #e6dad4;border-radius:10px;padding:10px 12px;font-size:13px;color:#2f2524;resize:vertical;font-family:inherit;box-sizing:border-box;outline:none" ' +
          'onfocus="this.style.borderColor=\'#c99591\'" onblur="this.style.borderColor=\'#e6dad4\'"></textarea>' +
        '<p style="font-size:12px;color:#9a918a;margin:0">This note appears at the top of the email, above the package details.</p>' +
      '</div>' +
      '<div style="display:flex;flex-direction:column;gap:10px">' +
        '<label style="font-size:13px;font-weight:600;color:#2f2524">Send via</label>' +
        '<div style="display:flex;gap:12px">' +
          '<label style="display:flex;align-items:center;gap:8px;font-size:13px;color:#2f2524;cursor:pointer">' +
            '<input type="checkbox" id="resendOptEmail" checked style="accent-color:var(--brand-primary);width:15px;height:15px"> Email' +
          '</label>' +
          '<label style="display:flex;align-items:center;gap:8px;font-size:13px;color:#2f2524;cursor:pointer">' +
            '<input type="checkbox" id="resendOptSms" checked style="accent-color:var(--brand-primary);width:15px;height:15px"> SMS' +
          '</label>' +
        '</div>' +
      '</div>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end;padding-top:4px">' +
        '<button id="resendCancelBtn" class="btn btn-ghost btn-sm">Cancel</button>' +
        '<button id="resendSendBtn" class="btn btn-primary btn-sm"><i class="fa-solid fa-rotate-right"></i> Re-send Proposal</button>' +
      '</div>' +
    '</div>';

  document.body.appendChild(overlay);

  document.getElementById('resendCancelBtn').addEventListener('click', function() { overlay.remove(); });
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });

  document.getElementById('resendSendBtn').addEventListener('click', function() {
    var btn = document.getElementById('resendSendBtn');
    btn.disabled = true;
    btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending...';
    var note = (document.getElementById('resendNote').value || '').trim();
    var sendEmail = document.getElementById('resendOptEmail').checked;
    var sendSms = document.getElementById('resendOptSms').checked;
    overlay.remove();
    doResend_({ personalNote: note, sendEmail: sendEmail, sendSms: sendSms });
  });
}

// PATH 2: flush full state, then resend.
//
// P1-3 fix (2026-08-31, Proposal Builder Agent, BRIEFS/RESULTS-proposal-builder-
// grade3-xray-2026-08-30.md). This is the exact function Cowork's live Test 1
// exercised (resendProposal, showResendModal_, here) and could not tell whether
// the checksum guard silently blocked the send, the send actually succeeded but
// the panel did not refresh, or it hit the systemic 40-55s relay timeout root-
// caused separately the same day. All three are now distinguishable, matching
// doApproveAndSend's existing handling which this function was missing:
//   1. payload.status === 'error': includes the checksum guard's own message
//      (already worked before this fix, kept as-is).
//   2. payload.status === 'ok' but payload.emailSent === false: the guard/send
//      held the proposal at READY and did NOT mark it Sent. Previously this
//      function showed a generic success banner regardless, hiding the failure.
//   3. A timeout/network throw: previously a bare generic error banner with no
//      way to tell "definitely failed" from "may have succeeded, check status".
//      Now runs the same pbResolveSendOutcome_ check as doApproveAndSend, which
//      re-reads the bride's live workspace status and reports definitively, or
//      shows a persistent (not auto-fading) Outcome Unknown banner if it can't.
function doResend_(opts) {
  var _emailWasRequested = opts.sendEmail !== false;
  var doSend = function() {
    pbApproveAndSendWarm_(state.currentBride, {
      sendEmail:    opts.sendEmail !== false,
      sendSms:      opts.sendSms !== false,
      personalNote: opts.personalNote || '',
      isResend:     true,
      firedBy:      (window.jvAuth && window.jvAuth.email) || ''
    })
    .then(function(res) {
      var payload = (res && res.data) || res;
      if (!payload || payload.status === 'error') {
        showBanner((payload && payload.message) || 'Re-send failed', 'error');
        return;
      }
      // Same guard as doApproveAndSend: status:'ok' but emailSent:false means the
      // server intentionally held the proposal (did not mark it Sent) and fired
      // an admin alert. Never claim success in this case.
      if (_emailWasRequested && payload.emailSent === false) {
        showBanner('Re-send did not go out. The proposal was not marked Sent. (' + (payload.emailError || 'email error') + ')', 'error');
        loadBrides();
        if (state.currentBride) loadProposalHistory_(state.currentBride);
        return;
      }
      state.workspace.proposal.status = 'SENT';
      state.workspace.proposal.sentDate = new Date().toISOString().split('T')[0];
      state.workspace.proposal.token = payload.token || '';
      // FIX (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-confirmation-
      // 2026-09-30.md): same optimistic channel stamp as doApproveAndSend -- see
      // that function's comment for why. Re-fetched/reload paths still get the
      // real PROPOSAL_EMAIL_SENT / PROPOSAL_SMS_SENT from the CRM row.
      var _resendStamp = pbNowStamp_();
      if (!payload.deferredJobId) {
        if (_emailWasRequested) state.workspace.proposal.emailSentAt = _resendStamp;
        if (opts.sendSms !== false) state.workspace.proposal.smsSentAt = _resendStamp;
      }
      renderStageSend();
      renderSavePill_();
      patchBrideLocal_(state.currentBride, { proposalStatus: 'SENT', sentDate: state.workspace.proposal.sentDate });
      loadBrides();
      var _resendChannels = pbSendChannelSummary_(state.workspace.proposal);
      var _resendToLabel = (state.workspace.bride && state.workspace.bride.email) || 'the bride';
      showBanner((payload.deferredJobId
        ? 'Proposal re-sent to ' + _resendToLabel + '. Finishing background tasks...'
        : 'Re-sent to ' + _resendToLabel + (_resendChannels ? '  (' + _resendChannels + ')' : '')), 'success', true);
      if (state.currentBride) {
        if (payload.deferredJobId) {
          setTimeout(function() { if (state.currentBride) loadProposalHistory_(state.currentBride); }, 8000);
        } else {
          loadProposalHistory_(state.currentBride);
        }
      }
    })
    .catch(function(err) {
      // Same recovery path as doApproveAndSend: a genuine timeout re-checks the
      // bride's live status rather than leaving the admin guessing whether it
      // actually sent. No button to re-enable here (the resend modal is already
      // closed by the time doResend_ runs), so pass a no-op reset.
      if (pbClassifySendOutcome_(err) === 'unknown') {
        pbResolveSendOutcome_(state.currentBride, function() {});
        return;
      }
      showBanner(getErrorMessage(err), 'error');
    });
  };

  flushToGas_().then(doSend).catch(function(err) {
    // Fixed 2026-09-06 (BRIEFS/PB-resend-silent-stale-pricing-2026-08-29.md): this
    // used to fall through to doSend() even on a failed save, meaning a resend
    // could silently go out with whatever was last successfully saved to CRM,
    // ignoring whatever the admin was actually looking at on screen. Same failure
    // shape as the confirmed Lucy Wilson A$110 underquote incident
    // (ProposalBuilderAPI.gs:3843-3868). Now matches approveAndSend()'s pattern:
    // block on save failure, show the error, never send anyway.
    showBanner('Failed to save before re-send: ' + getErrorMessage(err), 'error');
  });
}

function voidProposal() {
  if (!state.currentBride) return;
  showVoidConfirmModal(state.currentBride, function() {
    var btn = document.querySelector('button[onclick="voidProposal()"]');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Voiding...';
    }
    var bride = (state.workspace && state.workspace.bride) || {};
    pbCfBuilderWrite_('state-set', {
      action: 'void',
      brideUid: bride.brideUid || '',
      email: bride.email || ''
    }, pbAcceptCfVoid_)
      .then(function(result) {
        if (result && result.refusal) {
          if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<i class="fa-solid fa-ban"></i> Void Proposal';
          }
          showBanner(result.refusal.error || result.refusal.message || 'Void refused', 'error');
          return;
        }
        if (!result || !result.cf) {
          if (btn) {
            btn.disabled = false;
            btn.innerHTML = '<i class="fa-solid fa-ban"></i> Void Proposal';
          }
          showBanner('Could not void the proposal through Cloudflare. Nothing was changed.', 'error');
          return;
        }
        state.workspace.proposal.status = 'VOID';
        showBanner('Proposal voided.', 'success');
        renderStageSend();
        patchBrideLocal_(state.currentBride, { proposalStatus: 'VOID' });
        loadBrides();
      })
      .catch(function(err) {
        if (btn) {
          btn.disabled = false;
          btn.innerHTML = '<i class="fa-solid fa-ban"></i> Void Proposal';
        }
        showBanner(getErrorMessage(err), 'error');
      });
  });
}

function showVoidConfirmModal(brideName, onConfirm) {
  var overlay = document.createElement('div');
  overlay.id = 'voidOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.55);z-index:1000;display:flex;align-items:center;justify-content:center';
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:12px;padding:28px 28px 20px;max-width:400px;width:90%;box-shadow:0 8px 40px rgba(47,37,36,0.18)">' +
      '<div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">' +
        '<i class="fa-solid fa-ban" style="color:#c0392b;font-size:20px"></i>' +
        '<span style="font-size:17px;font-weight:700;color:#1e1716">Void Proposal</span>' +
      '</div>' +
      '<p style="font-size:14px;color:#555;line-height:1.6;margin:0 0 20px">' +
        'Are you sure you want to void this proposal for <strong>' + escapeHtml(brideName) + '</strong>?<br>' +
        'This cannot be undone.' +
      '</p>' +
      '<div style="display:flex;gap:10px;justify-content:flex-end">' +
        '<button id="voidCancelBtn" class="btn btn-ghost btn-sm">Cancel</button>' +
        '<button id="voidConfirmBtn" class="btn btn-danger btn-sm"><i class="fa-solid fa-ban"></i> Yes, void proposal</button>' +
      '</div>' +
    '</div>';
  document.body.appendChild(overlay);
  document.getElementById('voidCancelBtn').addEventListener('click', function() { overlay.remove(); });
  document.getElementById('voidConfirmBtn').addEventListener('click', function() { overlay.remove(); onConfirm(); });
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });
}

/* =================================================================
   P.1 -- EDIT PROPOSAL (reopen sent proposal for editing)
   ================================================================= */
function editProposal() {
  if (!state.currentBride) return;
  var brideName = state.currentBride;
  var btn = document.getElementById('editProposalBtn');
  function setBtnBusy(busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.innerHTML = busy
      ? '<i class="fa-solid fa-spinner fa-spin"></i> Reopening...'
      : '<i class="fa-solid fa-pen-to-square"></i> Edit Proposal';
  }
  pbReopenProposalForEdit_(brideName, {
    onBusy: setBtnBusy,
    onSuccess: function(res) {
      // Update local state
      if (state.workspace && state.workspace.proposal) {
        state.workspace.proposal.status = 'DRAFT_EDIT';
        state.workspace.proposal.revisionCount = res.revisionCount || 0;
      }
      showBanner('Proposal reopened for editing (Rev ' + (res.revisionCount || '') + ')', 'success');
      setStage('builder');
    },
    onFailure: function(res) {
      showBanner((res && (res.message || res.error)) || 'Could not reopen proposal for editing', 'error');
    }
  });
}

/* =================================================================
   P.3 -- CLIENT SEARCH TYPEAHEAD (dead code removed 2026-05-31)
   Progressive global search is now inline in renderBrides() via
   triggerGlobalSearch_() + selectClientSearchResult_().
   ================================================================= */

/* =================================================================
   P.4 -- MANUAL PARTY MEMBERS
   ================================================================= */
// PATH 2: party-member adds/removes are instant + local. Flushed on Save.
function addManualPartyMember() {
  var nameEl   = document.getElementById('manualMemberName');
  var roleEl   = document.getElementById('manualMemberRole');
  var hairEl   = document.getElementById('manualSvcHair');
  var makeupEl = document.getElementById('manualSvcMakeup');

  var name     = nameEl   ? nameEl.value.trim() : '';
  var role     = roleEl   ? roleEl.value : 'Guest';
  var services = [];
  if (hairEl   && hairEl.checked)   services.push('Hair');
  if (makeupEl && makeupEl.checked) services.push('Makeup');
  if (!services.length) services.push('Hair');

  if (!name) { showBanner('Enter a name for the party member.', 'warn'); return; }

  var svcType = manualAdditionServiceType_(services);
  state.manualPartyMembers.push({ name: name, role: role, services: services, qty: 1, serviceType: svcType });
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  showBanner('Added ' + name + ' to party', 'success');
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

// Composition-card controls for manual additions (mirror adjustPartyQty / updatePartyService).
function adjustManualQty(idx, delta) {
  if (idx < 0 || idx >= state.manualPartyMembers.length) return;
  var m = state.manualPartyMembers[idx];
  var cur = Math.max(1, (typeof m.qty === 'number' && m.qty > 0) ? m.qty : 1);
  var next = cur + delta;
  if (next < 1) return; // use the remove (x) to drop a member entirely
  m.qty = next;
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

function updateManualService(idx, value) {
  if (idx < 0 || idx >= state.manualPartyMembers.length) return;
  var m = state.manualPartyMembers[idx];
  m.serviceType = normaliseServiceType_(value);
  m.services = manualSvcToServicesArr_(m.serviceType); // keep legacy array in sync for server sites
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

function removeManualPartyMember(idx) {
  if (idx < 0 || idx >= state.manualPartyMembers.length) return;
  var removed = state.manualPartyMembers.splice(idx, 1)[0];
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  showBanner('Removed ' + (removed ? removed.name : 'member') + ' from party', 'success');
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

function toggleNoTrial(checked) {
  state.noTrialRequired = !!checked;
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

// 4.3 (2026-07-10): show a hint when junior bridesmaids are set but there are no
// bridesmaids for the discount to attach to (enquiry party + manual additions both empty
// of bridesmaids). Without this the junior field was a silent no-op on such brides.
function juniorNoEligibleHint_() {
  if (!(Number(state.juniorBridesmaids) > 0)) return '';
  var party = (state.workspace && state.workspace.party) || [];
  var manuals = state.manualPartyMembers || [];
  var bmInParty = party.some(function(p) { return /bridesmaid/i.test(String(p && p.role || '')); });
  var bmInManuals = manuals.some(function(m) { return /bridesmaid/i.test(String(m && m.role || '')); });
  if (bmInParty || bmInManuals) return '';
  return '<div style="margin-top:6px;font-size:12px;color:var(--muted,#8b7d6b);line-height:1.4">' +
    'No eligible bridesmaids to discount yet. Add a bridesmaid to the party (or via Add party member) for the junior discount to apply.' +
    '</div>';
}

// Junior bridesmaids: subset of the bridesmaid count (age 10-14) that receive 25% off
// the base bridesmaid rate, max 2 discounted per wedding. Upgrades stay full price.
// recalcAndMerge_ + calcLocalEstimate_ apply the split; flushToGas_ persists the count.
function setJuniorBridesmaids(value) {
  var n = Math.max(0, parseInt(value, 10) || 0);
  state.juniorBridesmaids = n;
  recalcAndMerge_();
  markDirty_();
  renderStageBuilder();
}

function toggleBrideWithMe(isMe) {
  // Back-compat shim — translates to the new bookingTier control.
  setBookingTier(isMe ? 'jessica' : 'proTeam');
}

/* =================================================================
   BOOKING TIER -- proposal-level toggle (Jessica Vegas / JV Pro Team)
   ================================================================= */
function buildBookingTierControl_() {
  var tier = state.bookingTier || 'jessica';
  var locked = !!state.bookingTierLockedAt;
  var lockedNote = locked
    ? '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:6px;font-style:italic">Tier locked on send — the quoted price stays valid for this bride even if Jessica\'s availability changes.</div>' +
      '<button type="button" onclick="unlockBookingTier()" title="Re-open the tier toggle so you can re-quote this bride. Re-sending will re-price and re-lock." ' +
        'style="margin-top:8px;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;background:transparent;color:var(--brand-primary);border:1px solid var(--brand-primary);border-radius:var(--radius-pill,3rem);font-size:12px;font-weight:600;cursor:pointer;transition:0.15s">' +
        '<i class="fa-solid fa-lock-open"></i>Unlock tier</button>'
    : '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:6px">Auto-selected from availability. Override before sending if needed.</div>';

  var pillStyle = function(active) {
    var bg = active ? 'var(--brand-primary)' : 'var(--brand-bg-soft,#f6f1ee)';
    var col = active ? '#fff' : 'var(--brand-text,#2f2524)';
    var op = locked ? '0.55' : '1';
    var cursor = locked ? 'not-allowed' : 'pointer';
    return 'flex:1;padding:8px 14px;border:none;cursor:' + cursor + ';transition:0.15s;font-size:13px;font-weight:600;background:' + bg + ';color:' + col + ';opacity:' + op;
  };

  return '<div style="margin-top:14px;padding:12px 14px;background:var(--brand-bg-soft,#f6f1ee);border-radius:var(--radius-medium,1rem)">' +
    '<div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--brand-text,#2f2524);margin-bottom:8px">' +
      '<i class="fa-solid fa-user-tie" style="margin-right:6px;color:var(--brand-primary)"></i>Booking Tier' +
    '</div>' +
    '<div style="display:flex;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-pill,3rem);overflow:hidden;background:#fff">' +
      '<button ' + (locked ? 'disabled' : '') + ' onclick="setBookingTier(\'jessica\')" style="' + pillStyle(tier === 'jessica') + '" title="Bride pricing $350 (Hair &amp; Makeup) — Jessica attends personally">' +
        'Jessica Vegas' +
      '</button>' +
      '<button ' + (locked ? 'disabled' : '') + ' onclick="setBookingTier(\'proTeam\')" style="' + pillStyle(tier === 'proTeam') + '" title="Bride pricing $325 (Hair &amp; Makeup) — JV Pro Team artists attend">' +
        'JV Pro Team' +
      '</button>' +
    '</div>' +
    lockedNote +
  '</div>';
}

// PATH 2: tier flip is instant + local. The bride-line overlay applies in
// recalcAndMerge_(). NOTE: tier change MAY change wedding-artist allocation
// (Pro Team artists' home suburbs vs Jessica). That re-allocation happens on
// the next flush (GAS-side: tierChanged path triggers full JV AUTO call).
function setBookingTier(tier) {
  if (state.bookingTierLockedAt) {
    // A1b (2026-07-15): the tier is locked once the proposal is sent so the quoted
    // price stays valid. Tell the admin how to change it rather than just refusing.
    showBanner('Tier is locked because this proposal was sent. Use "Unlock" (below the toggle) to re-open it, then re-send to re-quote.', 'warn');
    return;
  }
  if (tier !== 'jessica' && tier !== 'proTeam') return;

  state.bookingTier = tier;
  state.brideWithMe = (tier === 'jessica');
  recalcAndMerge_();
  state.editedLineItems = null;
  state.lineItemsManuallyEdited = false;
  markDirty_();
  renderStageBuilder();

  _recomputeTravelOnChange_();
}

// Admin re-open: clears the post-send tier lock so the toggle is editable again.
// Writes through to CRM immediately (the lock lives on PROPOSAL_CALC_JSON). After
// unlock the admin flips the tier and re-sends, which re-prices and re-locks.
function unlockBookingTier() {
  if (!state.currentBride) return;
  if (!confirm('Unlock the booking tier for ' + state.currentBride + '?\n\n'
    + 'This re-opens the Jessica / Pro Team toggle on a proposal that has already been sent. '
    + 'Changing the tier and re-sending will re-quote this bride at the new price.')) return;
  var bride = (state.workspace && state.workspace.bride) || {};
  pbCfBuilderWrite_('state-actions', {
    action: 'unlock_tier',
    brideUid: bride.brideUid || '',
    email: bride.email || ''
  }, pbAcceptCfUnlockTier_)
    .then(function(result) {
      if (result && result.refusal) {
        showBanner(result.refusal.error || result.refusal.message || 'Unlock refused', 'error');
        return;
      }
      var payload = result && result.cf;
      if (!payload) {
        showBanner('Could not unlock the booking tier through Cloudflare. Nothing was changed.', 'error');
        return;
      }
      if (payload.calculation && state.workspace) {
        applyCalcResponse_(payload.calculation, { clearDraft: true, render: false });
      }
      state.bookingTierLockedAt = '';
      if (state.workspace && state.workspace.calculation) {
        state.workspace.calculation.bookingTierLockedAt = '';
      }
      if (_draftSaveTimer) { clearTimeout(_draftSaveTimer); _draftSaveTimer = null; }
      _draftDirty = false;
      try { localStorage.removeItem(draftKey_(state.currentBride)); } catch (e) {}
      showBanner('Tier unlocked. Flip the toggle and re-send to re-quote.', 'success');
      renderStageBuilder();
      renderSavePill_();
    })
    .catch(function(err) { showBanner(getErrorMessage(err), 'error'); });
}

/* =================================================================
   TRAVEL ORIGIN -- per-quote current/future address choice (2026-07-14)
   Only shown once a future address is on file (state.travelOriginFutureAddressSet).
   Auto = use the global default Jess controls in Settings; Current/Future pin
   this specific quote regardless of the global default.
   ================================================================= */
function buildTravelOriginControl_() {
  var val = state.travelOriginOverride; // null | 'current' | 'future'
  var locked = !!state.travelOriginLockedAt;
  var globalDefault = state.travelOriginGlobalDefault === 'future' ? 'future' : 'current';
  var lockedNote = locked
    ? '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:6px;font-style:italic">Travel origin locked on send, the quoted travel fee stays valid for this bride.</div>' +
      '<button type="button" onclick="unlockTravelOrigin()" title="Re-open the travel origin toggle so you can re-quote this bride. Re-sending will re-price and re-lock." ' +
        'style="margin-top:8px;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;background:transparent;color:var(--brand-primary);border:1px solid var(--brand-primary);border-radius:var(--radius-pill,3rem);font-size:12px;font-weight:600;cursor:pointer;transition:0.15s">' +
        '<i class="fa-solid fa-lock-open"></i>Unlock travel origin</button>'
    : ('<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:6px;display:flex;align-items:center;gap:6px;flex-wrap:wrap">' +
        '<span>' + (val ? 'Pinned to this quote.' : 'Following the global default (' + (globalDefault === 'future' ? 'Future' : 'Current') + ').') + '</span>' +
        '<button type="button" onclick="editTravelOriginGlobalDefault()" style="background:none;border:none;padding:0;color:var(--brand-primary);text-decoration:underline;cursor:pointer;font-size:11px">Change global default</button>' +
      '</div>');

  var pillStyle = function(active) {
    var bg = active ? 'var(--brand-primary)' : 'var(--brand-bg-soft,#f6f1ee)';
    var col = active ? '#fff' : 'var(--brand-text,#2f2524)';
    var op = locked ? '0.55' : '1';
    var cursor = locked ? 'not-allowed' : 'pointer';
    return 'flex:1;padding:8px 14px;border:none;cursor:' + cursor + ';transition:0.15s;font-size:13px;font-weight:600;background:' + bg + ';color:' + col + ';opacity:' + op;
  };

  return '<div style="margin-top:14px;padding:12px 14px;background:var(--brand-bg-soft,#f6f1ee);border-radius:var(--radius-medium,1rem)">' +
    '<div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--brand-text,#2f2524);margin-bottom:8px">' +
      '<i class="fa-solid fa-house-circle-check" style="margin-right:6px;color:var(--brand-primary)"></i>Travel Origin' +
    '</div>' +
    '<div style="display:flex;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-pill,3rem);overflow:hidden;background:#fff">' +
      '<button ' + (locked ? 'disabled' : '') + ' onclick="setTravelOriginOverride(null)" style="' + pillStyle(!val) + '" title="Use the global default set in Settings">' +
        'Auto' +
      '</button>' +
      '<button ' + (locked ? 'disabled' : '') + ' onclick="setTravelOriginOverride(\'current\')" style="' + pillStyle(val === 'current') + '" title="Quote travel from the current address for this bride only">' +
        'Current' +
      '</button>' +
      '<button ' + (locked ? 'disabled' : '') + ' onclick="setTravelOriginOverride(\'future\')" style="' + pillStyle(val === 'future') + '" title="Quote travel from the future address for this bride only">' +
        'Future' +
      '</button>' +
    '</div>' +
    lockedNote +
  '</div>';
}

// Travel origin change requires a fresh GAS-side calc (JV AUTO recomputes every
// travel line from the newly-selected origin), unlike the instant-local tier flip.
// Flush immediately so the price shown matches what will actually be sent.
function setTravelOriginOverride(val) {
  if (state.travelOriginLockedAt) {
    showBanner('Travel origin is locked (proposal already sent). Cannot change after send.', 'warn');
    return;
  }
  if (val !== null && val !== 'current' && val !== 'future') return;
  state.travelOriginOverride = val;
  markDirty_();
  renderStageBuilder();
  _recomputeTravelOnChange_();
}

// Admin re-open: clears the post-send travel origin lock so the toggle is
// editable again. Writes through to CRM immediately (the lock lives on
// PROPOSAL_CALC_JSON). After unlock the admin flips the origin and re-sends,
// which re-prices and re-locks.
function unlockTravelOrigin() {
  if (!state.currentBride) return;
  if (!confirm('Unlock the travel origin for ' + state.currentBride + '?\n\n'
    + 'This re-opens the Auto / Current / Future toggle on a proposal that has already been sent. '
    + 'Changing it and re-sending will re-quote this bride at the new travel price.')) return;
  var bride = (state.workspace && state.workspace.bride) || {};
  pbCfBuilderWrite_('state-actions', {
    action: 'unlock_travel_origin',
    brideUid: bride.brideUid || '',
    email: bride.email || ''
  }, pbAcceptCfUnlockTravelOrigin_)
    .then(function(result) {
      if (result && result.refusal) {
        showBanner(result.refusal.error || result.refusal.message || 'Unlock refused', 'error');
        return;
      }
      if (!result || !result.cf) {
        showBanner('Could not unlock the travel origin through Cloudflare. Nothing was changed.', 'error');
        return;
      }
      state.travelOriginLockedAt = '';
      if (state.workspace && state.workspace.calculation) {
        state.workspace.calculation.travelOriginLockedAt = '';
      }
      showBanner('Travel origin unlocked. Change it and re-send to re-quote.', 'success');
      renderStageBuilder();
    })
    .catch(function(err) { showBanner(getErrorMessage(err), 'error'); });
}

// Sets the global default that every quote without a per-quote override falls
// back to (JV_TRAVEL_ORIGIN_DEFAULT script property, JV AUTO project). Confirms
// before applying since this affects every future quote, not just the one open.
function editTravelOriginGlobalDefault() {
  var current = state.travelOriginGlobalDefault === 'future' ? 'future' : 'current';
  var next = current === 'future' ? 'current' : 'future';
  if (!confirm('Change the global travel origin default from "' + current + '" to "' + next + '"?\n\n'
    + 'This applies to every quote that does not have its own per-quote override (most quotes).')) return;
  jvBridge.callAdmin('proposalTravelOrigin_setGlobalDefault', { value: next })
    .then(function(res) {
      var payload = (res && res.data) || res;
      if (!payload || payload.ok !== true) {
        showBanner((payload && payload.error) || 'Failed to change the global default', 'error');
        return;
      }
      state.travelOriginGlobalDefault = payload.globalDefault === 'future' ? 'future' : 'current';
      showBanner('Global travel origin default is now "' + state.travelOriginGlobalDefault + '".', 'success');
      renderStageBuilder();
    })
    .catch(function(err) { showBanner(getErrorMessage(err), 'error'); });
}

/* =================================================================
   TIMING -- finish time / artists / start time override + locks
   ================================================================= */
function buildTimingControl_() {
  var calc = (state.workspace && state.workspace.calculation) || {};
  // P3f: timing-field lock toggles were removed in favour of the override-via-clear
  // model (type a value to override, click the pill to revert to auto). The
  // calc.timingLocks object is still honoured by commitFinishTime_ + GAS as the
  // start-recompute gate, but is no longer surfaced as a decorative toggle here.
  var finishVal  = fmtTime12h_(String(calc.finishTime || (state.workspace && state.workspace.bride && state.workspace.bride.finishTime) || '').trim());
  var artistsVal = calc.artistsRequired != null ? Number(calc.artistsRequired) : '';
  var startVal   = fmtTime12h_(String(calc.estimatedStartTime || '').trim());

  // Shows the set value with an x to clear it. No chip when the field is empty.
  function clearChip(field, value) {
    if (!value && value !== 0) return '<span style="font-size:11px;color:var(--muted,#8b7d6b)">auto</span>';
    return '<button type="button" class="timing-clear-chip" onclick="clearTimingField(\'' + field + '\')" title="Click to revert to auto-calculated">' +
      '<span class="chip-value">' + escapeHtml(String(value)) + '</span>' +
      '<span class="chip-x">&times;</span>' +
    '</button>';
  }

  function row(label, field, inputHtml, currentValue) {
    return '<div style="display:flex;align-items:center;gap:8px;margin-top:8px">' +
      '<div style="font-size:11px;font-weight:600;color:var(--brand-text);min-width:90px">' + label + '</div>' +
      '<div style="flex:1">' + inputHtml + '</div>' +
      clearChip(field, currentValue) +
    '</div>';
  }

  // P1d: finish time routes through commitFinishTime_ (single source of truth +
  // start recompute). onchange fires once on a committed edit, never per keystroke.
  var finishInput = '<input type="text" id="timingFinishInput" value="' + escapeHtml(finishVal) +
    '" placeholder="e.g. 2:30 PM" ' +
    'onchange="commitFinishTime_(this.value)" ' +
    'style="width:100%;padding:6px 10px;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-medium,1rem);font-size:13px">';

  var startInput = '<input type="text" id="timingStartInput" value="' + escapeHtml(startVal) +
    '" placeholder="e.g. 11:00 AM" ' +
    'oninput="setTimingField(\'estimatedStartTime\', this.value)" ' +
    'onblur="setTimingField(\'estimatedStartTime\', this.value)" ' +
    'style="width:100%;padding:6px 10px;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-medium,1rem);font-size:13px">';

  // Header removed 2026-07-12: the enclosing card now supplies the "Wedding-Day
  // Timing" title, so this inner control renders just the hint + the two rows.
  return '<div>' +
    '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-bottom:6px">Type a value to override. Click the pill to revert to auto.</div>' +
    row('Finish time',  'finishTime',         finishInput,  finishVal || null) +
    row('Est. start',   'estimatedStartTime', startInput,   startVal || null) +
  '</div>';
}

// PATH 2: timing edits are instant + local. To revert to formula-derived,
// admin clears the input (empty value = no override). flushToGas_() persists.
function setTimingField(field, value) {
  if (['finishTime', 'artistsRequired', 'estimatedStartTime'].indexOf(field) < 0) return;
  if (!state.workspace) state.workspace = {};
  if (!state.workspace.calculation) state.workspace.calculation = {};
  if (field === 'artistsRequired') {
    var n = parseInt(value, 10);
    var _isValid = !(isNaN(n) || n < 1);
    state.workspace.calculation[field] = _isValid ? n : '';
    // BUG FIX (2026-09-15): a typed/stepped artist count is a genuine admin
    // override and MUST be marked as one, mirroring the CRM-side write in
    // proposalRecalculate (ProposalBuilderAPI.gs ~L6543) and clearTimingField's
    // counterpart below. Without this, isAdminOverrideJs_(existing) returns
    // false and the very next recalcAndMerge_() (which runs synchronously,
    // before any server round-trip) treats the typed value as a stale derived
    // number and reverts it to the formula-derived count via
    // proposalArtistCountResolve_ -- visible whenever
    // JV_PROPOSAL_ARTIST_COUNT_CORRECTABLE is ON. Jess reported this live
    // 2026-09-15: manual artist-count edits "saved" then reverted to the
    // pre-calculated number on Generate Preview.
    if (_isValid) {
      state.workspace.calculation.artistsRequiredOverride = true;
      state.workspace.calculation.artistsRequiredOverriddenAt = new Date().toISOString();
    } else {
      // Blank/invalid input reverts to auto -- same as clearTimingField.
      state.workspace.calculation.artistsRequiredOverride = false;
      delete state.workspace.calculation.artistsRequiredOverriddenAt;
    }
  } else {
    state.workspace.calculation[field] = String(value || '').trim();
  }
  // Path 2 (client-owned state): update state only, do NOT re-render. A
  // renderStageBuilder() here rebuilds the entire page-2 subtree on every
  // keystroke, destroying the input being typed into (focus/caret/scroll lost).
  // Timing chips/preview refresh on the next Save/Preview render.
  markDirty_();
}

// Committed edit of the Artists override (fires once on change/blur, not per keystroke).
// Writes state, then recalcs + re-renders so the wedding-day travel lines re-cap to the
// new artist count immediately (proposalCapWeddingTravelLines_). Clearing it (blank)
// reverts to the auto-calculated count and restores any capped travel lines.
/* WIN D (2026-07-12): subtle "updating travel" cue during a background GAS
   travel recalc. Non-blocking: the admin can keep working; it just signals the
   travel figure is settling. Uses a small info banner (auto-dismisses) plus a
   dimmed state on the line-items table via a body class the CSS can style. Safe
   no-op if the elements are absent. */
function pbSetTravelUpdating_(on) {
  try {
    var host = document.querySelector('.jv-proposal-builder') || document.body;
    if (host) host.classList.toggle('pb-travel-updating', !!on);
    if (on) {
      showBanner('Updating travel...', 'info');
    }
  } catch (e) { /* cue is cosmetic, never let it break the recalc */ }
}

function commitArtistsField_(value) {
  setTimingField('artistsRequired', value);
  _recomputeTravelOnChange_();
}

// Clears a timing field back to auto-calculated (called by the clear chip x button).
function clearTimingField(field) {
  if (['finishTime', 'artistsRequired', 'estimatedStartTime'].indexOf(field) < 0) return;
  if (!state.workspace || !state.workspace.calculation) return;
  state.workspace.calculation[field] = '';
  if (state.workspace.calculation.timingLocks) {
    state.workspace.calculation.timingLocks[field] = false;
  }
  if (field === 'artistsRequired') {
    // RATCHET FIX FOLLOWUP (2026-07-23): weddingArtistsLockedAt is also treated
    // as a sticky admin-override signal by isAdminOverrideJs_ / the CRM mirror.
    // Without clearing it here, an earlier Add/Remove Artist action in the same
    // session left this lock stamped, so "revert to auto" never actually
    // released the override. Mirrors the CRM-side fix in ProposalBuilderAPI.gs.
    state.workspace.calculation.artistsRequiredOverride = false;
    delete state.workspace.calculation.artistsRequiredOverriddenAt;
    delete state.workspace.calculation.weddingArtistsLockedAt;
  }
  markDirty_();
  renderStageBuilder();
}

// P3f: toggleTimingLock removed. The lock-toggle UI no longer exists (replaced by
// the override-via-clear model). calc.timingLocks is still read by commitFinishTime_
// and GAS as the start-recompute gate, but is never set from the client anymore.

/* =================================================================
   PAYMENT OPTIONS -- three explicit payment methods for the client
   ================================================================= */

/**
 * Normalise the stored paymentOption, with backwards compat for the old
 * { enabled, installmentCount } paymentPlan shape.
 * @returns {{ type: string, months: number }}
 */
function getPaymentOption_() {
  var calc = (state.workspace && state.workspace.calculation) || {};
  if (calc.paymentOption && calc.paymentOption.type) {
    return { type: calc.paymentOption.type, months: Number(calc.paymentOption.months) || 6 };
  }
  // Backward compat: old paymentPlan model defaults to gc_plan
  if (calc.paymentPlan && calc.paymentPlan.enabled) {
    return { type: 'gc_plan', months: Number(calc.paymentPlan.installmentCount) || 6 };
  }
  return { type: 'stripe', months: 6 };
}

// Pay in Full (2026-07-14): a bride happy to pay everything at once, chosen by the
// admin on her behalf. Two variants: full_stripe (card, full balance) and full_gc
// (direct debit mandate, full balance charged on signing). Distinct from
// 'gc_plan' (instalments, 3-12 months), this is a single one-off charge.
function buildPaymentPlanControl_() {
  var opt = getPaymentOption_();
  var selectedType = opt.type;
  var months = opt.months;

  var calc = (state.workspace && state.workspace.calculation) || {};
  var total = (calc.grandTotal) || (calc.total) || 0;
  if (!total && state.workspace && state.workspace.bride) {
    total = parseFloat(state.workspace.bride.total || 0);
  }
  var weddingDate = (state.workspace && state.workspace.bride && state.workspace.bride.weddingDate) || '';

  // Compute last payment date (wedding date minus 1 calendar month)
  var lastPaymentLabel = '';
  if (weddingDate) {
    var m = weddingDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      var wDay = parseInt(m[1], 10), wMo = parseInt(m[2], 10) - 1, wYr = parseInt(m[3], 10);
      var lastDate = new Date(wYr, wMo - 1, wDay);
      lastPaymentLabel = lastDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }

  var isPlan = selectedType === 'gc_plan';
  var isFullStripe = selectedType === 'full_stripe';
  var isFullGc = selectedType === 'full_gc';
  var isPayInFull = isFullStripe || isFullGc;
  var mode = isPlan ? 'plan' : isPayInFull ? 'full' : 'standard';

  var modePillStyle = function(active) {
    var bg = active ? 'var(--brand-primary)' : 'var(--brand-bg-soft,#f6f1ee)';
    var col = active ? '#fff' : 'var(--brand-text,#2f2524)';
    return 'flex:1;padding:8px 10px;border:none;cursor:pointer;transition:0.15s;font-size:12px;font-weight:600;background:' + bg + ';color:' + col + ';';
  };

  var modePills = '<div style="display:flex;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-pill,3rem);overflow:hidden;background:#fff;margin-bottom:10px">'
    + '<button type="button" onclick="setPaymentMode_(\'standard\')" style="' + modePillStyle(mode === 'standard') + '" title="50% booking fee now, balance due 1 month before the wedding, card or direct debit">Standard</button>'
    + '<button type="button" onclick="setPaymentMode_(\'plan\')" style="' + modePillStyle(mode === 'plan') + '" title="Monthly instalments via direct debit (Stripe PayTo), 3-12 months">Payment Plan</button>'
    + '<button type="button" onclick="setPaymentMode_(\'full\')" style="' + modePillStyle(mode === 'full') + '" title="Whole balance paid at booking, no deposit split">Pay in Full</button>'
    + '</div>';

  // Payment plan expanded controls (shown only when gc_plan is selected)
  var perInstalment = (total > 0 && months > 0) ? '$' + (total / months).toFixed(2) + ' inc GST' : 'TBC';
  var planDetails = isPlan
    ? '<div style="padding:10px 12px;border-radius:var(--radius-medium,1rem);border:1px solid var(--line,#e8ddd3);background:rgba(201,149,145,0.04)">'
        + '<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">'
          + '<span style="font-size:11px;font-weight:600;color:var(--brand-text,#2f2524);min-width:80px">Months</span>'
          + '<select onchange="setPaymentPlanMonths(parseInt(this.value,10))" '
            + 'style="padding:5px 8px;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-medium,1rem);font-size:12px;flex:1;'
            + 'background:var(--brand-bg-white,#fff);color:var(--brand-text,#2f2524)">'
            + [3,4,5,6,7,8,9,10,11,12].map(function(n) {
                return '<option value="' + n + '"' + (n === months ? ' selected' : '') + '>' + n + ' months</option>';
              }).join('')
          + '</select>'
        + '</div>'
        + '<div style="font-size:11px;color:var(--muted,#8b7d6b)">'
          + months + ' monthly payments of approx <strong>' + perInstalment + '</strong>'
          + (lastPaymentLabel ? '<br>Last payment: ' + lastPaymentLabel + ' (1 month before wedding)' : '')
          + '<br>First instalment due on signing or mandate approval.'
        + '</div>'
      + '</div>'
    : '';

  // Pay in Full expanded controls (shown only when 'full' mode is selected), the
  // Stripe/GoCardless sub-choice, since "pay everything now" still needs to pick a rail.
  var fullTotal = total > 0 ? '$' + total.toFixed(2) + ' inc GST' : 'TBC';
  var fullPillStyle = function(active) {
    var bg = active ? 'var(--brand-primary)' : 'var(--brand-bg-soft,#f6f1ee)';
    var col = active ? '#fff' : 'var(--brand-text,#2f2524)';
    return 'flex:1;padding:7px 10px;border:none;cursor:pointer;transition:0.15s;font-size:11px;font-weight:600;background:' + bg + ';color:' + col + ';';
  };
  // Late-enquiry full-payment auto-rule (2026-08-31, Proposal Builder Agent).
  // Resolved rule: External Deliverables/late-enquiry-full-payment-rule-
  // deliverables-v2.md. UX principle from that review: "automatic decision,
  // visible reasoning, intentional override" -- this banner is the "visible
  // reasoning" half. The server (proposalBuilder_applyLateEnquiryFullPayment_,
  // ProposalBuilderAPI.gs) is the actual authority on whether the rule applied
  // (calc.lateEnquiryFullPaymentAuto === true); this is a client-side days-until
  // calc purely for DISPLAY, matching the server's own calendar-day math, not a
  // second decision engine. Never used to gate any pricing/send logic client-side.
  var lateEnquiryReasonHtml = '';
  if (isPayInFull && calc.lateEnquiryFullPaymentAuto === true) {
    var lateEnquiryDaysLabel = '';
    if (weddingDate) {
      var _lem = weddingDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (_lem) {
        var _lewDate = new Date(parseInt(_lem[3], 10), parseInt(_lem[2], 10) - 1, parseInt(_lem[1], 10));
        var _leToday = new Date(); _leToday.setHours(0, 0, 0, 0);
        var _leDays = Math.round((_lewDate.getTime() - _leToday.getTime()) / 86400000);
        if (_leDays >= 0) lateEnquiryDaysLabel = _leDays;
      }
    }
    lateEnquiryReasonHtml = '<div style="padding:10px 12px;border-radius:var(--radius-medium,1rem);'
      + 'background:var(--brand-bg-soft,#f6f1ee);border:1px solid rgba(201,149,145,0.25);margin-bottom:10px">'
      + '<div style="font-size:11px;font-weight:700;color:var(--brand-primary,#c99591);text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px">'
        + 'Automatically selected</div>'
      + '<div style="font-size:12px;color:var(--brand-text,#2f2524);line-height:1.5">'
        + (lateEnquiryDaysLabel !== '' ? 'Wedding is in ' + lateEnquiryDaysLabel + ' days. ' : '')
        + 'Full payment applies to proposals sent 45 days or fewer before the wedding.'
      + '</div>'
    + '</div>';
  }

  var fullDetails = isPayInFull
    ? '<div style="padding:10px 12px;border-radius:var(--radius-medium,1rem);border:1px solid var(--line,#e8ddd3);background:rgba(201,149,145,0.04)">'
        + lateEnquiryReasonHtml
        + '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-bottom:8px">Whole balance <strong>' + fullTotal + '</strong>, paid at booking. No 50% deposit split.</div>'
        + '<div style="display:flex;border:1px solid var(--line,#e8ddd3);border-radius:var(--radius-pill,3rem);overflow:hidden;background:#fff">'
          + '<button type="button" onclick="selectPaymentOption(\'full_stripe\')" style="' + fullPillStyle(isFullStripe) + '" title="Charged by card via Stripe as soon as she signs">Card (Stripe)</button>'
          + '<button type="button" onclick="selectPaymentOption(\'full_gc\')" style="' + fullPillStyle(isFullGc) + '" title="She authorises a direct debit mandate on signing, the full balance charges immediately">Direct Debit (PayTo)</button>'
        + '</div>'
        + (isFullGc ? '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-top:6px">Charged as soon as she signs and authorises the mandate.</div>' : '')
      + '</div>'
    : '';

  return '<div style="margin-top:14px">'
    + '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--brand-text,#2f2524);margin-bottom:6px">'
      + '<i class="fa-solid fa-credit-card" style="margin-right:6px;color:var(--brand-primary)"></i>Payment'
    + '</div>'
    + '<div style="font-size:11px;color:var(--muted,#8b7d6b);margin-bottom:10px">'
      + 'Your invoice includes both card and direct debit payment options by default.'
    + '</div>'
    + modePills
    + planDetails
    + fullDetails
  + '</div>';
}

// Sets the top-level Standard / Payment Plan / Pay in Full mode. 'full' does not
// itself resolve to a paymentOption.type, the Stripe/GoCardless sub-choice below
// does that (defaults to full_stripe so the mode switch always has a valid state).
function setPaymentMode_(mode) {
  if (!state.workspace) state.workspace = {};
  if (!state.workspace.calculation) state.workspace.calculation = {};
  var existing = state.workspace.calculation.paymentOption || {};
  if (mode === 'plan') {
    state.workspace.calculation.paymentOption = { type: 'gc_plan', months: existing.months || 6 };
  } else if (mode === 'full') {
    var currentType = existing.type;
    state.workspace.calculation.paymentOption = {
      type: (currentType === 'full_stripe' || currentType === 'full_gc') ? currentType : 'full_stripe',
      months: 6
    };
  } else {
    state.workspace.calculation.paymentOption = { type: 'xero_standard', months: 6 };
  }
  markDirty_();
  renderStageBuilder();
}

// Kept for the Booking Tier / Travel Origin cards' shared naming convention,
// same effect as setPaymentMode_('plan') / setPaymentMode_('standard').
function togglePaymentPlan_(enabled) { setPaymentMode_(enabled ? 'plan' : 'standard'); }

function selectPaymentOption(type) {
  if (['stripe', 'gc_5050', 'gc_plan', 'full_stripe', 'full_gc'].indexOf(type) < 0) return;
  if (!state.workspace) state.workspace = {};
  if (!state.workspace.calculation) state.workspace.calculation = {};
  var existing = state.workspace.calculation.paymentOption || {};
  state.workspace.calculation.paymentOption = {
    type: type,
    months: existing.months || 6
  };
  markDirty_();
  renderStageBuilder();
}

function setPaymentPlanMonths(n) {
  var count = parseInt(n, 10);
  if (isNaN(count) || count < 3 || count > 12) return;
  if (!state.workspace) state.workspace = {};
  if (!state.workspace.calculation) state.workspace.calculation = {};
  if (!state.workspace.calculation.paymentOption) state.workspace.calculation.paymentOption = { type: 'gc_plan' };
  state.workspace.calculation.paymentOption.months = count;
  markDirty_();
  renderStageBuilder();
}

// Kept for backward compat with any persisted state using old paymentPlan model.
function togglePaymentPlan(enabled) { selectPaymentOption(enabled ? 'gc_plan' : 'stripe'); }
function setPaymentPlanInstallmentCount(n) { setPaymentPlanMonths(n); }

/* =================================================================
   P.5b -- PROPOSAL HISTORY
   ================================================================= */
function loadProposalHistory_(name) {
  // Issue #8 (Sub-B1, 2026-10-24): D1 read via cors-relay entity
  // 'proposal-history' (secret injected server-side) instead of GAS
  // proposalGetHistory. Opt-in only: window.JV_PB_HISTORY_CF must be true
  // (default OFF) AND jv-crm-mirror's PROPOSAL_HISTORY_READ_RELAY_ENABLED=1.
  // Any non-ok / {fallback:'gas'} answer falls through to the GAS call below
  // (cutover window only; removed once the D1 read is soaked).
  if (window.JV_PB_HISTORY_CF === true && window._pbAdminProxyBase) {
    var _phNf = (window._jvNativeFetch || window.fetch).bind(window);
    var _phUrl = String(window._pbAdminProxyBase).replace(/\/+$/, '') + '/partner/proposal-history/read';
    _phNf(_phUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      credentials: 'same-origin',
      body: JSON.stringify({ name: name })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        if (body && body.ok === true && Array.isArray(body.history)) {
          if (state.currentBride && state.currentBride !== name) return;
          state.proposalHistory = body.history;
          var panelCf = document.getElementById('proposalHistoryPanel');
          if (panelCf) panelCf.outerHTML = renderProposalHistoryHtml_();
          return;
        }
        loadProposalHistoryGas_(name);
      })
      .catch(function () { loadProposalHistoryGas_(name); });
    return;
  }
  loadProposalHistoryGas_(name);
}

function loadProposalHistoryGas_(name) {
  jvBridge.callAdmin('proposalGetHistory', { name: name })
    .then(function(res) {
      var payload = (res && res.data) || res;
      if (payload && payload.ok && Array.isArray(payload.history)) {
        state.proposalHistory = payload.history;
        var panel = document.getElementById('proposalHistoryPanel');
        if (panel) panel.outerHTML = renderProposalHistoryHtml_();
      }
    })
    .catch(function() { /* non-fatal — history panel stays empty */ });
}

function proposalIsAccepted_(status) {
  if (!status) return false;
  var s = status.toLowerCase();
  return s === 'signed' || s === 'accepted' || s === 'proposal accepted' || s === 'booked';
}

function downloadAcceptedSummary(btn) {
  if (!state.currentBride) return;
  var origHtml = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Loading…';

  pbCfBuilderRead_('accepted-summary', { name: state.currentBride }, pbAcceptCfAcceptedSummary_)
    .then(function(cfRes) { return cfRes || jvBridge.callAdmin('proposalGetAcceptedSummary', { name: state.currentBride }); })
    .then(function(res) {
      btn.disabled = false;
      btn.innerHTML = origHtml;
      var payload = (res && res.data) || res;
      if (!payload || !payload.ok || !payload.summary) {
        showBanner((payload && payload.error) || 'Could not load signed summary', 'error');
        return;
      }
      jvRenderAcceptedSummaryDoc_(payload.summary);
    })
    .catch(function(err) {
      btn.disabled = false;
      btn.innerHTML = origHtml;
      showBanner(getErrorMessage(err), 'error');
    });
}

function jvRenderAcceptedSummaryDoc_(s) {
  function fmtIso(str) {
    var m = String(str || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return str || '—';
    var mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return parseInt(m[3], 10) + ' ' + mo[parseInt(m[2], 10) - 1] + ' ' + m[1];
  }
  function fmtTs(str) {
    if (!str) return '—';
    return String(str).replace('T', ' ').substring(0, 16);
  }
  function esc(v) { return String(v || '').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function aud(n) { return 'A$' + Number(n || 0).toFixed(2); }

  var lineItems  = Array.isArray(s.lineItems)  ? s.lineItems  : [];
  var addonItems = Array.isArray(s.addonItems) ? s.addonItems : [];
  var bride      = esc(s.brideName || 'Bride');

  var liRows = lineItems.map(function(li) {
    var desc     = esc(li.description || li.name || '');
    var subtitle = esc(li.subtitle || '');
    var cell     = desc + (subtitle ? '<br><span style="font-size:11px;color:#8b7d6b">' + subtitle + '</span>' : '');
    return '<tr><td>' + cell + '</td>' +
      '<td style="text-align:center;width:54px;vertical-align:top">' + (li.qty || 1) + '</td>' +
      '<td style="text-align:right;width:96px;vertical-align:top">' + aud(li.total) + '</td></tr>';
  }).join('');

  var addonRows = addonItems.map(function(a) {
    return '<tr><td>' + esc(a.name || a.id || '') + '</td>' +
      '<td style="text-align:center;width:54px">1</td>' +
      '<td style="text-align:right;width:96px">' + aud(a.price) + '</td></tr>';
  }).join('');

  var hasAddons     = addonItems.length > 0;
  var acceptedTotal = Number(s.acceptedTotal || 0);
  var deposit       = Number(s.acceptedDeposit || 0);
  var balance       = Number(s.balanceDue || 0);
  var balanceDate   = esc(s.balanceDueDate || '—');
  var acceptedSub   = Number(s.acceptedSubtotal || 0);
  var acceptedGst   = Number(s.acceptedGst || 0);

  var html = '<!DOCTYPE html><html><head><meta charset="UTF-8">' +
    '<title>Signed Proposal — ' + bride + '</title>' +
    '<style>' +
      'body{font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#2f2524;margin:36px;max-width:700px}' +
      'h1{font-size:18px;margin:0 0 3px}' +
      '.email{font-size:12px;color:#8b7d6b;margin-bottom:18px}' +
      '.meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px 20px;padding:12px 0;' +
        'border-top:1px solid #e8ddd3;border-bottom:1px solid #e8ddd3;margin-bottom:20px}' +
      '.meta label{display:block;font-size:10px;text-transform:uppercase;color:#8b7d6b;margin-bottom:2px}' +
      '.meta .val{font-size:13px;font-weight:600}' +
      'h3{font-size:11px;font-weight:700;text-transform:uppercase;color:#8b7d6b;letter-spacing:0.5px;margin:18px 0 8px}' +
      'table{width:100%;border-collapse:collapse;margin-bottom:10px}' +
      'th{text-align:left;padding:5px 8px;border-bottom:2px solid #e8ddd3;font-size:10px;text-transform:uppercase;color:#8b7d6b}' +
      'td{padding:5px 8px;border-bottom:1px solid #f0ebe7;vertical-align:top}' +
      '.totals{text-align:right;font-size:13px;line-height:1.9;margin-bottom:20px}' +
      '.total-line{font-size:15px;font-weight:700;color:#c99591}' +
      '.pay{background:#f6f1ee;border-radius:8px;padding:14px;margin-bottom:16px}' +
      '.pay h3{margin-top:0}' +
      '.pay-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #e8ddd3;font-size:13px}' +
      '.pay-row:last-child{border:none;font-weight:700;font-size:14px;padding-top:10px}' +
      '.note{font-size:11px;color:#8b7d6b;border-top:1px solid #e8ddd3;padding-top:12px;margin-top:6px}' +
      '@media print{body{margin:20px}}' +
    '</style></head><body>' +
    '<h1>' + bride + ' — Signed Proposal</h1>' +
    '<div class="email">' + esc(s.email) + '</div>' +
    '<div class="meta">' +
      '<div><label>Wedding Date</label><div class="val">' + fmtIso(s.weddingDate) + '</div></div>' +
      '<div><label>Quote Sent</label><div class="val">' + fmtTs(s.quoteSentDate) + '</div></div>' +
      '<div><label>Signed</label><div class="val">' + fmtTs(s.signedDate) + '</div></div>' +
    '</div>' +
    '<h3>Package Details</h3>' +
    '<table><thead><tr>' +
      '<th>Description</th>' +
      '<th style="text-align:center;width:54px">Qty</th>' +
      '<th style="text-align:right;width:96px">Total</th>' +
    '</tr></thead><tbody>' + liRows + '</tbody></table>' +
    (hasAddons ?
      '<h3>Bride-Selected Upgrades &amp; Add-ons</h3>' +
      '<table><thead><tr>' +
        '<th>Upgrade</th>' +
        '<th style="text-align:center;width:54px">Qty</th>' +
        '<th style="text-align:right;width:96px">Total</th>' +
      '</tr></thead><tbody>' + addonRows + '</tbody></table>'
    : '') +
    '<div class="totals">' +
      'Subtotal (ex GST): ' + aud(acceptedSub) + '<br>' +
      'GST (10%): ' + aud(acceptedGst) + '<br>' +
      '<span class="total-line">Total (inc GST): ' + aud(acceptedTotal) + '</span>' +
    '</div>' +
    '<div class="pay">' +
      '<h3>Payment Summary</h3>' +
      '<div class="pay-row">' +
        '<span>Booking Fee (non-refundable, 50%)</span>' +
        '<span>' + aud(deposit) + '</span>' +
      '</div>' +
      '<div class="pay-row">' +
        '<span>Balance Due — 14 days before wedding' + (s.balanceDueDate ? ' (' + balanceDate + ')' : '') + '</span>' +
        '<span>' + aud(balance) + '</span>' +
      '</div>' +
    '</div>' +
    '<div class="note">Digitally signed ' + fmtTs(s.signedDate) + '. ' +
      'This is an administrative summary. The binding signed agreement is stored in the bride’s Drive folder.</div>' +
    '</body></html>';

  var pw = window.open('', '_jvaccepted', 'width=820,height=760');
  if (!pw) return;
  pw.document.open();
  pw.document.write(html);
  pw.document.close();
  setTimeout(function() { try { pw.print(); } catch(e) {} }, 400);
}

function jvPrintVersion(idx) {
  var v = (state.proposalHistory || [])[idx];
  if (!v) return;

  var vNum      = v.revisionnumber || (idx + 1);
  var sentAt    = v.sentat ? String(v.sentat).replace('T', ' ').substring(0, 16) : '—';
  var lineItems = Array.isArray(v.lineItems) ? v.lineItems : [];
  var subtotal  = Number(v.subtotal || 0).toFixed(2);
  var gst       = Number(v.gst     || 0).toFixed(2);
  var total     = Number(v.total   || 0).toFixed(2);

  var liRows = lineItems.map(function(li) {
    var desc     = (li.description || li.name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    var subtitle = (li.subtitle || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    var qty      = li.qty || 1;
    var price    = 'A$' + Number(li.total || 0).toFixed(2);
    var descCell = desc + (subtitle ? '<br><span style="font-size:11px;color:#8b7d6b">' + subtitle + '</span>' : '');
    return '<tr><td>' + descCell + '</td><td style="text-align:center;width:60px;vertical-align:top">' + qty + '</td><td style="text-align:right;width:100px;vertical-align:top">' + price + '</td></tr>';
  }).join('');

  var bride = (v.bridename || 'Bride').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Format wedding date — handles ISO strings, Date objects, DD/MM/YYYY strings
  var wdRaw = String(v.weddingdate || '');
  var wdFormatted = wdRaw;
  var isoMatch = wdRaw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    wdFormatted = parseInt(isoMatch[3], 10) + ' ' + months[parseInt(isoMatch[2], 10) - 1] + ' ' + isoMatch[1];
  }

  var html = '<!DOCTYPE html><html><head><meta charset="UTF-8">' +
    '<title>Proposal v' + vNum + ' — ' + bride + '</title>' +
    '<style>body{font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#2f2524;margin:36px;max-width:680px}' +
    'h1{font-size:18px;margin:0 0 4px}' +
    '.sub{font-size:12px;color:#8b7d6b;margin-bottom:20px}' +
    'table{width:100%;border-collapse:collapse;margin-bottom:14px}' +
    'th{text-align:left;padding:6px 8px;border-bottom:2px solid #e8ddd3;font-size:11px;text-transform:uppercase;color:#8b7d6b}' +
    'td{padding:6px 8px;border-bottom:1px solid #f0ebe7;vertical-align:top}' +
    '.totals{text-align:right;font-size:13px;line-height:1.8}' +
    '.grand{font-size:15px;font-weight:700;color:#c99591}' +
    '@media print{body{margin:20px}}</style>' +
    '</head><body>' +
    '<h1>' + bride + ' — Proposal v' + vNum + '</h1>' +
    '<div class="sub">' +
      (v.email ? v.email.replace(/</g, '&lt;') + ' &nbsp;&middot;&nbsp; ' : '') +
      (wdFormatted ? 'Wedding: ' + wdFormatted.replace(/</g, '&lt;') + ' &nbsp;&middot;&nbsp; ' : '') +
      'Sent: ' + sentAt +
    '</div>' +
    '<table><thead><tr>' +
      '<th>Description</th><th style="text-align:center;width:60px">Qty</th><th style="text-align:right;width:100px">Total</th>' +
    '</tr></thead><tbody>' + liRows + '</tbody></table>' +
    '<div class="totals">' +
      'Subtotal (ex GST): A$' + subtotal + '<br>' +
      'GST (10%): A$' + gst + '<br>' +
      '<span class="grand">Total (inc GST): A$' + total + '</span>' +
    '</div>' +
    '</body></html>';

  var pw = window.open('', '_jvprint_' + idx, 'width=780,height=680');
  if (!pw) return;
  pw.document.open();
  pw.document.write(html);
  pw.document.close();
  setTimeout(function() { try { pw.print(); } catch(e) {} }, 400);
}

// Copy the bride-facing share link for a specific sent version to the clipboard.
// Rebuilds the link from the token against the current canonical viewer base so the
// copied URL never carries a retired base; falls back to the stored proposalurl.
function jvCopyVersionLink(idx) {
  var v = (state.proposalHistory || [])[idx];
  if (!v) return;
  var url = v.token ? proposalViewerUrl_(v.token) : (v.proposalurl || '');
  if (!url) { showBanner('No share link for this version', 'error'); return; }
  copyProposalLink_(url);
}

function renderProposalHistoryHtml_() {
  var history = state.proposalHistory || [];
  if (!history.length) {
    return '<div id="proposalHistoryPanel" class="card" style="margin-top:0">' +
      '<div class="card-title"><i class="fa-solid fa-clock-rotate-left"></i> Sent Versions</div>' +
      '<div style="font-size:13px;color:var(--muted);padding:4px 0">No versions sent yet.</div>' +
    '</div>';
  }

  var rows = '';
  for (var i = 0; i < history.length; i++) {
    var v = history[i];
    var vNum = v.revisionnumber || (history.length - i);
    var sentAt = v.sentat ? v.sentat.toString().replace('T', ' ').substring(0, 16) : '—';
    var total = v.total ? 'A$' + Number(v.total).toFixed(2) + ' inc GST' : '—';
    var emailFlag = v.emailsent === 'YES'
      ? '<span style="font-size:11px;background:#e8f5e9;color:#2e7d32;border-radius:20px;padding:2px 8px">email sent</span>'
      : '<span style="font-size:11px;background:#fce4ec;color:#b71c1c;border-radius:20px;padding:2px 8px">no email</span>';

    // Lifecycle marker (frozen-per-version Phase 3): versionStatus is attached server-side
    // from the Proposal Tokens sheet (ACTIVE is current/payable, others are view-only). Empty for
    // legacy single-token brides, so render nothing rather than guessing which one is live by position.
    var vStatus = String(v.versionStatus || '').trim().toUpperCase();
    var statusFlag = '';
    if (vStatus === 'ACTIVE') {
      statusFlag = '<span style="font-size:11px;font-weight:700;background:var(--brand-primary);color:#fff;border-radius:20px;padding:2px 9px">current</span>';
    } else if (vStatus === 'ACCEPTED') {
      statusFlag = '<span style="font-size:11px;font-weight:600;background:#e8f5e9;color:#2e7d32;border-radius:20px;padding:2px 9px">accepted</span>';
    } else if (vStatus === 'SUPERSEDED') {
      statusFlag = '<span style="font-size:11px;background:#f0ebe7;color:var(--muted);border-radius:20px;padding:2px 9px">superseded</span>';
    } else if (vStatus === 'VOID') {
      statusFlag = '<span style="font-size:11px;background:#fce4ec;color:#b71c1c;border-radius:20px;padding:2px 9px">void</span>';
    } else if (vStatus === 'EXPIRED') {
      statusFlag = '<span style="font-size:11px;background:#f0ebe7;color:var(--muted);border-radius:20px;padding:2px 9px">expired</span>';
    }

    var lineItems = Array.isArray(v.lineItems) ? v.lineItems : [];
    var liRows = '';
    for (var j = 0; j < lineItems.length; j++) {
      var li = lineItems[j];
      var liDesc = escapeHtml(li.description || li.name || '');
      var liTotal = li.total != null ? 'A$' + Number(li.total).toFixed(2) : '';
      liRows += '<tr style="border-bottom:1px solid #f0ebe7">' +
        '<td style="padding:4px 8px;font-size:12px;color:var(--brand-text)">' + liDesc + '</td>' +
        '<td style="padding:4px 8px;font-size:12px;color:var(--muted);text-align:center">' + (li.qty || 1) + '</td>' +
        '<td style="padding:4px 8px;font-size:12px;color:var(--brand-text);text-align:right">' + liTotal + '</td>' +
      '</tr>';
    }

    // Share link is rebuilt from the token against the CURRENT canonical viewer base
    // (stored proposalurl may carry a retired base that now 404s); fall back to it
    // only when a version predates token capture. agreementUrl is present only on the
    // signed version (set server-side in proposalGetHistory).
    var shareUrl     = v.token ? proposalViewerUrl_(v.token) : (v.proposalurl || '');
    var agreementUrl = v.agreementUrl ? String(v.agreementUrl) : '';

    rows +=
      '<details style="border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);overflow:hidden;margin-bottom:8px">' +
        '<summary style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;cursor:pointer;background:var(--brand-bg-soft);list-style:none;gap:8px">' +
          '<div style="display:flex;align-items:center;gap:10px">' +
            '<span style="font-weight:700;font-size:13px;color:var(--brand-primary)">v' + vNum + '</span>' +
            statusFlag +
            '<span style="font-size:13px;color:var(--brand-text)">' + escapeHtml(sentAt) + '</span>' +
            emailFlag +
          '</div>' +
          '<span style="font-size:14px;font-weight:700;color:var(--brand-text)">' + escapeHtml(total) + '</span>' +
        '</summary>' +
        '<div style="padding:12px 14px">' +
          '<table style="width:100%;border-collapse:collapse;margin-bottom:8px">' +
            '<thead><tr style="border-bottom:2px solid #e8ddd3">' +
              '<th style="text-align:left;padding:4px 8px;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase">Description</th>' +
              '<th style="text-align:center;padding:4px 8px;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase">Qty</th>' +
              '<th style="text-align:right;padding:4px 8px;font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase">Total</th>' +
            '</tr></thead>' +
            '<tbody>' + liRows + '</tbody>' +
          '</table>' +
          '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">' +
            (shareUrl ? '<a href="' + escapeHtml(shareUrl) + '" target="_blank" rel="noopener" class="btn btn-ghost btn-sm" style="text-decoration:none"><i class="fa-solid fa-eye"></i> View this version</a>' : '') +
            (shareUrl ? '<button type="button" class="btn btn-ghost btn-sm" onclick="jvCopyVersionLink(' + i + ')"><i class="fa-solid fa-copy"></i> Copy link</button>' : '') +
            (agreementUrl ? '<a href="' + escapeHtml(agreementUrl) + '" target="_blank" rel="noopener" class="btn btn-ghost btn-sm" style="text-decoration:none"><i class="fa-solid fa-file-signature"></i> View agreement</a>' : '') +
            '<button type="button" class="btn btn-ghost btn-sm" onclick="jvPrintVersion(' + i + ')"><i class="fa-solid fa-file-arrow-down"></i> Download Summary</button>' +
          '</div>' +
        '</div>' +
      '</details>';
  }

  return '<div id="proposalHistoryPanel" class="card" style="margin-top:0">' +
    '<div class="card-title"><i class="fa-solid fa-clock-rotate-left"></i> Sent Versions <span style="font-size:12px;font-weight:400;color:var(--muted);margin-left:6px">' + history.length + ' total</span></div>' +
    rows +
  '</div>';
}

/* =================================================================
   P.6 -- EMAIL PREVIEW MODAL
   ================================================================= */
function showEmailPreviewModal() {
  if (!state.currentBride) return;
  var btn = document.getElementById('previewEmailBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Loading...'; }

  jvBridge.callAdmin('proposalGetEmailPreview', { name: state.currentBride })
    .then(function(res) {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-envelope-open-text"></i> Preview Email'; }
      var payload = (res && res.data) || res;
      if (!payload || payload.ok === false) {
        showBanner((payload && payload.error) || 'Could not load email preview', 'error');
        return;
      }
      renderEmailPreviewModal_(payload);
    })
    .catch(function(err) {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-envelope-open-text"></i> Preview Email'; }
      showBanner(getErrorMessage(err), 'error');
    });
}

function renderEmailPreviewModal_(data) {
  var existing = document.getElementById('emailPreviewOverlay');
  if (existing) existing.remove();

  var overlay = document.createElement('div');
  overlay.id = 'emailPreviewOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';

  var subject = data.subject || '';
  var toEmail = data.toEmail || '';
  var bodyHtml = data.bodyHtml || '';
  var bodyText = data.bodyText || '';

  overlay.innerHTML =
    '<div style="background:#fff;border-radius:var(--radius-large);padding:0;max-width:720px;width:100%;max-height:88vh;display:flex;flex-direction:column;box-shadow:0 16px 48px rgba(47,37,36,0.18)">' +
      '<div style="padding:20px 24px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;align-items:center;gap:12px">' +
        '<i class="fa-solid fa-envelope-open-text" style="font-size:18px;color:var(--brand-primary)"></i>' +
        '<div style="flex:1">' +
          '<div style="font-size:16px;font-weight:700;color:var(--brand-dark)">Email Preview</div>' +
          '<div style="font-size:12px;color:var(--muted)">Review before sending</div>' +
        '</div>' +
        '<button onclick="document.getElementById(\'emailPreviewOverlay\').remove()" style="background:none;border:none;cursor:pointer;font-size:18px;color:var(--muted);padding:4px">&times;</button>' +
      '</div>' +
      '<div style="padding:16px 24px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;flex-direction:column;gap:10px">' +
        '<div style="display:flex;align-items:center;gap:10px">' +
          '<label style="font-size:12px;font-weight:600;color:var(--muted);width:60px;flex-shrink:0">To:</label>' +
          '<input type="email" id="emailPreviewTo" value="' + escapeHtml(toEmail) + '" ' +
            'style="flex:1;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:7px 12px;font-size:13px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:10px">' +
          '<label style="font-size:12px;font-weight:600;color:var(--muted);width:60px;flex-shrink:0">Subject:</label>' +
          '<input type="text" id="emailPreviewSubject" value="' + escapeHtml(subject) + '" ' +
            'style="flex:1;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:7px 12px;font-size:13px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
        '</div>' +
      '</div>' +
      '<div style="flex:1;overflow:hidden;display:flex;flex-direction:column">' +
        '<div style="padding:10px 24px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;gap:8px;align-items:center">' +
          '<span style="font-size:12px;color:var(--muted);font-weight:600">View:</span>' +
          '<button class="btn btn-ghost btn-sm" id="previewModeHtmlBtn" onclick="setEmailPreviewMode(\'html\')" style="font-size:12px">HTML</button>' +
          '<button class="btn btn-ghost btn-sm" id="previewModeTextBtn" onclick="setEmailPreviewMode(\'text\')" style="font-size:12px">Plain Text (Editable)</button>' +
        '</div>' +
        '<div id="emailPreviewHtmlPane" style="flex:1;overflow-y:auto;padding:16px 24px">' +
          '<iframe id="emailPreviewIframe" sandbox="allow-same-origin" ' +
            'style="width:100%;min-height:320px;border:none;border-radius:var(--radius-medium);background:#fff" ' +
            'srcdoc="' + bodyHtml.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '">' +
          '</iframe>' +
        '</div>' +
        '<div id="emailPreviewTextPane" style="display:none;flex:1;padding:16px 24px">' +
          '<textarea id="emailBodyOverride" ' +
            'style="width:100%;min-height:280px;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:12px;font-size:13px;font-family:inherit;resize:vertical;background:#fff;color:var(--brand-text)">' +
            escapeHtml(bodyText) +
          '</textarea>' +
        '</div>' +
      '</div>' +
      '<div style="padding:16px 24px;border-top:1px solid var(--border-color,#e8ddd3);display:flex;justify-content:flex-end;gap:10px">' +
        '<button class="btn btn-ghost" onclick="document.getElementById(\'emailPreviewOverlay\').remove()">Cancel</button>' +
        '<button class="btn btn-primary" id="emailPreviewSendBtn" onclick="sendFromEmailPreview()">' +
          '<i class="fa-solid fa-paper-plane"></i> Send Proposal' +
        '</button>' +
      '</div>' +
    '</div>';

  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });
  // Set iframe content properly to avoid XSS issues with srcdoc escaping
  var iframe = overlay.querySelector('#emailPreviewIframe');
  if (iframe) {
    try {
      iframe.removeAttribute('srcdoc');
      iframe.contentDocument.open();
      iframe.contentDocument.write(bodyHtml);
      iframe.contentDocument.close();
    } catch (ex) {
      iframe.srcdoc = bodyHtml;
    }
  }
}

function setEmailPreviewMode(mode) {
  var htmlPane = document.getElementById('emailPreviewHtmlPane');
  var textPane = document.getElementById('emailPreviewTextPane');
  var htmlBtn = document.getElementById('previewModeHtmlBtn');
  var textBtn = document.getElementById('previewModeTextBtn');
  if (!htmlPane || !textPane) return;
  if (mode === 'html') {
    htmlPane.style.display = 'block';
    textPane.style.display = 'none';
    if (htmlBtn) htmlBtn.style.fontWeight = '700';
    if (textBtn) textBtn.style.fontWeight = '400';
  } else {
    htmlPane.style.display = 'none';
    textPane.style.display = 'block';
    if (htmlBtn) htmlBtn.style.fontWeight = '400';
    if (textBtn) textBtn.style.fontWeight = '700';
  }
}

function sendFromEmailPreview() {
  if (!state.currentBride) return;
  var btn = document.getElementById('emailPreviewSendBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending...'; }

  var customSubject = (document.getElementById('emailPreviewSubject') || {}).value || '';
  var customEmail = (document.getElementById('emailPreviewTo') || {}).value || '';
  var textPane = document.getElementById('emailPreviewTextPane');
  var customBodyOverride = '';
  if (textPane && textPane.style.display !== 'none') {
    customBodyOverride = (document.getElementById('emailBodyOverride') || {}).value || '';
  }

  var options = {
    sendEmail: true,
    sendSms: document.getElementById('optSms') ? document.getElementById('optSms').checked : true,
    customSubject: customSubject,
    customEmail: customEmail,
    customBodyOverride: customBodyOverride || undefined,
    firedBy: (window.jvAuth && window.jvAuth.email) || ''
  };

  // PATH 2: flush, then send.
  var doSend = function() {
    pbApproveAndSendWarm_(state.currentBride, options)
      .then(function(res) {
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> Send Proposal'; }
        var payload = (res && res.data) || res;
        if (!payload || payload.status === 'error') {
          showBanner((payload && payload.message) || 'Send failed', 'error');
          return;
        }
        var overlay = document.getElementById('emailPreviewOverlay');
        if (overlay) overlay.remove();
        showBanner('Proposal sent successfully!', 'success');
        state.workspace.proposal.status = 'SENT';
        state.workspace.proposal.sentDate = new Date().toISOString().split('T')[0];
        state.workspace.proposal.token = payload.token || '';
        renderStageSend();
        patchBrideLocal_(state.currentBride, { proposalStatus: 'SENT', sentDate: state.workspace.proposal.sentDate });
        loadBrides();
        if (state.currentBride) loadProposalHistory_(state.currentBride);
      })
      .catch(function(err) {
        var resetPreviewSendBtn = function() {
          if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> Send Proposal'; }
        };
        if (pbClassifySendOutcome_(err) === 'unknown') {
          pbResolveSendOutcome_(state.currentBride, resetPreviewSendBtn).then(function(outcome) {
            // Only dismiss the preview overlay once CONFIRMED sent -- an
            // 'unknown' outcome leaves the overlay open (and the persistent
            // banner visible) so the admin isn't left staring at a closed
            // modal with no idea whether anything actually happened.
            if (outcome === 'sent') {
              var overlay = document.getElementById('emailPreviewOverlay');
              if (overlay) overlay.remove();
            }
          });
          return;
        }
        resetPreviewSendBtn();
        showBanner(getErrorMessage(err), 'error');
      });
  };

  flushToGas_()
    .then(doSend)
    .catch(function(err) {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> Send Proposal'; }
      showBanner('Failed to save before send: ' + getErrorMessage(err), 'error');
    });
}

/* =================================================================
   INIT
   ================================================================= */

/* ----------------------------------------------------------------- */
function buildFinishTimeOptions_(current) {
  var currentUpper = String(current || '').trim().toUpperCase();
  var html = '<option value=""></option>';
  html += '<option value="TBC"' + (currentUpper === 'TBC' ? ' selected' : '') + '>To be confirmed (TBC)</option>';
  for (var h = 6; h < 24; h++) {
    for (var m = 0; m < 60; m += 30) {
      var period = h >= 12 ? 'PM' : 'AM';
      var h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
      var label = h12 + ':' + (m === 0 ? '00' : '30') + ' ' + period;
      html += '<option value="' + label + '"' + (label === current ? ' selected' : '') + '>' + label + '</option>';
    }
  }
  return html;
}

// Normalise a time string to 12-hour "h:mm AM/PM" for display. Accepts 24h
// ("06:00"/"14:30"), already-12h ("6:00 am"), or "TBC"/blank (passed through).
// Keeps finish time and est. start in one consistent format (P1d, diagnostic D2).
function fmtTime12h_(val) {
  var s = String(val == null ? '' : val).trim();
  if (!s || s.toUpperCase() === 'TBC') return s;
  if (/[ap]\.?m\.?$/i.test(s)) {
    // Already 12-hour, just normalise the AM/PM casing + spacing.
    return s.replace(/\s*([ap])\.?m\.?$/i, function(_, p) { return ' ' + p.toUpperCase() + 'M'; });
  }
  var m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return s;
  var h = parseInt(m[1], 10);
  var period = h >= 12 ? 'PM' : 'AM';
  var h12 = h % 12; if (h12 === 0) h12 = 12;
  return h12 + ':' + m[2] + ' ' + period;
}

// PATH 2 / P1d: finish time is the SINGLE source of truth for the start-time
// formula. Both finish inputs (Stage-1 select, Stage-2 text) route here so they
// can never desync. Writes calc.finishTime (the override the formula reads) AND
// bride.finishTime (the raw enquiry value shown elsewhere). On an explicit change
// it drops the stale auto start so GAS recomputes it from the NEW finish, unless
// the admin has locked the start (timingLocks.estimatedStartTime). No-op when the
// value is unchanged, so a blur without an edit never triggers a recalc.
function commitFinishTime_(value) {
  if (!state.workspace) return;
  var trimmed = String(value || '').trim();
  var calc = state.workspace.calculation = state.workspace.calculation || {};
  if (String(calc.finishTime || '').trim() === trimmed) return; // unchanged, nothing to do
  calc.finishTime = trimmed;
  if (state.workspace.bride) state.workspace.bride.finishTime = trimmed;
  var locks = calc.timingLocks || {};
  if (!locks.estimatedStartTime) calc.estimatedStartTime = ''; // let GAS recompute from new finish
  markDirty_();
  // Recompute the start immediately from the new finish time (one round-trip on a
  // deliberate commit; oninput is not wired so this never fires per keystroke).
  flushToGas_().then(function() { renderActiveStage(); }).catch(function() {});
}

// Back-compat shim: Stage-1 select onchange still calls saveFinishTime.
function saveFinishTime(value) { commitFinishTime_(value); }

/* =================================================================
   P.7 -- DECLINE CONTEXT MODAL ("Mark Unavailable & Notify")
   ================================================================= */

var _declineCtx = null; // { clientId, emailPreview }

function openDeclineContextModal() {
  if (!state.currentBride) return;

  var existing = document.getElementById('declineCtxOverlay');
  if (existing) existing.remove();

  // Tiny spinner overlay while we fetch bride info + email (fast CRM-only call)
  var overlay = document.createElement('div');
  overlay.id = 'declineCtxOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);z-index:1000;display:flex;align-items:center;justify-content:center';
  overlay.innerHTML = '<div style="background:#fff;border-radius:var(--radius-large);padding:32px 40px;text-align:center;box-shadow:0 16px 48px rgba(47,37,36,0.18)">' +
    '<i class="fa-solid fa-spinner fa-spin" style="font-size:22px;color:var(--brand-primary)"></i>' +
    '<div style="margin-top:10px;font-size:13px;color:var(--muted)">Loading…</div></div>';
  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });

  jvBridge.callAdmin('bridge_admin_generic', { function: 'proposalGetDeclineContext', args: { name: state.currentBride } })
    .then(function(res) {
      var payload = (res && res.data) || res;
      if (!payload || payload.ok === false) {
        var ov = document.getElementById('declineCtxOverlay');
        if (ov) ov.remove();
        showBanner((payload && payload.error) || 'Could not load bride data', 'error');
        return;
      }
      // CROSS-LANE FINDING (2026-09-02, live verify of the Lakeita fix surfaced a
      // second, separate instance of the same bug class): this decline path never
      // sent email to adminCrm_declineUnavailable either. That backend function
      // (AdminCRMAPI.gs, Admin CRM Agent's file, not this lane's) has NO row-position
      // verification of any kind today, unlike its sibling adminCrm_closeEnquiry,
      // which now correctly requires and checks email. Flagged separately as an owed
      // cross-lane fix (that function is not this file). Threading email through
      // HERE regardless, since proposalGetDeclineContext already returns it
      // (bride.email) and it costs nothing to have ready the moment the backend
      // guard is added, and it is useful defensive data even without a backend
      // check consuming it yet.
      _declineCtx = {
        clientId: payload.clientId,
        email: (payload.bride && payload.bride.email) || '',
        emailPreview: payload.emailPreview,
        // Stashed for the persistent confirmation message after a successful
        // decline (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-
        // confirmation-2026-09-30.md addendum) -- state.currentBride/workspace
        // are cleared to null right before that message is built, so the
        // decline handler needs its own copy of who/when this was.
        name: (payload.bride && payload.bride.name) || state.currentBride || '',
        weddingDateLabel: (payload.bride && (payload.bride.weddingDateLabel || payload.bride.weddingDate)) || ''
      };
      // Render modal immediately — schedule panel loads async inside
      renderDeclineContextModal_(payload);
    })
    .catch(function(err) {
      var ov = document.getElementById('declineCtxOverlay');
      if (ov) ov.remove();
      showBanner(getErrorMessage(err), 'error');
    });
}

function renderDeclineContextModal_(data) {
  var existing = document.getElementById('declineCtxOverlay');
  if (existing) existing.remove();

  var bride     = data.bride        || {};
  var ep        = data.emailPreview || {};
  var dateLabel = bride.weddingDateLabel || bride.weddingDate || '';

  var overlay = document.createElement('div');
  overlay.id = 'declineCtxOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);z-index:1000;display:flex;align-items:flex-start;justify-content:center;padding:16px;overflow-y:auto';

  // ── Assemble modal — email-only (schedule is visible on Stage 1) ──
  overlay.innerHTML =
    '<div style="background:#fff;border-radius:var(--radius-large);width:100%;max-width:680px;margin:auto;box-shadow:0 16px 48px rgba(47,37,36,0.18);display:flex;flex-direction:column">' +

      // Header
      '<div style="padding:20px 24px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;align-items:center;gap:12px">' +
        '<i class="fa-solid fa-circle-xmark" style="font-size:18px;color:#c94040"></i>' +
        '<div style="flex:1">' +
          '<div style="font-size:16px;font-weight:700;color:#c94040">Mark Unavailable — ' + escapeHtml(bride.name || state.currentBride) + '</div>' +
          '<div style="font-size:12px;color:var(--muted)">' + escapeHtml(dateLabel) + ' · Close enquiry and notify the bride</div>' +
        '</div>' +
        '<button onclick="document.getElementById(\'declineCtxOverlay\').remove()" style="background:none;border:none;cursor:pointer;font-size:20px;color:var(--muted);padding:4px;line-height:1">&times;</button>' +
      '</div>' +

      // Email fields
      '<div style="padding:14px 20px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;flex-direction:column;gap:8px">' +
        '<div style="display:flex;align-items:center;gap:8px">' +
          '<label style="font-size:12px;font-weight:600;color:var(--muted);width:58px;flex-shrink:0">To:</label>' +
          '<input type="email" id="declineEmailTo" value="' + escapeHtml(ep.toEmail || bride.email || '') + '" ' +
            'style="flex:1;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:6px 10px;font-size:13px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
        '</div>' +
        '<div style="display:flex;align-items:center;gap:8px">' +
          '<label style="font-size:12px;font-weight:600;color:var(--muted);width:58px;flex-shrink:0">Subject:</label>' +
          '<input type="text" id="declineEmailSubject" value="' + escapeHtml(ep.subject || '') + '" ' +
            'style="flex:1;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:6px 10px;font-size:13px;font-family:inherit;background:#fff;color:var(--brand-text)">' +
        '</div>' +
      '</div>' +

      // HTML / Text toggle
      '<div style="padding:8px 20px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;gap:6px;align-items:center">' +
        '<span style="font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:0.5px">View:</span>' +
        '<button class="btn btn-ghost btn-sm" id="declinePreviewHtmlBtn" onclick="setDeclinePreviewMode(\'html\')" style="font-size:12px;font-weight:700">HTML</button>' +
        '<button class="btn btn-ghost btn-sm" id="declinePreviewTextBtn" onclick="setDeclinePreviewMode(\'text\')" style="font-size:12px">Plain Text (Editable)</button>' +
      '</div>' +

      // HTML iframe pane
      '<div id="declineEmailHtmlPane" style="overflow-y:auto;max-height:340px;padding:12px 20px">' +
        '<iframe id="declineEmailIframe" sandbox="allow-same-origin" style="width:100%;min-height:260px;border:none;border-radius:8px;background:#fff"></iframe>' +
      '</div>' +

      // Plain text pane (editable)
      '<div id="declineEmailTextPane" style="display:none;padding:12px 20px">' +
        '<textarea id="declineBodyOverride" style="width:100%;min-height:220px;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:10px;font-size:13px;font-family:inherit;resize:vertical;background:#fff;color:var(--brand-text)">' +
          escapeHtml(ep.bodyText || '') +
        '</textarea>' +
      '</div>' +

      // Footer
      '<div style="padding:16px 24px;border-top:1px solid var(--border-color,#e8ddd3);display:flex;flex-wrap:wrap;gap:14px;align-items:flex-end">' +
        '<div style="flex:1;min-width:200px">' +
          '<label style="font-size:12px;font-weight:600;color:var(--muted);display:block;margin-bottom:4px">Internal Notes (optional)</label>' +
          '<textarea id="declineNotesInput" placeholder="e.g. Date fully committed, trial + 3 weddings" rows="2" ' +
            'style="width:100%;border:1px solid var(--border-color,#e8ddd3);border-radius:var(--radius-medium);padding:8px 10px;font-size:13px;font-family:inherit;resize:vertical;background:#fff;color:var(--brand-text)"></textarea>' +
          '<label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;margin-top:8px">' +
            '<input type="checkbox" id="declineSendEmail" checked style="width:15px;height:15px;accent-color:var(--brand-primary)"> Send decline email to bride' +
          '</label>' +
          // BUILD-decline-closes-date-to-future-enquiries-2026-07-22: OFF by default so a
          // normal decline (not a good fit, out of budget, etc) never closes a date by
          // accident. Only ticking this closes the DATE, not just this one bride's enquiry.
          '<label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;margin-top:6px;color:#c94040;font-weight:600">' +
            '<input type="checkbox" id="declineCloseDate" style="width:15px;height:15px;accent-color:#c94040"> Also close this date to future enquiries' +
          '</label>' +
          '<div style="font-size:11.5px;color:var(--muted);margin-top:2px;padding-left:23px">Any later enquiry for ' + escapeHtml(dateLabel || 'this date') + ' will be auto-declined with the same email, until you manually reopen the date.</div>' +
          '<button type="button" class="btn btn-ghost btn-sm" onclick="openClosedDatesPanel_()" style="margin-top:8px;font-size:12px"><i class="fa-solid fa-calendar-xmark"></i> Manage Closed Dates</button>' +
        '</div>' +
        '<div style="display:flex;gap:8px;flex-shrink:0">' +
          '<button class="btn btn-ghost" onclick="document.getElementById(\'declineCtxOverlay\').remove()">Cancel</button>' +
          '<button class="btn btn-danger" id="declineConfirmBtn" onclick="sendFromDeclineModal()">' +
            '<i class="fa-solid fa-circle-xmark"></i> Mark Unavailable' +
          '</button>' +
        '</div>' +
      '</div>' +

    '</div>'; // end modal card

  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });

  // Load iframe with HTML body
  var iframe = overlay.querySelector('#declineEmailIframe');
  if (iframe && ep.bodyHtml) {
    try {
      iframe.contentDocument.open();
      iframe.contentDocument.write(ep.bodyHtml);
      iframe.contentDocument.close();
    } catch (ex) {
      iframe.srcdoc = ep.bodyHtml;
    }
  }

}

/**
 * BUILD-decline-closes-date-to-future-enquiries-2026-07-22: small standalone panel
 * listing every currently-closed wedding date with a Reopen button. Reachable from
 * the Decline modal's "Manage Closed Dates" link. Low-traffic admin utility, kept
 * deliberately minimal, not a new page.
 */
function openClosedDatesPanel_() {
  var existing = document.getElementById('closedDatesOverlay');
  if (existing) existing.remove();

  var overlay = document.createElement('div');
  overlay.id = 'closedDatesOverlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(30,23,22,0.6);z-index:1100;display:flex;align-items:center;justify-content:center;padding:16px';
  overlay.innerHTML = '<div style="background:#fff;border-radius:var(--radius-large);width:100%;max-width:480px;box-shadow:0 16px 48px rgba(47,37,36,0.18)">' +
    '<div style="padding:18px 22px;border-bottom:1px solid var(--border-color,#e8ddd3);display:flex;align-items:center;gap:10px">' +
      '<i class="fa-solid fa-calendar-xmark" style="color:#c94040"></i>' +
      '<div style="flex:1;font-size:15px;font-weight:700;color:var(--brand-dark)">Closed Wedding Dates</div>' +
      '<button onclick="document.getElementById(\'closedDatesOverlay\').remove()" style="background:none;border:none;cursor:pointer;font-size:20px;color:var(--muted);padding:4px;line-height:1">&times;</button>' +
    '</div>' +
    '<div id="closedDatesListBody" style="padding:16px 22px;max-height:400px;overflow-y:auto;font-size:13px;color:var(--muted)">' +
      '<i class="fa-solid fa-spinner fa-spin"></i> Loading…' +
    '</div>' +
  '</div>';
  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });

  pbCfBuilderRead_('closed-dates', {}, pbAcceptCfClosedDates_)
    .then(function(res) {
      var body = document.getElementById('closedDatesListBody');
      if (!body) return;
      if (!res) {
        body.innerHTML = '<div style="color:#c94040">Could not load closed dates through Cloudflare.</div>';
        return;
      }
      var list = res.dates;
      if (!list.length) {
        body.innerHTML = '<div>No dates are currently closed.</div>';
        return;
      }
      body.innerHTML = list.map(function(d) {
        return '<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border-color,#e8ddd3)">' +
          '<div style="flex:1">' +
            '<div style="font-weight:700;color:var(--brand-text)">' + escapeHtml(formatDate(d.dateIso) || d.dateIso) + '</div>' +
            '<div style="font-size:11.5px;color:var(--muted)">Closed ' + escapeHtml(formatDate(d.closedAt) || '') +
              (d.sourceBrideName ? ' · from ' + escapeHtml(d.sourceBrideName) + '\'s decline' : '') + '</div>' +
          '</div>' +
          '<button class="btn btn-ghost btn-sm" onclick="reopenClosedDate_(\'' + escapeHtml(d.dateIso) + '\', this)">Reopen</button>' +
        '</div>';
      }).join('');
    })
    .catch(function(err) {
      var body = document.getElementById('closedDatesListBody');
      if (body) body.innerHTML = '<div style="color:#c94040">' + escapeHtml(getErrorMessage(err)) + '</div>';
    });
}

function reopenClosedDate_(dateIso, btn) {
  if (!confirm('Reopen ' + dateIso + '? Future enquiries for this date will build a normal proposal again.')) return;
  if (btn) { btn.disabled = true; btn.textContent = 'Reopening…'; }
  pbCfBuilderWrite_('closed-dates-reopen', { dateIso: dateIso, firedBy: (window.jvAuth && window.jvAuth.email) || '' }, pbAcceptCfClosedDateReopen_)
    .then(function(result) {
      if (result && result.refusal) {
        showBanner(result.refusal.error || 'Could not reopen date', 'error');
        if (btn) { btn.disabled = false; btn.textContent = 'Reopen'; }
        return;
      }
      if (!result || !result.cf) {
        showBanner('Could not reopen the date through Cloudflare. Nothing was changed.', 'error');
        if (btn) { btn.disabled = false; btn.textContent = 'Reopen'; }
        return;
      }
      showBanner('Reopened ' + dateIso, 'success');
      openClosedDatesPanel_(); // refresh the list
    })
    .catch(function(err) {
      showBanner(getErrorMessage(err), 'error');
      if (btn) { btn.disabled = false; btn.textContent = 'Reopen'; }
    });
}

function _buildDeclineScheduleHtml_(sched) {
  var weddings = (sched && sched.weddingsSummary) || [];
  var artists  = (sched && sched.artistsSummary)  || [];
  var externals = (sched && sched.externalBookings) || [];
  var coverage = (sched && sched.coverage) || null;
  var html = '';

  if (coverage) {
    var gaps = [];
    if (coverage.weeklyPatterns === false) gaps.push('weekly recurring availability');
    if (coverage.partialStatus === false) gaps.push('partial availability details');
    if (coverage.leaveReasons === false) gaps.push('leave reasons');
    if (coverage.bookingDetails === false) gaps.push('booking details');
    if (gaps.length) {
      html += '<div role="status" style="padding:9px 11px;margin-bottom:10px;border:1px solid #d8b36a;border-radius:6px;background:#fff8e8;color:#614b21;font-size:12px">' +
        '<strong>Limited availability data:</strong> this view does not include ' + escapeHtml(gaps.join(', ')) + '.</div>';
    }
  }

  if (weddings.length === 0 && externals.length === 0) {
    if (coverage && coverage.bookingDetails === false) {
      html += '<div style="padding:6px 0;font-size:13px;color:var(--muted)">Booking details are not included in this view.</div>';
    } else {
      html += '<div style="padding:6px 0;font-size:13px;display:flex;align-items:center;gap:8px;color:#2e7d32">' +
        '<i class="fa-solid fa-circle-check"></i> No other bookings on this date.</div>';
    }
  } else if (weddings.length === 0) {
    /* Skip the Confirmed Bookings header but still render externals below */
  } else {
    html += '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--muted);margin-bottom:8px">Confirmed Bookings</div>';
    weddings.forEach(function(w) {
      var timeStr = (w.callTime ? 'Call ' + w.callTime + ' · ' : '') + (w.startTime || '') + (w.endTime ? '–' + w.endTime : '');
      html +=
        '<div style="border:1px solid var(--border-color,#e8ddd3);border-radius:8px;padding:10px 12px;margin-bottom:8px;background:var(--brand-bg-soft,#f6f1ee)">' +
          '<div style="font-weight:700;font-size:13px;color:var(--brand-dark)">' + escapeHtml(w.brideName) + '</div>' +
          (w.venueShort ? '<div style="font-size:12px;color:var(--muted);margin-top:2px">' +
            '<i class="fa-solid fa-location-dot" style="color:var(--brand-primary);margin-right:4px"></i>' +
            (w.mapUrl ? '<a href="' + escapeHtml(w.mapUrl) + '" target="_blank" style="color:inherit;text-decoration:underline">' + escapeHtml(w.venueShort) + '</a>' : escapeHtml(w.venueShort)) +
          '</div>' : '') +
          (timeStr ? '<div style="font-size:12px;color:var(--muted);margin-top:3px"><i class="fa-regular fa-clock" style="margin-right:4px"></i>' + escapeHtml(timeStr) + '</div>' : '') +
          (w.assignedArtists && w.assignedArtists.length
            ? '<div style="font-size:12px;color:var(--muted);margin-top:3px"><i class="fa-solid fa-user-group" style="margin-right:4px"></i>' + escapeHtml(w.assignedArtists.join(', ')) + '</div>'
            : '<div style="font-size:12px;color:#e65100;margin-top:3px"><i class="fa-solid fa-triangle-exclamation" style="margin-right:4px"></i>Unassigned</div>') +
        '</div>';
    });
  }

  if (externals.length > 0) {
    html += '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--muted);margin:12px 0 6px">External Bookings</div>';
    externals.forEach(function(ex) {
      var timeStr = ex.isAllDay ? 'All day'
        : ((ex.startTime || '') + (ex.endTime ? '–' + ex.endTime : ''));
      html +=
        '<div style="border:1px solid var(--border-color,#e8ddd3);border-radius:8px;padding:10px 12px;margin-bottom:8px;background:var(--brand-bg-soft,#f6f1ee)">' +
          '<div style="font-weight:700;font-size:13px;color:var(--brand-dark)">' + escapeHtml(ex.title || 'External event') + '</div>' +
          (timeStr ? '<div style="font-size:12px;color:var(--muted);margin-top:3px"><i class="fa-regular fa-clock" style="margin-right:4px"></i>' + escapeHtml(timeStr) + '</div>' : '') +
        '</div>';
    });
  }

  if (artists.length > 0) {
    var availIcons = { available: '✅', partial: '🟡', unavailable: '🚫', not_working_day: '⚪', leave: '🏖️', blocked: '🚫' };
    html += '<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--muted);margin:12px 0 6px">Artist Availability</div>';
    artists.forEach(function(a) {
      var icon = availIcons[a.availability] || '⚪';
      var note = a.availabilityReason ? (' <span style="color:var(--muted);font-size:11px">— ' + escapeHtml(a.availabilityReason) + '</span>') : '';
      var jobBadge = a.jobCount > 0
        ? ' <span style="background:var(--brand-primary);color:#fff;border-radius:3px;padding:1px 5px;font-size:10px;font-weight:700">' + a.jobCount + ' job' + (a.jobCount > 1 ? 's' : '') + '</span>'
        : '';
      html += '<div style="font-size:13px;padding:4px 0;display:flex;align-items:center;gap:6px">' +
        icon + ' <span style="font-weight:600">' + escapeHtml(a.name) + '</span>' + jobBadge + note +
      '</div>';
    });
  }
  return html;
}

function setDeclinePreviewMode(mode) {
  var htmlPane = document.getElementById('declineEmailHtmlPane');
  var textPane = document.getElementById('declineEmailTextPane');
  var htmlBtn  = document.getElementById('declinePreviewHtmlBtn');
  var textBtn  = document.getElementById('declinePreviewTextBtn');
  if (!htmlPane || !textPane) return;
  if (mode === 'html') {
    // Sync edited plain text into the iframe before showing it
    var ta = document.getElementById('declineBodyOverride');
    var iframe = document.getElementById('declineEmailIframe');
    if (ta && iframe && _declineCtx) {
      var currentText = ta.value;
      var originalText = (_declineCtx.emailPreview || {}).bodyText || '';
      var hasEdits = currentText.trim() !== originalText.trim();
      var htmlToShow = hasEdits
        ? '<div style="font-family:Georgia,serif;color:#2f2524;padding:1.5rem;white-space:pre-line;font-size:15px;line-height:1.7">' + currentText.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') + '</div>'
        : ((_declineCtx.emailPreview || {}).bodyHtml || '');
      try { iframe.contentDocument.open(); iframe.contentDocument.write(htmlToShow); iframe.contentDocument.close(); }
      catch(e) { iframe.srcdoc = htmlToShow; }
    }
    htmlPane.style.display = 'block';
    textPane.style.display = 'none';
    if (htmlBtn) htmlBtn.style.fontWeight = '700';
    if (textBtn) textBtn.style.fontWeight = '400';
  } else {
    htmlPane.style.display = 'none';
    textPane.style.display = 'block';
    if (htmlBtn) htmlBtn.style.fontWeight = '400';
    if (textBtn) textBtn.style.fontWeight = '700';
  }
}

function sendFromDeclineModal() {
  if (!_declineCtx || !_declineCtx.clientId) {
    showBanner('Context missing — please reopen the modal.', 'error');
    return;
  }
  var btn = document.getElementById('declineConfirmBtn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Processing…'; }

  var notes      = (document.getElementById('declineNotesInput')  || {}).value || '';
  var sendEmail  = document.getElementById('declineSendEmail') ? document.getElementById('declineSendEmail').checked : true;
  var closeDate  = document.getElementById('declineCloseDate') ? document.getElementById('declineCloseDate').checked : false;
  var subject    = (document.getElementById('declineEmailSubject') || {}).value || '';

  // Read plain text regardless of which tab is active — compare to original to detect edits
  var bodyOverride = '';
  var ta = document.getElementById('declineBodyOverride');
  if (ta) {
    var editedText   = ta.value.trim();
    var originalText = ((_declineCtx.emailPreview || {}).bodyText || '').trim();
    if (editedText && editedText !== originalText) {
      bodyOverride = ta.value;
    }
  }

  jvBridge.callAdmin('bridge_admin_generic', {
    function: 'adminCrm_declineUnavailable',
    args: {
      clientId:                  _declineCtx.clientId,
      email:                     _declineCtx.email,
      notes:                     notes.trim(),
      sendEmail:                 sendEmail,
      customSubject:             subject,
      customBodyOverride:        bodyOverride || undefined,
      closeDateToFutureEnquiries: closeDate
    }
  })
  .then(function(res) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-circle-xmark"></i> Mark Unavailable'; }
    var data = (res && res.data) || res;
    var overlay = document.getElementById('declineCtxOverlay');
    if (overlay) overlay.remove();
    if (data && data.ok) {
      // FIX (2026-09-30, HANDOFF-proposal-builder-no-persistent-sent-confirmation-
      // 2026-09-30.md addendum, Jan Basaran incident): same gap as the proposal
      // send flow, a 5s toast was the ONLY feedback that a real decline email
      // went out and a date was closed. Build the confirmation text BEFORE
      // _declineCtx is nulled below (it holds the name/date this handler needs,
      // since state.currentBride/workspace are cleared right after).
      var _declineName = (_declineCtx && _declineCtx.name) || 'this bride';
      var _declineDateLabel = (_declineCtx && _declineCtx.weddingDateLabel) || '';
      var _declineMsg = 'Decline sent to ' + _declineName +
        (_declineDateLabel ? ', ' + _declineDateLabel + ' closed' : '') +
        ', ' + pbNowStamp_() +
        (data.emailSent === false ? ' (email NOT sent, check manually)' : '');
      showBanner(_declineMsg, data.emailSent === false ? 'warn' : 'success', true);
      // Mark declined bride in local state so the Declined badge is immediately visible.
      // patchBrideLocal_ already calls renderBrides() internally, matching the SENT/VOID
      // pattern elsewhere in this file (2026-09-01 consistency pass, no behaviour change,
      // this was previously a hand-rolled equivalent of the same patch+render).
      patchBrideLocal_(state.currentBride, { proposalStatus: 'DECLINED' });
      _declineCtx = null;
      state.currentBride = null;
      state.workspace    = null;
      renderStageBar();
      // FIX (same handoff, addendum): renderStageBar() alone never touched the
      // header title/meta/Save/Recalculate/Review & Send buttons; Jess saw
      // Jan Basaran's name and date still showing, with those buttons still
      // enabled, over this now-empty workspace panel underneath.
      pbResetHeaderToEmpty_();
      document.getElementById('workspace').innerHTML = '<div style="text-align:center;padding:3rem;color:var(--muted)">Select a bride from the list to begin.</div>';
      // Note: loadBrides() is intentionally not called here. proposalGetPendingBrides filters
      // out DECLINED rows, so calling it would make the bride vanish before the user sees the badge.
    } else {
      showBanner((data && data.message) || 'Failed to close lead', 'error');
    }
  })
  .catch(function(err) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-circle-xmark"></i> Mark Unavailable'; }
    var overlay = document.getElementById('declineCtxOverlay');
    if (overlay) overlay.remove();
    showBanner(getErrorMessage(err), 'error');
  });
}

/* =======================================================================
   PACKAGE OPTIONS -- full-workspace variant builder (2026-07-02 v2)
   Lives on page 3 (review stage). "Add package option" takes you into
   a full page-2 build session for that variant (party, services, tier,
   timing, travel, line items). "Save Option" writes the snapshot back
   and returns to page 3. Max 3 variants (base + 2 additional).
   ======================================================================= */

/**
 * Returns the current variants array from calc, or null when uninitialised.
 * @private
 */
function pvGetVariants_() {
  var calc = (state.workspace && state.workspace.calculation) || {};
  if (!Array.isArray(calc.packageVariants) || calc.packageVariants.length === 0) return null;
  return calc.packageVariants;
}

/** Returns the label for variant at idx, or a default. @private */
function pvGetVariantLabel_(idx) {
  var variants = pvGetVariants_();
  if (!variants || !variants[idx]) return 'Option ' + idx;
  return variants[idx].label || 'Option ' + idx;
}

/**
 * Writes variants back to state and marks draft dirty.
 * @private
 */
function pvSaveVariants_(variants) {
  if (!state.workspace || !state.workspace.calculation) return;
  state.workspace.calculation.packageVariants = variants;
  _draftDirty = true;
}

/**
 * Snapshot the current base page-2 state so it can be restored after editing a variant.
 * @private
 */
function pvSnapshotBaseState_() {
  // Strip packageVariants before cloning the calculation -- it belongs on the
  // top-level base calc only. Without this, every variant snapshot embeds a full
  // copy of every other variant (including itself on re-save), and each of THOSE
  // copies embeds another copy, growing the PROPOSAL_CALC_JSON blob unboundedly
  // with every Save Option cycle. Fixed alongside the D2 recalc-wipe fix
  // (2026-07-02) which is what started populating packageVariants on the
  // calculation object in the first place.
  var calcForSnapshot = state.workspace.calculation || {};
  if (calcForSnapshot.packageVariants) {
    calcForSnapshot = Object.assign({}, calcForSnapshot);
    delete calcForSnapshot.packageVariants;
  }
  return {
    party:               JSON.parse(JSON.stringify(state.workspace.party || [])),
    manualPartyMembers:  JSON.parse(JSON.stringify(state.manualPartyMembers || [])),
    noTrialRequired:     !!state.noTrialRequired,
    juniorBridesmaids:   Number(state.juniorBridesmaids) || 0,
    priceOverrides:      JSON.parse(JSON.stringify(state.priceOverrides || {})),
    editedLineItems:     state.editedLineItems ? JSON.parse(JSON.stringify(state.editedLineItems)) : null,
    travelSuppressions:  JSON.parse(JSON.stringify(state.travelSuppressions || {})),
    bookingTier:         state.bookingTier || 'jessica',
    calculation:         JSON.parse(JSON.stringify(calcForSnapshot))
  };
}

/**
 * Load a variant snapshot into the active page-2 state fields.
 * The variant's packageVariants array is preserved in the calculation so it
 * survives the swap (we write it back before loading variant calc).
 * @private
 */
function pvLoadVariantState_(snap) {
  // Preserve the packageVariants array -- it lives on the base calc, not the variant
  var existingVariants = state.workspace.calculation && state.workspace.calculation.packageVariants;

  state.workspace.party      = JSON.parse(JSON.stringify(snap.party || []));
  state.manualPartyMembers   = JSON.parse(JSON.stringify(snap.manualPartyMembers || []));
  state.noTrialRequired      = !!snap.noTrialRequired;
  state.juniorBridesmaids    = Number(snap.juniorBridesmaids) || 0;
  state.priceOverrides       = JSON.parse(JSON.stringify(snap.priceOverrides || {}));
  state.editedLineItems      = snap.editedLineItems ? JSON.parse(JSON.stringify(snap.editedLineItems)) : null;
  state.travelSuppressions   = JSON.parse(JSON.stringify(snap.travelSuppressions || {}));
  // JESS RULING 2026-11-03: unknown tier defaults to proTeam. A package variant saved before
  // bookingTier existed has no `snap.bookingTier`, and this used to resolve it to jessica -
  // reintroducing the same screen-vs-server split the initialiser fix above closes. brideWithMe is
  // set alongside it because this function restores state wholesale and previously left the
  // pricing input untouched, desyncing price from label on every variant load.
  state.bookingTier          = snap.bookingTier || 'proTeam';
  state.brideWithMe          = (state.bookingTier === 'jessica');
  state.workspace.calculation = JSON.parse(JSON.stringify(snap.calculation || {}));

  // Restore packageVariants onto the calc so they are not lost
  if (existingVariants && Array.isArray(existingVariants) && existingVariants.length > 0) {
    state.workspace.calculation.packageVariants = existingVariants;
  }
}

/**
 * Applies a package's OWN artist count to the active calculation.
 *
 * Each package variant is a standalone quote, so its staffing must not be inherited from
 * whichever package was open before it. This is the single place that decides what the
 * resolver will see as `stored` for the package now being edited.
 *
 * @param {Object|null} v the variant whose count to apply. Pass null for a brand-new
 *        package, which clears the count so the formula recomputes from its own party
 *        (Jess's decision, 2026-08-04: new packages start fresh, never inherit).
 * @private
 */
function pvApplyVariantArtists_(v) {
  var calc = state.workspace && state.workspace.calculation;
  if (!calc) return;

  var n   = v ? (Number(v.artistsRequired) || 0) : 0;
  var ovr = v ? (v.artistsRequiredOverride === true) : false;

  // This package's own notes (2026-08-04). Same standalone-package principle as the artist
  // count: load the notes belonging to the package being entered, and give a brand-new
  // package an empty list rather than inheriting the previously-open package's notes.
  calc.packageNotes = (v && Array.isArray(v.notes)) ? JSON.parse(JSON.stringify(v.notes)) : [];

  calc.artistsRequired = n;

  // The override markers travel with the package too. isAdminOverrideJs_ reads all three,
  // so a stale marker left over from the previous package would make this one's count
  // sticky even when the admin never touched it here. Clear all three, then re-assert
  // only if THIS package genuinely carries an override.
  calc.artistsRequiredOverride = false;
  delete calc.artistsRequiredOverriddenAt;
  if (calc.timingLocks && calc.timingLocks.artistsRequired) {
    calc.timingLocks = Object.assign({}, calc.timingLocks);
    delete calc.timingLocks.artistsRequired;
  }
  delete calc.weddingArtistsLockedAt;

  if (ovr) calc.artistsRequiredOverride = true;

  // Re-derive from this package's own party. With stored cleared (new package) the
  // resolver returns the formula value; with a real stored count it is respected.
  if (typeof recalcAndMerge_ === 'function') recalcAndMerge_();
}

/**
 * Enter full-page-2 builder mode for a variant.
 * Saves the current base state, loads the variant's snapshot, sets editingVariantIdx.
 */
function pvEnterVariantBuilder_(idx) {
  if (!state.workspace) return;
  var variants = pvGetVariants_();
  if (!variants || !variants[idx]) return;

  // Save base state so we can restore it on Cancel or Save
  state._pvBaseSnapshot = pvSnapshotBaseState_();
  state.editingVariantIdx = idx;

  // Load the variant's saved workspace snapshot into active state
  var v = variants[idx];
  var variantSnap = v.workspaceSnapshot || null;
  if (variantSnap) {
    pvLoadVariantState_(variantSnap);
    // Restore THIS package's own artist count (2026-08-04). Without this the shared calc
    // still holds whatever the previously-open package left behind, and because the
    // resolver treats any non-zero stored count as authoritative it can only hold or
    // climb, never fall for a smaller package. Confirmed live: party 8 -> 2 with artists
    // stuck at 3, and RECALCULATE would not bring it down.
    pvApplyVariantArtists_(v);
  } else {
    // First time editing this variant: start from the base state (already in active state)
    // Just set the bookingTier from the variant
    state.bookingTier = v.bookingTier || state.bookingTier || 'jessica';
    if (state.workspace.calculation) {
      state.workspace.calculation.bookingTier = state.bookingTier;
    }
    // FRESH FROM THE FORMULA for a brand-new package (Jess, 2026-08-04). A new package
    // does NOT inherit the base's artist count, and does NOT inherit an admin override
    // set on the base. Clearing the stored count to 0 makes the resolver fall through to
    // the freshly computed value for this package's own party, and it does so in BOTH
    // states of JV_PROPOSAL_ARTIST_COUNT_CORRECTABLE (stored===0 returns `fresh` on
    // either branch), which is what keeps this fix independent of that flag decision.
    pvApplyVariantArtists_(null);
  }

  // Go to page 2 in variant mode (no GAS flush -- variant edits are local until Save Option)
  state.activeStage = 'builder';
  renderStageBar();
  renderStageBuilder();
}

/**
 * Save the current page-2 state as the variant's workspace snapshot and return to page 3.
 */
function pvSaveAndExitVariantBuilder_() {
  if (!state.workspace) return;
  var idx = state.editingVariantIdx;
  if (idx == null) return;

  // Read the label input from the banner before snapshotting
  var labelInput = document.getElementById('pvVariantLabelInput');
  if (labelInput && labelInput.value.trim()) {
    pvRenameVariant_(idx, labelInput.value.trim());
  }

  // Capture the EXACT set of rows visible on page 2 right now. _currentDisplayItems
  // is the authoritative merge renderStageBuilder() just used to draw the table --
  // base calc items with price overrides applied, PLUS any custom rows added via
  // addLineItem() (Hair Styling Upgrade etc.), MINUS suppressed travel lines. Every
  // other source (state.editedLineItems alone, or calc.normalizedLineItems alone) is
  // only ONE side of that merge and silently drops the other side depending on
  // whether the admin has ever added a custom row this session. Whatever line items
  // are saved in the builder on page 2 MUST carry forward onto the variant exactly
  // as shown -- this is that guarantee. (2026-07-03, fixes both the original
  // Hair-Styling-Upgrade-dropped bug and the inverse case my first fix introduced.)
  var itemsForVariant = (_currentDisplayItems && _currentDisplayItems.length)
    ? JSON.parse(JSON.stringify(_currentDisplayItems))
    : (state.editedLineItems || (state.workspace.calculation && (state.workspace.calculation.normalizedLineItems || state.workspace.calculation.lineItems)) || []);

  // Capture current page-2 state as the variant snapshot
  var snap = pvSnapshotBaseState_();

  // Compute summary total from the same authoritative list.
  var subtotal = 0;
  for (var i = 0; i < itemsForVariant.length; i++) {
    subtotal += Number(itemsForVariant[i].qty || itemsForVariant[i].quantity || 1) * Number(itemsForVariant[i].unitPrice || itemsForVariant[i].rate || 0);
  }
  subtotal = Math.round(subtotal * 100) / 100;
  var gst   = Math.round(subtotal * 0.10 * 100) / 100;
  var total = subtotal + gst;

  // Restore base state first
  if (state._pvBaseSnapshot) {
    pvLoadVariantState_(state._pvBaseSnapshot);
    state._pvBaseSnapshot = null;
  }

  // Each package is a STANDALONE quote, so its staffing belongs to it (2026-08-04).
  // artistsRequired was previously carried only incidentally inside workspaceSnapshot.
  // calculation, never as a first-class per-variant value the way total/gst/lineItems
  // are. Promoting it here is what lets pvEnterVariantBuilder_ resolve THIS package's
  // own count instead of inheriting whatever the last-open package left in the shared
  // calc. `snap` was taken before the base state was restored above, so it holds this
  // variant's own calculation, not the base's.
  var snapCalc          = (snap && snap.calculation) || {};
  var variantArtists    = Number(snapCalc.artistsRequired) || 0;
  // An admin override belongs to the package it was set on. Carry the markers with the
  // variant so re-opening it respects the override, without it leaking to siblings.
  var variantArtistsOvr = (snapCalc.artistsRequiredOverride === true) ||
                          !!(snapCalc.timingLocks && snapCalc.timingLocks.artistsRequired) ||
                          !!snapCalc.weddingArtistsLockedAt;

  // Now write the variant snapshot back into the variants array
  var variants = pvGetVariants_();
  if (variants && variants[idx]) {
    variants[idx].workspaceSnapshot = snap;
    variants[idx].bookingTier       = snap.bookingTier;
    variants[idx].subtotal          = subtotal;
    variants[idx].gst               = gst;
    variants[idx].total             = total;
    variants[idx].lineItems         = itemsForVariant;
    variants[idx].artistsRequired   = variantArtists;
    variants[idx].artistsRequiredOverride = variantArtistsOvr;
    // This package's own notes (2026-08-04). Stored per-variant for the same reason as
    // lineItems and artistsRequired: each package is a standalone quote, so a note that
    // only applies to (say) the destination-wedding package must not appear on the others.
    // snapCalc is this variant's calculation, captured before the base was restored.
    variants[idx].notes = Array.isArray(snapCalc.packageNotes) ? snapCalc.packageNotes : [];
    pvSaveVariants_(variants);
  }

  state.editingVariantIdx = null;
  state.activeStage = 'review';
  renderStageBar();
  renderStageReview();
}

/**
 * Cancel variant editing and return to page 3 without saving changes.
 */
function pvCancelVariantBuilder_() {
  // Restore base state
  if (state._pvBaseSnapshot) {
    pvLoadVariantState_(state._pvBaseSnapshot);
    state._pvBaseSnapshot = null;
  }
  state.editingVariantIdx = null;
  state.activeStage = 'review';
  renderStageBar();
  renderStageReview();
}

/**
 * Builds the Package Options card rendered on page 3.
 * Shows existing variants and an Add option button (max 3 total).
 * Each variant can be renamed or deleted; clicking Edit opens a
 * full inline editor for that variant.
 */
/**
 * Builds the Package Options card on page 3.
 * Shows each variant as a row with label, total, Edit (full page-2 builder) and Remove.
 */
function buildVariantBuilderCard_() {
  var variants = pvGetVariants_();
  var MAX = 3;

  var html = '<div class="card" id="pvCard" style="margin-top:16px">' +
    '<div class="card-title"><i class="fa-solid fa-layer-group"></i> Package Options' +
      '<span style="margin-left:8px;font-size:0.75rem;font-weight:400;color:var(--muted)">Optional, up to 3 variants</span>' +
    '</div>' +
    '<div style="font-size:0.875rem;color:var(--muted);margin-bottom:12px">' +
      'Offer the bride a choice of packages. Click "Add package option" to build a full alternative proposal (party, tier, services, timing and travel). She sees a toggle in her viewer and picks whichever suits.' +
    '</div>';

  if (variants && variants.length > 0) {
    html += '<div id="pvList" style="margin-bottom:12px">';
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var vTotal = typeof v.total === 'number' ? v.total : null;
      var tierLabel = v.bookingTier === 'proTeam' ? 'Pro Team' : 'Jessica Vegas';
      var lineCount = (v.lineItems && v.lineItems.length) ? v.lineItems.length : 0;
      var meta = tierLabel + (lineCount ? ' \xb7 ' + lineCount + ' items' : '');
      html +=
        '<div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--brand-bg-soft,#f6f1ee)">' +
          '<div style="flex:1;min-width:0">' +
            '<div style="font-weight:600;font-size:14px;color:var(--brand-dark)">' +
              escapeHtml(v.label || 'Option ' + i) +
              (v.isBase ? ' <span style="font-size:11px;font-weight:400;color:var(--muted)">(base)</span>' : '') +
            '</div>' +
            '<div style="font-size:12px;color:var(--muted);margin-top:2px">' + escapeHtml(meta) + '</div>' +
          '</div>' +
          '<div style="font-weight:700;color:var(--brand-primary);min-width:90px;text-align:right">' +
            (vTotal != null ? formatCurrency(vTotal) : '<span style="font-weight:400;font-size:12px;color:var(--muted)">Not yet saved</span>') +
          '</div>' +
          '<button class="btn btn-ghost btn-sm" onclick="pvEnterVariantBuilder_(' + i + ')" style="padding:4px 10px">' +
            '<i class="fa-solid fa-pen"></i> Edit' +
          '</button>' +
          (!v.isBase
            ? '<button class="btn btn-ghost btn-sm" onclick="pvRemoveVariant_(' + i + ')" style="padding:4px 8px;color:var(--muted)" title="Remove option">' +
                '<i class="fa-solid fa-xmark"></i>' +
              '</button>'
            : '<div style="width:36px"></div>') +
        '</div>';
    }
    html += '</div>';
  }

  if (!variants || variants.length < MAX) {
    html += '<button class="btn btn-ghost btn-sm" onclick="pvAddVariant_()" style="margin-right:8px">' +
      '<i class="fa-solid fa-plus"></i> Add package option' +
    '</button>';
  }

  if (variants && variants.length > 0) {
    html += '<button class="btn btn-ghost btn-sm" onclick="pvClearAll_()" style="color:var(--muted)">' +
      '<i class="fa-solid fa-trash-can"></i> Remove all options' +
    '</button>';
  }

  html += '</div>';
  return html;
}

/**
 * Add a new variant slot and immediately enter the full page-2 builder for it.
 */
function pvAddVariant_() {
  if (!state.workspace) return;
  var calc     = (state.workspace && state.workspace.calculation) || {};
  var existing = Array.isArray(calc.packageVariants) ? calc.packageVariants.slice() : [];
  if (existing.length >= 3) return;

  if (existing.length === 0) {
    var baseItems = calc.normalizedLineItems || calc.lineItems || [];
    existing.push({
      id: 'base', label: 'Hair & Makeup', isBase: true,
      bookingTier: calc.bookingTier || state.bookingTier || 'jessica',
      lineItems: JSON.parse(JSON.stringify(baseItems)),
      subtotal: calc.subtotal || 0, gst: calc.gst || 0, total: calc.total || 0,
      workspaceSnapshot: pvSnapshotBaseState_(),
      paymentLinkUrl: '', xeroInvoiceId: ''
    });
  }

  var newIdx = existing.length;
  existing.push({
    id: 'custom_' + newIdx,
    label: 'Option ' + newIdx,
    isBase: false,
    // JESS RULING 2026-11-03: unknown tier defaults to proTeam. A new custom variant inheriting
    // from a base variant that carries no tier used to inherit jessica.
    bookingTier: existing[0].bookingTier || 'proTeam',
    lineItems: [], subtotal: null, gst: null, total: null,
    workspaceSnapshot: null,
    paymentLinkUrl: '', xeroInvoiceId: ''
  });

  pvSaveVariants_(existing);
  pvEnterVariantBuilder_(newIdx);
}

/** Remove a non-base variant by index. */
function pvRemoveVariant_(idx) {
  var calc     = (state.workspace && state.workspace.calculation) || {};
  var variants = Array.isArray(calc.packageVariants) ? calc.packageVariants.slice() : [];
  if (!variants[idx] || variants[idx].isBase) return;
  if (!confirm('Remove "' + (variants[idx].label || 'this option') + '"?')) return;
  variants.splice(idx, 1);
  if (variants.length <= 1) variants = [];
  pvSaveVariants_(variants);
  renderStageReview();
}

/** Remove all variants and return to single-package mode. */
function pvClearAll_() {
  if (!confirm('Remove all package options?')) return;
  pvSaveVariants_([]);
  renderStageReview();
}

/** Rename a variant label (used internally when saving from the builder). */
function pvRenameVariant_(idx, newLabel) {
  var calc = (state.workspace && state.workspace.calculation) || {};
  var variants = Array.isArray(calc.packageVariants) ? calc.packageVariants.slice() : [];
  if (!variants[idx]) return;
  variants[idx].label = (newLabel || '').trim() || variants[idx].label;
  pvSaveVariants_(variants);
}

renderStageBar();
// Sync the sort dropdown + filter pills to the initial state (All, newest enquiry).
try {
  var sortEl = document.getElementById('sortSelect');
  if (sortEl && state.activeSortOrder) sortEl.value = state.activeSortOrder;
  var initBtns = document.querySelectorAll('.filter-btn');
  for (var fi = 0; fi < initBtns.length; fi++) {
    var dfi = initBtns[fi].getAttribute('data-filter');
    initBtns[fi].classList.toggle('active', dfi === state.activeFilter);
  }
} catch (e) {}
// Immediate brideUid deep-link (2026-09-10, BUILD-proposal-builder-live-verify-followup
// item 2). Confirmed live: an admin arriving from a Pipeline card at
// ?bride=<email>&brideUid=<uid> sat on a BLANK "Select a bride to begin" screen for as
// long as the live proposalGetPendingBrides call took (8-15s documented, sometimes never
// resolved within 60s in the live verify pass) -- because the non-quiet deep-link pass
// that would trigger the durable-uid direct-load fallback only ran inside loadBrides()'s
// own .then(), and the quiet cache-only attempt deliberately shows nothing on a miss.
// A brideUid deep link does NOT need the bride list at all (proposalGetWorkspace resolves
// by brideUid directly, see pbApplyDeepLink_'s own comment above) -- so fire it here,
// immediately, in PARALLEL with loadBrides(), instead of waiting on it.
// pbApplyDeepLink_'s own _pbDeepLinkApplied/state.currentBride guards make this safe to
// compose with loadBrides()'s existing quiet + live deep-link attempts -- whichever
// resolves first wins, the others cleanly no-op.
function pbTryImmediateUidDeepLink_() {
  try {
    var params = new URLSearchParams(window.location.search);
    var deepLinkUid = (params.get('brideUid') || '').trim();
    if (!deepLinkUid) return; // no uid -- nothing to do here, loadBrides()'s own passes still own email-only links
    var deepLinkEmail = (params.get('bride') || '').trim().toLowerCase();
    // name= (2026-09-10, live-verify addendum): optional, display-only. When the
    // Pipeline card already sent it, shows the bride's real name in the loading header
    // instead of her raw email for the duration of the workspace load.
    var deepLinkName = (params.get('name') || '').trim();
    if (_pbDeepLinkApplied || state.currentBride) return;
    _pbDeepLinkApplied = true;
    selectBride(deepLinkEmail || deepLinkUid, deepLinkUid, deepLinkName);
  } catch (e) { /* deep-link parse error, non-fatal -- loadBrides()'s own passes still run */ }
}

/**
 * Delegated click handler for the bride list (2026-09-30, HANDOFF-proposal-
 * builder-apostrophe-name-bride-not-found-2026-09-30.md). Attached ONCE to the
 * static #brideList container -- it survives every renderBrides() re-render,
 * since only the container's CHILDREN are replaced via innerHTML, never the
 * container itself. Reads the bride's name back from the data-bride-name
 * attribute renderBrides() writes on each .bride-item (see that function),
 * so the name only ever goes through the browser's normal HTML attribute
 * parsing -- never re-embedded as literal JS source text the way the old
 * inline onclick="selectBride('<name>')" did.
 * @private
 */
function pbInitBrideListDelegatedClick_() {
  var list = document.getElementById('brideList');
  if (!list) return;
  list.addEventListener('click', function(e) {
    var item = e.target && e.target.closest ? e.target.closest('.bride-item') : null;
    if (!item || !list.contains(item)) return;
    var name = (item.dataset && item.dataset.brideName) || item.getAttribute('data-bride-name') || '';
    if (name) selectBride(name);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    pbInitBrideListDelegatedClick_();
    pbTryImmediateUidDeepLink_();
    loadBrides();
  });
} else {
  pbInitBrideListDelegatedClick_();
  pbTryImmediateUidDeepLink_();
  loadBrides();
}

// Move-aware travel origin (2026-07-14): fetch once at page load, not per-bride.
// Governs whether the Travel Origin card ever renders (only when Jess has a future
// address on file) and seeds the read-only global-default label in the card. Failure
// here is non-fatal, the card simply stays hidden (same as if nothing is configured).
//
// CDN fast path (2026-10-01, GAS-shrink build): this call previously ALWAYS hit
// the GAS bridge (proposalTravelOrigin_getStatus, which itself already has a
// 5-min GAS-side CacheService layer -- the browser still paid a full GAS
// cold-start just to read that cache). Trace: this value is a single admin-set
// global toggle (current vs future travel address), rarely changed, mirrored
// GAS-side into a shop metafield on every real change (proposalTravelOriginCache_
// write_, ProposalBuilderAPI.gs). FLAG-GATED OFF (window.JV_TRAVEL_ORIGIN_CACHE_
// FASTPATH ships false in sections/jv-proposal-builder.liquid) until someone
// verifies the island's payload matches a live call and flips it -- flag off is
// byte-identical to the prior behaviour below. Even with the flag on, the live
// GAS call ALWAYS still fires in the background to self-correct (same stale-
// while-revalidate discipline as every other cache on this page) -- a cache hit
// only means the card doesn't have to wait for it before it can render.
loadTravelOriginStatus_();
function loadTravelOriginStatus_() {
  if (window.JV_TRAVEL_ORIGIN_CACHE_FASTPATH === true) {
    try {
      var islandEl = document.getElementById('jv-pb-travel-origin-cache');
      if (islandEl) {
        var cached = JSON.parse(islandEl.textContent || islandEl.innerHTML);
        if (cached && cached.ok !== false) {
          state.travelOriginFutureAddressSet = !!cached.futureAddressSet;
          state.travelOriginGlobalDefault = cached.globalDefault === 'future' ? 'future' : 'current';
        }
      }
    } catch (e) { /* island parse failed; live fetch below still covers it */ }
  }
  jvBridge.callAdmin('proposalTravelOrigin_getStatus', {})
    .then(function(res) {
      var data = (res && res.data) || res;
      if (!data || data.ok === false) return;
      state.travelOriginFutureAddressSet = !!data.futureAddressSet;
      state.travelOriginGlobalDefault = data.globalDefault === 'future' ? 'future' : 'current';
    })
    .catch(function() { /* non-fatal, card stays hidden (or keeps the cache-painted value) */ });
}

// deploy-sync: 8c4dc71
